// Curated verse pool with the metadata needed to generate verse-specific
// questions (speaker, audience, surrounding context, theme, a key word) plus
// "did you know" facts for shame-free wrong-answer reveals.
//
// Text here is the public-domain Berean Standard Bible. In connected mode the
// exact passage can also be fetched live via the bible-api (see config.ts);
// the generator only relies on the fields below, so it stays self-consistent.
//
// Scale: this pool is large (250+ entries) and the date -> verse mapping uses a
// no-repeat rotation (see questions.ts getVerseForDate), so every verse in the
// pool is shown once before any repeats, reshuffled each full cycle.

export type Testament = 'OT' | 'NT'

export interface VerseSeed {
  reference: string
  book: string
  chapter: number
  verseStart: number
  verseEnd?: number
  text: string
  testament: Testament
  speaker: string
  audience: string
  before: string
  after: string
  theme: string
  keyword: string // a distinctive word appearing in `text`, for fill-in-the-blank
  facts: string[]
}

// The 66 books in canonical order. Used to sort the Focus-practice book picker
// (so books read Genesis → Revelation rather than in pool-insertion order) and
// by scripts/check-pool.mjs, which fails the build if any book loses coverage.
export const BIBLE_BOOKS: string[] = [
  'Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy',
  'Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings',
  '1 Chronicles', '2 Chronicles', 'Ezra', 'Nehemiah', 'Esther',
  'Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 'Song of Solomon',
  'Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel',
  'Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum',
  'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi',
  'Matthew', 'Mark', 'Luke', 'John', 'Acts',
  'Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians',
  'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians',
  '1 Timothy', '2 Timothy', 'Titus', 'Philemon', 'Hebrews', 'James',
  '1 Peter', '2 Peter', '1 John', '2 John', '3 John', 'Jude', 'Revelation',
]

// Every book carries at least this many verses. scripts/check-pool.mjs enforces
// it, so a stale-base merge can't silently shrink a book back to one verse.
export const MIN_VERSES_PER_BOOK = 10

export const VERSE_POOL: VerseSeed[] = [
  // ————————————————————————— Old Testament: Torah —————————————————————————
  {
    reference: 'Genesis 1:1', book: 'Genesis', chapter: 1, verseStart: 1,
    text: 'In the beginning God created the heavens and the earth.',
    testament: 'OT', speaker: 'The narrator (Moses, traditionally)', audience: 'Readers of Israel',
    before: 'Nothing — these are the opening words of the Bible.',
    after: 'The earth is described as formless and empty, with darkness over the deep.',
    theme: 'Creation', keyword: 'beginning',
    facts: ['These are the very first words of the entire Bible.', 'The Hebrew opening word is "Bereshit," meaning "in the beginning."'],
  },
  {
    reference: 'Genesis 1:27', book: 'Genesis', chapter: 1, verseStart: 27,
    text: 'So God created man in His own image; in the image of God He created him; male and female He created them.',
    testament: 'OT', speaker: 'The narrator', audience: 'Readers of Israel',
    before: 'God announces the plan to make mankind in His image.',
    after: 'God blesses them and tells them to be fruitful and multiply.',
    theme: 'The image of God in humanity', keyword: 'image',
    facts: ['This verse is the basis for the belief that every human has inherent dignity.'],
  },
  {
    reference: 'Genesis 50:20', book: 'Genesis', chapter: 50, verseStart: 20,
    text: 'As for you, what you intended against me for evil, God intended for good, to accomplish what is now being done, the saving of many lives.',
    testament: 'OT', speaker: 'Joseph', audience: 'His brothers',
    before: 'Joseph’s brothers fear he will take revenge now that their father has died.',
    after: 'Joseph reassures and comforts his brothers.',
    theme: 'God works evil for good', keyword: 'good',
    facts: ['Joseph’s brothers had sold him into slavery years earlier.'],
  },
  {
    reference: 'Exodus 14:14', book: 'Exodus', chapter: 14, verseStart: 14,
    text: 'The LORD will fight for you; you need only to be still.',
    testament: 'OT', speaker: 'Moses', audience: 'The Israelites',
    before: 'Israel is trapped at the Red Sea as Pharaoh’s army closes in.',
    after: 'God parts the Red Sea so Israel can cross on dry ground.',
    theme: 'God fights for His people', keyword: 'still',
    facts: ['This is spoken moments before the parting of the Red Sea.'],
  },
  {
    reference: 'Exodus 20:3', book: 'Exodus', chapter: 20, verseStart: 3,
    text: 'You shall have no other gods before Me.',
    testament: 'OT', speaker: 'God', audience: 'Israel at Mount Sinai',
    before: 'God reminds Israel He brought them out of Egypt.',
    after: 'God forbids making or worshiping idols.',
    theme: 'Worship God alone', keyword: 'gods',
    facts: ['This is the first of the Ten Commandments.'],
  },
  {
    reference: 'Numbers 6:24', book: 'Numbers', chapter: 6, verseStart: 24, verseEnd: 26,
    text: 'The LORD bless you and keep you; the LORD make His face shine upon you and be gracious to you; the LORD lift up His countenance upon you and give you peace.',
    testament: 'OT', speaker: 'God (through Aaron)', audience: 'The Israelites',
    before: 'God tells Moses how the priests are to bless Israel.',
    after: 'God says His name will rest on the people and He will bless them.',
    theme: 'Blessing and peace', keyword: 'peace',
    facts: ['Known as the Aaronic (or priestly) blessing, still used in worship today.'],
  },
  {
    reference: 'Deuteronomy 6:5', book: 'Deuteronomy', chapter: 6, verseStart: 5,
    text: 'Love the LORD your God with all your heart and with all your soul and with all your strength.',
    testament: 'OT', speaker: 'Moses', audience: 'Israel',
    before: 'Moses declares that the LORD is one.',
    after: 'Moses tells the people to keep these words on their hearts and teach them to their children.',
    theme: 'Wholehearted love for God', keyword: 'strength',
    facts: ['This is part of the Shema, Israel’s central daily confession of faith.', 'Jesus called this the greatest commandment.'],
  },
  {
    reference: 'Deuteronomy 31:6', book: 'Deuteronomy', chapter: 31, verseStart: 6,
    text: 'Be strong and courageous. Do not be afraid or terrified because of them, for the LORD your God goes with you; He will never leave you nor forsake you.',
    testament: 'OT', speaker: 'Moses', audience: 'Israel',
    before: 'Moses tells Israel they will cross the Jordan into the promised land.',
    after: 'Moses commissions Joshua to lead the people.',
    theme: 'Courage through God’s presence', keyword: 'courageous',
    facts: ['Moses spoke this near the end of his life, before Israel entered Canaan.'],
  },
  {
    reference: 'Joshua 1:9', book: 'Joshua', chapter: 1, verseStart: 9,
    text: 'Have I not commanded you? Be strong and courageous. Do not be afraid; do not be discouraged, for the LORD your God will be with you wherever you go.',
    testament: 'OT', speaker: 'God', audience: 'Joshua',
    before: 'God tells Joshua to lead Israel across the Jordan into the promised land.',
    after: 'Joshua orders the officers to prepare the people to cross.',
    theme: 'Courage through God’s presence', keyword: 'courageous',
    facts: ['God repeats "be strong and courageous" several times to Joshua in this chapter.'],
  },
  {
    reference: 'Joshua 24:15', book: 'Joshua', chapter: 24, verseStart: 15,
    text: 'But as for me and my house, we will serve the LORD.',
    testament: 'OT', speaker: 'Joshua', audience: 'The tribes of Israel',
    before: 'Joshua challenges Israel to choose whom they will serve.',
    after: 'The people respond that they too will serve the LORD.',
    theme: 'Commitment to serve God', keyword: 'serve',
    facts: ['Joshua gave this challenge in his farewell speech at Shechem.'],
  },
  // ————————————————————————— History & Wisdom —————————————————————————
  {
    reference: '1 Samuel 16:7', book: '1 Samuel', chapter: 16, verseStart: 7,
    text: 'The LORD does not look at the things man looks at. Man looks at the outward appearance, but the LORD looks at the heart.',
    testament: 'OT', speaker: 'God (to Samuel)', audience: 'Samuel',
    before: 'Samuel assumes the tall eldest son must be God’s chosen king.',
    after: 'God chooses David, the youngest, as the future king.',
    theme: 'God sees the heart', keyword: 'heart',
    facts: ['This is spoken while choosing David to be anointed king over Israel.'],
  },
  {
    reference: '1 Chronicles 16:11', book: '1 Chronicles', chapter: 16, verseStart: 11,
    text: 'Seek out the LORD and His strength; seek His face always.',
    testament: 'OT', speaker: 'David', audience: 'Israel',
    before: 'David appoints singers to give thanks as the ark is brought to Jerusalem.',
    after: 'The song recalls God’s covenant and wonderful works.',
    theme: 'Seeking God', keyword: 'seek',
    facts: ['These words are part of a psalm of thanks David gave when the ark came to Jerusalem.'],
  },
  {
    reference: '2 Chronicles 7:14', book: '2 Chronicles', chapter: 7, verseStart: 14,
    text: 'If My people who are called by My name will humble themselves and pray and seek My face and turn from their wicked ways, then I will hear from heaven, forgive their sin, and heal their land.',
    testament: 'OT', speaker: 'God', audience: 'Solomon',
    before: 'God appears to Solomon at night after the temple is dedicated.',
    after: 'God promises His eyes and heart will always be on the temple.',
    theme: 'Repentance and healing', keyword: 'humble',
    facts: ['God gave this promise after Solomon dedicated the first temple.'],
  },
  {
    reference: 'Nehemiah 8:10', book: 'Nehemiah', chapter: 8, verseStart: 10,
    text: 'Do not grieve, for the joy of the LORD is your strength.',
    testament: 'OT', speaker: 'Nehemiah', audience: 'The returned exiles',
    before: 'The people weep as they hear the Law read for the first time in years.',
    after: 'The people celebrate with a great feast and much joy.',
    theme: 'Joy as strength', keyword: 'joy',
    facts: ['The people wept because they realized how far they had drifted from God’s Law.'],
  },
  {
    reference: 'Job 19:25', book: 'Job', chapter: 19, verseStart: 25,
    text: 'But I know that my Redeemer lives, and in the end He will stand upon the earth.',
    testament: 'OT', speaker: 'Job', audience: 'His friends',
    before: 'Job’s friends accuse him of hidden sin as the cause of his suffering.',
    after: 'Job longs to see God with his own eyes.',
    theme: 'Hope in a living Redeemer', keyword: 'Redeemer',
    facts: ['Job says this in the middle of immense personal loss and suffering.'],
  },
  {
    reference: 'Psalm 23:1', book: 'Psalms', chapter: 23, verseStart: 1,
    text: 'The LORD is my shepherd; I shall not want.',
    testament: 'OT', speaker: 'David', audience: 'God / worshipers',
    before: 'This is the opening line of one of the best-loved psalms.',
    after: 'David says God makes him lie down in green pastures and leads him beside still waters.',
    theme: 'God’s provision and care', keyword: 'shepherd',
    facts: ['David wrote this psalm; he had been a shepherd himself as a boy.', 'Psalm 23 is one of the most memorized passages in the world.'],
  },
  {
    reference: 'Psalm 23:4', book: 'Psalms', chapter: 23, verseStart: 4,
    text: 'Even though I walk through the valley of the shadow of death, I will fear no evil, for You are with me; Your rod and Your staff, they comfort me.',
    testament: 'OT', speaker: 'David', audience: 'God',
    before: 'David says God leads him in paths of righteousness.',
    after: 'David says God prepares a table before him in the presence of his enemies.',
    theme: 'God’s presence in dark times', keyword: 'valley',
    facts: ['This verse is often read at funerals for its comfort in the face of death.'],
  },
  {
    reference: 'Psalm 27:1', book: 'Psalms', chapter: 27, verseStart: 1,
    text: 'The LORD is my light and my salvation— whom shall I fear? The LORD is the stronghold of my life— whom shall I dread?',
    testament: 'OT', speaker: 'David', audience: 'God / worshipers',
    before: 'This is the opening of a psalm of confidence.',
    after: 'David says that even if an army besieges him, his heart will not fear.',
    theme: 'Fearlessness in God', keyword: 'light',
    facts: ['David wrote many psalms while being pursued by enemies.'],
  },
  {
    reference: 'Psalm 34:8', book: 'Psalms', chapter: 34, verseStart: 8,
    text: 'Taste and see that the LORD is good; blessed is the man who takes refuge in Him.',
    testament: 'OT', speaker: 'David', audience: 'Worshipers',
    before: 'David praises God for delivering him from all his fears.',
    after: 'David invites the reader to fear the LORD and lack no good thing.',
    theme: 'Experiencing God’s goodness', keyword: 'taste',
    facts: ['David wrote this after escaping from a dangerous situation with a foreign king.'],
  },
  {
    reference: 'Psalm 37:4', book: 'Psalms', chapter: 37, verseStart: 4,
    text: 'Delight yourself in the LORD, and He will give you the desires of your heart.',
    testament: 'OT', speaker: 'David', audience: 'Worshipers',
    before: 'David urges the reader not to fret over those who do wrong.',
    after: 'David says to commit your way to the LORD and trust in Him.',
    theme: 'Delighting in God', keyword: 'delight',
    facts: ['Psalm 37 contrasts the lasting security of the righteous with the fading of the wicked.'],
  },
  {
    reference: 'Psalm 46:1', book: 'Psalms', chapter: 46, verseStart: 1,
    text: 'God is our refuge and strength, an ever-present help in times of trouble.',
    testament: 'OT', speaker: 'The sons of Korah', audience: 'Worshipers',
    before: 'This is the opening of a psalm about God’s protection.',
    after: 'The psalm says we will not fear even if the earth gives way.',
    theme: 'God as refuge', keyword: 'refuge',
    facts: ['This psalm inspired Martin Luther’s hymn "A Mighty Fortress Is Our God."'],
  },
  {
    reference: 'Psalm 46:10', book: 'Psalms', chapter: 46, verseStart: 10,
    text: 'Be still and know that I am God; I will be exalted among the nations, I will be exalted over the earth.',
    testament: 'OT', speaker: 'God', audience: 'The nations / worshipers',
    before: 'The psalm describes God making wars cease to the ends of the earth.',
    after: 'The psalm declares the LORD of Hosts is with us.',
    theme: 'Stillness before God', keyword: 'still',
    facts: ['The phrase "be still" can also carry the sense of "let go" or "cease striving."'],
  },
  {
    reference: 'Psalm 51:10', book: 'Psalms', chapter: 51, verseStart: 10,
    text: 'Create in me a clean heart, O God, and renew a right spirit within me.',
    testament: 'OT', speaker: 'David', audience: 'God',
    before: 'David confesses his sin and asks God for mercy.',
    after: 'David asks God not to cast him from His presence.',
    theme: 'Repentance and renewal', keyword: 'clean',
    facts: ['David wrote this psalm of repentance after the prophet Nathan confronted his sin.'],
  },
  {
    reference: 'Psalm 91:1', book: 'Psalms', chapter: 91, verseStart: 1,
    text: 'He who dwells in the shelter of the Most High will abide in the shadow of the Almighty.',
    testament: 'OT', speaker: 'The psalmist', audience: 'Worshipers',
    before: 'This opens a psalm about God’s protection.',
    after: 'The psalmist calls the LORD his refuge and fortress.',
    theme: 'God’s protection', keyword: 'shelter',
    facts: ['Psalm 91 is often called the psalm of protection.'],
  },
  {
    reference: 'Psalm 118:24', book: 'Psalms', chapter: 118, verseStart: 24,
    text: 'This is the day the LORD has made; we will rejoice and be glad in it.',
    testament: 'OT', speaker: 'The psalmist', audience: 'Worshipers',
    before: 'The psalm celebrates the stone the builders rejected becoming the cornerstone.',
    after: 'The psalmist cries out for the LORD to save and grant success.',
    theme: 'Rejoicing in God’s day', keyword: 'day',
    facts: ['Psalm 118 was likely sung during festivals in ancient Israel.'],
  },
  {
    reference: 'Psalm 119:105', book: 'Psalms', chapter: 119, verseStart: 105,
    text: 'Your word is a lamp to my feet and a light to my path.',
    testament: 'OT', speaker: 'The psalmist', audience: 'God',
    before: 'The psalmist vows to keep God’s righteous laws.',
    after: 'The psalmist asks God to preserve his life according to His word.',
    theme: 'God’s word as guidance', keyword: 'lamp',
    facts: ['Psalm 119 is the longest chapter in the Bible, almost entirely about God’s word.'],
  },
  {
    reference: 'Psalm 139:14', book: 'Psalms', chapter: 139, verseStart: 14,
    text: 'I praise You, for I am fearfully and wonderfully made. Marvelous are Your works, and I know this very well.',
    testament: 'OT', speaker: 'David', audience: 'God',
    before: 'David marvels that God formed him in his mother’s womb.',
    after: 'David says all his days were written in God’s book before one came to be.',
    theme: 'God’s intimate care in creating us', keyword: 'wonderfully',
    facts: ['Psalm 139 celebrates that God knows us completely and is always present.'],
  },
  {
    reference: 'Psalm 147:3', book: 'Psalms', chapter: 147, verseStart: 3,
    text: 'He heals the brokenhearted and binds up their wounds.',
    testament: 'OT', speaker: 'The psalmist', audience: 'Worshipers',
    before: 'The psalm calls the people to praise God who rebuilds Jerusalem.',
    after: 'The psalm says God counts the stars and calls them each by name.',
    theme: 'God heals the hurting', keyword: 'brokenhearted',
    facts: ['This psalm was likely written after the exiles returned to rebuild Jerusalem.'],
  },
  {
    reference: 'Proverbs 3:5', book: 'Proverbs', chapter: 3, verseStart: 5, verseEnd: 6,
    text: 'Trust in the LORD with all your heart, and lean not on your own understanding; in all your ways acknowledge Him, and He will make your paths straight.',
    testament: 'OT', speaker: 'Solomon', audience: 'His son / the reader',
    before: 'Solomon urges the reader not to forget his teaching.',
    after: 'Solomon warns against being wise in your own eyes.',
    theme: 'Trusting God over self', keyword: 'understanding',
    facts: ['Proverbs is largely a father’s wisdom addressed to his son.'],
  },
  {
    reference: 'Proverbs 16:3', book: 'Proverbs', chapter: 16, verseStart: 3,
    text: 'Commit your works to the LORD, and your plans will be achieved.',
    testament: 'OT', speaker: 'Solomon', audience: 'The reader',
    before: 'The proverb notes that people plan their way but the LORD directs their steps.',
    after: 'The proverb says the LORD works out everything to its proper end.',
    theme: 'Committing plans to God', keyword: 'commit',
    facts: ['Proverbs 16 has many sayings about God directing human plans.'],
  },
  {
    reference: 'Proverbs 18:10', book: 'Proverbs', chapter: 18, verseStart: 10,
    text: 'The name of the LORD is a strong tower; the righteous run to it and are safe.',
    testament: 'OT', speaker: 'Solomon', audience: 'The reader',
    before: 'The proverbs contrast the words of the wise and the foolish.',
    after: 'The next proverb notes the rich see their wealth as a fortified city.',
    theme: 'God as refuge', keyword: 'tower',
    facts: ['In the ancient world a strong tower was the safest place in a walled city.'],
  },
  {
    reference: 'Proverbs 22:6', book: 'Proverbs', chapter: 22, verseStart: 6,
    text: 'Train up a child in the way he should go, and when he is old he will not depart from it.',
    testament: 'OT', speaker: 'Solomon', audience: 'The reader',
    before: 'The proverbs commend a good name above great riches.',
    after: 'The proverbs warn that the borrower is servant to the lender.',
    theme: 'Raising children well', keyword: 'child',
    facts: ['This proverb is often quoted about the lasting impact of early teaching.'],
  },
  {
    reference: 'Ecclesiastes 3:1', book: 'Ecclesiastes', chapter: 3, verseStart: 1,
    text: 'To everything there is a season, and a time for every purpose under heaven.',
    testament: 'OT', speaker: 'The Preacher (Qoheleth)', audience: 'The reader',
    before: 'The Preacher reflects on the repetitive rhythms of life.',
    after: 'A list follows: a time to be born and a time to die, a time to plant and to uproot.',
    theme: 'Seasons of life', keyword: 'season',
    facts: ['These words inspired the well-known song "Turn! Turn! Turn!"'],
  },
  {
    reference: 'Isaiah 40:31', book: 'Isaiah', chapter: 40, verseStart: 31,
    text: 'But those who wait upon the LORD will renew their strength; they will mount up with wings like eagles; they will run and not grow weary; they will walk and not faint.',
    testament: 'OT', speaker: 'Isaiah', audience: 'Israel in exile',
    before: 'Isaiah says even youths grow tired and weary.',
    after: 'Isaiah continues comforting the people with God’s greatness.',
    theme: 'Renewed strength from God', keyword: 'eagles',
    facts: ['Isaiah 40 opens with "Comfort, comfort My people," a message of hope for exiles.'],
  },
  {
    reference: 'Isaiah 41:10', book: 'Isaiah', chapter: 41, verseStart: 10,
    text: 'Do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with My righteous right hand.',
    testament: 'OT', speaker: 'God', audience: 'Israel',
    before: 'God assures Israel He has chosen them and not rejected them.',
    after: 'God says all who rage against Israel will come to nothing.',
    theme: 'God’s presence removes fear', keyword: 'uphold',
    facts: ['"Do not fear" is one of the most repeated commands in the Bible.'],
  },
  {
    reference: 'Isaiah 53:5', book: 'Isaiah', chapter: 53, verseStart: 5,
    text: 'But He was pierced for our transgressions, He was crushed for our iniquities; the punishment that brought us peace was upon Him, and by His stripes we are healed.',
    testament: 'OT', speaker: 'Isaiah', audience: 'Israel',
    before: 'Isaiah describes the servant as despised and rejected.',
    after: 'Isaiah says we all like sheep have gone astray.',
    theme: 'The suffering servant', keyword: 'pierced',
    facts: ['Isaiah 53 is often called the "Suffering Servant" passage and is applied to Jesus in the New Testament.'],
  },
  {
    reference: 'Jeremiah 29:11', book: 'Jeremiah', chapter: 29, verseStart: 11,
    text: 'For I know the plans I have for you, declares the LORD, plans to prosper you and not to harm you, plans to give you a future and a hope.',
    testament: 'OT', speaker: 'God (through Jeremiah)', audience: 'The exiles in Babylon',
    before: 'God tells the exiles to settle in Babylon and seek the peace of the city.',
    after: 'God promises that when they seek Him with all their heart, they will find Him.',
    theme: 'God’s good plans', keyword: 'hope',
    facts: ['This was written to people who would spend 70 years in exile before returning home.'],
  },
  {
    reference: 'Lamentations 3:22', book: 'Lamentations', chapter: 3, verseStart: 22, verseEnd: 23,
    text: 'Because of the LORD’s loving devotion we are not consumed, for His mercies never fail. They are new every morning; great is Your faithfulness.',
    testament: 'OT', speaker: 'Jeremiah', audience: 'The people of Judah',
    before: 'Jeremiah pours out grief over the destruction of Jerusalem.',
    after: 'Jeremiah says the LORD is his portion, so he will hope in Him.',
    theme: 'New mercies each day', keyword: 'morning',
    facts: ['These words of hope sit in the middle of a book of grief over a fallen city.'],
  },
  {
    reference: 'Micah 6:8', book: 'Micah', chapter: 6, verseStart: 8,
    text: 'He has shown you, O man, what is good. And what does the LORD require of you but to act justly, to love mercy, and to walk humbly with your God?',
    testament: 'OT', speaker: 'Micah', audience: 'Israel',
    before: 'Micah asks what offering could please the LORD.',
    after: 'Micah warns the city about dishonest scales and violence.',
    theme: 'Justice, mercy, humility', keyword: 'humbly',
    facts: ['Micah was a prophet who spoke out against injustice toward the poor.'],
  },
  {
    reference: 'Habakkuk 3:19', book: 'Habakkuk', chapter: 3, verseStart: 19,
    text: 'The Lord GOD is my strength; He makes my feet like the feet of a deer and enables me to walk on the heights.',
    testament: 'OT', speaker: 'Habakkuk', audience: 'God / the reader',
    before: 'Habakkuk resolves to rejoice in God even if the crops fail.',
    after: 'The book closes with this song of trust.',
    theme: 'Joy in hardship', keyword: 'strength',
    facts: ['Habakkuk chose to rejoice in God even while expecting hard times ahead.'],
  },
  {
    reference: 'Zephaniah 3:17', book: 'Zephaniah', chapter: 3, verseStart: 17,
    text: 'The LORD your God is among you; He is mighty to save. He will take great delight in you; He will quiet you with His love; He will rejoice over you with singing.',
    testament: 'OT', speaker: 'Zephaniah', audience: 'Jerusalem',
    before: 'Zephaniah tells Jerusalem to sing and not fear.',
    after: 'God promises to gather His scattered people and restore them.',
    theme: 'God delights in His people', keyword: 'delight',
    facts: ['This is one of the few verses that pictures God singing over His people.'],
  },
  // ————————————————————————— New Testament: Gospels —————————————————————————
  {
    reference: 'Matthew 4:4', book: 'Matthew', chapter: 4, verseStart: 4,
    text: 'It is written: Man shall not live on bread alone, but on every word that comes from the mouth of God.',
    testament: 'NT', speaker: 'Jesus', audience: 'The tempter',
    before: 'After fasting forty days in the wilderness, Jesus is urged to turn stones into bread.',
    after: 'The devil takes Jesus to the holy city to tempt Him again.',
    theme: 'Trusting God’s word', keyword: 'bread',
    facts: ['Jesus answers each temptation by quoting Scripture, here from Deuteronomy 8:3.'],
  },
  {
    reference: 'Matthew 5:6', book: 'Matthew', chapter: 5, verseStart: 6,
    text: 'Blessed are those who hunger and thirst for righteousness, for they will be filled.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus blesses the meek, who will inherit the earth.',
    after: 'Jesus blesses the merciful, who will be shown mercy.',
    theme: 'Longing for righteousness', keyword: 'righteousness',
    facts: ['This is one of the Beatitudes that open the Sermon on the Mount.'],
  },
  {
    reference: 'Matthew 5:8', book: 'Matthew', chapter: 5, verseStart: 8,
    text: 'Blessed are the pure in heart, for they will see God.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus blesses the merciful, who will be shown mercy.',
    after: 'Jesus blesses the peacemakers, who will be called sons of God.',
    theme: 'Purity of heart', keyword: 'pure',
    facts: ['This Beatitude echoes Psalm 24, which asks who may ascend the hill of the Lord.'],
  },
  {
    reference: 'Matthew 5:9', book: 'Matthew', chapter: 5, verseStart: 9,
    text: 'Blessed are the peacemakers, for they will be called sons of God.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus continues the Beatitudes, blessing the merciful and pure in heart.',
    after: 'Jesus blesses those who are persecuted for righteousness.',
    theme: 'Peacemaking', keyword: 'peacemakers',
    facts: ['This is part of the Sermon on the Mount, Jesus’ most famous teaching.'],
  },
  {
    reference: 'Matthew 5:13', book: 'Matthew', chapter: 5, verseStart: 13,
    text: 'You are the salt of the earth. But if the salt loses its savor, how can it be made salty again? It is no longer good for anything, except to be thrown out and trampled by men.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus finishes the Beatitudes, promising reward to the persecuted.',
    after: 'Jesus calls His followers the light of the world.',
    theme: 'Being salt', keyword: 'salt',
    facts: ['In the ancient world salt was a precious preservative, so the image spoke of great value.'],
  },
  {
    reference: 'Matthew 5:14', book: 'Matthew', chapter: 5, verseStart: 14,
    text: 'You are the light of the world. A city on a hill cannot be hidden.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus calls His followers the salt of the earth.',
    after: 'Jesus says a lamp is set on a stand to give light to all.',
    theme: 'Being light', keyword: 'light',
    facts: ['Jesus later calls Himself the Light of the world, and here shares that light with His people.'],
  },
  {
    reference: 'Matthew 5:16', book: 'Matthew', chapter: 5, verseStart: 16,
    text: 'In the same way, let your light shine before men, that they may see your good deeds and glorify your Father in heaven.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus says no one lights a lamp to hide it under a basket.',
    after: 'Jesus explains He came not to abolish the Law but to fulfill it.',
    theme: 'Shining for God', keyword: 'shine',
    facts: ['Good works here are meant to point others to God, not to the doer.'],
  },
  {
    reference: 'Matthew 5:44', book: 'Matthew', chapter: 5, verseStart: 44,
    text: 'But I tell you, love your enemies and pray for those who persecute you,',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus recalls the saying to love your neighbor and hate your enemy.',
    after: 'Jesus explains this makes us like our Father, who sends sun and rain on all.',
    theme: 'Loving enemies', keyword: 'enemies',
    facts: ['This teaching overturned the common expectation of repaying wrong for wrong.'],
  },
  {
    reference: 'Matthew 6:9', book: 'Matthew', chapter: 6, verseStart: 9,
    text: 'So then, this is how you should pray: Our Father in heaven, hallowed be Your name.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus warns against praying with empty repetition to be seen by others.',
    after: 'Jesus continues the prayer, asking for God’s kingdom to come.',
    theme: 'The Lord’s Prayer', keyword: 'Father',
    facts: ['These words begin the Lord’s Prayer, the pattern of prayer Jesus gave His followers.'],
  },
  {
    reference: 'Matthew 6:14', book: 'Matthew', chapter: 6, verseStart: 14,
    text: 'For if you forgive men their trespasses, your heavenly Father will also forgive you.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus finishes teaching the Lord’s Prayer.',
    after: 'Jesus warns that refusing to forgive keeps us from being forgiven.',
    theme: 'Forgiveness', keyword: 'forgive',
    facts: ['Forgiveness is the one line of the Lord’s Prayer Jesus pauses to explain further.'],
  },
  {
    reference: 'Matthew 6:21', book: 'Matthew', chapter: 6, verseStart: 21,
    text: 'For where your treasure is, there your heart will be also.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus urges storing up treasures in heaven rather than on earth.',
    after: 'Jesus teaches that the eye is the lamp of the body.',
    theme: 'Treasure and the heart', keyword: 'treasure',
    facts: ['Jesus links what we value most with the true direction of our hearts.'],
  },
  {
    reference: 'Matthew 6:33', book: 'Matthew', chapter: 6, verseStart: 33,
    text: 'But seek first the kingdom of God and His righteousness, and all these things will be added unto you.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus says our heavenly Father knows all that we need.',
    after: 'Jesus urges us not to worry about tomorrow.',
    theme: 'Seeking God first', keyword: 'seek',
    facts: ['This verse follows Jesus’ call not to worry about food, drink, or clothing.'],
  },
  {
    reference: 'Matthew 6:34', book: 'Matthew', chapter: 6, verseStart: 34,
    text: 'Therefore do not worry about tomorrow, for tomorrow will worry about itself. Today has enough trouble of its own.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus tells us to seek first the kingdom of God.',
    after: 'Jesus begins teaching about not judging others.',
    theme: 'Freedom from worry', keyword: 'tomorrow',
    facts: ['Jesus invites us to live one day at a time, trusting God with the future.'],
  },
  {
    reference: 'Matthew 7:1', book: 'Matthew', chapter: 7, verseStart: 1,
    text: 'Do not judge, or you will be judged.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus finishes teaching about trusting God rather than worrying.',
    after: 'Jesus warns that we will be measured by the standard we use for others.',
    theme: 'Judging others', keyword: 'judge',
    facts: ['Jesus goes on to speak of removing the plank from our own eye first.'],
  },
  {
    reference: 'Matthew 7:7', book: 'Matthew', chapter: 7, verseStart: 7,
    text: 'Ask, and it will be given to you; seek, and you will find; knock, and the door will be opened to you.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus teaches about not giving what is holy to those who trample it.',
    after: 'Jesus explains that everyone who asks receives.',
    theme: 'Persistent prayer', keyword: 'knock',
    facts: ['The three verbs ask, seek, and knock all encourage bold, ongoing prayer.'],
  },
  {
    reference: 'Matthew 7:12', book: 'Matthew', chapter: 7, verseStart: 12,
    text: 'In everything, then, do to others as you would have them do to you. For this is the essence of the Law and the Prophets.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds and disciples',
    before: 'Jesus says God gives good gifts to those who ask Him.',
    after: 'Jesus urges His hearers to enter through the narrow gate.',
    theme: 'The Golden Rule', keyword: 'others',
    facts: ['This verse is widely known as the Golden Rule.'],
  },
  {
    reference: 'Matthew 11:28', book: 'Matthew', chapter: 11, verseStart: 28,
    text: 'Come to Me, all you who are weary and burdened, and I will give you rest.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds',
    before: 'Jesus praises the Father for revealing truth to the humble.',
    after: 'Jesus invites His hearers to take His yoke upon them.',
    theme: 'Rest in Christ', keyword: 'rest',
    facts: ['This tender invitation is among the most beloved verses in the Gospels.'],
  },
  {
    reference: 'Matthew 11:29', book: 'Matthew', chapter: 11, verseStart: 29,
    text: 'Take My yoke upon you and learn from Me; for I am gentle and humble in heart, and you will find rest for your souls.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds',
    before: 'Jesus calls the weary and burdened to come to Him for rest.',
    after: 'Jesus assures us that His yoke is easy and His burden is light.',
    theme: 'Gentle and humble Savior', keyword: 'yoke',
    facts: ['A yoke joined two animals so they could share a load, picturing walking alongside Jesus.'],
  },
  {
    reference: 'Matthew 11:30', book: 'Matthew', chapter: 11, verseStart: 30,
    text: 'For My yoke is easy and My burden is light.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowds',
    before: 'Jesus invites the weary to learn from His gentle and humble heart.',
    after: 'Jesus and His disciples pass through the grainfields on the Sabbath.',
    theme: 'A light burden', keyword: 'burden',
    facts: ['Jesus contrasts His gentle way with the heavy demands laid on people by others.'],
  },
  {
    reference: 'Matthew 18:20', book: 'Matthew', chapter: 18, verseStart: 20,
    text: 'For where two or three gather together in My name, there am I with them.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples',
    before: 'Jesus teaches about agreeing together in prayer.',
    after: 'Peter asks how many times he must forgive his brother.',
    theme: 'Christ’s presence', keyword: 'gather',
    facts: ['This promise assures believers that Jesus is present when they meet in His name.'],
  },
  {
    reference: 'Matthew 19:26', book: 'Matthew', chapter: 19, verseStart: 26,
    text: 'With man this is impossible, but with God all things are possible.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples',
    before: 'The disciples ask who then can be saved.',
    after: 'Peter notes that they have left everything to follow Jesus.',
    theme: 'God’s power to save', keyword: 'possible',
    facts: ['Jesus speaks these words after saying it is hard for the rich to enter the kingdom.'],
  },
  {
    reference: 'Matthew 21:22', book: 'Matthew', chapter: 21, verseStart: 22,
    text: 'If you believe, you will receive whatever you ask for in prayer.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples',
    before: 'The disciples marvel that the fig tree Jesus cursed withered so quickly.',
    after: 'Jesus enters the temple and is questioned about His authority.',
    theme: 'Believing prayer', keyword: 'prayer',
    facts: ['Jesus ties answered prayer to sincere faith in God.'],
  },
  {
    reference: 'Matthew 22:37', book: 'Matthew', chapter: 22, verseStart: 37,
    text: 'Love the Lord your God with all your heart and with all your soul and with all your mind.',
    testament: 'NT', speaker: 'Jesus', audience: 'A Pharisee and the crowd',
    before: 'A lawyer asks Jesus which is the greatest commandment in the Law.',
    after: 'Jesus names loving your neighbor as the second great commandment.',
    theme: 'The greatest commandment', keyword: 'heart',
    facts: ['Jesus draws this command from the Shema in Deuteronomy 6:5.'],
  },
  {
    reference: 'Matthew 25:40', book: 'Matthew', chapter: 25, verseStart: 40,
    text: 'Truly I tell you, whatever you did for one of the least of these brothers of Mine, you did for Me.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples',
    before: 'The King welcomes those who fed, clothed, and visited the needy.',
    after: 'The King addresses those who neglected the needy.',
    theme: 'Serving the least', keyword: 'least',
    facts: ['This is part of Jesus’ parable of the sheep and the goats.'],
  },
  {
    reference: 'Matthew 28:19', book: 'Matthew', chapter: 28, verseStart: 19,
    text: 'Therefore go and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit,',
    testament: 'NT', speaker: 'Jesus', audience: 'The eleven disciples',
    before: 'The risen Jesus declares that all authority in heaven and on earth is His.',
    after: 'Jesus promises to be with His disciples always.',
    theme: 'The Great Commission', keyword: 'disciples',
    facts: ['These closing words of Matthew are known as the Great Commission.'],
  },
  {
    reference: 'Matthew 28:20', book: 'Matthew', chapter: 28, verseStart: 20,
    text: 'and teaching them to obey all that I have commanded you. And surely I am with you always, even to the end of the age.',
    testament: 'NT', speaker: 'Jesus', audience: 'The eleven disciples',
    before: 'Jesus commands His followers to make disciples of all nations.',
    after: 'The Gospel of Matthew closes with this promise of Christ’s presence.',
    theme: 'Christ with us always', keyword: 'always',
    facts: ['Matthew opened with Immanuel, God with us, and closes with Jesus promising to remain with us.'],
  },
  {
    reference: 'Mark 1:15', book: 'Mark', chapter: 1, verseStart: 15,
    text: 'The time is fulfilled, and the kingdom of God is near. Repent and believe in the gospel.',
    testament: 'NT', speaker: 'Jesus', audience: 'The people of Galilee',
    before: 'Jesus comes into Galilee proclaiming the gospel of God.',
    after: 'Jesus calls Simon and Andrew to follow Him.',
    theme: 'The kingdom is near', keyword: 'repent',
    facts: ['These are the first recorded words of Jesus’ public ministry in Mark.'],
  },
  {
    reference: 'Mark 1:17', book: 'Mark', chapter: 1, verseStart: 17,
    text: 'Come, follow Me, and I will make you fishers of men.',
    testament: 'NT', speaker: 'Jesus', audience: 'Simon Peter and Andrew',
    before: 'Jesus sees Simon and Andrew casting a net into the sea.',
    after: 'They leave their nets at once and follow Him.',
    theme: 'The call to follow', keyword: 'fishers',
    facts: ['Simon and Andrew were fishermen by trade, so Jesus spoke in the language of their work.'],
  },
  {
    reference: 'Mark 2:17', book: 'Mark', chapter: 2, verseStart: 17,
    text: 'It is not the healthy who need a doctor, but the sick. I have not come to call the righteous, but sinners.',
    testament: 'NT', speaker: 'Jesus', audience: 'The scribes of the Pharisees',
    before: 'The scribes question why Jesus eats with tax collectors and sinners.',
    after: 'People ask why Jesus’ disciples do not fast.',
    theme: 'Grace for sinners', keyword: 'sinners',
    facts: ['Jesus was dining at the home of Levi, a tax collector He had just called.'],
  },
  {
    reference: 'Mark 4:39', book: 'Mark', chapter: 4, verseStart: 39,
    text: 'Then Jesus got up and rebuked the wind and the sea. Silence! Be still! Then the wind died down and it was perfectly calm.',
    testament: 'NT', speaker: 'The narrator', audience: 'Readers of the Gospel',
    before: 'A furious storm rises while Jesus sleeps in the boat.',
    after: 'Jesus asks the disciples why they were so afraid.',
    theme: 'Power over the storm', keyword: 'still',
    facts: ['The disciples respond in awe, wondering who can command even wind and waves.'],
  },
  {
    reference: 'Mark 5:36', book: 'Mark', chapter: 5, verseStart: 36,
    text: 'Do not be afraid; just believe.',
    testament: 'NT', speaker: 'Jesus', audience: 'Jairus, a synagogue leader',
    before: 'Word comes that Jairus’ daughter has died.',
    after: 'Jesus goes to the house and raises the girl to life.',
    theme: 'Faith over fear', keyword: 'believe',
    facts: ['Jesus speaks these words on the way to raise a little girl from death.'],
  },
  {
    reference: 'Mark 6:50', book: 'Mark', chapter: 6, verseStart: 50,
    text: 'Take courage! It is I. Do not be afraid.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples in the boat',
    before: 'The disciples are terrified to see Jesus walking on the sea.',
    after: 'Jesus climbs into the boat and the wind dies down.',
    theme: 'Do not be afraid', keyword: 'courage',
    facts: ['Jesus reassures the disciples during the fourth watch of the night, near dawn.'],
  },
  {
    reference: 'Mark 8:34', book: 'Mark', chapter: 8, verseStart: 34,
    text: 'If anyone wants to come after Me, he must deny himself and take up his cross and follow Me.',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowd and the disciples',
    before: 'Jesus foretells His suffering, death, and resurrection.',
    after: 'Jesus warns that whoever seeks to save his life will lose it.',
    theme: 'Taking up the cross', keyword: 'cross',
    facts: ['Jesus calls His followers to wholehearted, self-giving discipleship.'],
  },
  {
    reference: 'Mark 8:36', book: 'Mark', chapter: 8, verseStart: 36,
    text: 'What does it profit a man to gain the whole world, yet forfeit his soul?',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowd and the disciples',
    before: 'Jesus says whoever loses his life for the gospel will save it.',
    after: 'Jesus asks what a man can give in exchange for his soul.',
    theme: 'The worth of the soul', keyword: 'soul',
    facts: ['Jesus weighs all the world’s riches against the value of one soul.'],
  },
  {
    reference: 'Mark 9:23', book: 'Mark', chapter: 9, verseStart: 23,
    text: 'If you can? All things are possible to him who believes.',
    testament: 'NT', speaker: 'Jesus', audience: 'A boy’s father',
    before: 'A father begs Jesus to help his son if He is able.',
    after: 'The father cries out for Jesus to help his unbelief.',
    theme: 'Faith and possibility', keyword: 'believes',
    facts: ['This father’s honest prayer, I believe; help my unbelief, has comforted many.'],
  },
  {
    reference: 'Mark 9:35', book: 'Mark', chapter: 9, verseStart: 35,
    text: 'If anyone wants to be first, he must be the last of all and the servant of all.',
    testament: 'NT', speaker: 'Jesus', audience: 'The Twelve',
    before: 'The disciples had argued about who was the greatest.',
    after: 'Jesus takes a little child in His arms as an example.',
    theme: 'Servant greatness', keyword: 'servant',
    facts: ['Jesus repeatedly turned the world’s idea of greatness upside down.'],
  },
  {
    reference: 'Mark 10:9', book: 'Mark', chapter: 10, verseStart: 9,
    text: 'Therefore what God has joined together, let man not separate.',
    testament: 'NT', speaker: 'Jesus', audience: 'The Pharisees',
    before: 'The Pharisees test Jesus with a question about divorce.',
    after: 'The disciples ask Jesus about this again inside the house.',
    theme: 'The bond of marriage', keyword: 'joined',
    facts: ['These words are often read at Christian weddings today.'],
  },
  {
    reference: 'Mark 10:14', book: 'Mark', chapter: 10, verseStart: 14,
    text: 'Let the little children come to Me, and do not hinder them; for the kingdom of God belongs to such as these.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples',
    before: 'The disciples rebuke those bringing children to Jesus.',
    after: 'Jesus takes the children in His arms and blesses them.',
    theme: 'Welcoming children', keyword: 'children',
    facts: ['Jesus says the kingdom belongs to those with childlike trust.'],
  },
  {
    reference: 'Mark 10:27', book: 'Mark', chapter: 10, verseStart: 27,
    text: 'With man this is impossible, but not with God; for all things are possible with God.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples',
    before: 'The disciples wonder aloud who then can be saved.',
    after: 'Peter reminds Jesus they have left everything to follow Him.',
    theme: 'All things possible with God', keyword: 'possible',
    facts: ['Jesus speaks after teaching how hard it is for the rich to enter the kingdom.'],
  },
  {
    reference: 'Mark 10:45', book: 'Mark', chapter: 10, verseStart: 45,
    text: 'For even the Son of Man did not come to be served, but to serve, and to give His life as a ransom for many.',
    testament: 'NT', speaker: 'Jesus', audience: 'The Twelve',
    before: 'James and John seek places of honor beside Jesus.',
    after: 'Jesus and His disciples arrive in Jericho.',
    theme: 'The Servant King', keyword: 'ransom',
    facts: ['This verse beautifully sums up the purpose of Jesus’ life and death.'],
  },
  {
    reference: 'Mark 11:24', book: 'Mark', chapter: 11, verseStart: 24,
    text: 'Therefore I tell you, whatever you ask for in prayer, believe that you have received it, and it will be yours.',
    testament: 'NT', speaker: 'Jesus', audience: 'The disciples',
    before: 'Jesus teaches the disciples to have faith in God.',
    after: 'Jesus urges them to forgive when they stand praying.',
    theme: 'Praying in faith', keyword: 'prayer',
    facts: ['Jesus taught this after the disciples marveled at a withered fig tree.'],
  },
  {
    reference: 'Mark 12:29', book: 'Mark', chapter: 12, verseStart: 29,
    text: 'The most important is this: Hear, O Israel, the Lord our God, the Lord is One.',
    testament: 'NT', speaker: 'Jesus', audience: 'A scribe',
    before: 'A scribe asks Jesus which commandment is the most important.',
    after: 'Jesus commands loving God with all your heart, soul, mind, and strength.',
    theme: 'The Lord is One', keyword: 'Israel',
    facts: ['Jesus quotes the Shema, the daily confession of faith recited by Israel.'],
  },
  {
    reference: 'Mark 12:30', book: 'Mark', chapter: 12, verseStart: 30,
    text: 'Love the Lord your God with all your heart and with all your soul and with all your mind and with all your strength.',
    testament: 'NT', speaker: 'Jesus', audience: 'A scribe',
    before: 'Jesus recalls the Shema, that the Lord our God is one.',
    after: 'Jesus names loving your neighbor as the second commandment.',
    theme: 'Loving God fully', keyword: 'strength',
    facts: ['Jesus calls this the first and greatest of all the commandments.'],
  },
  {
    reference: 'Mark 12:31', book: 'Mark', chapter: 12, verseStart: 31,
    text: 'The second is this: Love your neighbor as yourself. No commandment is greater than these.',
    testament: 'NT', speaker: 'Jesus', audience: 'A scribe',
    before: 'Jesus names loving God as the first and greatest commandment.',
    after: 'The scribe agrees that Jesus has answered wisely.',
    theme: 'Loving your neighbor', keyword: 'neighbor',
    facts: ['Jesus draws this command from Leviticus 19:18.'],
  },
  {
    reference: 'Mark 16:15', book: 'Mark', chapter: 16, verseStart: 15,
    text: 'Go into all the world and preach the gospel to every creature.',
    testament: 'NT', speaker: 'Jesus', audience: 'The eleven disciples',
    before: 'The risen Jesus appears to the eleven as they are eating.',
    after: 'Jesus speaks of signs that will accompany those who believe.',
    theme: 'Preaching the gospel', keyword: 'gospel',
    facts: ['This is Mark’s version of the Great Commission to the whole world.'],
  },
  {
    reference: 'Mark 16:16', book: 'Mark', chapter: 16, verseStart: 16,
    text: 'Whoever believes and is baptized will be saved, but whoever does not believe will be condemned.',
    testament: 'NT', speaker: 'Jesus', audience: 'The eleven disciples',
    before: 'Jesus commissions the disciples to preach the gospel everywhere.',
    after: 'Jesus describes signs that will follow those who believe.',
    theme: 'Believe and be saved', keyword: 'baptized',
    facts: ['This verse joins saving faith with the public step of baptism.'],
  },
  {
    reference: 'John 1:1', book: 'John', chapter: 1, verseStart: 1,
    text: 'In the beginning was the Word, and the Word was with God, and the Word was God.',
    testament: 'NT', speaker: 'John the Apostle', audience: 'Readers of the Gospel',
    before: 'John opens his Gospel not with a birth narrative but with eternity.',
    after: 'John explains that through the Word all things were made.',
    theme: 'The eternal Word', keyword: 'Word',
    facts: ['The Greek term for Word is Logos, a title John uses for Christ.'],
  },
  {
    reference: 'John 1:12', book: 'John', chapter: 1, verseStart: 12,
    text: 'But to all who did receive Him, to those who believed in His name, He gave the right to become children of God.',
    testament: 'NT', speaker: 'John the Apostle', audience: 'Readers of the Gospel',
    before: 'John notes that the world did not recognize its own Maker.',
    after: 'John says these children are born of God, not of human will.',
    theme: 'Becoming children of God', keyword: 'children',
    facts: ['This verse promises adoption into God’s family through faith in Christ.'],
  },
  {
    reference: 'John 1:14', book: 'John', chapter: 1, verseStart: 14,
    text: 'The Word became flesh and made His dwelling among us. We have seen His glory, the glory of the one and only Son from the Father, full of grace and truth.',
    testament: 'NT', speaker: 'John the Apostle', audience: 'Readers of the Gospel',
    before: 'John describes the Word as the true Light coming into the world.',
    after: 'John the Baptist testifies about the One who surpasses him.',
    theme: 'The incarnation', keyword: 'flesh',
    facts: ['This verse declares the incarnation, that God took on human nature in Jesus.'],
  },
  {
    reference: 'John 1:29', book: 'John', chapter: 1, verseStart: 29,
    text: 'The next day John saw Jesus coming toward him and said, "Look, the Lamb of God, who takes away the sin of the world!"',
    testament: 'NT', speaker: 'John the Baptist', audience: 'Those gathered near the Jordan',
    before: 'John the Baptist testifies that he is not the Christ.',
    after: 'John explains that he saw the Spirit descend on Jesus like a dove.',
    theme: 'The Lamb of God', keyword: 'Lamb',
    facts: ['The title Lamb of God points to Jesus as the sacrifice for sin.'],
  },
  {
    reference: 'John 3:16', book: 'John', chapter: 3, verseStart: 16,
    text: 'For God so loved the world that He gave His one and only Son, that everyone who believes in Him shall not perish but have eternal life.',
    testament: 'NT', speaker: 'Jesus', audience: 'Nicodemus',
    before: 'Jesus tells Nicodemus how the Son of Man must be lifted up.',
    after: 'Jesus says God sent His Son to save the world, not to condemn it.',
    theme: 'God’s saving love', keyword: 'loved',
    facts: ['Often called the Gospel in miniature, it summarizes the message of salvation.'],
  },
  {
    reference: 'John 3:17', book: 'John', chapter: 3, verseStart: 17,
    text: 'For God did not send His Son into the world to condemn the world, but to save the world through Him.',
    testament: 'NT', speaker: 'Jesus', audience: 'Nicodemus',
    before: 'Jesus declares God’s love in giving His one and only Son.',
    after: 'Jesus explains that whoever believes in Him is not condemned.',
    theme: 'Salvation, not condemnation', keyword: 'save',
    facts: ['This verse follows directly after John 3:16 in Jesus’ night conversation with Nicodemus.'],
  },
  {
    reference: 'John 4:24', book: 'John', chapter: 4, verseStart: 24,
    text: 'God is Spirit, and His worshipers must worship in spirit and in truth.',
    testament: 'NT', speaker: 'Jesus', audience: 'A Samaritan woman',
    before: 'Jesus tells the woman that true worship is not bound to one mountain.',
    after: 'The woman speaks of the Messiah, and Jesus reveals He is the One.',
    theme: 'True worship', keyword: 'worship',
    facts: ['Jesus spoke this to a Samaritan woman at Jacob’s well.'],
  },
  {
    reference: 'John 6:35', book: 'John', chapter: 6, verseStart: 35,
    text: 'Jesus answered, "I am the bread of life. Whoever comes to Me will never hunger, and whoever believes in Me will never thirst."',
    testament: 'NT', speaker: 'Jesus', audience: 'The crowd at Capernaum',
    before: 'The crowd asks Jesus for the bread that gives life.',
    after: 'Jesus says that all the Father gives Him will come to Him.',
    theme: 'The bread of life', keyword: 'bread',
    facts: ['This is the first of seven famous "I am" statements in John’s Gospel.'],
  },
  {
    reference: 'John 8:12', book: 'John', chapter: 8, verseStart: 12,
    text: 'Once again, Jesus spoke to the people and said, "I am the light of the world. Whoever follows Me will never walk in the darkness, but will have the light of life."',
    testament: 'NT', speaker: 'Jesus', audience: 'The people in the temple',
    before: 'Jesus has just shown mercy to a woman caught in sin.',
    after: 'The Pharisees challenge the validity of His testimony.',
    theme: 'The light of the world', keyword: 'light',
    facts: ['Jesus spoke this during the Feast of Tabernacles, when great lamps were lit in the temple.'],
  },
  {
    reference: 'John 8:32', book: 'John', chapter: 8, verseStart: 32,
    text: 'Then you will know the truth, and the truth will set you free.',
    testament: 'NT', speaker: 'Jesus', audience: 'Jews who had believed in Him',
    before: 'Jesus tells those who believe to hold to His teaching as true disciples.',
    after: 'They protest that they have never been slaves to anyone.',
    theme: 'Freedom through truth', keyword: 'truth',
    facts: ['Jesus links true discipleship with holding to His word.'],
  },
  {
    reference: 'John 8:36', book: 'John', chapter: 8, verseStart: 36,
    text: 'So if the Son sets you free, you will be free indeed.',
    testament: 'NT', speaker: 'Jesus', audience: 'Jews in the temple',
    before: 'Jesus explains that everyone who sins is a slave to sin.',
    after: 'Jesus acknowledges they are Abraham’s descendants yet seek to kill Him.',
    theme: 'True freedom in Christ', keyword: 'free',
    facts: ['Jesus contrasts slavery to sin with the lasting freedom the Son gives.'],
  },
  {
    reference: 'John 10:10', book: 'John', chapter: 10, verseStart: 10,
    text: 'The thief comes only to steal and kill and destroy. I have come that they may have life, and have it in all its fullness.',
    testament: 'NT', speaker: 'Jesus', audience: 'The Pharisees and listeners',
    before: 'Jesus describes Himself as the gate for the sheep.',
    after: 'Jesus declares that He is the good shepherd who lays down His life.',
    theme: 'Abundant life', keyword: 'life',
    facts: ['Jesus contrasts the thief’s destruction with the abundant life He offers.'],
  },
  {
    reference: 'John 10:11', book: 'John', chapter: 10, verseStart: 11,
    text: 'I am the good shepherd. The good shepherd lays down His life for the sheep.',
    testament: 'NT', speaker: 'Jesus', audience: 'The Pharisees and listeners',
    before: 'Jesus says He came to give life in all its fullness.',
    after: 'Jesus contrasts the good shepherd with the hired hand who flees.',
    theme: 'The good shepherd', keyword: 'shepherd',
    facts: ['This is one of the seven "I am" sayings in the Gospel of John.'],
  },
  {
    reference: 'John 11:25', book: 'John', chapter: 11, verseStart: 25,
    text: 'Jesus said to her, "I am the resurrection and the life. Whoever believes in Me will live, even though he dies."',
    testament: 'NT', speaker: 'Jesus', audience: 'Martha',
    before: 'Martha tells Jesus her brother would not have died if He had been there.',
    after: 'Jesus asks Martha if she believes, and she confesses He is the Christ.',
    theme: 'Resurrection and life', keyword: 'resurrection',
    facts: ['Jesus spoke this just before raising Lazarus from the dead.'],
  },
  {
    reference: 'John 11:35', book: 'John', chapter: 11, verseStart: 35,
    text: 'Jesus wept.',
    testament: 'NT', speaker: 'John the Apostle', audience: 'Readers of the Gospel',
    before: 'Jesus sees Mary and the mourners weeping over Lazarus.',
    after: 'The onlookers marvel at how much Jesus loved Lazarus.',
    theme: 'The compassion of Jesus', keyword: 'wept',
    facts: ['This is the shortest verse in most English Bibles.'],
  },
  {
    reference: 'John 13:34', book: 'John', chapter: 13, verseStart: 34,
    text: 'A new commandment I give you: Love one another. As I have loved you, so you also must love one another.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus tells His disciples He will be with them only a little longer.',
    after: 'Jesus says all will know they are His disciples by their love.',
    theme: 'The new commandment', keyword: 'commandment',
    facts: ['Jesus gave this command at the Last Supper, after washing the disciples’ feet.'],
  },
  {
    reference: 'John 13:35', book: 'John', chapter: 13, verseStart: 35,
    text: 'By this everyone will know that you are My disciples, if you love one another.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus gives the new command to love one another as He has loved them.',
    after: 'Jesus foretells that Peter will deny Him three times.',
    theme: 'Love as the mark of discipleship', keyword: 'disciples',
    facts: ['Jesus taught that love, not status, identifies His followers.'],
  },
  {
    reference: 'John 14:1', book: 'John', chapter: 14, verseStart: 1,
    text: 'Do not let your hearts be troubled. You believe in God; believe in Me as well.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus foretells that Peter will deny Him three times.',
    after: 'Jesus promises to prepare a place for them in His Father’s house.',
    theme: 'Comfort and trust', keyword: 'troubled',
    facts: ['Jesus spoke these comforting words at the Last Supper before His arrest.'],
  },
  {
    reference: 'John 14:2', book: 'John', chapter: 14, verseStart: 2,
    text: 'In My Father’s house are many rooms. If it were not so, would I have told you that I am going there to prepare a place for you?',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus tells His troubled disciples to trust in God and in Him.',
    after: 'Jesus promises to come back and take them to be with Him.',
    theme: 'A place prepared in heaven', keyword: 'rooms',
    facts: ['Jesus gave this promise of heaven to comfort His disciples.'],
  },
  {
    reference: 'John 14:6', book: 'John', chapter: 14, verseStart: 6,
    text: 'Jesus answered, "I am the way and the truth and the life. No one comes to the Father except through Me."',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Thomas asks Jesus how they can know the way.',
    after: 'Jesus says that to know Him is to know the Father.',
    theme: 'Jesus as the way', keyword: 'way',
    facts: ['Jesus said this at the Last Supper, comforting His disciples before His death.'],
  },
  {
    reference: 'John 14:27', book: 'John', chapter: 14, verseStart: 27,
    text: 'Peace I leave with you; My peace I give to you. I do not give to you as the world gives. Do not let your hearts be troubled; do not be afraid.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus promises the Holy Spirit will teach and remind them.',
    after: 'Jesus tells them He is going away but will come back to them.',
    theme: 'The gift of peace', keyword: 'Peace',
    facts: ['Jesus offered this peace to comfort His disciples before His crucifixion.'],
  },
  {
    reference: 'John 15:5', book: 'John', chapter: 15, verseStart: 5,
    text: 'I am the vine and you are the branches. The one who remains in Me, and I in him, will bear much fruit. For apart from Me you can do nothing.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus calls His Father the gardener who tends the vine.',
    after: 'Jesus warns that a branch cut off from the vine withers.',
    theme: 'Abiding in Christ', keyword: 'vine',
    facts: ['This is the last of the seven "I am" statements in John’s Gospel.'],
  },
  {
    reference: 'John 15:12', book: 'John', chapter: 15, verseStart: 12,
    text: 'This is My commandment, that you love one another as I have loved you.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus tells them to remain in His love by keeping His commands.',
    after: 'Jesus says the greatest love is to lay down one’s life for friends.',
    theme: 'The command to love', keyword: 'commandment',
    facts: ['Jesus repeats and deepens His call to love one another at the Last Supper.'],
  },
  {
    reference: 'John 15:13', book: 'John', chapter: 15, verseStart: 13,
    text: 'Greater love has no one than this, that he lay down his life for his friends.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus commands them to love one another as He has loved them.',
    after: 'Jesus calls them friends, not servants, for He has made known the Father.',
    theme: 'Sacrificial love', keyword: 'love',
    facts: ['Jesus spoke this foreshadowing His own death for His people.'],
  },
  {
    reference: 'John 16:33', book: 'John', chapter: 16, verseStart: 33,
    text: 'I have told you these things so that in Me you may have peace. In the world you will have tribulation. But take courage; I have overcome the world.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus tells them a time is coming when they will be scattered.',
    after: 'Jesus lifts His eyes to heaven and prays for His disciples.',
    theme: 'Overcoming the world', keyword: 'overcome',
    facts: ['These are Jesus’ closing words to His disciples before His high priestly prayer.'],
  },
  {
    reference: 'John 20:29', book: 'John', chapter: 20, verseStart: 29,
    text: 'Jesus said to him, "Because you have seen Me, you have believed; blessed are those who have not seen and yet have believed."',
    testament: 'NT', speaker: 'Jesus', audience: 'Thomas',
    before: 'Thomas touches the risen Jesus and confesses Him as Lord and God.',
    after: 'John explains that these signs are written so that we may believe.',
    theme: 'Faith without seeing', keyword: 'blessed',
    facts: ['This encounter gives rise to the phrase "doubting Thomas."'],
  },
  {
    reference: 'Luke 1:37', book: 'Luke', chapter: 1, verseStart: 37,
    text: 'For no word from God will ever fail.',
    testament: 'NT', speaker: 'The angel Gabriel', audience: 'Mary',
    before: 'Gabriel tells Mary that her relative Elizabeth is also with child.',
    after: 'Mary responds in humble surrender as the Lord’s servant.',
    theme: 'The power of God’s word', keyword: 'fail',
    facts: ['Gabriel spoke this to Mary at the announcement of Jesus’ birth.'],
  },
  {
    reference: 'Luke 1:46', book: 'Luke', chapter: 1, verseStart: 46, verseEnd: 47,
    text: 'And Mary said: "My soul magnifies the Lord, and my spirit rejoices in God my Savior."',
    testament: 'NT', speaker: 'Mary', audience: 'Elizabeth',
    before: 'Elizabeth blesses Mary as the mother of her Lord.',
    after: 'Mary continues her song praising God for lifting up the humble.',
    theme: 'Mary’s song of praise', keyword: 'magnifies',
    facts: ['These verses open the song known as the Magnificat.'],
  },
  {
    reference: 'Luke 2:11', book: 'Luke', chapter: 2, verseStart: 11,
    text: 'Today in the city of David a Savior has been born to you. He is Christ the Lord.',
    testament: 'NT', speaker: 'An angel of the Lord', audience: 'Shepherds near Bethlehem',
    before: 'An angel appears to shepherds keeping watch over their flocks at night.',
    after: 'A great host of angels praises God, saying Glory to God in the highest.',
    theme: 'The birth of the Savior', keyword: 'Savior',
    facts: ['The city of David is Bethlehem, where Jesus was born.'],
  },
  {
    reference: 'Luke 2:14', book: 'Luke', chapter: 2, verseStart: 14,
    text: 'Glory to God in the highest, and on earth peace to men on whom His favor rests.',
    testament: 'NT', speaker: 'The heavenly host', audience: 'Shepherds near Bethlehem',
    before: 'An angel announces the birth of the Savior to the shepherds.',
    after: 'The shepherds hurry to Bethlehem to find the newborn Child.',
    theme: 'Glory and peace at Christ’s birth', keyword: 'Glory',
    facts: ['This angelic praise is echoed in the Christmas hymn Gloria in Excelsis Deo.'],
  },
  {
    reference: 'Luke 5:32', book: 'Luke', chapter: 5, verseStart: 32,
    text: 'I have not come to call the righteous, but sinners to repentance.',
    testament: 'NT', speaker: 'Jesus', audience: 'Pharisees and scribes',
    before: 'The Pharisees grumble that Jesus eats with tax collectors and sinners.',
    after: 'They question Jesus about why His disciples do not fast.',
    theme: 'Jesus calls sinners', keyword: 'sinners',
    facts: ['Jesus said this at a banquet hosted by Levi the tax collector.'],
  },
  {
    reference: 'Luke 6:31', book: 'Luke', chapter: 6, verseStart: 31,
    text: 'Do to others as you would have them do to you.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples and a great crowd',
    before: 'Jesus teaches His followers to love their enemies and give freely.',
    after: 'Jesus asks what credit it is to love only those who love you.',
    theme: 'The Golden Rule', keyword: 'others',
    facts: ['This teaching is commonly known as the Golden Rule.'],
  },
  {
    reference: 'Luke 6:38', book: 'Luke', chapter: 6, verseStart: 38,
    text: 'Give, and it will be given to you. A good measure, pressed down, shaken together, and running over, will be poured into your lap. For with the measure you use, it will be measured back to you.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples and a great crowd',
    before: 'Jesus teaches His followers not to judge or condemn but to forgive.',
    after: 'Jesus tells a parable about the blind leading the blind.',
    theme: 'Generosity rewarded', keyword: 'Give',
    facts: ['Jesus uses the image of grain measured generously and overflowing.'],
  },
  {
    reference: 'Luke 9:23', book: 'Luke', chapter: 9, verseStart: 23,
    text: 'Then Jesus said to all of them, "If anyone wants to come after Me, he must deny himself and take up his cross daily and follow Me."',
    testament: 'NT', speaker: 'Jesus', audience: 'All His disciples',
    before: 'Jesus foretells His suffering, death, and resurrection.',
    after: 'Jesus warns that whoever wants to save his life will lose it.',
    theme: 'The cost of discipleship', keyword: 'cross',
    facts: ['Luke uniquely adds the word daily to taking up one’s cross.'],
  },
  {
    reference: 'Luke 10:27', book: 'Luke', chapter: 10, verseStart: 27,
    text: 'He answered, "Love the Lord your God with all your heart and with all your soul and with all your strength and with all your mind, and love your neighbor as yourself."',
    testament: 'NT', speaker: 'An expert in the law', audience: 'Jesus',
    before: 'A lawyer asks Jesus what he must do to inherit eternal life.',
    after: 'Jesus tells the parable of the Good Samaritan to define a neighbor.',
    theme: 'The greatest commandments', keyword: 'neighbor',
    facts: ['This answer combines commands from Deuteronomy and Leviticus.'],
  },
  {
    reference: 'Luke 11:9', book: 'Luke', chapter: 11, verseStart: 9,
    text: 'So I tell you: Ask, and it will be given to you; seek, and you will find; knock, and the door will be opened to you.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus tells a parable about a persistent friend asking for bread at midnight.',
    after: 'Jesus assures them the Father gives good gifts to those who ask.',
    theme: 'Persistent prayer', keyword: 'knock',
    facts: ['This teaching follows Jesus giving the disciples the Lord’s Prayer.'],
  },
  {
    reference: 'Luke 11:28', book: 'Luke', chapter: 11, verseStart: 28,
    text: 'But He replied, "Blessed rather are those who hear the word of God and obey it."',
    testament: 'NT', speaker: 'Jesus', audience: 'A woman in the crowd',
    before: 'A woman calls out a blessing on the mother who bore Jesus.',
    after: 'Jesus speaks of the sign of Jonah to a growing crowd.',
    theme: 'Blessing in obedience', keyword: 'Blessed',
    facts: ['Jesus redirected praise from His mother to those who obey God’s word.'],
  },
  {
    reference: 'Luke 12:7', book: 'Luke', chapter: 12, verseStart: 7,
    text: 'And even the very hairs of your head are all numbered. So do not be afraid; you are worth more than many sparrows.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples and a crowd of thousands',
    before: 'Jesus reminds them that God does not forget even a single sparrow.',
    after: 'Jesus calls them to acknowledge Him before others.',
    theme: 'God’s care for you', keyword: 'sparrows',
    facts: ['Jesus used sparrows, sold cheaply in the market, to show God’s intimate care.'],
  },
  {
    reference: 'Luke 12:34', book: 'Luke', chapter: 12, verseStart: 34,
    text: 'For where your treasure is, there your heart will be also.',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'Jesus tells them to sell their possessions and store treasure in heaven.',
    after: 'Jesus urges them to be dressed and ready like servants awaiting their master.',
    theme: 'Treasure and the heart', keyword: 'treasure',
    facts: ['Jesus taught that our devotion follows whatever we truly value.'],
  },
  {
    reference: 'Luke 15:7', book: 'Luke', chapter: 15, verseStart: 7,
    text: 'In the same way, I tell you that there will be more joy in heaven over one sinner who repents than over ninety-nine righteous ones who do not need to repent.',
    testament: 'NT', speaker: 'Jesus', audience: 'Pharisees and tax collectors',
    before: 'Jesus tells the parable of the shepherd who leaves the ninety-nine to find one lost sheep.',
    after: 'Jesus tells the parable of the woman searching for her lost coin.',
    theme: 'Heaven’s joy over repentance', keyword: 'joy',
    facts: ['Jesus told this after being criticized for welcoming sinners.'],
  },
  {
    reference: 'Luke 18:27', book: 'Luke', chapter: 18, verseStart: 27,
    text: 'But Jesus said, "What is impossible with man is possible with God."',
    testament: 'NT', speaker: 'Jesus', audience: 'His disciples',
    before: 'A rich ruler goes away sad, unwilling to give up his wealth.',
    after: 'Peter reminds Jesus that they have left everything to follow Him.',
    theme: 'What God makes possible', keyword: 'impossible',
    facts: ['Jesus said this after teaching how hard it is for the rich to enter God’s kingdom.'],
  },
  {
    reference: 'Luke 19:10', book: 'Luke', chapter: 19, verseStart: 10,
    text: 'For the Son of Man came to seek and to save the lost.',
    testament: 'NT', speaker: 'Jesus', audience: 'Zacchaeus and onlookers',
    before: 'Zacchaeus the tax collector repents and welcomes Jesus into his home.',
    after: 'Jesus tells the parable of the ten minas as He nears Jerusalem.',
    theme: 'Jesus seeks the lost', keyword: 'lost',
    facts: ['Jesus spoke this after salvation came to the house of Zacchaeus.'],
  },
  // ————————————————————————— Acts & Pauline Epistles —————————————————————————
  {
    reference: 'Acts 1:8', book: 'Acts', chapter: 1, verseStart: 8,
    text: 'But you will receive power when the Holy Spirit comes upon you, and you will be My witnesses in Jerusalem, and in all Judea and Samaria, and to the ends of the earth.',
    testament: 'NT', speaker: 'Jesus', audience: 'His apostles before His ascension',
    before: 'The apostles ask whether Jesus will restore the kingdom to Israel at this time.',
    after: 'Jesus is taken up into heaven as the apostles watch.',
    theme: 'Empowered to witness', keyword: 'witnesses',
    facts: ['This verse traces the outline of the entire book of Acts, from Jerusalem outward.'],
  },
  {
    reference: 'Acts 2:21', book: 'Acts', chapter: 2, verseStart: 21,
    text: 'And everyone who calls on the name of the Lord will be saved.',
    testament: 'NT', speaker: 'Peter', audience: 'The crowd at Pentecost',
    before: 'Peter quotes the prophet Joel to explain the outpouring of the Spirit.',
    after: 'Peter proclaims Jesus of Nazareth, accredited by miracles and raised from the dead.',
    theme: 'Salvation for all who call', keyword: 'saved',
    facts: ['Peter is quoting Joel 2:32 in his Pentecost sermon.'],
  },
  {
    reference: 'Acts 2:38', book: 'Acts', chapter: 2, verseStart: 38,
    text: 'Repent and be baptized, every one of you, in the name of Jesus Christ for the forgiveness of your sins, and you will receive the gift of the Holy Spirit.',
    testament: 'NT', speaker: 'Peter', audience: 'The crowd at Pentecost',
    before: 'The crowd, cut to the heart, asks Peter and the apostles what they should do.',
    after: 'About three thousand people are added to the church that day.',
    theme: 'Repentance and forgiveness', keyword: 'baptized',
    facts: ['This was Peter’s answer to the crowd’s question about what they should do.'],
  },
  {
    reference: 'Acts 2:42', book: 'Acts', chapter: 2, verseStart: 42,
    text: 'They devoted themselves to the apostles’ teaching and to the fellowship, to the breaking of bread and to prayer.',
    testament: 'NT', speaker: 'The narrator (Luke)', audience: 'Readers of Acts',
    before: 'Three thousand are baptized and added to the believers at Pentecost.',
    after: 'A sense of awe comes over everyone, and the apostles perform many wonders.',
    theme: 'Life of the early church', keyword: 'fellowship',
    facts: ['This verse names four marks of the earliest Christian community.'],
  },
  {
    reference: 'Acts 4:12', book: 'Acts', chapter: 4, verseStart: 12,
    text: 'Salvation exists in no one else, for there is no other name under heaven given to men by which we must be saved.',
    testament: 'NT', speaker: 'Peter', audience: 'The Sanhedrin',
    before: 'Peter, filled with the Spirit, is questioned about healing a lame man.',
    after: 'The rulers marvel at the boldness of Peter and John, untrained men.',
    theme: 'Salvation in Christ alone', keyword: 'name',
    facts: ['Peter spoke this while on trial before Israel’s ruling council.'],
  },
  {
    reference: 'Acts 16:31', book: 'Acts', chapter: 16, verseStart: 31,
    text: 'Believe in the Lord Jesus and you will be saved, you and your household.',
    testament: 'NT', speaker: 'Paul and Silas', audience: 'The Philippian jailer',
    before: 'An earthquake opens the prison, and the trembling jailer asks how to be saved.',
    after: 'The jailer washes their wounds and is baptized with all his household that night.',
    theme: 'Faith brings salvation', keyword: 'household',
    facts: ['Paul and Silas spoke this after an earthquake shook the Philippian jail.'],
  },
  {
    reference: 'Acts 17:11', book: 'Acts', chapter: 17, verseStart: 11,
    text: 'Now the Bereans were more noble-minded than the Thessalonians, for they received the message with great eagerness and examined the Scriptures every day to see if these teachings were true.',
    testament: 'NT', speaker: 'The narrator (Luke)', audience: 'Readers of Acts',
    before: 'Paul and Silas arrive in Berea and go into the Jewish synagogue.',
    after: 'Many Bereans believe, along with prominent Greek women and men.',
    theme: 'Searching the Scriptures', keyword: 'Scriptures',
    facts: ['The Bereans’ daily study of Scripture became a lasting model of discernment.'],
  },
  {
    reference: 'Acts 20:24', book: 'Acts', chapter: 20, verseStart: 24,
    text: 'But I consider my life of no value to me, if only I may finish my course and complete the ministry I have received from the Lord Jesus, to testify to the good news of God’s grace.',
    testament: 'NT', speaker: 'Paul', audience: 'The Ephesian elders',
    before: 'Paul tells the elders that hardships and chains await him in every city.',
    after: 'Paul says they will not see his face again as he commends them to God.',
    theme: 'Finishing the race', keyword: 'ministry',
    facts: ['Paul spoke this farewell to the Ephesian elders at Miletus.'],
  },
  {
    reference: 'Acts 20:35', book: 'Acts', chapter: 20, verseStart: 35,
    text: 'In everything, I have shown you that by this kind of hard work we must help the weak, remembering the words of the Lord Jesus Himself: It is more blessed to give than to receive.',
    testament: 'NT', speaker: 'Paul', audience: 'The Ephesian elders',
    before: 'Paul reminds the elders that he worked with his own hands to support himself.',
    after: 'Paul kneels down with all of them and prays as they weep and embrace him.',
    theme: 'The blessing of giving', keyword: 'blessed',
    facts: ['This saying of Jesus is preserved only here, not in the four Gospels.'],
  },
  {
    reference: 'Romans 1:16', book: 'Romans', chapter: 1, verseStart: 16,
    text: 'I am not ashamed of the gospel, because it is the power of God for salvation to everyone who believes, first to the Jew, then to the Greek.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul longs to preach the gospel to those in Rome as well.',
    after: 'Paul explains that in the gospel the righteousness of God is revealed.',
    theme: 'The power of the gospel', keyword: 'gospel',
    facts: ['Many consider this verse the thesis statement of the letter to the Romans.'],
  },
  {
    reference: 'Romans 3:23', book: 'Romans', chapter: 3, verseStart: 23,
    text: 'for all have sinned and fall short of the glory of God,',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul declares there is no distinction between Jew and Greek before God.',
    after: 'Paul says we are justified freely by His grace through the redemption in Christ.',
    theme: 'Universal need for grace', keyword: 'glory',
    facts: ['This verse affirms that every person alike stands in need of God’s grace.'],
  },
  {
    reference: 'Romans 5:1', book: 'Romans', chapter: 5, verseStart: 1,
    text: 'Therefore, since we have been justified through faith, we have peace with God through our Lord Jesus Christ,',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul points to Abraham as the pattern of righteousness credited through faith.',
    after: 'Paul says we rejoice in hope of the glory of God, even in sufferings.',
    theme: 'Peace with God', keyword: 'justified',
    facts: ['Paul opens this chapter with the fruit of justification: peace with God.'],
  },
  {
    reference: 'Romans 5:8', book: 'Romans', chapter: 5, verseStart: 8,
    text: 'But God proves His love for us in this: While we were still sinners, Christ died for us.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul notes that rarely will anyone die even for a righteous person.',
    after: 'Paul says that, now justified by His blood, we will be saved from wrath.',
    theme: 'God’s love shown at the cross', keyword: 'sinners',
    facts: ['This verse shows that Christ’s death came while we were still His enemies.'],
  },
  {
    reference: 'Romans 6:23', book: 'Romans', chapter: 6, verseStart: 23,
    text: 'For the wages of sin is death, but the gift of God is eternal life in Christ Jesus our Lord.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul says that having been freed from sin, we now bear fruit unto holiness.',
    after: 'Paul turns to marriage as an illustration of freedom from the law.',
    theme: 'Death versus the gift of life', keyword: 'wages',
    facts: ['Paul contrasts earned wages with a freely given gift in a single verse.'],
  },
  {
    reference: 'Romans 8:1', book: 'Romans', chapter: 8, verseStart: 1,
    text: 'Therefore there is now no condemnation for those who are in Christ Jesus.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul cries out for deliverance from the body of death, thanking God through Christ.',
    after: 'Paul says the law of the Spirit of life has set us free from sin and death.',
    theme: 'No condemnation in Christ', keyword: 'condemnation',
    facts: ['This verse opens one of the most beloved chapters in all of Scripture.'],
  },
  {
    reference: 'Romans 8:28', book: 'Romans', chapter: 8, verseStart: 28,
    text: 'And we know that God works all things together for the good of those who love Him, who are called according to His purpose.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul says the Spirit helps us in our weakness and intercedes for us.',
    after: 'Paul says those God foreknew He also predestined to be conformed to Christ.',
    theme: 'God works for good', keyword: 'purpose',
    facts: ['This is one of the most quoted promises of comfort in the New Testament.'],
  },
  {
    reference: 'Romans 8:31', book: 'Romans', chapter: 8, verseStart: 31,
    text: 'What then shall we say in response to these things? If God is for us, who can be against us?',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul says those God foreknew He predestined, called, justified, and glorified.',
    after: 'Paul asks who will bring any charge against those whom God has chosen.',
    theme: 'God is for us', keyword: 'against',
    facts: ['Paul answers his own rhetorical question with the certainty of God’s favor.'],
  },
  {
    reference: 'Romans 8:37', book: 'Romans', chapter: 8, verseStart: 37,
    text: 'No, in all these things we are more than conquerors through Him who loved us.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul lists trouble, hardship, persecution, famine, and sword as trials believers face.',
    after: 'Paul declares that nothing can separate us from the love of God.',
    theme: 'More than conquerors', keyword: 'conquerors',
    facts: ['Paul insists these trials cannot defeat those held by Christ’s love.'],
  },
  {
    reference: 'Romans 10:9', book: 'Romans', chapter: 10, verseStart: 9,
    text: 'that if you confess with your mouth, Jesus is Lord, and believe in your heart that God raised Him from the dead, you will be saved.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul says the word of faith is near you, in your mouth and in your heart.',
    after: 'Paul explains that with the heart one believes and with the mouth confesses.',
    theme: 'Confess and believe', keyword: 'confess',
    facts: ['This verse joins heartfelt belief with open confession of Jesus as Lord.'],
  },
  {
    reference: 'Romans 12:2', book: 'Romans', chapter: 12, verseStart: 2,
    text: 'Do not be conformed to this world, but be transformed by the renewing of your mind. Then you will be able to test and approve what is the good, pleasing, and perfect will of God.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul urges believers to offer their bodies as living sacrifices to God.',
    after: 'Paul warns each not to think of himself more highly than he ought.',
    theme: 'Renewing the mind', keyword: 'transformed',
    facts: ['Paul contrasts being shaped by the world with being changed from within.'],
  },
  {
    reference: 'Romans 12:12', book: 'Romans', chapter: 12, verseStart: 12,
    text: 'Be joyful in hope, patient in affliction, persistent in prayer.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul calls believers to love sincerely and be devoted to one another.',
    after: 'Paul urges sharing with the saints and showing hospitality to strangers.',
    theme: 'Joy, patience, prayer', keyword: 'joyful',
    facts: ['Three short commands here anchor the believer in hope, endurance, and prayer.'],
  },
  {
    reference: 'Romans 15:13', book: 'Romans', chapter: 15, verseStart: 13,
    text: 'Now may the God of hope fill you with all joy and peace as you believe in Him, so that you may overflow with hope by the power of the Holy Spirit.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Rome',
    before: 'Paul quotes Isaiah that the root of Jesse will rise to rule the Gentiles.',
    after: 'Paul expresses his confidence that the believers are full of goodness.',
    theme: 'The God of hope', keyword: 'hope',
    facts: ['This benediction names God as the very source and giver of hope.'],
  },
  {
    reference: '1 Corinthians 1:18', book: '1 Corinthians', chapter: 1, verseStart: 18,
    text: 'For the message of the cross is foolishness to those who are perishing, but to us who are being saved it is the power of God.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says Christ sent him to preach the gospel, not with words of wisdom.',
    after: 'Paul cites Isaiah that God will destroy the wisdom of the wise.',
    theme: 'The power of the cross', keyword: 'cross',
    facts: ['Paul reframes the cross, a symbol of shame, as the very power of God.'],
  },
  {
    reference: '1 Corinthians 2:9', book: '1 Corinthians', chapter: 2, verseStart: 9,
    text: 'Rather, as it is written: No eye has seen, no ear has heard, no heart has imagined, what God has prepared for those who love Him.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul speaks of God’s wisdom, hidden and destined for our glory before time.',
    after: 'Paul says God has revealed these things to us by His Spirit.',
    theme: 'What God has prepared', keyword: 'prepared',
    facts: ['Paul draws on the words of Isaiah to describe God’s hidden blessings.'],
  },
  {
    reference: '1 Corinthians 6:19', book: '1 Corinthians', chapter: 6, verseStart: 19,
    text: 'Do you not know that your body is a temple of the Holy Spirit who is in you, whom you have received from God? You are not your own;',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul warns that sexual sin is a sin against one’s own body.',
    after: 'Paul says you were bought at a price, so glorify God with your body.',
    theme: 'The body as God’s temple', keyword: 'temple',
    facts: ['Paul calls the believer’s very body a dwelling place of the Holy Spirit.'],
  },
  {
    reference: '1 Corinthians 10:13', book: '1 Corinthians', chapter: 10, verseStart: 13,
    text: 'No temptation has seized you except what is common to man. And God is faithful; He will not let you be tempted beyond what you can bear. But when you are tempted, He will also provide an escape, so that you can stand up under it.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul warns that whoever thinks he stands should be careful not to fall.',
    after: 'Paul urges the believers to flee from idolatry.',
    theme: 'A way of escape', keyword: 'temptation',
    facts: ['Paul grounds this promise in the faithfulness of God Himself.'],
  },
  {
    reference: '1 Corinthians 10:31', book: '1 Corinthians', chapter: 10, verseStart: 31,
    text: 'So whether you eat or drink or whatever you do, do it all to the glory of God.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul discusses freedom in food while seeking the good of others.',
    after: 'Paul urges the Corinthians not to cause anyone to stumble.',
    theme: 'All to God’s glory', keyword: 'glory',
    facts: ['Paul lifts even ordinary eating and drinking into an act of worship.'],
  },
  {
    reference: '1 Corinthians 13:4', book: '1 Corinthians', chapter: 13, verseStart: 4,
    text: 'Love is patient, love is kind. It does not envy, it does not boast, it is not proud.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says that without love, even great gifts amount to nothing.',
    after: 'Paul continues that love is not rude, self-seeking, or easily angered.',
    theme: 'The character of love', keyword: 'patient',
    facts: ['This chapter is often called the Bible’s great hymn to love.'],
  },
  {
    reference: '1 Corinthians 13:7', book: '1 Corinthians', chapter: 13, verseStart: 7,
    text: 'It bears all things, believes all things, hopes all things, endures all things.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says love keeps no record of wrongs and rejoices in the truth.',
    after: 'Paul declares that love never fails, though prophecies will cease.',
    theme: 'Love that endures', keyword: 'endures',
    facts: ['Four sweeping phrases here describe love’s unfailing endurance.'],
  },
  {
    reference: '1 Corinthians 13:13', book: '1 Corinthians', chapter: 13, verseStart: 13,
    text: 'And now these three remain: faith, hope, and love. But the greatest of these is love.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says we now see only a reflection, but then we shall see face to face.',
    after: 'Paul urges the believers to follow the way of love and desire spiritual gifts.',
    theme: 'The greatest is love', keyword: 'greatest',
    facts: ['Paul ends his hymn to love by naming it above even faith and hope.'],
  },
  {
    reference: '1 Corinthians 15:57', book: '1 Corinthians', chapter: 15, verseStart: 57,
    text: 'But thanks be to God, who gives us the victory through our Lord Jesus Christ.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul asks where, O death, is your sting, since sin’s power is the law.',
    after: 'Paul urges the Corinthians to stand firm and let nothing move them.',
    theme: 'Victory over death', keyword: 'victory',
    facts: ['This verse crowns Paul’s great chapter on the resurrection.'],
  },
  {
    reference: '1 Corinthians 15:58', book: '1 Corinthians', chapter: 15, verseStart: 58,
    text: 'Therefore, my beloved brothers, be steadfast and immovable. Always excel in the work of the Lord, because you know that your labor in the Lord is not in vain.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul gives thanks to God who gives victory through our Lord Jesus Christ.',
    after: 'Paul turns to instructions about the collection for the saints.',
    theme: 'Labor that is not in vain', keyword: 'steadfast',
    facts: ['Paul draws a practical call to persevere from the hope of resurrection.'],
  },
  {
    reference: '1 Corinthians 16:14', book: '1 Corinthians', chapter: 16, verseStart: 14,
    text: 'Do everything in love.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul urges the believers to be watchful, stand firm, and be strong.',
    after: 'Paul commends the household of Stephanas for serving the saints.',
    theme: 'Love in all things', keyword: 'love',
    facts: ['One of the shortest verses in the letter, it sums up the whole of Christian conduct.'],
  },
  {
    reference: '2 Corinthians 1:3', book: '2 Corinthians', chapter: 1, verseStart: 3,
    text: 'Blessed be the God and Father of our Lord Jesus Christ, the Father of compassion and the God of all comfort,',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul opens the letter with grace and peace from God our Father.',
    after: 'Paul says God comforts us so we can comfort others in their troubles.',
    theme: 'The God of all comfort', keyword: 'comfort',
    facts: ['Paul begins this letter by praising God as the source of every comfort.'],
  },
  {
    reference: '2 Corinthians 4:16', book: '2 Corinthians', chapter: 4, verseStart: 16,
    text: 'Therefore we do not lose heart. Though our outer self is wasting away, yet our inner self is being renewed day by day.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says all things are for your benefit, to overflow in thanksgiving to God.',
    after: 'Paul calls our troubles light and momentary compared to eternal glory.',
    theme: 'Renewed day by day', keyword: 'renewed',
    facts: ['Paul contrasts the body’s decline with the daily renewal of the inner self.'],
  },
  {
    reference: '2 Corinthians 4:18', book: '2 Corinthians', chapter: 4, verseStart: 18,
    text: 'So we fix our eyes not on what is seen, but on what is unseen. For what is seen is temporary, but what is unseen is eternal.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says light and momentary troubles work an eternal weight of glory.',
    after: 'Paul speaks of our earthly tent and a building from God, eternal in the heavens.',
    theme: 'Eyes on the eternal', keyword: 'unseen',
    facts: ['Paul urges believers to weigh the eternal above the visible and passing.'],
  },
  {
    reference: '2 Corinthians 5:7', book: '2 Corinthians', chapter: 5, verseStart: 7,
    text: 'For we walk by faith, not by sight.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says that while at home in the body, we are away from the Lord.',
    after: 'Paul says we are confident and would prefer to be at home with the Lord.',
    theme: 'Walking by faith', keyword: 'faith',
    facts: ['This brief verse has become a watchword for trusting God beyond what is seen.'],
  },
  {
    reference: '2 Corinthians 5:17', book: '2 Corinthians', chapter: 5, verseStart: 17,
    text: 'Therefore if anyone is in Christ, he is a new creation. The old has passed away. Behold, the new has come!',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says Christ died for all, that we might live no longer for ourselves.',
    after: 'Paul says God reconciled us to Himself and gave the ministry of reconciliation.',
    theme: 'A new creation', keyword: 'creation',
    facts: ['Paul describes conversion as nothing less than a whole new creation.'],
  },
  {
    reference: '2 Corinthians 5:21', book: '2 Corinthians', chapter: 5, verseStart: 21,
    text: 'God made Him who knew no sin to be sin on our behalf, so that in Him we might become the righteousness of God.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul appeals to the Corinthians as Christ’s ambassadors: be reconciled to God.',
    after: 'Paul urges them not to receive God’s grace in vain.',
    theme: 'The great exchange', keyword: 'righteousness',
    facts: ['This verse captures the exchange of our sin for Christ’s righteousness.'],
  },
  {
    reference: '2 Corinthians 9:7', book: '2 Corinthians', chapter: 9, verseStart: 7,
    text: 'Each one should give what he has decided in his heart to give, not reluctantly or under compulsion, for God loves a cheerful giver.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says the one who sows generously will also reap generously.',
    after: 'Paul says God is able to make all grace abound to you.',
    theme: 'The cheerful giver', keyword: 'cheerful',
    facts: ['Paul teaches that God delights in the heart behind the gift, not the amount.'],
  },
  {
    reference: '2 Corinthians 10:5', book: '2 Corinthians', chapter: 10, verseStart: 5,
    text: 'We tear down arguments and every presumption set up against the knowledge of God, and we take captive every thought to make it obedient to Christ.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul says the weapons of our warfare are not of the flesh, but mighty in God.',
    after: 'Paul says he is ready to punish every act of disobedience.',
    theme: 'Taking thoughts captive', keyword: 'captive',
    facts: ['Paul pictures the mind itself as a battlefield to be surrendered to Christ.'],
  },
  {
    reference: '2 Corinthians 12:9', book: '2 Corinthians', chapter: 12, verseStart: 9,
    text: 'But He said to me, My grace is sufficient for you, for My power is perfected in weakness. Therefore I will boast all the more gladly in my weaknesses, so that the power of Christ may rest on me.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Corinth',
    before: 'Paul pleads three times for the Lord to take away his thorn in the flesh.',
    after: 'Paul says he delights in weaknesses for the sake of Christ.',
    theme: 'Strength in weakness', keyword: 'grace',
    facts: ['These words of the Lord to Paul answered his prayer about a lasting affliction.'],
  },
  // ————————————————————————— Epistles & Revelation —————————————————————————
  {
    reference: 'Galatians 2:20', book: 'Galatians', chapter: 2, verseStart: 20,
    text: 'I have been crucified with Christ, and I no longer live, but Christ lives in me. The life I live in the body, I live by faith in the Son of God, who loved me and gave Himself up for me.',
    testament: 'NT', speaker: 'Paul', audience: 'The churches of Galatia',
    before: 'Paul insists that no one is justified by works of the law, but through faith in Christ.',
    after: 'Paul asks why the Galatians, having begun in the Spirit, now turn back to the law.',
    theme: 'Crucified with Christ', keyword: 'crucified',
    facts: ['Paul roots the whole Christian life in Christ’s love and self-giving death.'],
  },
  {
    reference: 'Galatians 3:28', book: 'Galatians', chapter: 3, verseStart: 28,
    text: 'There is neither Jew nor Greek, slave nor free, male nor female, for you are all one in Christ Jesus.',
    testament: 'NT', speaker: 'Paul', audience: 'The churches of Galatia',
    before: 'Paul says all who are baptized into Christ have clothed themselves with Christ.',
    after: 'Paul concludes that if we belong to Christ, we are Abraham’s heirs.',
    theme: 'Oneness in Christ', keyword: 'one',
    facts: ['This verse proclaims the equal standing of every believer before God.'],
  },
  {
    reference: 'Galatians 5:1', book: 'Galatians', chapter: 5, verseStart: 1,
    text: 'It is for freedom that Christ has set us free. Stand firm, then, and do not be encumbered once more by a yoke of slavery.',
    testament: 'NT', speaker: 'Paul', audience: 'The churches of Galatia',
    before: 'Paul contrasts the covenant of slavery with the freedom of the promise.',
    after: 'Paul warns that turning to circumcision for righteousness severs one from Christ.',
    theme: 'Freedom in Christ', keyword: 'freedom',
    facts: ['Paul urges believers to guard the liberty Christ purchased for them.'],
  },
  {
    reference: 'Galatians 5:22', book: 'Galatians', chapter: 5, verseStart: 22, verseEnd: 23,
    text: 'But the fruit of the Spirit is love, joy, peace, patience, kindness, goodness, faithfulness, gentleness, and self-control. Against such things there is no law.',
    testament: 'NT', speaker: 'Paul', audience: 'The churches of Galatia',
    before: 'Paul lists the works of the flesh that keep people from God’s kingdom.',
    after: 'Paul says those who belong to Christ have crucified the flesh with its passions.',
    theme: 'Fruit of the Spirit', keyword: 'fruit',
    facts: ['These nine graces describe the character the Spirit grows in every believer.'],
  },
  {
    reference: 'Galatians 6:9', book: 'Galatians', chapter: 6, verseStart: 9,
    text: 'Let us not grow weary in well-doing, for in due time we will reap a harvest if we do not give up.',
    testament: 'NT', speaker: 'Paul', audience: 'The churches of Galatia',
    before: 'Paul teaches that whatever a man sows, he will also reap.',
    after: 'Paul urges believers to do good to everyone, especially the family of faith.',
    theme: 'Perseverance in doing good', keyword: 'weary',
    facts: ['Paul promises a harvest to those who keep doing good without giving up.'],
  },
  {
    reference: 'Ephesians 2:8', book: 'Ephesians', chapter: 2, verseStart: 8, verseEnd: 9,
    text: 'For it is by grace you have been saved through faith, and this not from yourselves; it is the gift of God, not by works, so that no one can boast.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Ephesus',
    before: 'Paul says God, rich in mercy, made us alive with Christ even when we were dead in sin.',
    after: 'Paul explains that we are God’s workmanship, created for good works.',
    theme: 'Saved by grace', keyword: 'grace',
    facts: ['Salvation is entirely a gift, leaving no room for human boasting.'],
  },
  {
    reference: 'Ephesians 2:10', book: 'Ephesians', chapter: 2, verseStart: 10,
    text: 'For we are God’s workmanship, created in Christ Jesus to do good works, which God prepared in advance for us to do.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Ephesus',
    before: 'Paul reminds them salvation is God’s gift, not earned by works.',
    after: 'Paul recalls how Gentiles, once far off, have been brought near by the blood of Christ.',
    theme: 'Created for good works', keyword: 'workmanship',
    facts: ['We are saved not by good works, but for them, as God’s handiwork.'],
  },
  {
    reference: 'Ephesians 3:20', book: 'Ephesians', chapter: 3, verseStart: 20, verseEnd: 21,
    text: 'Now to Him who is able to do immeasurably more than all we ask or imagine, according to His power that is at work within us, to Him be the glory in the church and in Christ Jesus throughout all generations, forever and ever. Amen.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Ephesus',
    before: 'Paul prays that they would know the love of Christ that surpasses knowledge.',
    after: 'Paul urges them to walk worthy of the calling they have received.',
    theme: 'God’s abundant power', keyword: 'immeasurably',
    facts: ['Paul closes his prayer with praise to the God who exceeds every request.'],
  },
  {
    reference: 'Ephesians 4:29', book: 'Ephesians', chapter: 4, verseStart: 29,
    text: 'Let no unwholesome talk come out of your mouths, but only what is helpful for building up the one in need and bringing grace to those who listen.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Ephesus',
    before: 'Paul calls them to put off falsehood and speak truth with one another.',
    after: 'Paul warns them not to grieve the Holy Spirit of God.',
    theme: 'Words that build up', keyword: 'unwholesome',
    facts: ['Paul teaches that our words should give grace to those who hear them.'],
  },
  {
    reference: 'Ephesians 4:32', book: 'Ephesians', chapter: 4, verseStart: 32,
    text: 'Be kind and tenderhearted to one another, forgiving each other just as in Christ God forgave you.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Ephesus',
    before: 'Paul tells them to put away bitterness, anger, and malice.',
    after: 'Paul calls them to be imitators of God as beloved children.',
    theme: 'Kindness and forgiveness', keyword: 'tenderhearted',
    facts: ['Our forgiveness of others is patterned on God’s forgiveness of us in Christ.'],
  },
  {
    reference: 'Ephesians 6:10', book: 'Ephesians', chapter: 6, verseStart: 10,
    text: 'Finally, be strong in the Lord and in His mighty power.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Ephesus',
    before: 'Paul instructs masters and servants to serve one another sincerely.',
    after: 'Paul tells them to put on the full armor of God.',
    theme: 'Strength in the Lord', keyword: 'strong',
    facts: ['Our strength for spiritual battle comes from the Lord, not ourselves.'],
  },
  {
    reference: 'Ephesians 6:11', book: 'Ephesians', chapter: 6, verseStart: 11,
    text: 'Put on the full armor of God, so that you can make your stand against the devil’s schemes.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Ephesus',
    before: 'Paul calls believers to be strong in the Lord and in His mighty power.',
    after: 'Paul explains that our struggle is not against flesh and blood but spiritual forces.',
    theme: 'The armor of God', keyword: 'armor',
    facts: ['Paul pictures God’s provision for the believer as a soldier’s full armor.'],
  },
  {
    reference: 'Philippians 1:6', book: 'Philippians', chapter: 1, verseStart: 6,
    text: 'being confident of this, that He who began a good work in you will carry it on to completion until the day of Christ Jesus.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Philippi',
    before: 'Paul gives thanks for the Philippians’ partnership in the gospel.',
    after: 'Paul tells them how he holds them in his heart with the affection of Christ.',
    theme: 'God finishes His work', keyword: 'completion',
    facts: ['Paul assures believers that the God who saves them will also complete them.'],
  },
  {
    reference: 'Philippians 1:21', book: 'Philippians', chapter: 1, verseStart: 21,
    text: 'For to me, to live is Christ and to die is gain.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Philippi',
    before: 'Paul rejoices that Christ is proclaimed, whether from true or false motives.',
    after: 'Paul weighs remaining in the body against departing to be with Christ.',
    theme: 'Christ is everything', keyword: 'gain',
    facts: ['Paul wrote this from prison, treasuring Christ above life itself.'],
  },
  {
    reference: 'Philippians 2:3', book: 'Philippians', chapter: 2, verseStart: 3,
    text: 'Do nothing out of selfish ambition or empty conceit, but in humility consider others more important than yourselves.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Philippi',
    before: 'Paul appeals for unity, being like-minded and one in spirit.',
    after: 'Paul points to Christ, who humbled Himself and took the form of a servant.',
    theme: 'Humility toward others', keyword: 'humility',
    facts: ['This verse leads into the great hymn of Christ’s humility in Philippians 2.'],
  },
  {
    reference: 'Philippians 4:6', book: 'Philippians', chapter: 4, verseStart: 6, verseEnd: 7,
    text: 'Do not be anxious about anything, but in everything, by prayer and petition, with thanksgiving, present your requests to God. And the peace of God, which surpasses all understanding, will guard your hearts and your minds in Christ Jesus.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Philippi',
    before: 'Paul urges the Philippians to rejoice in the Lord always.',
    after: 'Paul tells them to think on whatever is true, noble, and lovely.',
    theme: 'Peace over anxiety', keyword: 'anxious',
    facts: ['Paul wrote Philippians while in prison, yet it overflows with joy.'],
  },
  {
    reference: 'Philippians 4:8', book: 'Philippians', chapter: 4, verseStart: 8,
    text: 'Finally, brothers, whatever is true, whatever is honorable, whatever is right, whatever is pure, whatever is lovely, whatever is admirable, if anything is excellent or praiseworthy, think on these things.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Philippi',
    before: 'Paul promises that the peace of God will guard their hearts and minds.',
    after: 'Paul urges them to practice what they have learned and received from him.',
    theme: 'Set your mind on good', keyword: 'true',
    facts: ['Paul directs the believer’s thoughts toward all that is good and worthy of praise.'],
  },
  {
    reference: 'Philippians 4:13', book: 'Philippians', chapter: 4, verseStart: 13,
    text: 'I can do all things through Christ who gives me strength.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Philippi',
    before: 'Paul says he has learned the secret of contentment in every situation.',
    after: 'Paul thanks the Philippians for sharing in his troubles.',
    theme: 'Strength through Christ', keyword: 'strength',
    facts: ['Paul’s confidence is not in himself but in the Christ who strengthens him.'],
  },
  {
    reference: 'Philippians 4:19', book: 'Philippians', chapter: 4, verseStart: 19,
    text: 'And my God will supply all your needs according to His glorious riches in Christ Jesus.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Philippi',
    before: 'Paul thanks the Philippians for their generous gift to support his ministry.',
    after: 'Paul gives glory to God and sends greetings to the saints.',
    theme: 'God supplies every need', keyword: 'supply',
    facts: ['Paul assures the generous Philippians that God will provide for them in return.'],
  },
  {
    reference: 'Colossians 3:2', book: 'Colossians', chapter: 3, verseStart: 2,
    text: 'Set your minds on things above, not on earthly things.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Colossae',
    before: 'Paul says that since we are raised with Christ, we should seek things above.',
    after: 'Paul reminds them their life is now hidden with Christ in God.',
    theme: 'Mind on things above', keyword: 'above',
    facts: ['Paul calls believers to a heavenly focus that reshapes earthly living.'],
  },
  {
    reference: 'Colossians 3:13', book: 'Colossians', chapter: 3, verseStart: 13,
    text: 'Bear with one another and forgive any complaint you may have against someone else. Forgive as the Lord forgave you.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Colossae',
    before: 'Paul tells them to clothe themselves with compassion, kindness, and humility.',
    after: 'Paul says that above all, they should put on love, which binds all in unity.',
    theme: 'Forgiving one another', keyword: 'forgive',
    facts: ['The Lord’s forgiveness of us becomes the measure of our forgiveness of others.'],
  },
  {
    reference: 'Colossians 3:15', book: 'Colossians', chapter: 3, verseStart: 15,
    text: 'Let the peace of Christ rule in your hearts, since as members of one body you were called to peace. And be thankful.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Colossae',
    before: 'Paul urges them to put on love, which binds everything together in unity.',
    after: 'Paul tells them to let the word of Christ dwell in them richly.',
    theme: 'Peace of Christ rules', keyword: 'peace',
    facts: ['Paul links the peace of Christ with unity and gratitude in the church.'],
  },
  {
    reference: 'Colossians 3:23', book: 'Colossians', chapter: 3, verseStart: 23,
    text: 'Whatever you do, work at it with your whole being, for the Lord and not for men,',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Colossae',
    before: 'Paul instructs servants to obey their masters with sincerity of heart.',
    after: 'Paul promises the reward of the inheritance from the Lord, whom they serve.',
    theme: 'Work as unto the Lord', keyword: 'work',
    facts: ['Paul teaches that all honest work becomes worship when done for the Lord.'],
  },
  {
    reference: '1 Thessalonians 5:11', book: '1 Thessalonians', chapter: 5, verseStart: 11,
    text: 'Therefore encourage and build one another up, just as you are already doing.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Thessalonica',
    before: 'Paul reminds them that God appointed them to receive salvation, not wrath.',
    after: 'Paul asks them to respect and love those who labor among them.',
    theme: 'Encourage one another', keyword: 'encourage',
    facts: ['Paul commends the Thessalonians for the mutual encouragement they already practice.'],
  },
  {
    reference: '1 Thessalonians 5:16', book: '1 Thessalonians', chapter: 5, verseStart: 16, verseEnd: 18,
    text: 'Rejoice always, pray without ceasing, give thanks in every circumstance, for this is God’s will for you in Christ Jesus.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Thessalonica',
    before: 'Paul tells them to be at peace and to do good to one another and to all.',
    after: 'Paul tells them not to quench the Spirit or despise prophecies.',
    theme: 'Joy, prayer, thanks', keyword: 'Rejoice',
    facts: ['Three short commands here sum up a life turned continually toward God.'],
  },
  {
    reference: '2 Thessalonians 3:3', book: '2 Thessalonians', chapter: 3, verseStart: 3,
    text: 'But the Lord is faithful, and He will strengthen you and guard you from the evil one.',
    testament: 'NT', speaker: 'Paul', audience: 'The church in Thessalonica',
    before: 'Paul asks for prayer that the message may spread and that he be delivered from evil men.',
    after: 'Paul expresses confidence that they will keep doing what he commands.',
    theme: 'The faithful Lord', keyword: 'faithful',
    facts: ['Paul contrasts unfaithful people with the ever-faithful Lord who guards His own.'],
  },
  {
    reference: '1 Timothy 4:12', book: '1 Timothy', chapter: 4, verseStart: 12,
    text: 'Let no one despise your youth, but set an example for the believers in speech, in conduct, in love, in faith, in purity.',
    testament: 'NT', speaker: 'Paul', audience: 'Timothy, Paul’s son in the faith',
    before: 'Paul urges Timothy to devote himself to the public reading of Scripture.',
    after: 'Paul tells Timothy to give attention to teaching until he arrives.',
    theme: 'A young leader’s example', keyword: 'youth',
    facts: ['Paul encourages the young Timothy to lead by godly example, not age.'],
  },
  {
    reference: '1 Timothy 6:12', book: '1 Timothy', chapter: 6, verseStart: 12,
    text: 'Fight the good fight of the faith. Take hold of the eternal life to which you were called when you made the good confession before many witnesses.',
    testament: 'NT', speaker: 'Paul', audience: 'Timothy, Paul’s son in the faith',
    before: 'Paul charges Timothy to pursue righteousness, godliness, faith, and love.',
    after: 'Paul charges him in God’s sight to keep the command without spot until Christ appears.',
    theme: 'Fight the good fight', keyword: 'Fight',
    facts: ['Paul pictures faithful discipleship as a good fight worth every effort.'],
  },
  {
    reference: '2 Timothy 1:7', book: '2 Timothy', chapter: 1, verseStart: 7,
    text: 'For God has not given us a spirit of fear, but of power, love, and self-control.',
    testament: 'NT', speaker: 'Paul', audience: 'Timothy, Paul’s son in the faith',
    before: 'Paul reminds Timothy to fan into flame the gift of God within him.',
    after: 'Paul urges Timothy not to be ashamed of the testimony of the Lord.',
    theme: 'A spirit of power', keyword: 'fear',
    facts: ['Paul reassures the timid Timothy that God supplies courage, not fear.'],
  },
  {
    reference: '2 Timothy 3:16', book: '2 Timothy', chapter: 3, verseStart: 16, verseEnd: 17,
    text: 'All Scripture is God-breathed and is useful for instruction, for conviction, for correction, and for training in righteousness, so that the man of God may be complete, fully equipped for every good work.',
    testament: 'NT', speaker: 'Paul', audience: 'Timothy, Paul’s son in the faith',
    before: 'Paul reminds Timothy of the sacred writings he has known from childhood.',
    after: 'Paul solemnly charges Timothy to preach the word in season and out of season.',
    theme: 'Scripture is God-breathed', keyword: 'Scripture',
    facts: ['Paul affirms that all of Scripture comes from the very breath of God.'],
  },
  {
    reference: '2 Timothy 4:7', book: '2 Timothy', chapter: 4, verseStart: 7,
    text: 'I have fought the good fight, I have finished the race, I have kept the faith.',
    testament: 'NT', speaker: 'Paul', audience: 'Timothy, Paul’s son in the faith',
    before: 'Paul says the time of his departure is near, poured out like a drink offering.',
    after: 'Paul looks ahead to the crown of righteousness the Lord will award him.',
    theme: 'Finishing faithfully', keyword: 'race',
    facts: ['These are among Paul’s last recorded words, written near the end of his life.'],
  },
  {
    reference: 'Titus 2:11', book: 'Titus', chapter: 2, verseStart: 11,
    text: 'For the grace of God has appeared, bringing salvation to everyone.',
    testament: 'NT', speaker: 'Paul', audience: 'Titus, Paul’s true child in the faith',
    before: 'Paul instructs Titus on how various groups in the church should live.',
    after: 'Paul says this grace teaches us to renounce ungodliness and live self-controlled lives.',
    theme: 'Grace has appeared', keyword: 'salvation',
    facts: ['Paul describes God’s grace as a light that has dawned for all people.'],
  },
  {
    reference: 'Titus 3:5', book: 'Titus', chapter: 3, verseStart: 5,
    text: 'He saved us, not by the righteous deeds we had done, but according to His mercy, through the washing of new birth and renewal by the Holy Spirit.',
    testament: 'NT', speaker: 'Paul', audience: 'Titus, Paul’s true child in the faith',
    before: 'Paul recalls that we ourselves were once foolish, disobedient, and enslaved to sin.',
    after: 'Paul says God poured out the Spirit on us generously through Jesus Christ our Savior.',
    theme: 'Saved by His mercy', keyword: 'mercy',
    facts: ['Paul grounds salvation in God’s mercy and the renewing work of the Spirit.'],
  },
  {
    reference: 'Hebrews 4:12', book: 'Hebrews', chapter: 4, verseStart: 12,
    text: 'For the word of God is living and active. Sharper than any double-edged sword, it pierces even to dividing soul and spirit, joints and marrow; it judges the thoughts and intentions of the heart.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author warns against following Israel’s example of unbelief in the wilderness.',
    after: 'The author reminds them that nothing is hidden from the eyes of God.',
    theme: 'The living word', keyword: 'living',
    facts: ['Scripture is pictured as a sword that reaches the deepest places of the heart.'],
  },
  {
    reference: 'Hebrews 4:16', book: 'Hebrews', chapter: 4, verseStart: 16,
    text: 'Let us then approach the throne of grace with confidence, so that we may receive mercy and find grace to help us in our time of need.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author presents Jesus as a great high priest who sympathizes with our weakness.',
    after: 'The author explains how every high priest is chosen to represent people before God.',
    theme: 'Bold access to God', keyword: 'confidence',
    facts: ['Because of Jesus, believers may come boldly to God’s throne for mercy.'],
  },
  {
    reference: 'Hebrews 10:23', book: 'Hebrews', chapter: 10, verseStart: 23,
    text: 'Let us hold resolutely to the hope we profess, for He who promised is faithful.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author invites them to draw near to God with a sincere heart in full assurance.',
    after: 'The author urges them to spur one another on toward love and good deeds.',
    theme: 'Holding to hope', keyword: 'hope',
    facts: ['Our confidence rests not on ourselves but on the faithfulness of God who promised.'],
  },
  {
    reference: 'Hebrews 11:1', book: 'Hebrews', chapter: 11, verseStart: 1,
    text: 'Now faith is the assurance of what we hope for and the certainty of what we do not see.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author says the righteous will live by faith and not shrink back.',
    after: 'The author recounts how by faith the ancients were commended.',
    theme: 'The nature of faith', keyword: 'assurance',
    facts: ['This verse opens the great chapter honoring the heroes of faith.'],
  },
  {
    reference: 'Hebrews 11:6', book: 'Hebrews', chapter: 11, verseStart: 6,
    text: 'And without faith it is impossible to please God, because anyone who approaches Him must believe that He exists and that He rewards those who earnestly seek Him.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author tells how Enoch was taken up because he pleased God.',
    after: 'The author describes how by faith Noah built an ark to save his household.',
    theme: 'Faith pleases God', keyword: 'impossible',
    facts: ['God rewards not casual seekers, but those who earnestly seek Him in faith.'],
  },
  {
    reference: 'Hebrews 12:1', book: 'Hebrews', chapter: 12, verseStart: 1,
    text: 'Therefore, since we are surrounded by such a great cloud of witnesses, let us throw off every encumbrance and the sin that so easily entangles, and let us run with endurance the race set out for us.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author has just recounted the long roll of faithful men and women.',
    after: 'The author points to Jesus, the author and perfecter of our faith.',
    theme: 'Run with endurance', keyword: 'witnesses',
    facts: ['The faithful of chapter 11 become the cheering cloud around the runner.'],
  },
  {
    reference: 'Hebrews 12:2', book: 'Hebrews', chapter: 12, verseStart: 2,
    text: 'Let us fix our eyes on Jesus, the author and perfecter of our faith, who for the joy set before Him endured the cross, scorning its shame, and sat down at the right hand of the throne of God.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author calls us to run our race with endurance, laying aside every weight.',
    after: 'The author urges us to consider Him who endured such hostility from sinners.',
    theme: 'Fixing our eyes on Jesus', keyword: 'author',
    facts: ['Jesus is both the beginning and the goal of the believer’s faith.'],
  },
  {
    reference: 'Hebrews 13:5', book: 'Hebrews', chapter: 13, verseStart: 5,
    text: 'Keep your lives free from the love of money and be content with what you have, for God has said: Never will I leave you, never will I forsake you.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author calls them to let love continue and to honor marriage.',
    after: 'The author says we may boldly declare that the Lord is our helper.',
    theme: 'Contentment and God’s presence', keyword: 'content',
    facts: ['Contentment rests on God’s unbreakable promise never to abandon His people.'],
  },
  {
    reference: 'Hebrews 13:8', book: 'Hebrews', chapter: 13, verseStart: 8,
    text: 'Jesus Christ is the same yesterday and today and forever.',
    testament: 'NT', speaker: 'The author of Hebrews', audience: 'Hebrew Christians',
    before: 'The author tells them to remember their leaders and imitate their faith.',
    after: 'The author warns them not to be led astray by strange teachings.',
    theme: 'The unchanging Christ', keyword: 'yesterday',
    facts: ['Amid changing leaders and teachings, Christ Himself remains constant.'],
  },
  {
    reference: 'James 1:2', book: 'James', chapter: 1, verseStart: 2,
    text: 'Consider it pure joy, my brothers, when you encounter trials of many kinds,',
    testament: 'NT', speaker: 'James', audience: 'Jewish believers scattered abroad',
    before: 'James greets the twelve tribes dispersed among the nations.',
    after: 'James explains that the testing of faith produces perseverance.',
    theme: 'Joy in trials', keyword: 'trials',
    facts: ['James was likely the brother of Jesus and a leader of the Jerusalem church.'],
  },
  {
    reference: 'James 1:5', book: 'James', chapter: 1, verseStart: 5,
    text: 'Now if any of you lacks wisdom, he should ask God, who gives generously to all without finding fault, and it will be given to him.',
    testament: 'NT', speaker: 'James', audience: 'Jewish believers scattered abroad',
    before: 'James urges believers to let perseverance finish its work.',
    after: 'James says the one who asks must believe and not doubt.',
    theme: 'Asking God for wisdom', keyword: 'wisdom',
    facts: ['James emphasizes practical wisdom lived out in daily conduct.'],
  },
  {
    reference: 'James 1:17', book: 'James', chapter: 1, verseStart: 17,
    text: 'Every good and perfect gift is from above, coming down from the Father of the heavenly lights, with whom there is no change or shifting shadow.',
    testament: 'NT', speaker: 'James', audience: 'Jewish believers scattered abroad',
    before: 'James warns believers not to be deceived about the source of temptation.',
    after: 'James says God chose to give us birth through the word of truth.',
    theme: 'God’s unchanging goodness', keyword: 'gift',
    facts: ['James pictures God as the Father of the heavenly lights who never changes.'],
  },
  {
    reference: 'James 1:22', book: 'James', chapter: 1, verseStart: 22,
    text: 'Be doers of the word, and not hearers only. Otherwise, you are deceiving yourselves.',
    testament: 'NT', speaker: 'James', audience: 'Jewish believers scattered abroad',
    before: 'James tells believers to humbly accept the word planted in them.',
    after: 'James compares a mere hearer to a man who forgets his own face in a mirror.',
    theme: 'Living out the word', keyword: 'doers',
    facts: ['James calls for faith that shows itself in obedient action.'],
  },
  {
    reference: 'James 2:17', book: 'James', chapter: 2, verseStart: 17,
    text: 'So too, faith by itself, if it does not result in action, is dead.',
    testament: 'NT', speaker: 'James', audience: 'Jewish believers scattered abroad',
    before: 'James asks what good it is to claim faith yet have no deeds.',
    after: 'James argues that he will show his faith by his works.',
    theme: 'Faith and works', keyword: 'faith',
    facts: ['James teaches that genuine faith naturally produces good deeds.'],
  },
  {
    reference: 'James 4:7', book: 'James', chapter: 4, verseStart: 7,
    text: 'Submit yourselves, then, to God. Resist the devil, and he will flee from you.',
    testament: 'NT', speaker: 'James', audience: 'Jewish believers scattered abroad',
    before: 'James says God gives grace to the humble but opposes the proud.',
    after: 'James urges them to draw near to God.',
    theme: 'Submitting to God', keyword: 'Resist',
    facts: ['James pairs submission to God with active resistance to the devil.'],
  },
  {
    reference: 'James 4:8', book: 'James', chapter: 4, verseStart: 8,
    text: 'Draw near to God, and He will draw near to you. Cleanse your hands, you sinners, and purify your hearts, you double-minded.',
    testament: 'NT', speaker: 'James', audience: 'Jewish believers scattered abroad',
    before: 'James tells believers to resist the devil so he will flee.',
    after: 'James calls them to grieve, mourn, and humble themselves before the Lord.',
    theme: 'Drawing near to God', keyword: 'Draw',
    facts: ['James promises that those who draw near to God find Him drawing near in return.'],
  },
  {
    reference: '1 Peter 1:3', book: '1 Peter', chapter: 1, verseStart: 3,
    text: 'Blessed be the God and Father of our Lord Jesus Christ! By His great mercy He has given us new birth into a living hope through the resurrection of Jesus Christ from the dead,',
    testament: 'NT', speaker: 'Peter', audience: 'Believers scattered across Asia Minor',
    before: 'Peter greets the elect exiles chosen by God the Father.',
    after: 'Peter describes an inheritance that can never perish, kept in heaven.',
    theme: 'Living hope', keyword: 'hope',
    facts: ['Peter grounds the believer’s living hope in Christ’s resurrection.'],
  },
  {
    reference: '1 Peter 2:9', book: '1 Peter', chapter: 2, verseStart: 9,
    text: 'But you are a chosen people, a royal priesthood, a holy nation, a people for God’s own possession, to proclaim the virtues of Him who called you out of darkness into His marvelous light.',
    testament: 'NT', speaker: 'Peter', audience: 'Believers scattered across Asia Minor',
    before: 'Peter says Christ is precious to those who believe.',
    after: 'Peter reminds them that once they were not a people, but now are God’s people.',
    theme: 'A chosen people', keyword: 'priesthood',
    facts: ['Peter applies Israel’s covenant titles to the church of Jesus Christ.'],
  },
  {
    reference: '1 Peter 3:15', book: '1 Peter', chapter: 3, verseStart: 15,
    text: 'But in your hearts sanctify Christ as Lord. Always be prepared to give a defense to everyone who asks you the reason for the hope that is in you. But respond with gentleness and respect,',
    testament: 'NT', speaker: 'Peter', audience: 'Believers scattered across Asia Minor',
    before: 'Peter says not to fear what others fear or be troubled.',
    after: 'Peter urges them to keep a clear conscience so slanderers are ashamed.',
    theme: 'Ready to give a defense', keyword: 'defense',
    facts: ['Peter calls believers to explain their hope with gentleness and respect.'],
  },
  {
    reference: '1 Peter 5:7', book: '1 Peter', chapter: 5, verseStart: 7,
    text: 'Cast all your anxiety on Him, because He cares for you.',
    testament: 'NT', speaker: 'Peter', audience: 'Believers scattered across Asia Minor',
    before: 'Peter tells believers to humble themselves under God’s mighty hand.',
    after: 'Peter warns them to be alert against the devil.',
    theme: 'Casting cares on God', keyword: 'anxiety',
    facts: ['Peter wrote to Christians who were suffering for their faith.'],
  },
  {
    reference: '2 Peter 3:9', book: '2 Peter', chapter: 3, verseStart: 9,
    text: 'The Lord is not slow in keeping His promise as some understand slowness, but is patient with you, not wanting anyone to perish, but everyone to come to repentance.',
    testament: 'NT', speaker: 'Peter', audience: 'Believers growing in faith',
    before: 'Peter reminds them that with the Lord a day is like a thousand years.',
    after: 'Peter warns that the day of the Lord will come like a thief.',
    theme: 'God’s patience', keyword: 'patient',
    facts: ['Peter explains that God delays judgment to give room for repentance.'],
  },
  {
    reference: '1 John 1:9', book: '1 John', chapter: 1, verseStart: 9,
    text: 'If we confess our sins, He is faithful and just to forgive us our sins and to cleanse us from all unrighteousness.',
    testament: 'NT', speaker: 'John', audience: 'The early church, children of God',
    before: 'John says that if we claim to be without sin, we deceive ourselves.',
    after: 'John warns that claiming we have not sinned makes God out to be a liar.',
    theme: 'Confession and forgiveness', keyword: 'confess',
    facts: ['John assures believers that God faithfully forgives those who confess.'],
  },
  {
    reference: '1 John 3:1', book: '1 John', chapter: 3, verseStart: 1,
    text: 'Behold what manner of love the Father has given us, that we should be called children of God. And that is what we are! The reason the world does not know us is that it did not know Him.',
    testament: 'NT', speaker: 'John', audience: 'The early church, children of God',
    before: 'John says everyone who practices righteousness has been born of God.',
    after: 'John says that when Christ appears, we shall be like Him.',
    theme: 'Children of God', keyword: 'children',
    facts: ['John marvels that the Father’s love makes believers His own children.'],
  },
  {
    reference: '1 John 4:8', book: '1 John', chapter: 4, verseStart: 8,
    text: 'Whoever does not love does not know God, because God is love.',
    testament: 'NT', speaker: 'John', audience: 'The early church, children of God',
    before: 'John calls believers to love one another, for love comes from God.',
    after: 'John says God showed His love by sending His only Son into the world.',
    theme: 'God is love', keyword: 'love',
    facts: ['John gives one of Scripture’s clearest statements: God is love.'],
  },
  {
    reference: '1 John 4:18', book: '1 John', chapter: 4, verseStart: 18,
    text: 'There is no fear in love, but perfect love drives out fear, because fear involves punishment. The one who fears has not been perfected in love.',
    testament: 'NT', speaker: 'John', audience: 'The early church, children of God',
    before: 'John says we can have confidence on the day of judgment.',
    after: 'John reminds believers that we love because He first loved us.',
    theme: 'Love casts out fear', keyword: 'fear',
    facts: ['John teaches that being secure in God’s love removes the dread of punishment.'],
  },
  {
    reference: '1 John 4:19', book: '1 John', chapter: 4, verseStart: 19,
    text: 'We love because He first loved us.',
    testament: 'NT', speaker: 'John', audience: 'The early church, children of God',
    before: 'John says perfect love drives out fear.',
    after: 'John says anyone who claims to love God yet hates his brother is a liar.',
    theme: 'Responding to God’s love', keyword: 'first',
    facts: ['John roots all Christian love in God’s prior love for us.'],
  },
  {
    reference: '1 John 5:14', book: '1 John', chapter: 5, verseStart: 14,
    text: 'And this is the confidence that we have before Him: that if we ask anything according to His will, He hears us.',
    testament: 'NT', speaker: 'John', audience: 'The early church, children of God',
    before: 'John writes so that believers may know they have eternal life.',
    after: 'John says that if we know He hears us, we know we have what we asked of Him.',
    theme: 'Confidence in prayer', keyword: 'confidence',
    facts: ['John assures believers that prayer aligned with God’s will is always heard.'],
  },
  {
    reference: 'Jude 1:24', book: 'Jude', chapter: 1, verseStart: 24,
    text: 'Now to Him who is able to keep you from stumbling and to present you unblemished in His glorious presence, with great joy,',
    testament: 'NT', speaker: 'Jude', audience: 'Believers called and kept in Christ',
    before: 'Jude urges believers to build themselves up in their most holy faith.',
    after: 'Jude ascribes glory, majesty, dominion, and authority to God our Savior.',
    theme: 'God who keeps us', keyword: 'stumbling',
    facts: ['Jude closes his letter with a beloved doxology of praise to God.'],
  },
  {
    reference: 'Revelation 3:20', book: 'Revelation', chapter: 3, verseStart: 20,
    text: 'Behold, I stand at the door and knock. If anyone hears My voice and opens the door, I will come in and dine with him, and he with Me.',
    testament: 'NT', speaker: 'Jesus', audience: 'The church in Laodicea',
    before: 'Jesus counsels the lukewarm church to be earnest and repent.',
    after: 'Jesus promises the victor a place with Him on His throne.',
    theme: 'Christ knocking', keyword: 'knock',
    facts: ['Jesus offers fellowship to the lukewarm Laodiceans who will open the door.'],
  },
  {
    reference: 'Revelation 21:4', book: 'Revelation', chapter: 21, verseStart: 4,
    text: 'He will wipe away every tear from their eyes, and there will be no more death or mourning or crying or pain, for the former things have passed away.',
    testament: 'NT', speaker: 'A voice from the throne', audience: 'John and the churches',
    before: 'John sees the new Jerusalem coming down as God’s dwelling with man.',
    after: 'The One on the throne declares that He is making all things new.',
    theme: 'No more tears', keyword: 'tear',
    facts: ['John’s vision promises the end of death, mourning, and pain in the new creation.'],
  },
  {
    reference: 'Revelation 22:13', book: 'Revelation', chapter: 22, verseStart: 13,
    text: 'I am the Alpha and the Omega, the First and the Last, the Beginning and the End.',
    testament: 'NT', speaker: 'Jesus', audience: 'John and the churches',
    before: 'Jesus announces that He is coming soon with His reward.',
    after: 'Jesus blesses those who wash their robes and enter the city.',
    theme: 'The First and the Last', keyword: 'Beginning',
    facts: ['Jesus applies to Himself the same eternal title given to the Lord God.'],
  },
  {
    reference: 'Psalm 1:1', book: 'Psalms', chapter: 1, verseStart: 1,
    text: 'Blessed is the man who does not walk in the counsel of the wicked, or set foot on the path of sinners, or sit in the seat of mockers.',
    testament: 'OT', speaker: 'The psalmist', audience: 'God’s people',
    before: 'The Psalter opens with a contrast between the righteous and the wicked.',
    after: 'The psalmist says the blessed man delights in the law of the LORD.',
    theme: 'The blessed life', keyword: 'Blessed',
    facts: ['Psalm 1 serves as a gateway to the entire book of Psalms.'],
  },
  {
    reference: 'Psalm 19:1', book: 'Psalms', chapter: 19, verseStart: 1,
    text: 'The heavens declare the glory of God; the skies proclaim the work of His hands.',
    testament: 'OT', speaker: 'David', audience: 'God’s people',
    before: 'David opens a psalm celebrating God’s revelation in creation.',
    after: 'David says day after day the skies pour forth speech.',
    theme: 'Creation reveals God', keyword: 'heavens',
    facts: ['David points to the sky itself as a silent witness to God’s glory.'],
  },
  {
    reference: 'Psalm 19:14', book: 'Psalms', chapter: 19, verseStart: 14,
    text: 'May the words of my mouth and the meditation of my heart be pleasing in Your sight, O LORD, my Rock and my Redeemer.',
    testament: 'OT', speaker: 'David', audience: 'God’s people',
    before: 'David asks God to keep him from willful sins.',
    after: 'This verse closes the psalm as a prayer of devotion.',
    theme: 'Pleasing words and thoughts', keyword: 'meditation',
    facts: ['David asks that even his private thoughts would please the LORD.'],
  },
  {
    reference: 'Psalm 42:1', book: 'Psalms', chapter: 42, verseStart: 1,
    text: 'As the deer pants for streams of water, so my soul longs after You, O God.',
    testament: 'OT', speaker: 'The sons of Korah', audience: 'God’s people',
    before: 'This psalm begins a heartfelt cry of spiritual thirst for God.',
    after: 'The psalmist asks when he may come and appear before God.',
    theme: 'Thirsting for God', keyword: 'pants',
    facts: ['The sons of Korah picture the soul’s longing as a deer seeking water.'],
  },
  {
    reference: 'Psalm 55:22', book: 'Psalms', chapter: 55, verseStart: 22,
    text: 'Cast your burden upon the LORD and He will sustain you; He will never let the righteous be shaken.',
    testament: 'OT', speaker: 'David', audience: 'God’s people',
    before: 'David laments the betrayal of a close friend.',
    after: 'David expresses trust that God will bring down the wicked.',
    theme: 'Casting our burdens on God', keyword: 'burden',
    facts: ['Peter later echoes this verse when he tells believers to cast their cares on God.'],
  },
  {
    reference: 'Psalm 56:3', book: 'Psalms', chapter: 56, verseStart: 3,
    text: 'When I am afraid, I put my trust in You.',
    testament: 'OT', speaker: 'David', audience: 'God’s people',
    before: 'David cries out while seized by the Philistines in Gath.',
    after: 'David praises God, whose word he trusts, and does not fear man.',
    theme: 'Trusting God in fear', keyword: 'afraid',
    facts: ['David wrote this psalm during a time of great personal danger.'],
  },
  {
    reference: 'Psalm 100:1', book: 'Psalms', chapter: 100, verseStart: 1,
    text: 'Make a joyful noise to the LORD, all the earth.',
    testament: 'OT', speaker: 'The psalmist', audience: 'All the earth',
    before: 'This psalm is titled a psalm for giving thanks.',
    after: 'The psalmist calls all to serve the LORD with gladness.',
    theme: 'Joyful worship', keyword: 'joyful',
    facts: ['Psalm 100 is a beloved call to thankful, exuberant praise.'],
  },
  {
    reference: 'Psalm 103:1', book: 'Psalms', chapter: 103, verseStart: 1,
    text: 'Bless the LORD, O my soul, and all that is within me, bless His holy name.',
    testament: 'OT', speaker: 'David', audience: 'God’s people',
    before: 'David begins a psalm calling his own soul to worship.',
    after: 'David recalls that God forgives sins and heals diseases.',
    theme: 'Blessing the Lord', keyword: 'Bless',
    facts: ['David stirs up his own soul to praise God from the depths of his being.'],
  },
  {
    reference: 'Psalm 121:1', book: 'Psalms', chapter: 121, verseStart: 1, verseEnd: 2,
    text: 'I lift up my eyes to the hills. From where does my help come? My help comes from the LORD, the Maker of heaven and earth.',
    testament: 'OT', speaker: 'The psalmist', audience: 'God’s people',
    before: 'This song of ascents begins a pilgrim’s meditation on God’s protection.',
    after: 'The psalmist affirms that God will not let his foot slip.',
    theme: 'Looking to God for help', keyword: 'help',
    facts: ['Psalm 121 is one of the songs of ascents sung by pilgrims traveling to Jerusalem.'],
  },
  {
    reference: 'Psalm 143:8', book: 'Psalms', chapter: 143, verseStart: 8,
    text: 'Let me hear Your loving devotion in the morning, for I have put my trust in You. Teach me the way I should walk, for to You I lift up my soul.',
    testament: 'OT', speaker: 'David', audience: 'God’s people',
    before: 'David pleads for God’s answer as his spirit grows faint.',
    after: 'David asks God to rescue him from his enemies.',
    theme: 'Morning trust in God', keyword: 'morning',
    facts: ['David begins each day by lifting his soul to God in trust.'],
  },
  {
    reference: 'Psalm 145:8', book: 'Psalms', chapter: 145, verseStart: 8,
    text: 'The LORD is gracious and compassionate, slow to anger and abounding in loving devotion.',
    testament: 'OT', speaker: 'David', audience: 'God’s people',
    before: 'David praises God’s greatness from one generation to the next.',
    after: 'David declares that the LORD is good to all.',
    theme: 'God’s gracious character', keyword: 'gracious',
    facts: ['This verse echoes God’s own self-description given to Moses at Sinai.'],
  },
  {
    reference: 'Proverbs 4:23', book: 'Proverbs', chapter: 4, verseStart: 23,
    text: 'Guard your heart with all diligence, for from it flow springs of life.',
    testament: 'OT', speaker: 'Solomon', audience: 'Those seeking wisdom',
    before: 'Solomon urges his son to hold on to instruction and not let it go.',
    after: 'Solomon tells him to put away perversity from his mouth.',
    theme: 'Guarding the heart', keyword: 'Guard',
    facts: ['Solomon teaches that the heart is the wellspring of one’s whole life.'],
  },
  {
    reference: 'Proverbs 15:1', book: 'Proverbs', chapter: 15, verseStart: 1,
    text: 'A gentle answer turns away wrath, but a harsh word stirs up anger.',
    testament: 'OT', speaker: 'Solomon', audience: 'Those seeking wisdom',
    before: 'Solomon contrasts the ways of the wise and the foolish in speech.',
    after: 'Solomon says the tongue of the wise commends knowledge.',
    theme: 'The power of gentle words', keyword: 'gentle',
    facts: ['Solomon shows how a calm reply can defuse rising conflict.'],
  },
  {
    reference: 'Proverbs 27:17', book: 'Proverbs', chapter: 27, verseStart: 17,
    text: 'As iron sharpens iron, so one man sharpens another.',
    testament: 'OT', speaker: 'Solomon', audience: 'Those seeking wisdom',
    before: 'Solomon reflects on the value of honest friendship.',
    after: 'Solomon notes that whoever tends a fig tree will eat its fruit.',
    theme: 'Sharpening one another', keyword: 'iron',
    facts: ['Solomon pictures friends refining each other like blades on a whetstone.'],
  },
  {
    reference: 'Proverbs 31:25', book: 'Proverbs', chapter: 31, verseStart: 25,
    text: 'Strength and honor are her clothing; she can laugh at the days to come.',
    testament: 'OT', speaker: 'King Lemuel', audience: 'Those seeking wisdom',
    before: 'The passage describes the worth and diligence of a virtuous woman.',
    after: 'She opens her mouth with wisdom, and faithful instruction is on her tongue.',
    theme: 'A woman of strength', keyword: 'Strength',
    facts: ['This praise comes from the oracle King Lemuel’s mother taught him.'],
  },

  // ————————————————— Coverage expansion: 10+ verses per book —————————————————
  // Fills out every book that had fewer than ten verses so Focus practice has
  // real depth per book. Same metadata contract as the entries above.
  // — Genesis —
  {
    reference: "Genesis 1:3", book: "Genesis", chapter: 1, verseStart: 3,
    text: "And God said, \"Let there be light,\" and there was light.",
    testament: "OT", speaker: "The narrator (Moses, traditionally)", audience: "Readers of Israel",
    before: "The earth is formless and empty, with darkness over the surface of the deep and the Spirit of God hovering over the waters.",
    after: "God sees that the light is good and separates the light from the darkness.",
    theme: "Creation by God's word", keyword: "light",
    facts: ["This is the first spoken command in the Bible.", "The Latin phrase for this verse, 'fiat lux,' remains a common motto for universities."],
  },
  {
    reference: "Genesis 2:24", book: "Genesis", chapter: 2, verseStart: 24,
    text: "For this reason a man will leave his father and mother and be united to his wife, and they will become one flesh.",
    testament: "OT", speaker: "The narrator (Moses, traditionally)", audience: "Readers of Israel",
    before: "God forms woman from the man's rib, and the man welcomes her as bone of his bones and flesh of his flesh.",
    after: "The man and his wife are both naked and feel no shame.",
    theme: "Marriage and union", keyword: "flesh",
    facts: ["Jesus quotes this verse when teaching about marriage and divorce.", "It is one of the most frequently read passages at weddings."],
  },
  {
    reference: "Genesis 3:15", book: "Genesis", chapter: 3, verseStart: 15,
    text: "And I will put enmity between you and the woman, and between your seed and her seed. He will crush your head, and you will strike his heel.",
    testament: "OT", speaker: "The LORD God", audience: "The serpent",
    before: "God confronts the serpent for deceiving Eve and pronounces a curse upon it.",
    after: "God tells the woman that her pain in childbearing will be greatly increased.",
    theme: "The first promise of redemption", keyword: "enmity",
    facts: ["This verse is often called the 'protoevangelium,' or first gospel.", "Many Christians read the crushing of the serpent's head as a prophecy of Christ's victory over evil."],
  },
  {
    reference: "Genesis 12:3", book: "Genesis", chapter: 12, verseStart: 3,
    text: "I will bless those who bless you and curse those who curse you; and all the families of the earth will be blessed through you.",
    testament: "OT", speaker: "The LORD", audience: "Abram (Abraham)",
    before: "God calls Abram to leave his country and promises to make him into a great nation.",
    after: "Abram departs as the LORD had directed him, taking Sarai and Lot toward Canaan.",
    theme: "God's covenant blessing", keyword: "families",
    facts: ["Paul cites this promise as the gospel announced in advance to Abraham.", "It marks the start of God's covenant with the patriarchs."],
  },
  {
    reference: "Genesis 15:6", book: "Genesis", chapter: 15, verseStart: 6,
    text: "Abram believed the LORD, and it was credited to him as righteousness.",
    testament: "OT", speaker: "The narrator (Moses, traditionally)", audience: "Readers of Israel",
    before: "God brings Abram outside and promises that his offspring will be as numerous as the stars.",
    after: "The LORD reminds Abram that He brought him out of Ur to give him the land as an inheritance.",
    theme: "Faith counted as righteousness", keyword: "credited",
    facts: ["Both Paul and James quote this verse in the New Testament.", "It became a cornerstone text for the doctrine of justification by faith."],
  },
  {
    reference: "Genesis 22:8", book: "Genesis", chapter: 22, verseStart: 8,
    text: "Abraham answered, \"God Himself will provide the lamb for the burnt offering, my son.\" And the two walked on together.",
    testament: "OT", speaker: "Abraham", audience: "Isaac",
    before: "Isaac asks his father where the lamb for the burnt offering is as they climb the mountain.",
    after: "They reach the place God had designated, and Abraham builds an altar and binds Isaac.",
    theme: "God provides", keyword: "lamb",
    facts: ["Abraham later names the place 'The LORD Will Provide.'", "Christians often connect this provided sacrifice with Jesus, called the Lamb of God."],
  },
  {
    reference: "Genesis 28:15", book: "Genesis", chapter: 28, verseStart: 15,
    text: "Look, I am with you, and I will watch over you wherever you go, and I will bring you back to this land. For I will not leave you until I have done what I have promised you.",
    testament: "OT", speaker: "The LORD", audience: "Jacob",
    before: "Jacob dreams of a stairway to heaven with angels ascending and descending on it.",
    after: "Jacob wakes in awe and declares that the LORD is surely in that place.",
    theme: "God's faithful presence", keyword: "watch",
    facts: ["This promise comes to Jacob while he flees from his brother Esau.", "The dream gives the site its name, Bethel, meaning 'house of God.'"],
  },
  // — Exodus —
  {
    reference: "Exodus 3:14", book: "Exodus", chapter: 3, verseStart: 14,
    text: "God said to Moses, \"I AM WHO I AM. This is what you are to say to the Israelites: I AM has sent me to you.\"",
    testament: "OT", speaker: "God", audience: "Moses",
    before: "Moses asks God what name he should give when the Israelites ask who sent him.",
    after: "God further instructs Moses to tell Israel that the LORD, the God of their fathers, has sent him.",
    theme: "God's eternal name", keyword: "sent",
    facts: ["The divine name revealed here is linked to the Hebrew name YHWH.", "Jesus' 'I am' statements in John's Gospel echo this declaration."],
  },
  {
    reference: "Exodus 15:2", book: "Exodus", chapter: 15, verseStart: 2,
    text: "The LORD is my strength and my song, and He has become my salvation. He is my God, and I will praise Him, my father's God, and I will exalt Him.",
    testament: "OT", speaker: "Moses and the Israelites", audience: "The LORD",
    before: "Israel witnesses the Egyptian army drowned in the sea and begins to sing the Song of the Sea.",
    after: "The song continues, declaring that the LORD is a warrior and praising His triumph over Pharaoh.",
    theme: "God's deliverance", keyword: "strength",
    facts: ["This is part of the oldest song recorded in the Bible.", "Miriam later leads the women in a reprise of this praise with tambourines."],
  },
  {
    reference: "Exodus 20:12", book: "Exodus", chapter: 20, verseStart: 12,
    text: "Honor your father and your mother, so that your days may be long in the land that the LORD your God is giving you.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "God gives commands about the Sabbath as part of the Ten Commandments at Mount Sinai.",
    after: "God commands the people not to murder.",
    theme: "Honoring parents", keyword: "Honor",
    facts: ["Paul calls this the first commandment with a promise attached.", "It is the fifth of the Ten Commandments and bridges duties toward God and neighbor."],
  },
  {
    reference: "Exodus 20:13", book: "Exodus", chapter: 20, verseStart: 13,
    text: "You shall not murder.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "God commands the people to honor their father and mother.",
    after: "God commands the people not to commit adultery.",
    theme: "Sanctity of life", keyword: "murder",
    facts: ["Jesus expands this command to include anger in the Sermon on the Mount.", "It is the sixth of the Ten Commandments."],
  },
  {
    reference: "Exodus 33:14", book: "Exodus", chapter: 33, verseStart: 14,
    text: "And the LORD answered, \"My Presence will go with you, and I will give you rest.\"",
    testament: "OT", speaker: "The LORD", audience: "Moses",
    before: "Moses pleads with God not to send Israel onward unless His presence goes with them.",
    after: "Moses responds that if God's presence does not go with them, they should not be sent up from there.",
    theme: "God's abiding presence", keyword: "Presence",
    facts: ["This exchange follows Israel's sin with the golden calf.", "Moses' bold intercession here leads to his request to see God's glory."],
  },
  {
    reference: "Exodus 34:6", book: "Exodus", chapter: 34, verseStart: 6,
    text: "Then the LORD passed in front of Moses and called out: \"The LORD, the LORD God, is compassionate and gracious, slow to anger, abounding in loving devotion and faithfulness,\"",
    testament: "OT", speaker: "The LORD", audience: "Moses",
    before: "Moses ascends Mount Sinai with two new stone tablets as the LORD descends in a cloud.",
    after: "The LORD continues, declaring that He maintains love and forgives sin yet does not leave the guilty unpunished.",
    theme: "God's mercy and character", keyword: "compassionate",
    facts: ["This self-description of God is quoted or echoed throughout the Old Testament.", "It is sometimes called the 'thirteen attributes of mercy' in Jewish tradition."],
  },
  {
    reference: "Exodus 12:13", book: "Exodus", chapter: 12, verseStart: 13,
    text: "The blood on the houses where you are staying will be a distinguishing mark; when I see the blood, I will pass over you. No plague will fall on you to destroy you when I strike the land of Egypt.",
    testament: "OT", speaker: "The LORD", audience: "Moses and Aaron",
    before: "God instructs Israel to slaughter a lamb and put its blood on the doorframes of their houses.",
    after: "God commands that this day be commemorated as a lasting ordinance throughout the generations.",
    theme: "Redemption through the blood", keyword: "blood",
    facts: ["This event gives the festival of Passover its name.", "The New Testament portrays Christ as the Passover lamb sacrificed for believers."],
  },
  {
    reference: "Exodus 19:5", book: "Exodus", chapter: 19, verseStart: 5,
    text: "Now if you will indeed obey My voice and keep My covenant, you will be My treasured possession out of all the nations, for the whole earth is Mine.",
    testament: "OT", speaker: "The LORD", audience: "Israel (through Moses)",
    before: "God reminds Israel how He carried them out of Egypt on eagles' wings and brought them to Himself.",
    after: "God declares that Israel will be to Him a kingdom of priests and a holy nation.",
    theme: "Israel as God's chosen people", keyword: "treasured",
    facts: ["This passage sets the terms of the covenant God makes at Mount Sinai.", "The New Testament applies the phrase 'a kingdom of priests' to the church."],
  },
  // — Numbers —
  {
    reference: "Numbers 6:25", book: "Numbers", chapter: 6, verseStart: 25,
    text: "may the LORD cause His face to shine upon you and be gracious to you;",
    testament: "OT", speaker: "The LORD (through Aaron)", audience: "Israel",
    before: "The LORD instructs Aaron and his sons to bless Israel, beginning with a prayer for the LORD to bless and keep them.",
    after: "The blessing concludes with a prayer for the LORD to lift up His countenance and give peace.",
    theme: "Priestly blessing", keyword: "shine",
    facts: ["This is part of the Aaronic or priestly blessing still recited in synagogues and churches today.", "A silver amulet inscribed with this blessing is among the oldest known biblical texts ever found."],
  },
  {
    reference: "Numbers 6:26", book: "Numbers", chapter: 6, verseStart: 26,
    text: "may the LORD lift up His countenance toward you and give you peace.",
    testament: "OT", speaker: "The LORD (through Aaron)", audience: "Israel",
    before: "The blessing asks the LORD to make His face shine upon Israel and be gracious to them.",
    after: "God says that in this way the priests will put His name on the Israelites, and He will bless them.",
    theme: "Peace from God", keyword: "peace",
    facts: ["This is the closing line of the threefold Aaronic blessing.", "The Hebrew word for peace here, 'shalom,' conveys wholeness and well-being."],
  },
  {
    reference: "Numbers 23:19", book: "Numbers", chapter: 23, verseStart: 19,
    text: "God is not a man, that He should lie, or a son of man, that He should change His mind. Does He speak and not act? Does He promise and not fulfill?",
    testament: "OT", speaker: "Balaam", audience: "Balak, king of Moab",
    before: "Balak brings Balaam to another height, hoping he will finally curse Israel.",
    after: "Balaam declares that he has received a command to bless, and God's blessing cannot be reversed.",
    theme: "God's unchanging faithfulness", keyword: "lie",
    facts: ["Balaam was hired to curse Israel but could only speak blessing.", "His story includes a donkey that speaks to rebuke him."],
  },
  {
    reference: "Numbers 14:18", book: "Numbers", chapter: 14, verseStart: 18,
    text: "The LORD is slow to anger and abounding in loving devotion, forgiving iniquity and transgression. Yet He will by no means leave the guilty unpunished; He will visit the iniquity of the fathers upon their children to the third and fourth generation.",
    testament: "OT", speaker: "Moses", audience: "The LORD",
    before: "After the people rebel over the spies' report, Moses intercedes and appeals to God's great power.",
    after: "Moses asks God to pardon the people's iniquity according to His great loving devotion.",
    theme: "God's mercy and justice", keyword: "iniquity",
    facts: ["Moses quotes God's own self-description from Exodus 34 to plead for mercy.", "This intercession spares Israel from immediate destruction after the spy rebellion."],
  },
  {
    reference: "Numbers 32:23", book: "Numbers", chapter: 32, verseStart: 23,
    text: "But if you do not do this, you will surely sin against the LORD; and be sure that your sin will find you out.",
    testament: "OT", speaker: "Moses", audience: "The tribes of Reuben and Gad",
    before: "Moses agrees the eastern tribes may settle their land if they first help conquer Canaan with the others.",
    after: "The Reubenites and Gadites promise to do as Moses has commanded.",
    theme: "Sin exposed", keyword: "sin",
    facts: ["The phrase 'your sin will find you out' has become a common English proverb.", "It was spoken to tribes seeking land on the east side of the Jordan River."],
  },
  {
    reference: "Numbers 12:3", book: "Numbers", chapter: 12, verseStart: 3,
    text: "Now Moses was a very humble man, more so than any man on the face of the earth.",
    testament: "OT", speaker: "The narrator (Moses, traditionally)", audience: "Readers of Israel",
    before: "Miriam and Aaron speak against Moses because of his Cushite wife and question his unique authority.",
    after: "The LORD suddenly summons Moses, Aaron, and Miriam to the Tent of Meeting.",
    theme: "Humility", keyword: "humble",
    facts: ["This verse is often noted because Moses is traditionally its author.", "God defends Moses by speaking with him face to face, unlike other prophets."],
  },
  {
    reference: "Numbers 21:8", book: "Numbers", chapter: 21, verseStart: 8,
    text: "And the LORD said to Moses, \"Make a fiery serpent and mount it on a pole. When anyone who is bitten looks at it, he will live.\"",
    testament: "OT", speaker: "The LORD", audience: "Moses",
    before: "The people grumble against God, and venomous snakes are sent among them so that many die.",
    after: "Moses makes a bronze serpent, and all who look at it after being bitten live.",
    theme: "Healing through faith", keyword: "serpent",
    facts: ["Jesus compares His own crucifixion to this lifted-up serpent in John 3.", "The bronze serpent was later destroyed by King Hezekiah when Israel began to worship it."],
  },
  {
    reference: "Numbers 11:23", book: "Numbers", chapter: 11, verseStart: 23,
    text: "The LORD answered Moses, \"Is the LORD's arm too short? Now you will see whether or not My word will come to pass for you.\"",
    testament: "OT", speaker: "The LORD", audience: "Moses",
    before: "Moses doubts how God could possibly provide meat for six hundred thousand people in the wilderness.",
    after: "God sends a wind that drives quail in from the sea to surround the camp.",
    theme: "God's limitless power", keyword: "arm",
    facts: ["'The LORD's arm' is a common biblical image for God's power to act.", "God answers Moses' doubt by providing quail in overwhelming abundance."],
  },
  {
    reference: "Numbers 10:35", book: "Numbers", chapter: 10, verseStart: 35,
    text: "Whenever the ark set out, Moses would say, \"Rise up, O LORD! May Your enemies be scattered; may those who hate You flee before You.\"",
    testament: "OT", speaker: "Moses", audience: "The LORD",
    before: "Israel sets out from the mountain of the LORD for the first time, with the ark of the covenant leading the way.",
    after: "Whenever the ark came to rest, Moses would say, 'Return, O LORD, to the countless thousands of Israel.'",
    theme: "God leading His people", keyword: "scattered",
    facts: ["This prayer is echoed at the opening of Psalm 68.", "In Jewish tradition these two verses are set off by special marks in the Torah scroll."],
  },
  // — Deuteronomy —
  {
    reference: "Deuteronomy 6:4", book: "Deuteronomy", chapter: 6, verseStart: 4,
    text: "Hear, O Israel: The LORD our God, the LORD is One.",
    testament: "OT", speaker: "Moses", audience: "Israel",
    before: "Moses urges Israel to keep God's commands so that it may go well with them in the promised land.",
    after: "Moses commands the people to love the LORD with all their heart, soul, and strength.",
    theme: "God's oneness", keyword: "Hear",
    facts: ["This verse, called the 'Shema' from its first Hebrew word, is Judaism's central declaration of faith.", "Jesus cited it as part of the greatest commandment."],
  },
  {
    reference: "Deuteronomy 8:3", book: "Deuteronomy", chapter: 8, verseStart: 3,
    text: "He humbled you, and in your hunger He gave you manna to eat, which neither you nor your fathers had known, so that you might understand that man does not live on bread alone, but on every word that comes from the mouth of the LORD.",
    testament: "OT", speaker: "Moses", audience: "Israel",
    before: "Moses tells Israel to remember how the LORD led them forty years in the wilderness to humble and test them.",
    after: "Moses reminds them that their clothes did not wear out and their feet did not swell during those years.",
    theme: "Dependence on God's word", keyword: "bread",
    facts: ["Jesus quoted this verse to resist Satan's temptation in the wilderness.", "It interprets the daily gift of manna as a lesson in trusting God."],
  },
  {
    reference: "Deuteronomy 30:19", book: "Deuteronomy", chapter: 30, verseStart: 19,
    text: "I call heaven and earth as witnesses against you today that I have set before you life and death, blessing and cursing. Therefore choose life, so that you and your descendants may live,",
    testament: "OT", speaker: "Moses", audience: "Israel",
    before: "Moses declares that God's command is not too difficult, but very near, in their mouth and in their heart.",
    after: "Moses urges them to love the LORD, obey His voice, and hold fast to Him, for He is their life.",
    theme: "Choosing life", keyword: "choose",
    facts: ["This appeal comes near the end of Moses' final address to Israel.", "The call to 'choose life' frames obedience to God as a matter of life and death."],
  },
  {
    reference: "Deuteronomy 31:8", book: "Deuteronomy", chapter: 31, verseStart: 8,
    text: "The LORD Himself goes before you; He will be with you. He will never leave you nor forsake you. Do not be afraid or discouraged.",
    testament: "OT", speaker: "Moses", audience: "Joshua and Israel",
    before: "Moses publicly commissions Joshua to lead the people into the land God promised.",
    after: "Moses writes down this law and gives it to the priests who carry the ark of the covenant.",
    theme: "God's faithful presence", keyword: "forsake",
    facts: ["This encouragement is given as Joshua prepares to succeed Moses.", "The promise that God will never leave nor forsake is repeated to believers in Hebrews 13."],
  },
  {
    reference: "Deuteronomy 32:4", book: "Deuteronomy", chapter: 32, verseStart: 4,
    text: "He is the Rock, His work is perfect; all His ways are just. A faithful God who does no wrong, upright and just is He.",
    testament: "OT", speaker: "Moses", audience: "Israel",
    before: "Moses begins his song, calling heaven and earth to listen as he proclaims the name of the LORD.",
    after: "Moses contrasts God's faithfulness with the corruption of His wayward children.",
    theme: "God as the faithful Rock", keyword: "Rock",
    facts: ["This is part of the Song of Moses, taught to Israel as a lasting witness.", "The title 'the Rock' for God recurs throughout this song."],
  },
  {
    reference: "Deuteronomy 7:9", book: "Deuteronomy", chapter: 7, verseStart: 9,
    text: "Know therefore that the LORD your God is God, the faithful God who keeps His covenant of loving devotion for a thousand generations of those who love Him and keep His commandments.",
    testament: "OT", speaker: "Moses", audience: "Israel",
    before: "Moses reminds Israel that God chose them not because of their size but because of His love and His oath to their fathers.",
    after: "Moses warns that God repays those who hate Him to their face by destroying them.",
    theme: "God's covenant faithfulness", keyword: "faithful",
    facts: ["This verse contrasts God's mercy to a thousand generations with judgment on the disobedient.", "It grounds Israel's identity in God's covenant love rather than their own merit."],
  },
  {
    reference: "Deuteronomy 10:12", book: "Deuteronomy", chapter: 10, verseStart: 12,
    text: "And now, O Israel, what does the LORD your God ask of you but to fear the LORD your God, to walk in all His ways, to love Him, and to serve the LORD your God with all your heart and with all your soul,",
    testament: "OT", speaker: "Moses", audience: "Israel",
    before: "Moses recounts how God restored His covenant and set apart the tribe of Levi to serve Him.",
    after: "Moses says this is for their own good, to observe the LORD's commandments and statutes.",
    theme: "What God requires", keyword: "serve",
    facts: ["This verse summarizes the heart of Israel's covenant obligations.", "Its call to 'fear, walk, love, and serve' echoes the prophet Micah's later summary of true religion."],
  },
  {
    reference: "Deuteronomy 33:27", book: "Deuteronomy", chapter: 33, verseStart: 27,
    text: "The eternal God is your dwelling place, and underneath are the everlasting arms. He drives out the enemy before you, giving the command, \"Destroy him!\"",
    testament: "OT", speaker: "Moses", audience: "Israel",
    before: "Moses blesses the tribes of Israel and declares that there is no one like the God of Jeshurun.",
    after: "Moses proclaims that Israel will dwell securely in a land of grain and new wine.",
    theme: "God as refuge", keyword: "everlasting",
    facts: ["This is part of Moses' final blessing over Israel before his death.", "The phrase 'the everlasting arms' has inspired well-known hymns of comfort."],
  },
  // — Joshua —
  {
    reference: "Joshua 1:8", book: "Joshua", chapter: 1, verseStart: 8,
    text: "This Book of the Law must not depart from your mouth; meditate on it day and night, so that you may be careful to do everything written in it. For then you will prosper and succeed in all you do.",
    testament: "OT", speaker: "The LORD", audience: "Joshua",
    before: "The LORD commissions Joshua to lead Israel across the Jordan after Moses' death.",
    after: "The LORD commands Joshua to be strong and courageous, for God will be with him wherever he goes.",
    theme: "Meditating on God's Word", keyword: "meditate",
    facts: ["This verse links continual meditation on Scripture with true success.", "It was spoken as Joshua prepared to lead Israel into the Promised Land."],
  },
  {
    reference: "Joshua 3:17", book: "Joshua", chapter: 3, verseStart: 17,
    text: "The priests carrying the ark of the covenant of the LORD stood firm on dry ground in the middle of the Jordan, while all Israel crossed over on dry ground, until the entire nation had crossed the Jordan.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "As the priests' feet touched the Jordan, the river's waters piled up in a heap.",
    after: "The LORD instructs Joshua to have twelve men take up stones from the riverbed as a memorial.",
    theme: "God's miraculous deliverance", keyword: "priests",
    facts: ["The crossing of the Jordan echoes Israel's earlier crossing of the Red Sea.", "The waters stopped near the town of Adam, far upstream."],
  },
  {
    reference: "Joshua 6:20", book: "Joshua", chapter: 6, verseStart: 20,
    text: "So when the rams' horns sounded, the people shouted. And when they heard the blast of the horn, the people gave a great shout, and the wall collapsed. Then all the people charged straight into the city and captured it.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Israel marched around Jericho seven times on the seventh day as the LORD had commanded.",
    after: "Rahab and her family are spared because she hid the Israelite spies.",
    theme: "Victory by faith", keyword: "collapsed",
    facts: ["Israel took Jericho without a conventional siege, simply by obeying God's unusual instructions.", "Hebrews 11 lists the fall of Jericho as an act of faith."],
  },
  {
    reference: "Joshua 10:13", book: "Joshua", chapter: 10, verseStart: 13,
    text: "So the sun stood still and the moon stopped until the nation took vengeance upon its enemies. Is this not written in the Book of Jashar? So the sun stopped in the middle of the sky and delayed going down about a full day.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Joshua commanded the sun to stand still over Gibeon during the battle against the Amorite kings.",
    after: "The narrator notes there had never been a day like it, when the LORD listened to a man.",
    theme: "God's power over creation", keyword: "sun",
    facts: ["This is one of the Bible's most dramatic miracles, with the sun halting in the sky.", "The Book of Jashar it cites is a lost ancient collection of Hebrew poetry."],
  },
  {
    reference: "Joshua 21:45", book: "Joshua", chapter: 21, verseStart: 45,
    text: "Not one of all the LORD's good promises to the house of Israel had failed; everything was fulfilled.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The narrator records that the LORD gave Israel rest on every side, just as He had sworn.",
    after: "The eastern tribes are sent home to their inheritance across the Jordan.",
    theme: "God's faithfulness to His promises", keyword: "fulfilled",
    facts: ["This verse sums up the whole conquest as proof of God's reliability.", "It affirms that every one of God's promises to Israel came true."],
  },
  {
    reference: "Joshua 23:14", book: "Joshua", chapter: 23, verseStart: 14,
    text: "Now behold, today I am going the way of all the earth, and you know with all your heart and soul that not one of all the good promises the LORD your God made to you has failed. Everything was fulfilled for you; not one promise has failed.",
    testament: "OT", speaker: "Joshua", audience: "Israel",
    before: "Joshua, now old, gathers Israel's leaders to give his farewell charge.",
    after: "Joshua warns that just as God kept His good promises, He will bring disaster if they turn away.",
    theme: "God keeps every promise", keyword: "promises",
    facts: ["This is part of Joshua's farewell speech near the end of his life.", "\"Going the way of all the earth\" is an ancient way of speaking of approaching death."],
  },
  {
    reference: "Joshua 24:14", book: "Joshua", chapter: 24, verseStart: 14,
    text: "Now therefore, fear the LORD and serve Him in sincerity and truth. Cast aside the gods your fathers served beyond the Euphrates and in Egypt, and serve the LORD.",
    testament: "OT", speaker: "Joshua", audience: "Israel",
    before: "Joshua recounts all the LORD did for Israel from Abraham to the conquest of Canaan.",
    after: "Joshua declares that he and his household will serve the LORD.",
    theme: "Wholehearted devotion to God", keyword: "sincerity",
    facts: ["This verse immediately precedes Joshua's famous declaration to serve the LORD.", "It was spoken at Shechem during a covenant renewal ceremony."],
  },
  {
    reference: "Joshua 2:11", book: "Joshua", chapter: 2, verseStart: 11,
    text: "When we heard this, our hearts melted and everyone's courage failed because of you, for the LORD your God is God in the heavens above and on the earth below.",
    testament: "OT", speaker: "Rahab", audience: "The Israelite spies",
    before: "Rahab tells the two spies how news of Israel's victories terrified the people of Jericho.",
    after: "Rahab asks the spies to swear they will spare her family when Israel takes the city.",
    theme: "A Gentile's confession of faith", keyword: "melted",
    facts: ["Rahab the Canaanite is later listed in the genealogy of Jesus in Matthew 1.", "Her confession recognizes the God of Israel as ruler of heaven and earth."],
  },
  // — 1 Samuel —
  {
    reference: "1 Samuel 2:2", book: "1 Samuel", chapter: 2, verseStart: 2,
    text: "There is no one holy like the LORD. There is none besides You! And there is no Rock like our God.",
    testament: "OT", speaker: "Hannah", audience: "The LORD",
    before: "Hannah rejoices in prayer after dedicating her son Samuel to the LORD's service.",
    after: "Hannah warns the proud not to boast, for the LORD is a God who knows and weighs all deeds.",
    theme: "The holiness of God", keyword: "holy",
    facts: ["Hannah's prayer of praise foreshadows Mary's Magnificat in Luke 1.", "She sang it after God answered her plea for a child."],
  },
  {
    reference: "1 Samuel 3:10", book: "1 Samuel", chapter: 3, verseStart: 10,
    text: "Then the LORD came and stood there, calling as before, \"Samuel! Samuel!\" And Samuel answered, \"Speak, for Your servant is listening.\"",
    testament: "OT", speaker: "The narrator and Samuel", audience: "The reader / the LORD",
    before: "The boy Samuel repeatedly runs to Eli, not yet realizing the LORD is the one calling him.",
    after: "The LORD reveals to Samuel His coming judgment against the house of Eli.",
    theme: "Answering God's call", keyword: "listening",
    facts: ["Samuel's reply has become a model prayer of readiness to obey God.", "This is the account of God first calling Samuel as a prophet."],
  },
  {
    reference: "1 Samuel 15:22", book: "1 Samuel", chapter: 15, verseStart: 22,
    text: "But Samuel declared: \"Does the LORD delight in burnt offerings and sacrifices as much as in obedience to His voice? Behold, obedience is better than sacrifice, and attentiveness is better than the fat of rams.\"",
    testament: "OT", speaker: "Samuel", audience: "King Saul",
    before: "Saul tries to justify sparing the best Amalekite livestock by claiming it was for sacrifice.",
    after: "Samuel tells Saul that because he rejected the word of the LORD, the LORD has rejected him as king.",
    theme: "Obedience over ritual", keyword: "obedience",
    facts: ["This verse teaches that God values a heart of obedience above religious offerings.", "It marks the turning point where Saul loses his kingdom."],
  },
  {
    reference: "1 Samuel 17:45", book: "1 Samuel", chapter: 17, verseStart: 45,
    text: "But David said to the Philistine, \"You come against me with sword and spear and javelin, but I come against you in the name of the LORD of Hosts, the God of the armies of Israel, whom you have defied.\"",
    testament: "OT", speaker: "David", audience: "Goliath",
    before: "Goliath curses David by his gods and threatens to feed him to the birds.",
    after: "David declares the LORD will hand Goliath over, so that all the earth will know there is a God in Israel.",
    theme: "Faith over might", keyword: "javelin",
    facts: ["The young shepherd David faced the giant with only a sling and trust in God.", "\"LORD of Hosts\" means the LORD commanding the armies of heaven."],
  },
  {
    reference: "1 Samuel 12:24", book: "1 Samuel", chapter: 12, verseStart: 24,
    text: "Above all, fear the LORD and serve Him faithfully with all your heart; consider what great things He has done for you.",
    testament: "OT", speaker: "Samuel", audience: "Israel",
    before: "Samuel assures the people that though they sinned in asking for a king, God will not abandon them.",
    after: "Samuel warns that if they persist in evil, both they and their king will be swept away.",
    theme: "Wholehearted service to God", keyword: "faithfully",
    facts: ["This is part of Samuel's farewell address to Israel.", "Samuel urges gratitude by recalling the great things God has done."],
  },
  {
    reference: "1 Samuel 2:30", book: "1 Samuel", chapter: 2, verseStart: 30,
    text: "Therefore, the LORD, the God of Israel, declares: \"I did indeed say that your house and the house of your father would walk before Me forever. But now the LORD declares: Far be it from Me! For those who honor Me I will honor, but those who despise Me will be disdained.\"",
    testament: "OT", speaker: "The LORD (through a man of God)", audience: "Eli the priest",
    before: "A man of God confronts Eli for honoring his corrupt sons above the LORD.",
    after: "The LORD announces He will cut off the strength of Eli's house so no one grows old in it.",
    theme: "God honors those who honor Him", keyword: "honor",
    facts: ["This oracle announced the end of Eli's priestly line.", "The principle \"those who honor Me I will honor\" echoes throughout Scripture."],
  },
  {
    reference: "1 Samuel 7:12", book: "1 Samuel", chapter: 7, verseStart: 12,
    text: "Then Samuel took a stone and set it upright between Mizpah and Shen. He named it Ebenezer, saying, \"Thus far the LORD has helped us.\"",
    testament: "OT", speaker: "Samuel", audience: "Israel",
    before: "The LORD thundered against the Philistines and routed them before Israel at Mizpah.",
    after: "The Philistines are subdued, and Samuel judges Israel throughout his life.",
    theme: "Remembering God's help", keyword: "Ebenezer",
    facts: ["\"Ebenezer\" means \"stone of help\" in Hebrew.", "The phrase inspired the hymn line \"Here I raise my Ebenezer.\""],
  },
  {
    reference: "1 Samuel 16:13", book: "1 Samuel", chapter: 16, verseStart: 13,
    text: "So Samuel took the horn of oil and anointed him in the presence of his brothers, and the Spirit of the LORD rushed upon David from that day forward. Then Samuel set out and went to Ramah.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The LORD tells Samuel that David, Jesse's youngest son, is the one He has chosen.",
    after: "The Spirit departs from Saul, and David is summoned to play the harp to soothe him.",
    theme: "God's choice and anointing", keyword: "anointed",
    facts: ["David was anointed king while still a young shepherd, years before he took the throne.", "The name \"David\" appears here for the first time in this scene of anointing."],
  },
  {
    reference: "1 Samuel 17:47", book: "1 Samuel", chapter: 17, verseStart: 47,
    text: "And all those gathered here will know that it is not by sword or spear that the LORD saves; for the battle is the LORD's, and He will give all of you into our hands.",
    testament: "OT", speaker: "David", audience: "Goliath and the Philistines",
    before: "Goliath despises the young David for coming against him with only a sling.",
    after: "David runs quickly toward the battle line to meet Goliath.",
    theme: "The battle belongs to the LORD", keyword: "battle",
    facts: ["David spoke these words as a shepherd boy facing a giant warrior.", "The phrase 'the battle is the LORD's' is quoted often for trusting God against the odds."],
  },
  // — 1 Chronicles —
  {
    reference: "1 Chronicles 4:10", book: "1 Chronicles", chapter: 4, verseStart: 10,
    text: "And Jabez called out to the God of Israel, \"If only You would bless me and enlarge my territory! May Your hand be with me and keep me from harm, so that I will be free from pain.\" And God granted his request.",
    testament: "OT", speaker: "Jabez", audience: "The God of Israel",
    before: "The genealogy notes that Jabez was more honorable than his brothers.",
    after: "The genealogical record continues with the clans descended from Judah.",
    theme: "Bold prayer for blessing", keyword: "Jabez",
    facts: ["The Prayer of Jabez is tucked into a long list of genealogies.", "It became widely known through a bestselling book in the year 2000."],
  },
  {
    reference: "1 Chronicles 16:8", book: "1 Chronicles", chapter: 16, verseStart: 8,
    text: "Give thanks to the LORD; call upon His name; make known His deeds among the nations.",
    testament: "OT", speaker: "David (in the psalm of thanks)", audience: "Israel / the assembly",
    before: "David appoints Asaph and his relatives to give thanks after the ark is brought to Jerusalem.",
    after: "The psalm calls Israel to sing to the LORD and tell of all His wonderful acts.",
    theme: "Thanksgiving and proclamation", keyword: "thanks",
    facts: ["This psalm of David is echoed almost word for word in Psalm 105.", "It was sung to celebrate the ark's arrival in Jerusalem."],
  },
  {
    reference: "1 Chronicles 16:34", book: "1 Chronicles", chapter: 16, verseStart: 34,
    text: "Give thanks to the LORD, for He is good; His loving devotion endures forever.",
    testament: "OT", speaker: "David (in the psalm of thanks)", audience: "Israel / the assembly",
    before: "The psalm calls the heavens and earth, the fields and trees, to rejoice before the LORD.",
    after: "The people cry out for God to save them and gather them from among the nations.",
    theme: "God's enduring goodness", keyword: "good",
    facts: ["This refrain of praise recurs in Psalms 106, 107, 118, and 136.", "\"His loving devotion endures forever\" celebrates God's covenant faithfulness."],
  },
  {
    reference: "1 Chronicles 16:23", book: "1 Chronicles", chapter: 16, verseStart: 23,
    text: "Sing to the LORD, all the earth. Proclaim His salvation day after day.",
    testament: "OT", speaker: "David (in the psalm of thanks)", audience: "All the earth",
    before: "David praises the LORD as greatly to be feared above all gods.",
    after: "The psalm declares the LORD's glory among the nations and His marvelous deeds.",
    theme: "Universal praise", keyword: "Sing",
    facts: ["This line also appears in Psalm 96, sung as a call to worldwide worship.", "It invites the whole earth, not just Israel, to praise God."],
  },
  {
    reference: "1 Chronicles 28:9", book: "1 Chronicles", chapter: 28, verseStart: 9,
    text: "As for you, Solomon my son, know the God of your father, and serve Him wholeheartedly and with a willing mind, for the LORD searches every heart and understands the intention of every thought. If you seek Him, He will be found by you; but if you forsake Him, He will reject you forever.",
    testament: "OT", speaker: "David", audience: "Solomon",
    before: "David charges the assembly and his son to build the temple of the LORD.",
    after: "David urges Solomon to be careful, for the LORD has chosen him to build the sanctuary.",
    theme: "Knowing and seeking God", keyword: "Solomon",
    facts: ["David gave this charge publicly as he passed the temple project to Solomon.", "It stresses that God sees the heart, not just outward acts."],
  },
  {
    reference: "1 Chronicles 28:20", book: "1 Chronicles", chapter: 28, verseStart: 20,
    text: "Then David said to his son Solomon, \"Be strong and courageous, and do the work. Do not be afraid or discouraged, for the LORD God, my God, is with you. He will not fail you or forsake you until all the work for the service of the house of the LORD is finished.\"",
    testament: "OT", speaker: "David", audience: "Solomon",
    before: "David gives Solomon the divinely inspired plans for the temple and its treasuries.",
    after: "David tells Solomon of the divisions of priests and skilled workers ready to help.",
    theme: "Courage for God's work", keyword: "courageous",
    facts: ["David echoes the same charge Moses once gave to Joshua.", "The promise that God will not fail or forsake reassures the new king."],
  },
  {
    reference: "1 Chronicles 29:11", book: "1 Chronicles", chapter: 29, verseStart: 11,
    text: "Yours, O LORD, is the greatness and the power and the glory and the splendor and the majesty, for everything in heaven and on earth belongs to You. Yours, O LORD, is the kingdom, and You are exalted as head over all.",
    testament: "OT", speaker: "David", audience: "The LORD / the assembly",
    before: "The people give willingly and rejoice as they offer for the building of the temple.",
    after: "David acknowledges that all their gifts came from God's own hand.",
    theme: "Ascribing all glory to God", keyword: "greatness",
    facts: ["This doxology influenced the traditional ending of the Lord's Prayer.", "David prayed it before the whole assembly near the end of his reign."],
  },
  {
    reference: "1 Chronicles 29:14", book: "1 Chronicles", chapter: 29, verseStart: 14,
    text: "But who am I, and who are my people, that we should be able to give so generously as this? For everything comes from You, and from Your own hand we have given to You.",
    testament: "OT", speaker: "David", audience: "The LORD",
    before: "David praises God's greatness and glory over all creation.",
    after: "David confesses that Israel's days on earth are like a fleeting shadow.",
    theme: "Humility in giving", keyword: "everything",
    facts: ["David recognizes that all offerings simply return what God first gave.", "This prayer models humility even at the height of Israel's prosperity."],
  },
  {
    reference: "1 Chronicles 17:20", book: "1 Chronicles", chapter: 17, verseStart: 20,
    text: "O LORD, there is none like You, and there is no God but You, according to all that we have heard with our own ears.",
    testament: "OT", speaker: "David", audience: "The LORD",
    before: "The LORD promises through Nathan to establish David's house and throne forever.",
    after: "David marvels that God has redeemed Israel to be His own people.",
    theme: "The incomparable God", keyword: "none",
    facts: ["David prayed this after receiving God's covenant promise of an everlasting dynasty.", "This chapter parallels the account in 2 Samuel 7."],
  },
  // — 2 Chronicles —
  {
    reference: "2 Chronicles 16:9", book: "2 Chronicles", chapter: 16, verseStart: 9,
    text: "For the eyes of the LORD roam to and fro over all the earth, to show Himself strong on behalf of those whose hearts are fully devoted to Him.",
    testament: "OT", speaker: "Hanani the seer", audience: "King Asa of Judah",
    before: "Hanani rebukes King Asa for relying on the king of Aram instead of on the LORD.",
    after: "Hanani tells Asa that because he acted foolishly, from now on he will have wars.",
    theme: "God's watchful faithfulness", keyword: "devoted",
    facts: ["This verse is often quoted to describe God actively seeking out those loyal to Him.", "It was spoken as a rebuke, not merely as encouragement."],
  },
  {
    reference: "2 Chronicles 20:15", book: "2 Chronicles", chapter: 20, verseStart: 15,
    text: "Do not be afraid or discouraged because of this vast army, for the battle does not belong to you, but to God.",
    testament: "OT", speaker: "Jahaziel (by the Spirit)", audience: "King Jehoshaphat and Judah",
    before: "As a huge enemy army approaches, the Spirit of the LORD comes upon Jahaziel in the assembly.",
    after: "He tells them where the enemy will be and that they will not need to fight this battle.",
    theme: "The battle belongs to God", keyword: "battle",
    facts: ["Judah's army went out singing praise, and the enemy forces destroyed one another.", "This verse is a favorite for facing overwhelming odds."],
  },
  {
    reference: "2 Chronicles 20:17", book: "2 Chronicles", chapter: 20, verseStart: 17,
    text: "You need not fight this battle. Take your positions, stand firm, and see the salvation of the LORD on your behalf, O Judah and Jerusalem. Do not be afraid or discouraged.",
    testament: "OT", speaker: "Jahaziel (by the Spirit)", audience: "The people of Judah and Jerusalem",
    before: "Jahaziel identifies where the invading army will be found the next day.",
    after: "Jehoshaphat and all the people bow down to worship the LORD.",
    theme: "Standing firm to see God's salvation", keyword: "salvation",
    facts: ["The people appointed singers to march ahead of the army the next morning.", "The command 'stand firm and see' echoes Moses at the Red Sea."],
  },
  {
    reference: "2 Chronicles 15:2", book: "2 Chronicles", chapter: 15, verseStart: 2,
    text: "The LORD is with you when you are with Him. If you seek Him, He will be found by you, but if you forsake Him, He will forsake you.",
    testament: "OT", speaker: "Azariah son of Oded", audience: "King Asa and Judah",
    before: "The Spirit of God comes upon Azariah, who goes out to meet King Asa.",
    after: "Azariah recalls Israel's past times without the true God and encourages Asa to be strong.",
    theme: "Seeking and finding God", keyword: "seek",
    facts: ["Asa was moved by this message to remove idols and repair the altar of the LORD.", "The promise 'if you seek Him, He will be found' recurs throughout Scripture."],
  },
  {
    reference: "2 Chronicles 20:12", book: "2 Chronicles", chapter: 20, verseStart: 12,
    text: "Our God, will You not judge them? For we are powerless before this vast army that comes against us. We do not know what to do, but our eyes are upon You.",
    testament: "OT", speaker: "King Jehoshaphat", audience: "The LORD, before all Judah",
    before: "Jehoshaphat recounts God's promises while facing an invading coalition.",
    after: "The Spirit of the LORD comes upon Jahaziel to answer the king's prayer.",
    theme: "Dependence in helplessness", keyword: "powerless",
    facts: ["This honest prayer admits 'we do not know what to do.'", "It is often prayed as a model of trust when out of options."],
  },
  {
    reference: "2 Chronicles 32:8", book: "2 Chronicles", chapter: 32, verseStart: 8,
    text: "With him is only the arm of flesh, but with us is the LORD our God to help us and to fight our battles.",
    testament: "OT", speaker: "King Hezekiah", audience: "The people of Judah",
    before: "Sennacherib of Assyria invades Judah, and Hezekiah prepares the city's defenses.",
    after: "The text says the people took confidence from the words of Hezekiah.",
    theme: "God greater than human power", keyword: "flesh",
    facts: ["Assyria's army was later struck down by the angel of the LORD.", "Hezekiah contrasts a mere 'arm of flesh' with the LORD Himself."],
  },
  {
    reference: "2 Chronicles 1:10", book: "2 Chronicles", chapter: 1, verseStart: 10,
    text: "Now grant me wisdom and knowledge, that I may lead this people, for who is able to govern this great people of Yours?",
    testament: "OT", speaker: "Solomon", audience: "God (in a night vision)",
    before: "God appears to Solomon and invites him to ask for whatever he wants.",
    after: "God grants Solomon wisdom and knowledge, plus riches and honor he did not request.",
    theme: "Asking God for wisdom", keyword: "wisdom",
    facts: ["Solomon asked for wisdom rather than wealth or long life.", "God gave him riches and honor as well, unmatched by other kings."],
  },
  {
    reference: "2 Chronicles 6:18", book: "2 Chronicles", chapter: 6, verseStart: 18,
    text: "But will God indeed dwell with man on the earth? Even heaven, the highest heaven, cannot contain You, much less this temple I have built.",
    testament: "OT", speaker: "Solomon", audience: "God, before the assembly of Israel",
    before: "Solomon prays at the dedication of the newly completed temple.",
    after: "Solomon asks God to hear the prayers offered toward this place.",
    theme: "The greatness of God", keyword: "temple",
    facts: ["Solomon acknowledges that no building can contain the infinite God.", "This prayer marked the dedication of the first temple in Jerusalem."],
  },
  {
    reference: "2 Chronicles 7:15", book: "2 Chronicles", chapter: 7, verseStart: 15,
    text: "Now My eyes will be open and My ears attentive to the prayers offered in this place.",
    testament: "OT", speaker: "The LORD", audience: "Solomon",
    before: "God promises that if His people humble themselves and pray, He will heal their land.",
    after: "God says He has chosen and consecrated this temple for His Name forever.",
    theme: "God attentive to prayer", keyword: "attentive",
    facts: ["This verse immediately follows the famous promise of 2 Chronicles 7:14.", "God pledges that both His eyes and ears will be open to prayer at the temple."],
  },
  // — Nehemiah —
  {
    reference: "Nehemiah 1:4", book: "Nehemiah", chapter: 1, verseStart: 4,
    text: "When I heard these words, I sat down and wept. I mourned for days, fasting and praying before the God of heaven.",
    testament: "OT", speaker: "Nehemiah", audience: "Readers",
    before: "Nehemiah learns that Jerusalem's wall is broken down and its gates burned with fire.",
    after: "Nehemiah pours out a prayer confessing Israel's sins and pleading for God's favor.",
    theme: "Grief that drives us to prayer", keyword: "fasting",
    facts: ["Nehemiah served as cupbearer to the Persian king Artaxerxes.", "His burden for Jerusalem began with prayer before any action."],
  },
  {
    reference: "Nehemiah 2:18", book: "Nehemiah", chapter: 2, verseStart: 18,
    text: "I also told them about the gracious hand of my God upon me, and about the words the king had spoken to me. \"Let us start rebuilding,\" they replied. So they began this good work.",
    testament: "OT", speaker: "Nehemiah", audience: "The Jews and officials of Jerusalem",
    before: "Nehemiah urges the people to rebuild the wall so they will no longer be a disgrace.",
    after: "Sanballat, Tobiah, and Geshem mock and ridicule the rebuilding effort.",
    theme: "God-inspired resolve to build", keyword: "rebuilding",
    facts: ["Nehemiah credited God's gracious hand for the king's support.", "The people's quick response shows the power of united vision."],
  },
  {
    reference: "Nehemiah 4:6", book: "Nehemiah", chapter: 4, verseStart: 6,
    text: "So we rebuilt the wall until all of it was joined together up to half its height, for the people had a mind to work.",
    testament: "OT", speaker: "Nehemiah", audience: "Readers",
    before: "Nehemiah prays for protection as enemies grow angry and plot against the builders.",
    after: "Sanballat and his allies conspire to come and fight against Jerusalem.",
    theme: "Wholehearted labor", keyword: "work",
    facts: ["\"A mind to work\" has become a proverb for wholehearted effort.", "The wall reached half its height despite constant opposition."],
  },
  {
    reference: "Nehemiah 6:3", book: "Nehemiah", chapter: 6, verseStart: 3,
    text: "So I sent messengers to them, saying, \"I am doing a great work and cannot come down. Why should the work cease while I leave it to come down to you?\"",
    testament: "OT", speaker: "Nehemiah", audience: "Sanballat and Geshem",
    before: "Sanballat and Geshem repeatedly invite Nehemiah to meet, intending to harm him.",
    after: "They send the same message four times, and each time Nehemiah refuses.",
    theme: "Staying focused on God's calling", keyword: "great",
    facts: ["Nehemiah recognized the invitation as a distraction meant to stop the work.", "This verse is often quoted about refusing to be pulled from one's purpose."],
  },
  {
    reference: "Nehemiah 6:15", book: "Nehemiah", chapter: 6, verseStart: 15,
    text: "So the wall was completed in fifty-two days, on the twenty-fifth day of the month of Elul.",
    testament: "OT", speaker: "Nehemiah", audience: "Readers",
    before: "Enemies try to intimidate Nehemiah with hired prophets and false accusations.",
    after: "Israel's enemies lose confidence, realizing the work was accomplished with God's help.",
    theme: "Completing the work God gives", keyword: "completed",
    facts: ["Rebuilding Jerusalem's wall took only fifty-two days.", "Elul corresponds to late summer, around August-September."],
  },
  {
    reference: "Nehemiah 9:6", book: "Nehemiah", chapter: 9, verseStart: 6,
    text: "You alone are the LORD. You created the heavens, the highest heavens with all their host, the earth and all that is on it, the seas and all that is in them. You give life to all things, and the host of heaven worships You.",
    testament: "OT", speaker: "The Levites", audience: "The LORD / the assembly",
    before: "The Levites call the people to stand and bless the LORD their God.",
    after: "The prayer recounts God's choosing of Abram and His covenant faithfulness.",
    theme: "God the Creator alone is worthy", keyword: "alone",
    facts: ["This is part of the longest recorded prayer in the Old Testament.", "It rehearses Israel's history from creation onward as a confession."],
  },
  {
    reference: "Nehemiah 2:20", book: "Nehemiah", chapter: 2, verseStart: 20,
    text: "So I answered them and said, \"The God of heaven is the One who will grant us success. We, His servants, will start rebuilding, but you have no portion, right, or claim in Jerusalem.\"",
    testament: "OT", speaker: "Nehemiah", audience: "Sanballat, Tobiah, and Geshem",
    before: "The enemies mock and accuse Nehemiah of rebelling against the king.",
    after: "Chapter 3 lists the families who rebuilt each section and gate of the wall.",
    theme: "Confidence in God's success", keyword: "success",
    facts: ["Nehemiah answered ridicule with confidence in the God of heaven.", "He drew a clear line that opponents had no share in the work."],
  },
  {
    reference: "Nehemiah 4:20", book: "Nehemiah", chapter: 4, verseStart: 20,
    text: "Wherever you hear the sound of the horn, rally to us there. Our God will fight for us!",
    testament: "OT", speaker: "Nehemiah", audience: "The people rebuilding the wall",
    before: "Nehemiah stations the people by families with their swords, spears, and bows.",
    after: "The workers labor from daybreak until the stars came out, staying on guard.",
    theme: "God fights for His people", keyword: "fight",
    facts: ["The builders worked with a tool in one hand and a weapon in the other."],
  },
  {
    reference: "Nehemiah 6:16", book: "Nehemiah", chapter: 6, verseStart: 16,
    text: "When all our enemies heard about this, all the surrounding nations were afraid and lost their confidence, for they realized that this work had been accomplished by our God.",
    testament: "OT", speaker: "Nehemiah", audience: "Readers",
    before: "The wall of Jerusalem is finished in just fifty-two days.",
    after: "Nehemiah notes the nobles of Judah kept exchanging letters with Tobiah.",
    theme: "God accomplishes His work", keyword: "accomplished",
    facts: ["Jerusalem's wall was rebuilt in only 52 days despite fierce opposition."],
  },
  // — Job —
  {
    reference: "Job 1:21", book: "Job", chapter: 1, verseStart: 21,
    text: "Naked I came from my mother's womb, and naked I will depart. The LORD gave, and the LORD has taken away. Blessed be the name of the LORD.",
    testament: "OT", speaker: "Job", audience: "Spoken in worship before God",
    before: "Messengers report that Job has lost his livestock, servants, and all his children.",
    after: "The narrator notes that in all this Job did not sin or charge God with wrongdoing.",
    theme: "Worship in loss", keyword: "Naked",
    facts: ["Job worshiped God at the very moment he learned of his greatest losses.", "This verse is frequently read at funerals."],
  },
  {
    reference: "Job 42:2", book: "Job", chapter: 42, verseStart: 2,
    text: "I know that You can do all things and that no plan of Yours can be thwarted.",
    testament: "OT", speaker: "Job", audience: "The LORD",
    before: "God has finished questioning Job out of the whirlwind.",
    after: "Job admits he spoke of things too wonderful for him to understand.",
    theme: "God's sovereignty", keyword: "thwarted",
    facts: ["This is the beginning of Job's final response to God.", "Job affirms that no purpose of God can be stopped."],
  },
  {
    reference: "Job 42:5", book: "Job", chapter: 42, verseStart: 5,
    text: "My ears had heard of You, but now my eyes have seen You.",
    testament: "OT", speaker: "Job", audience: "The LORD",
    before: "Job acknowledges God's power and his own lack of understanding.",
    after: "Job says he despises himself and repents in dust and ashes.",
    theme: "Encountering God firsthand", keyword: "heard",
    facts: ["Job contrasts secondhand knowledge with a direct encounter with God.", "This confession comes right before God restores Job's fortunes."],
  },
  {
    reference: "Job 38:4", book: "Job", chapter: 38, verseStart: 4,
    text: "Where were you when I laid the foundations of the earth? Tell Me, if you have understanding.",
    testament: "OT", speaker: "The LORD", audience: "Job",
    before: "The LORD answers Job out of the whirlwind.",
    after: "God continues asking who set the measurements of the earth.",
    theme: "The Creator's majesty", keyword: "foundations",
    facts: ["God answers Job not with explanations but with questions.", "This begins several chapters of God's speeches about creation."],
  },
  {
    reference: "Job 23:10", book: "Job", chapter: 23, verseStart: 10,
    text: "Yet He knows the way I have taken; when He has tested me, I will come forth as gold.",
    testament: "OT", speaker: "Job", audience: "His friends (in reply)",
    before: "Job laments that he cannot find God to present his case to Him.",
    after: "Job says his feet have closely followed God's steps without turning aside.",
    theme: "Refined through testing", keyword: "gold",
    facts: ["Job compares his testing to the refining of gold.", "He trusts God's knowledge of his path even when God seems absent."],
  },
  {
    reference: "Job 13:15", book: "Job", chapter: 13, verseStart: 15,
    text: "Though He slay me, I will hope in Him. I will still defend my ways to His face.",
    testament: "OT", speaker: "Job", audience: "His friends (in reply)",
    before: "Job insists on taking his case directly to God rather than to his friends.",
    after: "Job declares that this itself will be his salvation, for no godless person can come before God.",
    theme: "Unyielding hope in God", keyword: "slay",
    facts: ["This verse is one of the strongest statements of faith in the book of Job.", "Job maintains both his hope in God and his own integrity."],
  },
  {
    reference: "Job 2:10", book: "Job", chapter: 2, verseStart: 10,
    text: "Should we accept from God only good and not adversity?",
    testament: "OT", speaker: "Job", audience: "Job's wife",
    before: "Job's wife urges him to curse God and die after he is struck with painful sores.",
    after: "The narrator states that in all this, Job did not sin in what he said.",
    theme: "Accepting adversity from God", keyword: "adversity",
    facts: ["Job's reply challenges the idea that God should only ever send good.", "The verse notes Job did not sin with his lips."],
  },
  {
    reference: "Job 28:28", book: "Job", chapter: 28, verseStart: 28,
    text: "And He said to man, 'Behold, the fear of the Lord, that is wisdom, and to turn away from evil is understanding.'",
    testament: "OT", speaker: "Job", audience: "Reflecting on where wisdom is found",
    before: "Job asks where wisdom can be found, since it is hidden from all living things.",
    after: "Job resumes his discourse recalling his former days of blessing.",
    theme: "The fear of the Lord is wisdom", keyword: "wisdom",
    facts: ["This mirrors the theme of Proverbs that wisdom begins with fearing God.", "The whole chapter is a poem about the inaccessibility of wisdom."],
  },
  {
    reference: "Job 14:1", book: "Job", chapter: 14, verseStart: 1,
    text: "Man, who is born of woman, is short of days and full of trouble.",
    testament: "OT", speaker: "Job", audience: "God and his friends",
    before: "Job has been pleading his case and reflecting on human frailty.",
    after: "Job compares human life to a flower that withers and a fleeting shadow.",
    theme: "The brevity of human life", keyword: "trouble",
    facts: ["This verse is often read at funeral and memorial services.", "It captures Job's meditation on the shortness of life."],
  },
  // — Proverbs —
  {
    reference: "Proverbs 3:6", book: "Proverbs", chapter: 3, verseStart: 6,
    text: "in all your ways acknowledge Him, and He will make your paths straight.",
    testament: "OT", speaker: "Solomon", audience: "My son (the reader)",
    before: "The father urges trusting the LORD with all your heart rather than your own understanding.",
    after: "The reader is warned not to be wise in his own eyes but to fear the LORD.",
    theme: "Trusting God for direction", keyword: "paths",
    facts: ["This verse completes the well-loved couplet begun in Proverbs 3:5.", "It promises guidance to those who acknowledge God in everything."],
  },
  {
    reference: "Proverbs 17:22", book: "Proverbs", chapter: 17, verseStart: 22,
    text: "A joyful heart is good medicine, but a broken spirit dries up the bones.",
    testament: "OT", speaker: "Solomon", audience: "The reader seeking wisdom",
    before: "A proverb notes that a discerning person keeps wisdom in view.",
    after: "A proverb warns against accepting a bribe to subvert justice.",
    theme: "The power of a joyful heart", keyword: "medicine",
    facts: ["This proverb links emotional well-being to physical health.", "It is often cited on the benefits of joy and laughter."],
  },
  // — Ecclesiastes —
  {
    reference: "Ecclesiastes 1:2", book: "Ecclesiastes", chapter: 1, verseStart: 2,
    text: "\"Futility of futilities,\" says the Teacher, \"futility of futilities! Everything is futile!\"",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The book opens by identifying the words of the Teacher, son of David.",
    after: "The Teacher asks what profit a man gains from all his labor under the sun.",
    theme: "The vanity of life apart from God", keyword: "Futility",
    facts: ["Older translations render the key word as 'vanity' rather than 'futility.'", "The Hebrew word can mean 'vapor' or 'breath.'"],
  },
  {
    reference: "Ecclesiastes 12:13", book: "Ecclesiastes", chapter: 12, verseStart: 13,
    text: "When all has been heard, the conclusion of the matter is this: Fear God and keep His commandments, for this is the whole duty of man.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher warns that of making many books there is no end.",
    after: "The book closes noting that God will bring every deed into judgment.",
    theme: "The conclusion of the whole matter", keyword: "commandments",
    facts: ["This is presented as the summary and conclusion of the entire book.", "It brings the search for meaning back to God."],
  },
  {
    reference: "Ecclesiastes 3:11", book: "Ecclesiastes", chapter: 3, verseStart: 11,
    text: "He has made everything beautiful in its time. He has also set eternity in the hearts of men, yet they cannot fathom the work that God has done from beginning to end.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher describes a season and a time for every activity under heaven.",
    after: "The Teacher concludes there is nothing better than to rejoice and do good in life.",
    theme: "Eternity in the human heart", keyword: "eternity",
    facts: ["The phrase 'set eternity in the hearts of men' is widely quoted.", "It suggests humans long for meaning beyond their earthly life."],
  },
  {
    reference: "Ecclesiastes 4:9", book: "Ecclesiastes", chapter: 4, verseStart: 9,
    text: "Two are better than one, because they have a good return for their labor.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher observes the loneliness of one who toils endlessly with no companion.",
    after: "The Teacher explains that if one falls, the other can help him up.",
    theme: "The value of companionship", keyword: "labor",
    facts: ["This passage is often read at weddings.", "It leads into the famous image of a threefold cord."],
  },
  {
    reference: "Ecclesiastes 4:12", book: "Ecclesiastes", chapter: 4, verseStart: 12,
    text: "And though one may be overpowered, two can resist. Moreover, a cord of three strands is not quickly broken.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher notes that two lying together keep warm, but one alone cannot.",
    after: "The Teacher turns to reflect on a poor but wise youth versus an old foolish king.",
    theme: "Strength in unity", keyword: "strands",
    facts: ["The 'cord of three strands' is a popular image at weddings.", "It illustrates that added support brings greater strength."],
  },
  {
    reference: "Ecclesiastes 7:8", book: "Ecclesiastes", chapter: 7, verseStart: 8,
    text: "The end of a matter is better than the beginning, and a patient spirit is better than a proud spirit.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher says a good name is better than fine perfume.",
    after: "The Teacher warns against being quickly provoked in spirit.",
    theme: "Patience over pride", keyword: "patient",
    facts: ["This is part of a series of proverbs about what is truly 'better.'", "It values seeing things through to the end over impressive beginnings."],
  },
  {
    reference: "Ecclesiastes 9:10", book: "Ecclesiastes", chapter: 9, verseStart: 10,
    text: "Whatever you find to do with your hands, do it with all your might, for in Sheol, where you are going, there is no work or planning or knowledge or wisdom.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher urges people to enjoy life and their labor as God's gift.",
    after: "The Teacher observes that the race is not always to the swift.",
    theme: "Wholehearted labor now", keyword: "might",
    facts: ["This verse encourages diligence and full effort in one's work.", "It grounds that urgency in the brevity of life."],
  },
  {
    reference: "Ecclesiastes 11:1", book: "Ecclesiastes", chapter: 11, verseStart: 1,
    text: "Cast your bread upon the waters, for after many days you will find it again.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher has reflected on the mystery of life and death.",
    after: "The Teacher advises dividing your portion among many, since the future is uncertain.",
    theme: "Generosity and venture", keyword: "bread",
    facts: ["This verse is often read as counsel toward generosity or bold enterprise.", "The image may refer to overseas trade or to giving freely."],
  },
  {
    reference: "Ecclesiastes 5:10", book: "Ecclesiastes", chapter: 5, verseStart: 10,
    text: "He who loves money is never satisfied by money, and he who loves wealth is never satisfied by income. This too is futile.",
    testament: "OT", speaker: "The Teacher", audience: "Readers seeking meaning",
    before: "The Teacher observes the oppression of the poor and the denial of justice.",
    after: "The Teacher notes that as goods increase, so do those who consume them.",
    theme: "The emptiness of greed", keyword: "money",
    facts: ["This is a classic biblical warning about the love of money.", "It anticipates the New Testament teaching in 1 Timothy 6:10."],
  },
  // — Isaiah —
  {
    reference: "Isaiah 9:6", book: "Isaiah", chapter: 9, verseStart: 6,
    text: "For unto us a child is born, unto us a son is given, and the government will be upon His shoulders. And He will be called Wonderful Counselor, Mighty God, Everlasting Father, Prince of Peace.",
    testament: "OT", speaker: "Isaiah", audience: "Judah",
    before: "Isaiah promises that the people walking in darkness will see a great light.",
    after: "Isaiah declares that the increase of His government and peace will have no end.",
    theme: "The promised Messiah", keyword: "Counselor",
    facts: ["This verse is famously set to music in Handel's Messiah.", "The titles here are traditionally read as prophecies of Jesus Christ."],
  },
  {
    reference: "Isaiah 26:3", book: "Isaiah", chapter: 26, verseStart: 3,
    text: "You will keep in perfect peace the mind that is steadfast, because it trusts in You.",
    testament: "OT", speaker: "Isaiah", audience: "Judah",
    before: "Isaiah sings of a strong city whose gates the righteous nation may enter.",
    after: "Isaiah calls the people to trust in the LORD forever, for He is an everlasting Rock.",
    theme: "Peace through trust", keyword: "steadfast",
    facts: ["The Hebrew literally repeats 'peace, peace,' which the BSB renders 'perfect peace.'"],
  },
  {
    reference: "Isaiah 40:8", book: "Isaiah", chapter: 40, verseStart: 8,
    text: "The grass withers and the flowers fall, but the word of our God stands forever.",
    testament: "OT", speaker: "Isaiah", audience: "The exiles",
    before: "A voice cries out that all flesh is like grass, and its beauty like the flower of the field.",
    after: "Zion is told to go up on a high mountain and proclaim good news.",
    theme: "The enduring word of God", keyword: "withers",
    facts: ["The apostle Peter quotes this verse in 1 Peter 1:24-25."],
  },
  {
    reference: "Isaiah 43:2", book: "Isaiah", chapter: 43, verseStart: 2,
    text: "When you pass through the waters, I will be with you; and when you go through the rivers, they will not overwhelm you. When you walk through the fire, you will not be scorched; the flames will not set you ablaze.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "The LORD tells Israel not to fear, for He has redeemed them and called them by name.",
    after: "The LORD declares that He is the Holy One of Israel, their Savior.",
    theme: "God's presence in trials", keyword: "waters",
    facts: ["The imagery echoes Israel crossing the Red Sea and the fiery furnace of Daniel 3."],
  },
  {
    reference: "Isaiah 55:8", book: "Isaiah", chapter: 55, verseStart: 8,
    text: "For My thoughts are not your thoughts, neither are your ways My ways, declares the LORD.",
    testament: "OT", speaker: "The LORD", audience: "Judah",
    before: "The LORD calls the wicked to forsake their ways and return to Him for abundant pardon.",
    after: "The LORD says that as the heavens are higher than the earth, so are His ways higher than ours.",
    theme: "God's higher ways", keyword: "thoughts",
    facts: ["This chapter is part of Isaiah's great invitation, 'Come, all you who are thirsty.'"],
  },
  {
    reference: "Isaiah 6:8", book: "Isaiah", chapter: 6, verseStart: 8,
    text: "Then I heard the voice of the Lord saying: 'Whom shall I send? Who will go for Us?' And I said: 'Here am I. Send me!'",
    testament: "OT", speaker: "Isaiah", audience: "Readers of Israel",
    before: "A seraph touches Isaiah's lips with a coal, and his guilt is taken away.",
    after: "The LORD commissions Isaiah to go and speak to a people who will not understand.",
    theme: "Answering God's call", keyword: "send",
    facts: ["This is Isaiah's call vision in the year King Uzziah died."],
  },
  {
    reference: "Isaiah 1:18", book: "Isaiah", chapter: 1, verseStart: 18,
    text: "Come now, let us reason together, says the LORD. Though your sins are as scarlet, they will be as white as snow; though they are as red as crimson, they will become like wool.",
    testament: "OT", speaker: "The LORD", audience: "Judah",
    before: "The LORD rebukes empty sacrifices and calls the people to seek justice.",
    after: "The LORD promises that if they are willing and obedient, they will eat the best of the land.",
    theme: "Cleansing from sin", keyword: "scarlet",
    facts: ["The contrast of scarlet and snow is one of Scripture's most vivid pictures of forgiveness."],
  },
  // — Jeremiah —
  {
    reference: "Jeremiah 1:5", book: "Jeremiah", chapter: 1, verseStart: 5,
    text: "Before I formed you in the womb I knew you, and before you were born I set you apart and appointed you as a prophet to the nations.",
    testament: "OT", speaker: "The LORD", audience: "Jeremiah",
    before: "The word of the LORD comes to Jeremiah in the days of King Josiah.",
    after: "Jeremiah protests that he is only a youth and does not know how to speak.",
    theme: "God's calling and foreknowledge", keyword: "womb",
    facts: ["This is the opening of Jeremiah's prophetic call.", "It is often cited in discussions of God's foreknowledge and purpose."],
  },
  {
    reference: "Jeremiah 33:3", book: "Jeremiah", chapter: 33, verseStart: 3,
    text: "Call to Me, and I will answer and tell you great and unsearchable things you do not know.",
    testament: "OT", speaker: "The LORD", audience: "Jeremiah",
    before: "The word of the LORD comes to Jeremiah a second time while he is confined in the courtyard of the guard.",
    after: "The LORD speaks of healing and restoring the ruined city of Jerusalem.",
    theme: "Answered prayer", keyword: "unsearchable",
    facts: ["This verse is sometimes nicknamed 'God's telephone number' after its reference, 33:3."],
  },
  {
    reference: "Jeremiah 17:7", book: "Jeremiah", chapter: 17, verseStart: 7,
    text: "But blessed is the man who trusts in the LORD, whose confidence is in Him.",
    testament: "OT", speaker: "Jeremiah", audience: "Judah",
    before: "The LORD pronounces a curse on the one who trusts in man and turns his heart from the LORD.",
    after: "The trusting man is compared to a tree planted by the waters that does not fear when heat comes.",
    theme: "Trusting in the LORD", keyword: "confidence",
    facts: ["The passage closely echoes the imagery of Psalm 1 and its tree by the water."],
  },
  {
    reference: "Jeremiah 31:3", book: "Jeremiah", chapter: 31, verseStart: 3,
    text: "The LORD appeared to him from afar, saying: 'I have loved you with an everlasting love; therefore I have drawn you with loving devotion.'",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "The LORD promises that the survivors of the sword will find grace in the wilderness.",
    after: "The LORD promises to rebuild Israel, who will again take up her tambourines and dance.",
    theme: "God's everlasting love", keyword: "everlasting",
    facts: ["Jeremiah 31 also contains the famous promise of a new covenant."],
  },
  {
    reference: "Jeremiah 32:27", book: "Jeremiah", chapter: 32, verseStart: 27,
    text: "Behold, I am the LORD, the God of all flesh. Is anything too difficult for Me?",
    testament: "OT", speaker: "The LORD", audience: "Jeremiah",
    before: "Jeremiah prays, marveling at the LORD who made the heavens and the earth by His great power.",
    after: "The LORD declares He will hand Jerusalem over to the Babylonians, yet will surely restore it.",
    theme: "Nothing too hard for God", keyword: "flesh",
    facts: ["The same question, 'Is anything too hard for the LORD?', is asked of Sarah in Genesis 18:14."],
  },
  {
    reference: "Jeremiah 29:13", book: "Jeremiah", chapter: 29, verseStart: 13,
    text: "You will seek Me and find Me when you search for Me with all your heart.",
    testament: "OT", speaker: "The LORD", audience: "The exiles",
    before: "The LORD promises that when the exiles call on Him and pray, He will listen.",
    after: "The LORD promises to be found by them and to restore them from captivity.",
    theme: "Seeking God wholeheartedly", keyword: "search",
    facts: ["This verse immediately follows the well-known promise of Jeremiah 29:11."],
  },
  {
    reference: "Jeremiah 17:9", book: "Jeremiah", chapter: 17, verseStart: 9,
    text: "The heart is deceitful above all things and beyond cure. Who can understand it?",
    testament: "OT", speaker: "Jeremiah", audience: "Judah",
    before: "Judah's sin is described as engraved with an iron stylus on the tablet of their hearts.",
    after: "The LORD declares that He searches the heart and examines the mind to reward each person.",
    theme: "The sinful human heart", keyword: "deceitful",
    facts: ["This verse is often quoted in discussions of human nature and self-deception."],
  },
  {
    reference: "Jeremiah 31:33", book: "Jeremiah", chapter: 31, verseStart: 33,
    text: "But this is the covenant I will make with the house of Israel after those days, declares the LORD. I will put My law in their minds and inscribe it on their hearts. And I will be their God, and they will be My people.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "The LORD announces the days are coming when He will make a new covenant, unlike the one they broke.",
    after: "The LORD promises that all will know Him, and He will forgive their iniquity.",
    theme: "The new covenant", keyword: "inscribe",
    facts: ["The book of Hebrews quotes this new covenant passage at length in chapter 8."],
  },
  {
    reference: "Jeremiah 15:16", book: "Jeremiah", chapter: 15, verseStart: 16,
    text: "Your words were found, and I ate them. Your words became my joy and the delight of my heart. For I bear Your name, O LORD, God of Hosts.",
    testament: "OT", speaker: "Jeremiah", audience: "The LORD",
    before: "Jeremiah asks the LORD to remember him and take vengeance on his persecutors.",
    after: "Jeremiah says he never sat with the mockers, but sat alone because the LORD's hand was on him.",
    theme: "Delighting in God's word", keyword: "delight",
    facts: ["Ezekiel and John also describe eating a scroll of God's words."],
  },
  // — Lamentations —
  {
    reference: "Lamentations 3:23", book: "Lamentations", chapter: 3, verseStart: 23,
    text: "They are new every morning; great is Your faithfulness!",
    testament: "OT", speaker: "Jeremiah", audience: "The survivors of Jerusalem",
    before: "The prophet declares that the LORD's mercies never end but are renewed each day.",
    after: "The prophet says, 'The LORD is my portion; therefore I will hope in Him.'",
    theme: "God's daily faithfulness", keyword: "faithfulness",
    facts: ["This verse inspired the hymn 'Great Is Thy Faithfulness.'"],
  },
  {
    reference: "Lamentations 3:24", book: "Lamentations", chapter: 3, verseStart: 24,
    text: "The LORD is my portion, says my soul, therefore I will hope in Him.",
    testament: "OT", speaker: "Jeremiah", audience: "The survivors of Jerusalem",
    before: "The prophet recalls that the LORD's compassions are new every morning.",
    after: "The prophet affirms that the LORD is good to those who wait for Him.",
    theme: "Hope in the LORD", keyword: "portion",
    facts: ["Calling God one's 'portion' echoes the language of the Psalms and Israel's inheritance."],
  },
  {
    reference: "Lamentations 3:25", book: "Lamentations", chapter: 3, verseStart: 25,
    text: "The LORD is good to those who wait for Him, to the soul who seeks Him.",
    testament: "OT", speaker: "Jeremiah", audience: "The survivors of Jerusalem",
    before: "The prophet declares the LORD to be his portion and his hope.",
    after: "The prophet says it is good to wait quietly for the salvation of the LORD.",
    theme: "Waiting on God", keyword: "wait",
    facts: ["This central section of Lamentations is a sudden turn from grief to hope."],
  },
  {
    reference: "Lamentations 3:26", book: "Lamentations", chapter: 3, verseStart: 26,
    text: "It is good to wait quietly for the salvation of the LORD.",
    testament: "OT", speaker: "Jeremiah", audience: "The survivors of Jerusalem",
    before: "The prophet affirms that the LORD is good to those who wait for Him.",
    after: "The prophet says it is good for a man to bear the yoke while he is young.",
    theme: "Quiet trust in God", keyword: "quietly",
    facts: ["Lamentations is traditionally read on the Jewish fast of Tisha B'Av mourning the temple's destruction."],
  },
  {
    reference: "Lamentations 3:21", book: "Lamentations", chapter: 3, verseStart: 21,
    text: "Yet I call this to mind, and therefore I have hope:",
    testament: "OT", speaker: "Jeremiah", audience: "The survivors of Jerusalem",
    before: "The prophet's soul is downcast within him as he remembers his affliction.",
    after: "The prophet recalls that because of the LORD's loving devotion, we are not consumed.",
    theme: "Choosing to hope", keyword: "hope",
    facts: ["This verse marks the hinge of the whole book, where lament turns to hope."],
  },
  {
    reference: "Lamentations 3:40", book: "Lamentations", chapter: 3, verseStart: 40,
    text: "Let us examine and test our ways, and turn back to the LORD.",
    testament: "OT", speaker: "Jeremiah", audience: "The survivors of Jerusalem",
    before: "The prophet asks why anyone living should complain about the punishment of his sins.",
    after: "The prophet calls the people to lift up their hearts and hands to God in heaven.",
    theme: "Repentance", keyword: "examine",
    facts: ["This is a corporate call to self-examination amid national judgment."],
  },
  {
    reference: "Lamentations 5:21", book: "Lamentations", chapter: 5, verseStart: 21,
    text: "Restore us to Yourself, O LORD, so that we may return; renew our days as of old.",
    testament: "OT", speaker: "Jeremiah", audience: "The LORD",
    before: "The people mourn that their hearts are faint and their eyes grow dim over the ruin of Zion.",
    after: "The book closes asking why God would forget them and reject them so completely.",
    theme: "Prayer for restoration", keyword: "renew",
    facts: ["This is the next-to-last verse of Lamentations, a plea to be brought back to God."],
  },
  {
    reference: "Lamentations 1:1", book: "Lamentations", chapter: 1, verseStart: 1,
    text: "How lonely lies the city once so full of people! She who was great among the nations has become like a widow. The princess of the provinces has become a slave.",
    testament: "OT", speaker: "Jeremiah", audience: "Readers of Israel",
    before: "Nothing precedes this verse; it is the opening line of the book of Lamentations.",
    after: "The prophet describes Jerusalem weeping bitterly in the night with no one to comfort her.",
    theme: "Grief over fallen Jerusalem", keyword: "widow",
    facts: ["The Hebrew title of the book is 'Eikhah,' meaning 'How,' the first word of this verse."],
  },
  {
    reference: "Lamentations 3:32", book: "Lamentations", chapter: 3, verseStart: 32,
    text: "Even if He causes grief, He will show compassion according to His abundant loving devotion.",
    testament: "OT", speaker: "Jeremiah", audience: "The survivors of Jerusalem",
    before: "The prophet affirms that the Lord will not cast off forever.",
    after: "The prophet says that God does not willingly bring affliction or grief to the children of men.",
    theme: "God's compassion in suffering", keyword: "compassion",
    facts: ["This verse balances God's discipline with His unfailing love."],
  },
  // — Micah —
  {
    reference: "Micah 5:2", book: "Micah", chapter: 5, verseStart: 2,
    text: "But you, Bethlehem Ephrathah, who are small among the clans of Judah, out of you will come forth for Me One to be ruler over Israel—One whose origins are of old, from the days of eternity.",
    testament: "OT", speaker: "Micah", audience: "Judah",
    before: "Micah describes Israel's judge being struck on the cheek by their enemies.",
    after: "Micah says Israel will be abandoned until she who is in labor has given birth.",
    theme: "The ruler from Bethlehem", keyword: "Bethlehem",
    facts: ["The chief priests quote this verse to Herod as the Messiah's birthplace in Matthew 2."],
  },
  {
    reference: "Micah 7:18", book: "Micah", chapter: 7, verseStart: 18,
    text: "Who is a God like You, who pardons iniquity and passes over the transgression of the remnant of His inheritance? He does not retain His anger forever, because He delights in loving devotion.",
    testament: "OT", speaker: "Micah", audience: "Israel",
    before: "Micah declares that though he has fallen, the LORD will be his light and plead his case.",
    after: "Micah says God will again have compassion and tread their iniquities underfoot.",
    theme: "God who pardons sin", keyword: "pardons",
    facts: ["The name Micah itself means 'Who is like the LORD?', echoing this verse's opening question."],
  },
  {
    reference: "Micah 7:19", book: "Micah", chapter: 7, verseStart: 19,
    text: "He will again have compassion on us; He will vanquish our iniquities. You will cast out all our sins into the depths of the sea.",
    testament: "OT", speaker: "Micah", audience: "Israel",
    before: "Micah marvels at the God who pardons iniquity and delights in loving devotion.",
    after: "Micah affirms that God will show faithfulness and loving devotion as sworn to the patriarchs.",
    theme: "Sins cast into the sea", keyword: "vanquish",
    facts: ["This image inspired the Jewish Tashlich custom of casting bread into water at Rosh Hashanah."],
  },
  {
    reference: "Micah 4:2", book: "Micah", chapter: 4, verseStart: 2,
    text: "He will teach us His ways, so that we may walk in His paths. For the law will go forth from Zion, and the word of the LORD from Jerusalem.",
    testament: "OT", speaker: "Micah", audience: "Judah",
    before: "Many nations say, 'Come, let us go up to the mountain of the LORD, to the house of the God of Jacob.'",
    after: "Micah foretells that God will judge between many peoples and settle disputes for strong nations.",
    theme: "The nations coming to God", keyword: "paths",
    facts: ["This passage closely parallels Isaiah 2:2-4."],
  },
  {
    reference: "Micah 4:3", book: "Micah", chapter: 4, verseStart: 3,
    text: "Then they will beat their swords into plowshares and their spears into pruning hooks. Nation will no longer take up the sword against nation, nor train anymore for war.",
    testament: "OT", speaker: "Micah", audience: "Judah",
    before: "Micah says the LORD will judge between many peoples and settle disputes for strong nations far away.",
    after: "Micah promises that each man will sit under his own vine and fig tree, unafraid.",
    theme: "Universal peace", keyword: "plowshares",
    facts: ["The phrase 'swords into plowshares' is inscribed on a wall across from the United Nations headquarters."],
  },
  {
    reference: "Micah 4:4", book: "Micah", chapter: 4, verseStart: 4,
    text: "And each man will sit under his own vine and under his own fig tree, with no one to make him afraid. For the mouth of the LORD of Hosts has spoken.",
    testament: "OT", speaker: "Micah", audience: "Judah",
    before: "Micah foretells that the nations will beat their swords into plowshares and learn war no more.",
    after: "Micah declares that though the peoples walk in the name of their gods, Israel will walk in the name of the LORD.",
    theme: "Peace and security", keyword: "vine",
    facts: ["George Washington quoted this 'vine and fig tree' image dozens of times in his letters."],
  },
  {
    reference: "Micah 7:7", book: "Micah", chapter: 7, verseStart: 7,
    text: "But as for me, I will look to the LORD; I will wait for the God of my salvation. My God will hear me.",
    testament: "OT", speaker: "Micah", audience: "Israel",
    before: "Micah laments a corrupt society where a man's enemies are the members of his own household.",
    after: "Micah warns his enemy not to gloat, for though he has fallen he will rise again.",
    theme: "Waiting on God in darkness", keyword: "salvation",
    facts: ["This verse marks a turn from lament to confident hope near the end of Micah."],
  },
  {
    reference: "Micah 7:8", book: "Micah", chapter: 7, verseStart: 8,
    text: "Do not gloat over me, my enemy! Though I have fallen, I will arise; though I sit in darkness, the LORD will be my light.",
    testament: "OT", speaker: "Micah", audience: "Israel's enemies",
    before: "Micah declares that he will watch in hope for the LORD, the God of his salvation.",
    after: "Micah says he will bear the LORD's indignation until He pleads his case and brings him to light.",
    theme: "Rising from defeat", keyword: "arise",
    facts: ["The confidence 'I will arise' became a favorite theme in later worship songs and sermons."],
  },
  {
    reference: "Micah 6:6", book: "Micah", chapter: 6, verseStart: 6,
    text: "With what shall I come before the LORD and bow before the God on high? Shall I come to Him with burnt offerings, with year-old calves?",
    testament: "OT", speaker: "Micah", audience: "Judah",
    before: "The LORD presents His case against Israel, asking how He has wearied them.",
    after: "Micah answers that the LORD requires justice, mercy, and to walk humbly with God.",
    theme: "True worship over ritual", keyword: "offerings",
    facts: ["This question sets up the famous answer of Micah 6:8."],
  },
  // — Habakkuk —
  {
    reference: "Habakkuk 1:2", book: "Habakkuk", chapter: 1, verseStart: 2,
    text: "How long, O LORD, must I call for help but You do not hear, or cry out to You, 'Violence!' but You do not save?",
    testament: "OT", speaker: "The prophet Habakkuk", audience: "God, in prayer",
    before: "The book opens by naming this as the oracle that Habakkuk the prophet received.",
    after: "Habakkuk complains that he sees only injustice, strife, and a paralyzed law.",
    theme: "Crying out to God in complaint", keyword: "Violence",
    facts: ["Habakkuk is unusual among the prophets because he addresses his words to God rather than to the people."],
  },
  {
    reference: "Habakkuk 1:5", book: "Habakkuk", chapter: 1, verseStart: 5,
    text: "Look at the nations and observe—be utterly astounded! For I am doing a work in your days that you would not believe even if you were told.",
    testament: "OT", speaker: "The LORD", audience: "Habakkuk and Judah",
    before: "Habakkuk has asked how long God will tolerate the violence and injustice around him.",
    after: "God reveals that He is raising up the fierce Chaldeans as His instrument of judgment.",
    theme: "God's astonishing work in history", keyword: "astounded",
    facts: ["Paul quotes this verse in Acts 13:41 while preaching in a synagogue in Pisidian Antioch."],
  },
  {
    reference: "Habakkuk 2:3", book: "Habakkuk", chapter: 2, verseStart: 3,
    text: "For the vision awaits an appointed time; it testifies of the end and does not lie. Though it lingers, wait for it, since it will surely come and will not delay.",
    testament: "OT", speaker: "The LORD", audience: "Habakkuk",
    before: "God tells Habakkuk to write down the vision plainly on tablets.",
    after: "God contrasts the proud, whose soul is not upright, with the righteous who lives by faith.",
    theme: "Waiting patiently for God's promise", keyword: "vision",
    facts: ["Hebrews 10:37 draws on this verse to encourage believers awaiting Christ's return."],
  },
  {
    reference: "Habakkuk 2:4", book: "Habakkuk", chapter: 2, verseStart: 4,
    text: "Look at the proud; his soul is not upright—but the righteous will live by his faith.",
    testament: "OT", speaker: "The LORD", audience: "Habakkuk",
    before: "God assures Habakkuk that the vision will surely come at its appointed time.",
    after: "God pronounces a series of woes against the arrogant and the greedy.",
    theme: "The righteous live by faith", keyword: "faith",
    facts: ["This verse is quoted three times in the New Testament and became a cornerstone of the Protestant Reformation."],
  },
  {
    reference: "Habakkuk 2:14", book: "Habakkuk", chapter: 2, verseStart: 14,
    text: "For the earth will be filled with the knowledge of the glory of the LORD as the waters cover the sea.",
    testament: "OT", speaker: "The LORD", audience: "Habakkuk and the nations",
    before: "God pronounces woe on those who build a city with bloodshed and injustice.",
    after: "God pronounces woe on those who make their neighbors drink to shame.",
    theme: "The earth filled with God's glory", keyword: "knowledge",
    facts: ["This verse closely echoes Isaiah 11:9, which uses nearly identical imagery of waters covering the sea."],
  },
  {
    reference: "Habakkuk 2:20", book: "Habakkuk", chapter: 2, verseStart: 20,
    text: "But the LORD is in His holy temple; let all the earth be silent before Him.",
    testament: "OT", speaker: "The prophet Habakkuk", audience: "All the earth",
    before: "God has denounced idols of wood and stone that cannot speak or breathe.",
    after: "Habakkuk's prayer of chapter 3 begins, recounting God's mighty deeds.",
    theme: "Reverent silence before God", keyword: "temple",
    facts: ["This verse is often used as a traditional call to worship inviting congregations into reverent stillness."],
  },
  {
    reference: "Habakkuk 3:2", book: "Habakkuk", chapter: 3, verseStart: 2,
    text: "O LORD, I have heard the report of You; I stand in awe, O LORD, of Your deeds. Revive them in these years; make them known in these years. In Your wrath, remember mercy!",
    testament: "OT", speaker: "The prophet Habakkuk", audience: "God, in prayer",
    before: "Chapter 3 opens as a prayer of Habakkuk set to music.",
    after: "Habakkuk describes God coming in splendor from Teman and Mount Paran.",
    theme: "Pleading for revival and mercy", keyword: "mercy",
    facts: ["Chapter 3 carries a musical heading and closes with 'Selah,' suggesting it was sung in worship."],
  },
  {
    reference: "Habakkuk 3:17", book: "Habakkuk", chapter: 3, verseStart: 17,
    text: "Though the fig tree does not bud and no fruit is on the vines, though the olive crop fails and the fields produce no food, though the sheep are cut off from the fold and no cattle are in the stalls,",
    testament: "OT", speaker: "The prophet Habakkuk", audience: "God and the faithful",
    before: "Habakkuk trembles as he awaits the day of calamity coming upon the invading nation.",
    after: "Habakkuk declares that he will nevertheless rejoice in the LORD.",
    theme: "Faith amid total loss", keyword: "olive",
    facts: ["This verse lists the collapse of nearly every food source in ancient Judah, from orchards to flocks."],
  },
  {
    reference: "Habakkuk 3:18", book: "Habakkuk", chapter: 3, verseStart: 18,
    text: "yet I will exult in the LORD; I will rejoice in the God of my salvation!",
    testament: "OT", speaker: "The prophet Habakkuk", audience: "God and the faithful",
    before: "Habakkuk has pictured the failure of the fig tree, vine, olive, fields, and flocks.",
    after: "Habakkuk affirms that the Sovereign LORD is his strength.",
    theme: "Rejoicing in God despite circumstances", keyword: "rejoice",
    facts: ["Habakkuk ends his book with joy, moving from anguished questions to unshakable trust."],
  },
  // — Zephaniah —
  {
    reference: "Zephaniah 1:7", book: "Zephaniah", chapter: 1, verseStart: 7,
    text: "Be silent before the Lord GOD, for the Day of the LORD is near. Indeed, the LORD has prepared a sacrifice; He has consecrated His guests.",
    testament: "OT", speaker: "The prophet Zephaniah", audience: "Judah and Jerusalem",
    before: "God warns that He will sweep away everything from the face of the earth.",
    after: "God declares He will punish the officials and all who are dressed in foreign clothing.",
    theme: "The nearness of the Day of the LORD", keyword: "sacrifice",
    facts: ["Zephaniah prophesied during the reign of King Josiah, likely before Josiah's great reforms."],
  },
  {
    reference: "Zephaniah 1:12", book: "Zephaniah", chapter: 1, verseStart: 12,
    text: "And at that time I will search Jerusalem with lamps and punish the men who settle into complacency, who say to themselves, 'The LORD will do nothing, either good or bad.'",
    testament: "OT", speaker: "The LORD", audience: "Judah and Jerusalem",
    before: "God announces wailing rising up from the various quarters of the city.",
    after: "God declares that their wealth will be plundered and their houses laid waste.",
    theme: "Judgment on the complacent", keyword: "lamps",
    facts: ["The image of searching with lamps pictures God leaving no hidden corner unexamined."],
  },
  {
    reference: "Zephaniah 1:14", book: "Zephaniah", chapter: 1, verseStart: 14,
    text: "The great Day of the LORD is near—near and coming quickly. Listen, the Day of the LORD is bitter; the mighty man cries out there.",
    testament: "OT", speaker: "The prophet Zephaniah", audience: "Judah and Jerusalem",
    before: "God warns that neither silver nor gold will be able to deliver anyone.",
    after: "Zephaniah describes that day as one of wrath, distress, and darkness.",
    theme: "The dreadful Day of the LORD", keyword: "bitter",
    facts: ["The medieval hymn 'Dies Irae' draws its imagery of the day of wrath partly from Zephaniah 1."],
  },
  {
    reference: "Zephaniah 2:3", book: "Zephaniah", chapter: 2, verseStart: 3,
    text: "Seek the LORD, all you humble of the earth who carry out His justice. Seek righteousness; seek humility. Perhaps you will be sheltered on the day of the LORD's anger.",
    testament: "OT", speaker: "The prophet Zephaniah", audience: "The humble of Judah",
    before: "God calls the shameless nation to gather together before the decree takes effect.",
    after: "Zephaniah turns to pronounce judgment on the surrounding nations like Philistia.",
    theme: "Seek the LORD and be sheltered", keyword: "humility",
    facts: ["This is the central appeal of Zephaniah's message, urging repentance before judgment falls."],
  },
  {
    reference: "Zephaniah 3:9", book: "Zephaniah", chapter: 3, verseStart: 9,
    text: "For then I will restore pure lips to the peoples, that all may call upon the name of the LORD and serve Him shoulder to shoulder.",
    testament: "OT", speaker: "The LORD", audience: "The nations",
    before: "God declares He will pour out His indignation upon the nations.",
    after: "God promises that worshipers from beyond Cush will bring Him offerings.",
    theme: "Nations restored to worship God", keyword: "shoulder",
    facts: ["Purified 'lips' may reverse the confusion of languages that began at the Tower of Babel."],
  },
  {
    reference: "Zephaniah 3:12", book: "Zephaniah", chapter: 3, verseStart: 12,
    text: "But I will leave among you a meek and humble people, and they will trust in the name of the LORD.",
    testament: "OT", speaker: "The LORD", audience: "Remnant of Israel",
    before: "God promises to remove the proudly arrogant from His holy mountain.",
    after: "God says the remnant will do no wrong and will lie down with none to make them afraid.",
    theme: "A humble remnant that trusts God", keyword: "trust",
    facts: ["The theme of a faithful remnant runs through the prophets as a sign of hope beyond judgment."],
  },
  {
    reference: "Zephaniah 3:14", book: "Zephaniah", chapter: 3, verseStart: 14,
    text: "Sing for joy, O Daughter of Zion; shout aloud, O Israel! Be glad and rejoice with all your heart, O Daughter of Jerusalem!",
    testament: "OT", speaker: "The prophet Zephaniah", audience: "Jerusalem and Israel",
    before: "God has promised to leave a humble remnant who trust in His name.",
    after: "Zephaniah declares that the LORD has taken away their judgments and turned back their enemies.",
    theme: "Joyful praise after restoration", keyword: "Zion",
    facts: ["Zephaniah shifts dramatically from oracles of judgment to a closing song of pure joy."],
  },
  {
    reference: "Zephaniah 3:15", book: "Zephaniah", chapter: 3, verseStart: 15,
    text: "The LORD has taken away your punishment; He has turned back your enemy. The King of Israel, the LORD, is among you; you will fear disaster no more.",
    testament: "OT", speaker: "The prophet Zephaniah", audience: "Jerusalem and Israel",
    before: "Zephaniah calls the Daughter of Zion to sing and rejoice with all her heart.",
    after: "The prophet says that on that day Jerusalem will be told not to fear or let her hands hang limp.",
    theme: "God the King dwelling among His people", keyword: "disaster",
    facts: ["The promise that the King is 'among you' anticipates God dwelling with His people, fulfilled in Christ."],
  },
  {
    reference: "Zephaniah 3:20", book: "Zephaniah", chapter: 3, verseStart: 20,
    text: "At that time I will gather you; at that time I will bring you home. For I will give you honor and praise among all the peoples of the earth, when I restore your fortunes before your very eyes, says the LORD.",
    testament: "OT", speaker: "The LORD", audience: "Restored Israel",
    before: "God promises to deal with all who have afflicted His people and to gather the scattered.",
    after: "Nothing—these are the closing words of the book of Zephaniah.",
    theme: "Gathering and restoring God's people", keyword: "fortunes",
    facts: ["Zephaniah ends on a note of homecoming and honor, closing a book that began with sweeping judgment."],
  },
  // — Acts —
  {
    reference: "Acts 3:6", book: "Acts", chapter: 3, verseStart: 6,
    text: "But Peter said, \"Silver or gold I do not have, but what I have I give you: In the name of Jesus Christ of Nazareth, walk!\"",
    testament: "NT", speaker: "The apostle Peter", audience: "A lame beggar at the temple gate",
    before: "A man lame from birth asks Peter and John for money at the temple gate called Beautiful.",
    after: "Peter takes him by the right hand, and the man's feet and ankles are instantly made strong.",
    theme: "Healing in the name of Jesus", keyword: "Silver",
    facts: ["This healing at the temple gate led to Peter's sermon and the arrest of the apostles in Acts 4."],
  },
  // — 2 Corinthians —
  {
    reference: "2 Corinthians 13:14", book: "2 Corinthians", chapter: 13, verseStart: 14,
    text: "The grace of the Lord Jesus Christ, and the love of God, and the fellowship of the Holy Spirit be with all of you.",
    testament: "NT", speaker: "The apostle Paul", audience: "The church in Corinth",
    before: "Paul urges the Corinthians to rejoice, be restored, and live in peace.",
    after: "Nothing—this is the closing benediction of the letter.",
    theme: "Trinitarian blessing", keyword: "fellowship",
    facts: ["Known as 'The Grace,' this verse is one of the most widely used benedictions in Christian worship."],
  },
  // — Galatians —
  {
    reference: "Galatians 6:7", book: "Galatians", chapter: 6, verseStart: 7,
    text: "Do not be deceived: God is not to be mocked. Whatever a man sows, he will reap in return.",
    testament: "NT", speaker: "Paul", audience: "The churches in Galatia",
    before: "Paul urges believers to share all good things with their instructors.",
    after: "Paul explains that sowing to the flesh reaps destruction, but sowing to the Spirit reaps eternal life.",
    theme: "Sowing and reaping", keyword: "reap",
    facts: ["The farming image of sowing and reaping was instantly familiar to Paul's largely agrarian audience."],
  },
  {
    reference: "Galatians 5:13", book: "Galatians", chapter: 5, verseStart: 13,
    text: "For you, brothers, were called to freedom; but do not use your freedom as an opportunity for the flesh. Rather, serve one another in love.",
    testament: "NT", speaker: "Paul", audience: "The churches in Galatia",
    before: "Paul warns that those seeking to be justified by the law have fallen from grace.",
    after: "Paul says the whole law is fulfilled in loving your neighbor as yourself.",
    theme: "Freedom to serve in love", keyword: "freedom",
    facts: ["Galatians is often called the Magna Carta of Christian liberty for its strong emphasis on freedom in Christ."],
  },
  {
    reference: "Galatians 6:2", book: "Galatians", chapter: 6, verseStart: 2,
    text: "Carry one another's burdens, and in this way you will fulfill the law of Christ.",
    testament: "NT", speaker: "Paul", audience: "The churches in Galatia",
    before: "Paul instructs the spiritual to restore a sinning brother gently.",
    after: "Paul cautions that anyone who thinks he is something when he is nothing deceives himself.",
    theme: "Bearing one another's burdens", keyword: "burdens",
    facts: ["The phrase 'the law of Christ' points to Jesus' command to love one another."],
  },
  {
    reference: "Galatians 5:14", book: "Galatians", chapter: 5, verseStart: 14,
    text: "The entire law is fulfilled in a single decree: \"Love your neighbor as yourself.\"",
    testament: "NT", speaker: "Paul", audience: "The churches in Galatia",
    before: "Paul tells believers to serve one another in love rather than indulge the flesh.",
    after: "Paul warns that if they bite and devour one another, they will be consumed.",
    theme: "Love fulfills the law", keyword: "neighbor",
    facts: ["Paul quotes Leviticus 19:18, the same command Jesus called the second greatest."],
  },
  {
    reference: "Galatians 6:14", book: "Galatians", chapter: 6, verseStart: 14,
    text: "But as for me, may I never boast, except in the cross of our Lord Jesus Christ, through which the world has been crucified to me, and I to the world.",
    testament: "NT", speaker: "Paul", audience: "The churches in Galatia",
    before: "Paul says those pushing circumcision only want to avoid persecution for the cross.",
    after: "Paul declares that neither circumcision nor uncircumcision means anything, but a new creation.",
    theme: "Boasting only in the cross", keyword: "crucified",
    facts: ["Crucifixion was so shameful in the Roman world that boasting in a cross was a striking reversal."],
  },
  // — Ephesians —
  {
    reference: "Ephesians 2:19", book: "Ephesians", chapter: 2, verseStart: 19,
    text: "Therefore you are no longer strangers and foreigners, but fellow citizens with the saints and members of God's household,",
    testament: "NT", speaker: "Paul", audience: "The church in Ephesus",
    before: "Paul explains that through Christ both Jews and Gentiles have access to the Father by one Spirit.",
    after: "Paul describes this household as built on the foundation of the apostles and prophets, with Christ as cornerstone.",
    theme: "Belonging in God's household", keyword: "citizens",
    facts: ["Roman citizenship was a prized status, making 'fellow citizens with the saints' a powerful image for Paul's readers."],
  },
  {
    reference: "Ephesians 5:25", book: "Ephesians", chapter: 5, verseStart: 25,
    text: "Husbands, love your wives, just as Christ loved the church and gave Himself up for her",
    testament: "NT", speaker: "Paul", audience: "The church in Ephesus",
    before: "Paul instructs wives to submit to their own husbands as to the Lord.",
    after: "Paul says Christ did this to sanctify the church, cleansing her by the washing with water through the word.",
    theme: "Sacrificial love in marriage", keyword: "husbands",
    facts: ["Paul grounds the model of marriage in Christ's self-giving love for the church."],
  },
  {
    reference: "Ephesians 6:12", book: "Ephesians", chapter: 6, verseStart: 12,
    text: "For our struggle is not against flesh and blood, but against the rulers, against the authorities, against the powers of this world's darkness, and against the spiritual forces of evil in the heavenly realms.",
    testament: "NT", speaker: "Paul", audience: "The church in Ephesus",
    before: "Paul tells believers to put on the full armor of God to stand against the devil's schemes.",
    after: "Paul urges them to take up the full armor of God so they can stand their ground on the day of evil.",
    theme: "Spiritual warfare", keyword: "struggle",
    facts: ["Ephesus was a center of magic and occult practice, making this warning especially pointed."],
  },
  // — Philippians —
  {
    reference: "Philippians 4:7", book: "Philippians", chapter: 4, verseStart: 7,
    text: "And the peace of God, which surpasses all understanding, will guard your hearts and your minds in Christ Jesus.",
    testament: "NT", speaker: "Paul", audience: "The church in Philippi",
    before: "Paul urges believers to present their requests to God with prayer and thanksgiving.",
    after: "Paul directs them to dwell on whatever is true, noble, right, pure, lovely, and admirable.",
    theme: "The peace of God", keyword: "peace",
    facts: ["Paul wrote Philippians from prison, yet it is often called the epistle of joy and peace."],
  },
  {
    reference: "Philippians 3:14", book: "Philippians", chapter: 3, verseStart: 14,
    text: "I press on toward the goal to win the prize of God's heavenly calling in Christ Jesus.",
    testament: "NT", speaker: "Paul", audience: "The church in Philippi",
    before: "Paul says he forgets what is behind and strains toward what is ahead.",
    after: "Paul urges the mature to take this same view and trust God to reveal anything they see differently.",
    theme: "Pressing on toward the goal", keyword: "prize",
    facts: ["Paul draws on the imagery of a runner straining for the victor's prize in a race."],
  },
  {
    reference: "Philippians 2:13", book: "Philippians", chapter: 2, verseStart: 13,
    text: "For it is God who works in you to will and to act on behalf of His good purpose.",
    testament: "NT", speaker: "Paul", audience: "The church in Philippi",
    before: "Paul tells believers to work out their salvation with fear and trembling.",
    after: "Paul instructs them to do everything without grumbling or arguing.",
    theme: "God at work within us", keyword: "purpose",
    facts: ["This verse pairs human effort with God's empowering work, a balance central to Paul's teaching."],
  },
  // — Colossians —
  {
    reference: "Colossians 1:16", book: "Colossians", chapter: 1, verseStart: 16,
    text: "For in Him all things were created, things in heaven and on earth, visible and invisible, whether thrones or dominions or rulers or authorities. All things were created through Him and for Him.",
    testament: "NT", speaker: "Paul", audience: "The church in Colossae",
    before: "Paul describes the Son as the image of the invisible God, the firstborn over all creation.",
    after: "Paul declares that He is before all things, and in Him all things hold together.",
    theme: "Christ as Creator", keyword: "created",
    facts: ["This passage is part of a hymn-like section exalting Christ's supremacy over all creation."],
  },
  {
    reference: "Colossians 1:17", book: "Colossians", chapter: 1, verseStart: 17,
    text: "He is before all things, and in Him all things hold together.",
    testament: "NT", speaker: "Paul", audience: "The church in Colossae",
    before: "Paul affirms that all things were created through Christ and for Him.",
    after: "Paul names Christ the head of the body, the church, and the firstborn from among the dead.",
    theme: "Christ sustains all things", keyword: "together",
    facts: ["This verse presents Christ as the one who holds the entire universe in cohesion."],
  },
  {
    reference: "Colossians 2:6", book: "Colossians", chapter: 2, verseStart: 6,
    text: "Therefore, just as you have received Christ Jesus as Lord, continue to walk in Him,",
    testament: "NT", speaker: "Paul", audience: "The church in Colossae",
    before: "Paul expresses joy at the Colossians' orderly faith in Christ.",
    after: "Paul tells them to be rooted and built up in Him, established in the faith.",
    theme: "Walking in Christ", keyword: "walk",
    facts: ["Paul wrote to counter false teaching threatening to draw the Colossians away from Christ."],
  },
  {
    reference: "Colossians 3:1", book: "Colossians", chapter: 3, verseStart: 1,
    text: "Therefore, since you have been raised with Christ, strive for the things above, where Christ is seated at the right hand of God.",
    testament: "NT", speaker: "Paul", audience: "The church in Colossae",
    before: "Paul says believers died with Christ to the elemental forces of the world.",
    after: "Paul urges them to set their minds on things above, not on earthly things.",
    theme: "Seeking things above", keyword: "raised",
    facts: ["Paul links the believer's new pursuit directly to being raised with Christ."],
  },
  {
    reference: "Colossians 3:16", book: "Colossians", chapter: 3, verseStart: 16,
    text: "Let the word of Christ richly dwell within you as you teach and admonish one another with all wisdom, and as you sing psalms, hymns, and spiritual songs with gratitude in your hearts to God.",
    testament: "NT", speaker: "Paul", audience: "The church in Colossae",
    before: "Paul tells believers to let the peace of Christ rule in their hearts and to be thankful.",
    after: "Paul instructs them to do everything in the name of the Lord Jesus, giving thanks to God the Father.",
    theme: "The word of Christ dwelling richly", keyword: "psalms",
    facts: ["This verse is one of the earliest references to Christians singing psalms, hymns, and spiritual songs together."],
  },
  {
    reference: "Colossians 3:17", book: "Colossians", chapter: 3, verseStart: 17,
    text: "And whatever you do, in word or deed, do it all in the name of the Lord Jesus, giving thanks to God the Father through Him.",
    testament: "NT", speaker: "Paul", audience: "The church in Colossae",
    before: "Paul calls believers to let the word of Christ dwell in them richly with gratitude.",
    after: "Paul begins his instructions to households, addressing wives and husbands.",
    theme: "Doing all in Jesus' name", keyword: "deed",
    facts: ["Paul frames every ordinary word and action as an act done in the name of the Lord Jesus."],
  },
  // — 1 Thessalonians —
  {
    reference: "1 Thessalonians 4:16", book: "1 Thessalonians", chapter: 4, verseStart: 16,
    text: "For the Lord Himself will descend from heaven with a loud command, with the voice of an archangel, and with the trumpet of God, and the dead in Christ will be the first to rise.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul comforts believers that those who have died in Christ are not lost.",
    after: "Paul says the living will be caught up together with them in the clouds to meet the Lord in the air.",
    theme: "The Lord's return", keyword: "trumpet",
    facts: ["This is one of the New Testament's most detailed descriptions of Christ's return."],
  },
  {
    reference: "1 Thessalonians 4:17", book: "1 Thessalonians", chapter: 4, verseStart: 17,
    text: "After that, we who are alive and remaining will be caught up together with them in the clouds to meet the Lord in the air. And so we will always be with the Lord.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul says the dead in Christ will rise first at the Lord's coming.",
    after: "Paul tells the believers to encourage one another with these words.",
    theme: "Caught up to meet the Lord", keyword: "clouds",
    facts: ["The Latin translation of 'caught up' here, rapturo, gives us the term rapture."],
  },
  {
    reference: "1 Thessalonians 5:2", book: "1 Thessalonians", chapter: 5, verseStart: 2,
    text: "for you are fully aware that the Day of the Lord will come like a thief in the night.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul says there is no need to write about times and dates.",
    after: "Paul warns that destruction will come suddenly on those who say peace and safety.",
    theme: "The unexpected Day of the Lord", keyword: "thief",
    facts: ["The image of a thief in the night echoes Jesus' own warning about the timing of His return."],
  },
  {
    reference: "1 Thessalonians 5:17", book: "1 Thessalonians", chapter: 5, verseStart: 17,
    text: "pray without ceasing,",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul tells the believers to rejoice always.",
    after: "Paul instructs them to give thanks in every circumstance.",
    theme: "Constant prayer", keyword: "ceasing",
    facts: ["This is one of the shortest verses in the New Testament, yet among the most quoted on prayer."],
  },
  {
    reference: "1 Thessalonians 5:18", book: "1 Thessalonians", chapter: 5, verseStart: 18,
    text: "give thanks in every circumstance, for this is God's will for you in Christ Jesus.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul tells believers to pray without ceasing.",
    after: "Paul urges them not to quench the Spirit.",
    theme: "Thankfulness as God's will", keyword: "thanks",
    facts: ["Paul calls thankfulness in all circumstances part of God's clearly revealed will."],
  },
  {
    reference: "1 Thessalonians 5:21", book: "1 Thessalonians", chapter: 5, verseStart: 21,
    text: "but test all things. Hold fast to what is good.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul tells believers not to treat prophecies with contempt.",
    after: "Paul urges them to abstain from every form of evil.",
    theme: "Discernment and holding to good", keyword: "test",
    facts: ["Paul encourages careful discernment rather than uncritical acceptance of every teaching."],
  },
  {
    reference: "1 Thessalonians 5:23", book: "1 Thessalonians", chapter: 5, verseStart: 23,
    text: "Now may the God of peace Himself sanctify you completely, and may your entire spirit, soul, and body be kept blameless at the coming of our Lord Jesus Christ.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul urges the believers to abstain from every form of evil.",
    after: "Paul assures them that the One who calls them is faithful and will do it.",
    theme: "Complete sanctification", keyword: "blameless",
    facts: ["This verse is one of the few to name spirit, soul, and body together."],
  },
  {
    reference: "1 Thessalonians 5:24", book: "1 Thessalonians", chapter: 5, verseStart: 24,
    text: "The One who calls you is faithful, and He will do it.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul prays that God would sanctify the believers completely.",
    after: "Paul asks the believers to pray for him and his companions.",
    theme: "God's faithfulness", keyword: "faithful",
    facts: ["Paul roots the certainty of the believer's sanctification in God's own faithfulness."],
  },
  // — 2 Thessalonians —
  {
    reference: "2 Thessalonians 1:3", book: "2 Thessalonians", chapter: 1, verseStart: 3,
    text: "We are obligated to thank God for you, brothers, as is fitting, because your faith is growing more and more, and your love for one another is increasing.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul opens the letter with a greeting of grace and peace.",
    after: "Paul says he boasts about their perseverance and faith amid persecution.",
    theme: "Gratitude for growing faith", keyword: "growing",
    facts: ["Paul wrote both letters to Thessalonica within about a year of each other.", "The Thessalonians faced heavy persecution, which makes their growing faith notable."],
  },
  {
    reference: "2 Thessalonians 1:11", book: "2 Thessalonians", chapter: 1, verseStart: 11,
    text: "With this in mind, we constantly pray for you, that our God will count you worthy of His calling, and that He will powerfully fulfill your every good resolve and work of faith.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul describes the glory believers will share when Christ is revealed.",
    after: "Paul explains this is so the name of Jesus may be glorified in them.",
    theme: "Prayer for a worthy calling", keyword: "calling",
    facts: ["Paul frequently pairs his letters with reports of constant prayer for his readers."],
  },
  {
    reference: "2 Thessalonians 2:16", book: "2 Thessalonians", chapter: 2, verseStart: 16,
    text: "Now may our Lord Jesus Christ Himself and God our Father, who by grace has loved us and given us eternal comfort and good hope,",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul urges the believers to stand firm and hold to the teachings they received.",
    after: "Paul prays that God will encourage their hearts and strengthen them in every good work.",
    theme: "Eternal comfort and hope", keyword: "comfort",
    facts: ["This is one of Paul's benediction-style prayers embedded in the middle of a letter."],
  },
  {
    reference: "2 Thessalonians 2:17", book: "2 Thessalonians", chapter: 2, verseStart: 17,
    text: "encourage your hearts and strengthen you in every good word and deed.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul names the Lord Jesus and God the Father as the source of eternal comfort and hope.",
    after: "Paul asks the church to pray that the word of the Lord may spread quickly.",
    theme: "Strength for good works", keyword: "strengthen",
    facts: ["The verse completes a two-verse prayer that Paul begins in verse 16."],
  },
  {
    reference: "2 Thessalonians 3:1", book: "2 Thessalonians", chapter: 3, verseStart: 1,
    text: "Finally, brothers, pray for us, that the word of the Lord may spread quickly and be held in honor, just as it was with you.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul prays that God will strengthen the believers in every good word and deed.",
    after: "Paul asks prayer that he may be delivered from wicked and evil men.",
    theme: "Prayer for the gospel's advance", keyword: "spread",
    facts: ["Paul often asked churches to pray for the rapid spread of the gospel."],
  },
  {
    reference: "2 Thessalonians 3:5", book: "2 Thessalonians", chapter: 3, verseStart: 5,
    text: "May the Lord direct your hearts into God's love and Christ's perseverance.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul expresses confidence that the believers will do what he commands.",
    after: "Paul commands the church to keep away from every brother who is idle.",
    theme: "Love and perseverance", keyword: "perseverance",
    facts: ["The word rendered 'perseverance' can also be translated 'steadfastness' or 'endurance.'"],
  },
  {
    reference: "2 Thessalonians 3:10", book: "2 Thessalonians", chapter: 3, verseStart: 10,
    text: "For even while we were with you, we gave you this command: \"If anyone is unwilling to work, he shall not eat.\"",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul reminds them that he worked to support himself and not be a burden.",
    after: "Paul notes he hears some among them are idle and merely busybodies.",
    theme: "Diligence and work", keyword: "eat",
    facts: ["Some Thessalonians had reportedly stopped working, expecting Christ's imminent return."],
  },
  {
    reference: "2 Thessalonians 3:13", book: "2 Thessalonians", chapter: 3, verseStart: 13,
    text: "But as for you, brothers, do not grow weary in well-doing.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul instructs the idle to settle down and earn their own living.",
    after: "Paul says to take note of anyone who disobeys the instruction in this letter.",
    theme: "Perseverance in doing good", keyword: "weary",
    facts: ["A similar exhortation not to grow weary in doing good appears in Galatians 6:9."],
  },
  {
    reference: "2 Thessalonians 3:16", book: "2 Thessalonians", chapter: 3, verseStart: 16,
    text: "Now may the Lord of peace Himself give you peace at all times and in every way. The Lord be with all of you.",
    testament: "NT", speaker: "Paul", audience: "The church in Thessalonica",
    before: "Paul warns not to regard a disobedient brother as an enemy but to admonish him.",
    after: "Paul adds a greeting in his own hand as the mark of his letters.",
    theme: "The peace of the Lord", keyword: "peace",
    facts: ["Paul titles God 'the Lord of peace,' a phrase unique to this verse in his letters."],
  },
  // — 1 Timothy —
  {
    reference: "1 Timothy 1:15", book: "1 Timothy", chapter: 1, verseStart: 15,
    text: "This is a trustworthy saying, worthy of full acceptance: Christ Jesus came into the world to save sinners, of whom I am the worst.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul recalls that he was once a blasphemer and persecutor shown mercy.",
    after: "Paul says he was shown mercy so that Christ might display His perfect patience.",
    theme: "Christ came to save sinners", keyword: "sinners",
    facts: ["Paul calls himself the worst of sinners despite his prominent ministry.", "'Trustworthy saying' is a formula Paul uses several times in the Pastoral Epistles."],
  },
  {
    reference: "1 Timothy 2:1", book: "1 Timothy", chapter: 2, verseStart: 1,
    text: "First of all, then, I urge that petitions, prayers, intercession, and thanksgiving be offered for everyone,",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul charges Timothy to fight the good fight, holding to faith and a good conscience.",
    after: "Paul says such prayers should be made for kings and all in authority.",
    theme: "Prayer for all people", keyword: "intercession",
    facts: ["Paul lists four distinct kinds of prayer in a single verse."],
  },
  {
    reference: "1 Timothy 2:5", book: "1 Timothy", chapter: 2, verseStart: 5,
    text: "For there is one God, and one mediator between God and men, the man Christ Jesus,",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul says God desires all men to be saved and to know the truth.",
    after: "Paul states that Christ gave Himself as a ransom for all.",
    theme: "One God, one mediator", keyword: "mediator",
    facts: ["This verse is often cited in discussions of Christ's unique role between God and humanity."],
  },
  {
    reference: "1 Timothy 4:8", book: "1 Timothy", chapter: 4, verseStart: 8,
    text: "For physical exercise is of limited value, but godliness has value in all things, holding promise for the present life and for the life to come.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul tells Timothy to train himself for godliness rather than godless myths.",
    after: "Paul affirms that this is a trustworthy saying worthy of full acceptance.",
    theme: "The value of godliness", keyword: "godliness",
    facts: ["Paul draws an analogy from athletic training, familiar in the Greco-Roman world."],
  },
  {
    reference: "1 Timothy 6:6", book: "1 Timothy", chapter: 6, verseStart: 6,
    text: "Of course, godliness with contentment is great gain.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul warns against those who think godliness is a means to financial gain.",
    after: "Paul reminds Timothy that we brought nothing into the world.",
    theme: "Godliness with contentment", keyword: "contentment",
    facts: ["This verse redefines 'gain' apart from money, contrasting with false teachers who chased profit."],
  },
  {
    reference: "1 Timothy 6:7", book: "1 Timothy", chapter: 6, verseStart: 7,
    text: "For we brought nothing into the world, so we cannot carry anything out of it.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul states that godliness with contentment is great gain.",
    after: "Paul says that if we have food and clothing, we will be content.",
    theme: "The fleeting nature of wealth", keyword: "world",
    facts: ["The thought echoes Job 1:21 and Ecclesiastes 5:15 on entering and leaving life empty-handed."],
  },
  {
    reference: "1 Timothy 6:10", book: "1 Timothy", chapter: 6, verseStart: 10,
    text: "For the love of money is the root of all kinds of evil. By craving it, some have wandered away from the faith and pierced themselves with many sorrows.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul warns that those who want to be rich fall into temptation and a trap.",
    after: "Paul urges Timothy, as a man of God, to flee from these things.",
    theme: "The love of money", keyword: "money",
    facts: ["The verse is often misquoted as 'money is the root of all evil,' but it names the love of money."],
  },
  {
    reference: "1 Timothy 6:11", book: "1 Timothy", chapter: 6, verseStart: 11,
    text: "But you, O man of God, flee from these things and pursue righteousness, godliness, faith, love, perseverance, and gentleness.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul describes the sorrows that come from craving money.",
    after: "Paul charges Timothy to fight the good fight of the faith.",
    theme: "Pursue righteousness", keyword: "righteousness",
    facts: ["'Man of God' is an Old Testament title for prophets, here applied to Timothy."],
  },
  // — 2 Timothy —
  {
    reference: "2 Timothy 1:9", book: "2 Timothy", chapter: 1, verseStart: 9,
    text: "He has saved us and called us to a holy calling, not because of our works, but by His own purpose and grace, which He granted us in Christ Jesus before time began.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul urges Timothy to join him in suffering for the gospel by God's power.",
    after: "Paul says this grace has now been revealed through the appearing of Christ.",
    theme: "Saved by grace, not works", keyword: "grace",
    facts: ["Paul roots salvation in God's purpose given 'before time began.'"],
  },
  {
    reference: "2 Timothy 1:12", book: "2 Timothy", chapter: 1, verseStart: 12,
    text: "For this reason I suffer as I do. But I am not ashamed, because I know whom I have believed, and I am convinced that He is able to guard what I have entrusted to Him for that day.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul says he was appointed a herald, apostle, and teacher of the gospel.",
    after: "Paul urges Timothy to keep the pattern of sound teaching he has heard.",
    theme: "Confidence amid suffering", keyword: "entrusted",
    facts: ["Paul wrote this letter from prison, likely near the end of his life."],
  },
  {
    reference: "2 Timothy 2:3", book: "2 Timothy", chapter: 2, verseStart: 3,
    text: "Join me in suffering, like a good soldier of Christ Jesus.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul tells Timothy to entrust his teaching to faithful men who can teach others.",
    after: "Paul notes that a soldier avoids entangling himself in civilian affairs.",
    theme: "Endurance as a soldier", keyword: "soldier",
    facts: ["Paul uses soldier, athlete, and farmer images in this chapter to picture faithful ministry."],
  },
  {
    reference: "2 Timothy 2:15", book: "2 Timothy", chapter: 2, verseStart: 15,
    text: "Make every effort to present yourself approved to God, an unashamed workman who accurately handles the word of truth.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul warns against quarreling about words, which only ruins the hearers.",
    after: "Paul tells Timothy to avoid irreverent, empty chatter.",
    theme: "Rightly handling God's word", keyword: "workman",
    facts: ["The phrase 'accurately handles' translates a word meaning to cut a straight path."],
  },
  {
    reference: "2 Timothy 3:12", book: "2 Timothy", chapter: 3, verseStart: 12,
    text: "Indeed, all who desire to live godly lives in Christ Jesus will be persecuted,",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul recounts the persecutions he endured and how the Lord rescued him.",
    after: "Paul warns that evil men and impostors will go from bad to worse.",
    theme: "Suffering for godliness", keyword: "persecuted",
    facts: ["Paul lists specific cities, such as Antioch and Lystra, where he was persecuted."],
  },
  {
    reference: "2 Timothy 4:2", book: "2 Timothy", chapter: 4, verseStart: 2,
    text: "Preach the word; be prepared in season and out of season; correct, rebuke, and encourage, with great patience and careful instruction.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul solemnly charges Timothy before God and Christ Jesus, the coming Judge.",
    after: "Paul warns that people will not endure sound doctrine but seek teachers who suit their desires.",
    theme: "Preach the word", keyword: "preach",
    facts: ["This charge is among the most cited verses on the ministry of preaching."],
  },
  {
    reference: "2 Timothy 4:8", book: "2 Timothy", chapter: 4, verseStart: 8,
    text: "From now on there is reserved for me the crown of righteousness, which the Lord, the righteous Judge, will award to me on that day, and not only to me, but to all who crave His appearing.",
    testament: "NT", speaker: "Paul", audience: "Timothy",
    before: "Paul declares he has fought the good fight, finished the race, and kept the faith.",
    after: "Paul urges Timothy to come to him quickly.",
    theme: "The crown of righteousness", keyword: "crown",
    facts: ["The 'crown' imagery draws on the victory wreath awarded to winning athletes."],
  },
  // — Titus —
  {
    reference: "Titus 1:2", book: "Titus", chapter: 1, verseStart: 2,
    text: "in the hope of eternal life, which God, who cannot lie, promised before time began.",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul introduces himself as a servant of God and apostle of Jesus Christ.",
    after: "Paul says that in due time God revealed His word through preaching.",
    theme: "Hope of eternal life", keyword: "eternal",
    facts: ["Paul emphasizes that God 'cannot lie,' grounding the certainty of the promise."],
  },
  {
    reference: "Titus 1:16", book: "Titus", chapter: 1, verseStart: 16,
    text: "They profess to know God, but by their actions they deny Him. They are detestable, disobedient, and unfit for any good deed.",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul says that to the pure all things are pure, but to the defiled nothing is pure.",
    after: "Paul instructs Titus to teach what accords with sound doctrine.",
    theme: "Faith proven by actions", keyword: "profess",
    facts: ["Paul contrasts empty profession of faith with the actions that reveal true belief."],
  },
  {
    reference: "Titus 2:7", book: "Titus", chapter: 2, verseStart: 7,
    text: "In everything, show yourself to be an example by doing good works. In your teaching show integrity, dignity,",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul instructs the young men to be self-controlled.",
    after: "Paul says to teach sound speech that cannot be condemned.",
    theme: "Be an example", keyword: "example",
    facts: ["Paul urges Titus himself to model the conduct he teaches to others."],
  },
  {
    reference: "Titus 2:12", book: "Titus", chapter: 2, verseStart: 12,
    text: "It instructs us to renounce ungodliness and worldly passions, and to live sensible, upright, and godly lives in the present age,",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul says the grace of God has appeared, bringing salvation to all men.",
    after: "Paul points to the blessed hope and the appearing of our great God and Savior.",
    theme: "Grace that trains us", keyword: "ungodliness",
    facts: ["Paul presents God's grace not only as forgiving but as teaching us how to live."],
  },
  {
    reference: "Titus 2:13", book: "Titus", chapter: 2, verseStart: 13,
    text: "as we await the blessed hope and glorious appearance of our great God and Savior Jesus Christ.",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul describes the sensible, upright, and godly life grace trains us to live.",
    after: "Paul says Christ gave Himself to redeem us and purify a people for Himself.",
    theme: "The blessed hope", keyword: "blessed",
    facts: ["This verse plainly calls Jesus Christ 'our great God and Savior.'"],
  },
  {
    reference: "Titus 2:14", book: "Titus", chapter: 2, verseStart: 14,
    text: "He gave Himself for us to redeem us from all lawlessness and to purify for Himself a people for His own possession, zealous for good deeds.",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul points ahead to the glorious appearing of our God and Savior Jesus Christ.",
    after: "Paul tells Titus to declare these things and to exhort with all authority.",
    theme: "Redeemed for good works", keyword: "redeem",
    facts: ["The phrase 'a people for His own possession' echoes God's words to Israel in Exodus 19:5."],
  },
  {
    reference: "Titus 3:1", book: "Titus", chapter: 3, verseStart: 1,
    text: "Remind the people to be subject to rulers and authorities, to be obedient, to be ready for every good work,",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul tells Titus to teach with authority and let no one disregard him.",
    after: "Paul adds that they should slander no one and be peaceable and gentle.",
    theme: "Submission and good works", keyword: "authorities",
    facts: ["Paul gives similar instruction on submission to governing authorities in Romans 13."],
  },
  {
    reference: "Titus 3:2", book: "Titus", chapter: 3, verseStart: 2,
    text: "to malign no one, and to be peaceable and gentle, showing full consideration to everyone.",
    testament: "NT", speaker: "Paul", audience: "Titus",
    before: "Paul instructs believers to be obedient and ready for every good work.",
    after: "Paul reminds them that they too were once foolish and disobedient.",
    theme: "Gentleness toward all", keyword: "gentle",
    facts: ["Paul roots this gentleness in remembering that believers were once lost themselves."],
  },
  // — Hebrews —
  {
    reference: "Hebrews 13:2", book: "Hebrews", chapter: 13, verseStart: 2,
    text: "Do not neglect to show hospitality to strangers, for by so doing some people have entertained angels without knowing it.",
    testament: "NT", speaker: "The author of Hebrews", audience: "Jewish Christian believers",
    before: "The author urges that brotherly love continue among the believers.",
    after: "The author calls readers to remember prisoners and the mistreated as though suffering with them.",
    theme: "Hospitality to strangers", keyword: "hospitality",
    facts: ["This likely alludes to Abraham, who welcomed three visitors that turned out to be heavenly messengers."],
  },
  // — James —
  {
    reference: "James 1:19", book: "James", chapter: 1, verseStart: 19,
    text: "My beloved brothers, understand this: Everyone should be quick to listen, slow to speak, and slow to anger,",
    testament: "NT", speaker: "James", audience: "Scattered believers",
    before: "James warns that God does not tempt anyone and gives only good gifts.",
    after: "James explains that human anger does not bring about the righteousness of God.",
    theme: "Being quick to listen", keyword: "anger",
    facts: ["This verse is often summarized as 'listen more, speak less.'"],
  },
  {
    reference: "James 3:17", book: "James", chapter: 3, verseStart: 17,
    text: "But the wisdom from above is first of all pure, then peace-loving, gentle, accommodating, full of mercy and good fruit, impartial and sincere.",
    testament: "NT", speaker: "James", audience: "Scattered believers",
    before: "James contrasts earthly wisdom that is unspiritual and demonic.",
    after: "James says peacemakers who sow in peace reap a harvest of righteousness.",
    theme: "Heavenly wisdom", keyword: "wisdom",
    facts: ["James lists seven marks of godly wisdom in a single verse."],
  },
  {
    reference: "James 5:16", book: "James", chapter: 5, verseStart: 16,
    text: "Therefore confess your sins to each other and pray for one another, so that you may be healed. The prayer of a righteous man has great power to prevail.",
    testament: "NT", speaker: "James", audience: "Scattered believers",
    before: "James instructs the sick to call the elders to pray over them.",
    after: "James cites Elijah as an example of a righteous man whose prayer was powerful.",
    theme: "The power of prayer", keyword: "confess",
    facts: ["James points to Elijah as proof that a righteous person's prayer accomplishes much."],
  },
  // — 1 Peter —
  {
    reference: "1 Peter 1:8", book: "1 Peter", chapter: 1, verseStart: 8,
    text: "Though you have not seen Him, you love Him; and though you do not see Him now, you believe in Him and rejoice with an inexpressible and glorious joy,",
    testament: "NT", speaker: "Peter", audience: "Scattered believers",
    before: "Peter says trials prove the genuineness of faith, more precious than gold.",
    after: "Peter says they are receiving the salvation of their souls as the outcome of faith.",
    theme: "Joy in believing", keyword: "inexpressible",
    facts: ["Peter describes a joy so great that words cannot fully express it."],
  },
  {
    reference: "1 Peter 2:24", book: "1 Peter", chapter: 2, verseStart: 24,
    text: "He Himself bore our sins in His body on the tree, so that we might die to sin and live to righteousness. “By His stripes you are healed.”",
    testament: "NT", speaker: "Peter", audience: "Scattered believers",
    before: "Peter says Christ committed no sin, and no deceit was found in His mouth.",
    after: "Peter says they were like straying sheep now returned to the Shepherd of their souls.",
    theme: "Christ bore our sins", keyword: "stripes",
    facts: ["Peter quotes Isaiah 53 to describe Christ's atoning suffering."],
  },
  {
    reference: "1 Peter 3:8", book: "1 Peter", chapter: 3, verseStart: 8,
    text: "Finally, all of you, be like-minded and sympathetic, love as brothers, be tenderhearted and humble.",
    testament: "NT", speaker: "Peter", audience: "Scattered believers",
    before: "Peter gives instructions to husbands to honor their wives.",
    after: "Peter says do not repay evil with evil, but with blessing instead.",
    theme: "Unity and humility", keyword: "tenderhearted",
    facts: ["Peter lists five qualities that should mark relationships among believers."],
  },
  {
    reference: "1 Peter 4:8", book: "1 Peter", chapter: 4, verseStart: 8,
    text: "Above all, love one another deeply, because love covers over a multitude of sins.",
    testament: "NT", speaker: "Peter", audience: "Scattered believers",
    before: "Peter urges them to be clear-minded and sober for the sake of prayer.",
    after: "Peter tells them to show hospitality to one another without complaining.",
    theme: "Fervent love", keyword: "multitude",
    facts: ["Peter echoes Proverbs 10:12, that love covers all offenses."],
  },
  {
    reference: "1 Peter 4:10", book: "1 Peter", chapter: 4, verseStart: 10,
    text: "As each has received a gift, use it to serve one another, as good stewards of the manifold grace of God.",
    testament: "NT", speaker: "Peter", audience: "Scattered believers",
    before: "Peter tells them to show hospitality to one another without complaining.",
    after: "Peter says whoever speaks should speak as one conveying the words of God.",
    theme: "Serving with your gifts", keyword: "stewards",
    facts: ["Peter calls believers 'stewards' who manage God's varied grace for others."],
  },
  {
    reference: "1 Peter 5:8", book: "1 Peter", chapter: 5, verseStart: 8,
    text: "Be sober-minded and alert. Your adversary the devil prowls around like a roaring lion, seeking someone to devour.",
    testament: "NT", speaker: "Peter", audience: "Scattered believers",
    before: "Peter tells them to humble themselves under God's mighty hand.",
    after: "Peter urges them to resist the devil, standing firm in the faith.",
    theme: "Spiritual vigilance", keyword: "devour",
    facts: ["Peter is the only New Testament writer to compare the devil to a roaring lion."],
  },
  // — 2 Peter —
  {
    reference: "2 Peter 1:3", book: "2 Peter", chapter: 1, verseStart: 3,
    text: "His divine power has given us everything we need for life and godliness through the knowledge of Him who called us by His own glory and excellence.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter greets his readers, wishing grace and peace in abundance.",
    after: "Peter says God has given us His precious and magnificent promises.",
    theme: "Everything we need for godliness", keyword: "godliness",
    facts: ["Peter says believers already possess all they need for a godly life."],
  },
  {
    reference: "2 Peter 1:4", book: "2 Peter", chapter: 1, verseStart: 4,
    text: "Through these He has given us His precious and magnificent promises, so that through them you may become partakers of the divine nature, now that you have escaped the corruption in the world caused by evil desires.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter says God's divine power supplies everything for life and godliness.",
    after: "Peter urges them to make every effort to add virtue to their faith.",
    theme: "Partakers of the divine nature", keyword: "partakers",
    facts: ["This is the Bible's clearest statement that believers share in the divine nature."],
  },
  {
    reference: "2 Peter 1:5", book: "2 Peter", chapter: 1, verseStart: 5, verseEnd: 7,
    text: "For this very reason, make every effort to add to your faith virtue; and to virtue, knowledge; and to knowledge, self-control; and to self-control, perseverance; and to perseverance, godliness; and to godliness, brotherly kindness; and to brotherly kindness, love.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter says God's promises let us partake in the divine nature.",
    after: "Peter says these qualities keep us from being ineffective and unfruitful.",
    theme: "Adding to your faith", keyword: "perseverance",
    facts: ["Peter builds a ladder of virtues beginning with faith and ending with love."],
  },
  {
    reference: "2 Peter 1:16", book: "2 Peter", chapter: 1, verseStart: 16,
    text: "For we did not follow cleverly devised fables when we made known to you the power and coming of our Lord Jesus Christ, but we were eyewitnesses of His majesty.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter says he will always remind them of these truths.",
    after: "Peter recalls hearing God's voice on the sacred mountain at the Transfiguration.",
    theme: "Eyewitness testimony", keyword: "eyewitnesses",
    facts: ["Peter refers to witnessing the Transfiguration of Jesus."],
  },
  {
    reference: "2 Peter 1:20", book: "2 Peter", chapter: 1, verseStart: 20, verseEnd: 21,
    text: "Above all, you must understand that no prophecy of Scripture comes from one’s own interpretation. For no such prophecy was ever brought forth by the will of man, but men spoke from God as they were carried along by the Holy Spirit.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter says the prophetic word is a lamp shining in a dark place.",
    after: "Peter warns that false prophets and false teachers will arise among them.",
    theme: "The origin of Scripture", keyword: "prophecy",
    facts: ["This passage is a foundational text for the doctrine of biblical inspiration."],
  },
  {
    reference: "2 Peter 3:8", book: "2 Peter", chapter: 3, verseStart: 8,
    text: "Beloved, do not let this one thing escape your notice: With the Lord a day is like a thousand years, and a thousand years are like a day.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter reminds them that scoffers will come, following their own evil desires.",
    after: "Peter says the Lord is not slow in keeping His promise, but patient.",
    theme: "God's timing", keyword: "thousand",
    facts: ["Peter echoes Psalm 90:4 about how God views the passing of time."],
  },
  {
    reference: "2 Peter 3:10", book: "2 Peter", chapter: 3, verseStart: 10,
    text: "But the Day of the Lord will come like a thief. The heavens will disappear with a roar, the elements will be destroyed by fire, and the earth and its works will be laid bare.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter explains that God's patience is meant to give time for repentance.",
    after: "Peter asks what kind of holy people they ought to be while they wait.",
    theme: "The Day of the Lord", keyword: "thief",
    facts: ["Peter compares the Lord's return to a thief who comes unexpectedly."],
  },
  {
    reference: "2 Peter 3:13", book: "2 Peter", chapter: 3, verseStart: 13,
    text: "But in keeping with God’s promise, we are looking forward to a new heaven and a new earth, where righteousness dwells.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter describes the heavens being destroyed by fire on the Day of God.",
    after: "Peter urges them to be found spotless and blameless in His sight at peace.",
    theme: "A new heaven and new earth", keyword: "righteousness",
    facts: ["Peter's phrase 'new heaven and new earth' reappears in Revelation 21."],
  },
  {
    reference: "2 Peter 3:18", book: "2 Peter", chapter: 3, verseStart: 18,
    text: "But grow in the grace and knowledge of our Lord and Savior Jesus Christ. To Him be the glory both now and to the day of eternity. Amen.",
    testament: "NT", speaker: "Peter", audience: "Believers",
    before: "Peter warns them not to be carried away by the error of the lawless.",
    after: "The letter of 2 Peter concludes with this closing doxology.",
    theme: "Growing in grace", keyword: "grow",
    facts: ["This is the final verse of 2 Peter and Peter's closing exhortation."],
  },
  // — 1 John —
  {
    reference: "1 John 1:7", book: "1 John", chapter: 1, verseStart: 7,
    text: "But if we walk in the light as He is in the light, we have fellowship with one another, and the blood of Jesus His Son cleanses us from all sin.",
    testament: "NT", speaker: "John", audience: "Believers",
    before: "John says that if we claim fellowship yet walk in darkness, we lie.",
    after: "John says if we claim to be without sin, we deceive ourselves.",
    theme: "Walking in the light", keyword: "fellowship",
    facts: ["John presents light and darkness as opposing spheres of moral life."],
  },
  {
    reference: "1 John 2:15", book: "1 John", chapter: 2, verseStart: 15,
    text: "Do not love the world or anything in the world. If anyone loves the world, the love of the Father is not in him.",
    testament: "NT", speaker: "John", audience: "Believers",
    before: "John assures believers that their sins are forgiven for His name's sake.",
    after: "John explains that the world's cravings and pride do not come from the Father.",
    theme: "Do not love the world", keyword: "world",
    facts: ["John uses 'world' here to mean the system opposed to God, not the planet."],
  },
  {
    reference: "1 John 3:16", book: "1 John", chapter: 3, verseStart: 16,
    text: "By this we know what love is: Jesus laid down His life for us, and we ought to lay down our lives for our brothers.",
    testament: "NT", speaker: "John", audience: "Believers",
    before: "John contrasts Cain, who belonged to the evil one, with those who love.",
    after: "John asks how God's love can remain in one who ignores a brother in need.",
    theme: "Sacrificial love", keyword: "laid",
    facts: ["This verse defines love by Christ's self-sacrifice on the cross."],
  },
  {
    reference: "1 John 4:7", book: "1 John", chapter: 4, verseStart: 7,
    text: "Beloved, let us love one another, because love comes from God. Everyone who loves has been born of God and knows God.",
    testament: "NT", speaker: "John", audience: "Believers",
    before: "John warns them to test the spirits to see whether they are from God.",
    after: "John says whoever does not love does not know God, because God is love.",
    theme: "Love comes from God", keyword: "beloved",
    facts: ["John uses the word 'love' more than any other New Testament writer."],
  },
  // — Jude —
  {
    reference: "Jude 1:2", book: "Jude", chapter: 1, verseStart: 2,
    text: "Mercy, peace, and love be multiplied to you.",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude greets those who are called, loved by God and kept in Jesus Christ.",
    after: "Jude explains why he felt compelled to write about contending for the faith.",
    theme: "A greeting of blessing", keyword: "multiplied",
    facts: ["Jude's greeting of mercy, peace, and love is unique among the epistles."],
  },
  {
    reference: "Jude 1:3", book: "Jude", chapter: 1, verseStart: 3,
    text: "Beloved, although I made every effort to write to you about the salvation we share, I felt it necessary to write and urge you to contend earnestly for the faith entrusted once for all to the saints.",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude blesses his readers with mercy, peace, and love.",
    after: "Jude warns that certain ungodly men have secretly slipped in among them.",
    theme: "Contending for the faith", keyword: "contend",
    facts: ["Jude originally intended to write about salvation but changed his focus to warning."],
  },
  {
    reference: "Jude 1:4", book: "Jude", chapter: 1, verseStart: 4,
    text: "For certain men have crept in among you unnoticed—ungodly ones who were designated long ago for condemnation. They turn the grace of our God into a license for immorality and deny our only Master and Lord, Jesus Christ.",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude urges them to contend earnestly for the faith once delivered.",
    after: "Jude reminds them how the Lord destroyed those who did not believe.",
    theme: "Warning against false teachers", keyword: "license",
    facts: ["Jude accuses false teachers of twisting grace into an excuse for sin."],
  },
  {
    reference: "Jude 1:9", book: "Jude", chapter: 1, verseStart: 9,
    text: "But even the archangel Michael, when he disputed with the devil over the body of Moses, did not presume to bring a slanderous charge against him, but said, “The Lord rebuke you!”",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude describes false teachers who reject authority and slander celestial beings.",
    after: "Jude says these men slander whatever they do not understand.",
    theme: "Humility before authority", keyword: "archangel",
    facts: ["This is the only verse in the Bible that names Michael as an archangel."],
  },
  {
    reference: "Jude 1:20", book: "Jude", chapter: 1, verseStart: 20,
    text: "But you, beloved, by building yourselves up in your most holy faith and praying in the Holy Spirit,",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude reminds them that scoffers would come in the last times.",
    after: "Jude tells them to keep themselves in the love of God.",
    theme: "Building up your faith", keyword: "building",
    facts: ["Jude uses a construction image, telling believers to build themselves up."],
  },
  {
    reference: "Jude 1:21", book: "Jude", chapter: 1, verseStart: 21,
    text: "keep yourselves in the love of God as you await the mercy of our Lord Jesus Christ to bring you eternal life.",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude tells them to build themselves up and pray in the Holy Spirit.",
    after: "Jude instructs them to have mercy on those who doubt.",
    theme: "Keeping in God's love", keyword: "await",
    facts: ["Jude ties present faithfulness to the future hope of eternal life."],
  },
  {
    reference: "Jude 1:22", book: "Jude", chapter: 1, verseStart: 22,
    text: "And indeed, have mercy on those who doubt;",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude tells them to keep themselves in the love of God.",
    after: "Jude says to save others by snatching them from the fire.",
    theme: "Mercy toward doubters", keyword: "doubt",
    facts: ["Jude calls for compassion rather than condemnation toward the wavering."],
  },
  {
    reference: "Jude 1:23", book: "Jude", chapter: 1, verseStart: 23,
    text: "save others by snatching them from the fire; and to still others show mercy tempered with fear, hating even the clothing stained by the flesh.",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude tells them to have mercy on those who doubt.",
    after: "Jude breaks into a closing doxology to the God who can keep them from stumbling.",
    theme: "Rescuing the perishing", keyword: "snatching",
    facts: ["Jude pictures urgent rescue, like pulling someone out of a fire."],
  },
  {
    reference: "Jude 1:25", book: "Jude", chapter: 1, verseStart: 25,
    text: "to the only God our Savior be glory, majesty, dominion, and authority, through Jesus Christ our Lord, before all time, and now, and for all eternity. Amen.",
    testament: "NT", speaker: "Jude", audience: "The called",
    before: "Jude praises God who is able to keep them from stumbling and present them blameless.",
    after: "The letter of Jude concludes with this doxology of praise.",
    theme: "Doxology to God our Savior", keyword: "majesty",
    facts: ["This closing doxology is one of the most celebrated in the New Testament."],
  },
  // — Revelation —
  {
    reference: "Revelation 1:8", book: "Revelation", chapter: 1, verseStart: 8,
    text: "\"I am the Alpha and the Omega,\" says the Lord God, \"who is and was and is to come—the Almighty.\"",
    testament: "NT", speaker: "The Lord God", audience: "The seven churches and all readers",
    before: "John announces that Christ is coming with the clouds and every eye will see Him.",
    after: "John describes his exile on the island of Patmos and his vision in the Spirit.",
    theme: "God the eternal Almighty", keyword: "Almighty",
    facts: ["Alpha and Omega are the first and last letters of the Greek alphabet, signifying God as beginning and end."],
  },
  {
    reference: "Revelation 3:16", book: "Revelation", chapter: 3, verseStart: 16,
    text: "So because you are lukewarm—neither hot nor cold—I am about to vomit you out of My mouth!",
    testament: "NT", speaker: "The risen Christ", audience: "The church in Laodicea",
    before: "Christ says He knows the deeds of Laodicea and wishes they were either cold or hot.",
    after: "Christ rebukes them for saying they are rich while being wretched, poor, blind, and naked.",
    theme: "Warning against spiritual complacency", keyword: "lukewarm",
    facts: ["Laodicea's water arrived lukewarm through aqueducts, making the image especially pointed for its residents."],
  },
  {
    reference: "Revelation 5:12", book: "Revelation", chapter: 5, verseStart: 12,
    text: "In a loud voice they were saying: \"Worthy is the Lamb who was slain to receive power and riches and wisdom and strength and honor and glory and blessing!\"",
    testament: "NT", speaker: "Myriads of angels around the throne", audience: "Heaven and all creation",
    before: "The Lamb takes the scroll, and the living creatures and elders fall down in worship.",
    after: "Every creature in heaven and earth joins in praise to the One on the throne and to the Lamb.",
    theme: "Worship of the worthy Lamb", keyword: "Worthy",
    facts: ["This sevenfold acclamation inspired part of Handel's oratorio 'Messiah.'"],
  },
  {
    reference: "Revelation 7:17", book: "Revelation", chapter: 7, verseStart: 17,
    text: "For the Lamb in the center of the throne will be their shepherd. He will lead them to springs of living water, and God will wipe away every tear from their eyes.",
    testament: "NT", speaker: "One of the twenty-four elders", audience: "John and the reader",
    before: "An elder explains that the white-robed multitude came out of the great tribulation.",
    after: "The Lamb opens the seventh seal, and there is silence in heaven for about half an hour.",
    theme: "The Lamb as shepherd and comforter", keyword: "shepherd",
    facts: ["Here the Lamb is paradoxically also the Shepherd, blending two images for Christ."],
  },
  {
    reference: "Revelation 12:11", book: "Revelation", chapter: 12, verseStart: 11,
    text: "They have conquered him by the blood of the Lamb and by the word of their testimony. And they did not love their lives so as to shy away from death.",
    testament: "NT", speaker: "A loud voice in heaven", audience: "Heaven and the reader",
    before: "A voice announces that the accuser of the brothers has been thrown down.",
    after: "The voice calls the heavens to rejoice but warns the earth of the devil's great fury.",
    theme: "Victory over the accuser", keyword: "testimony",
    facts: ["This verse has long been quoted to describe the courage of Christian martyrs."],
  },
  {
    reference: "Revelation 21:1", book: "Revelation", chapter: 21, verseStart: 1,
    text: "Then I saw a new heaven and a new earth, for the first heaven and earth had passed away, and the sea was no more.",
    testament: "NT", speaker: "John the apostle (the narrator)", audience: "The seven churches and all readers",
    before: "Death and Hades are thrown into the lake of fire at the final judgment.",
    after: "John sees the New Jerusalem coming down out of heaven like a bride adorned for her husband.",
    theme: "A renewed creation", keyword: "heaven",
    facts: ["The promise of a new heaven and new earth echoes Isaiah 65:17."],
  },
  {
    reference: "Revelation 22:20", book: "Revelation", chapter: 22, verseStart: 20,
    text: "He who testifies to these things says, \"Yes, I am coming soon.\" Amen. Come, Lord Jesus!",
    testament: "NT", speaker: "The risen Christ and John", audience: "The seven churches and all readers",
    before: "A warning is given against adding to or taking away from the words of this prophecy.",
    after: "The book closes with a benediction of grace upon all the saints.",
    theme: "Longing for Christ's return", keyword: "Amen",
    facts: ["'Come, Lord Jesus' renders the early Aramaic prayer 'Maranatha' used by the first Christians."],
  },

  // ————————————— Full-canon coverage: books the pool was missing —————————————
  // Every book of the Bible now carries at least ten verses, so Focus practice
  // can drill any book a reader opens. Same metadata contract as above.
  // — Leviticus —
  {
    reference: "Leviticus 11:45", book: "Leviticus", chapter: 11, verseStart: 45,
    text: "For I am the LORD, who brought you up out of the land of Egypt to be your God; therefore be holy, because I am holy.",
    testament: "OT", speaker: "The LORD", audience: "Israel through Moses and Aaron",
    before: "The LORD gives Israel the laws distinguishing clean animals from unclean ones.",
    after: "The instruction closes the section on clean and unclean creatures.",
    theme: "God's holiness sets His people apart", keyword: "holy",
    facts: ["Peter quotes this call to holiness in his first letter.", "The word translated 'holy' carries the sense of being set apart for God."],
  },
  {
    reference: "Leviticus 16:30", book: "Leviticus", chapter: 16, verseStart: 30,
    text: "For on this day atonement will be made for you to cleanse you, and you will be clean from all your sins before the LORD.",
    testament: "OT", speaker: "The LORD", audience: "Israel through Moses",
    before: "The LORD sets apart the tenth day of the seventh month as a lasting ordinance.",
    after: "The day is declared a Sabbath of solemn rest on which the people must humble themselves.",
    theme: "The Day of Atonement", keyword: "atonement",
    facts: ["This is the Day of Atonement, known in Hebrew as Yom Kippur.", "It was the one day each year the high priest entered the Most Holy Place."],
  },
  {
    reference: "Leviticus 17:11", book: "Leviticus", chapter: 17, verseStart: 11,
    text: "For the life of the flesh is in the blood, and I have given it to you to make atonement for your souls upon the altar; for it is the blood that makes atonement for the soul.",
    testament: "OT", speaker: "The LORD", audience: "Israel through Moses",
    before: "The LORD forbids the Israelites from eating blood.",
    after: "The prohibition is extended to the foreigner living among them.",
    theme: "Life is in the blood", keyword: "blood",
    facts: ["Hebrews builds on this verse to explain why the sacrifice of Christ was necessary.", "It is the clearest Old Testament statement of why blood was central to sacrifice."],
  },
  {
    reference: "Leviticus 19:2", book: "Leviticus", chapter: 19, verseStart: 2,
    text: "Speak to the whole congregation of Israel and tell them: Be holy because I, the LORD your God, am holy.",
    testament: "OT", speaker: "The LORD", audience: "Moses, for all Israel",
    before: "The LORD begins a long section of laws for everyday community life.",
    after: "The commands open with honoring one's parents and keeping the Sabbaths.",
    theme: "A holy people", keyword: "congregation",
    facts: ["Leviticus 19 is sometimes called the heart of the book.", "Its laws mix worship, farming, business honesty, and neighborly love together."],
  },
  {
    reference: "Leviticus 19:18", book: "Leviticus", chapter: 19, verseStart: 18,
    text: "Do not seek revenge or bear a grudge against any of your people, but love your neighbor as yourself. I am the LORD.",
    testament: "OT", speaker: "The LORD", audience: "Moses, for all Israel",
    before: "The LORD forbids hating a brother in one's heart or standing by idly when a neighbor is in danger.",
    after: "The laws turn to keeping God's statutes about mixed seed and cloth.",
    theme: "Love your neighbor", keyword: "neighbor",
    facts: ["Jesus called this the second greatest commandment.", "Paul writes that this single command sums up the whole law."],
  },
  {
    reference: "Leviticus 19:34", book: "Leviticus", chapter: 19, verseStart: 34,
    text: "You must treat the foreigner living among you as native-born and love him as yourself, for you were foreigners in the land of Egypt. I am the LORD your God.",
    testament: "OT", speaker: "The LORD", audience: "Moses, for all Israel",
    before: "The LORD forbids mistreating a foreigner residing in the land.",
    after: "The laws move on to honest weights and measures in trade.",
    theme: "Love for the outsider", keyword: "foreigner",
    facts: ["Israel's own history as slaves in Egypt is given as the reason for the command.", "The same love commanded for a neighbor is here commanded for the stranger."],
  },
  {
    reference: "Leviticus 20:26", book: "Leviticus", chapter: 20, verseStart: 26,
    text: "You are to be holy to Me because I, the LORD, am holy, and I have set you apart from the nations to be My own.",
    testament: "OT", speaker: "The LORD", audience: "Israel through Moses",
    before: "The LORD tells Israel to distinguish between clean and unclean animals.",
    after: "The chapter closes with laws about mediums and spiritists.",
    theme: "Set apart for God", keyword: "nations",
    facts: ["The phrase 'set apart' is the root idea behind the word 'holy.'", "This verse gives the purpose behind Israel's distinctive food and purity laws."],
  },
  {
    reference: "Leviticus 23:3", book: "Leviticus", chapter: 23, verseStart: 3,
    text: "For six days work may be done, but the seventh day is a Sabbath of complete rest, a sacred assembly. You must not do any work; wherever you live, it is a Sabbath to the LORD.",
    testament: "OT", speaker: "The LORD", audience: "Israel through Moses",
    before: "The LORD introduces the appointed feasts that Israel is to proclaim as sacred assemblies.",
    after: "The instructions turn to the Passover on the fourteenth day of the first month.",
    theme: "Sabbath rest", keyword: "Sabbath",
    facts: ["The weekly Sabbath is listed first among Israel's appointed feasts.", "It applied 'wherever you live,' not only at the sanctuary."],
  },
  {
    reference: "Leviticus 25:10", book: "Leviticus", chapter: 25, verseStart: 10,
    text: "So you shall consecrate the fiftieth year and proclaim liberty in the land for all its inhabitants. It shall be your Jubilee, when each of you is to return to his property and to his clan.",
    testament: "OT", speaker: "The LORD", audience: "Israel through Moses",
    before: "The LORD tells Israel to count seven sets of seven years, forty-nine years in all.",
    after: "The fiftieth year is to be holy, with no sowing or reaping.",
    theme: "The year of Jubilee", keyword: "Jubilee",
    facts: ["Part of this verse is inscribed on the Liberty Bell in Philadelphia.", "In the Jubilee year, ancestral land returned to the family that first held it."],
  },
  {
    reference: "Leviticus 26:12", book: "Leviticus", chapter: 26, verseStart: 12,
    text: "I will walk among you and be your God, and you will be My people.",
    testament: "OT", speaker: "The LORD", audience: "Israel through Moses",
    before: "The LORD promises to make His dwelling among the people and not abhor them.",
    after: "The LORD reminds them that He broke the bars of their yoke in Egypt.",
    theme: "God dwelling with His people", keyword: "people",
    facts: ["Paul quotes this promise when calling the church God's temple.", "The same promise reappears in Revelation's vision of the new creation."],
  },
  // — Judges —
  {
    reference: "Judges 2:16", book: "Judges", chapter: 2, verseStart: 16,
    text: "Then the LORD raised up judges, who saved them from the hands of those who plundered them.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Israel abandons the LORD and serves the gods of the peoples around them.",
    after: "Israel refuses to listen to the judges and prostitutes itself to other gods.",
    theme: "God raises up deliverers", keyword: "judges",
    facts: ["This verse states the repeating cycle that shapes the whole book.", "The 'judges' were military deliverers more than courtroom officials."],
  },
  {
    reference: "Judges 4:4", book: "Judges", chapter: 4, verseStart: 4,
    text: "Now Deborah, a prophetess, the wife of Lappidoth, was judging Israel at that time.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Israel is oppressed by Jabin king of Canaan and his commander Sisera for twenty years.",
    after: "Deborah holds court under a palm tree in the hill country of Ephraim.",
    theme: "Deborah leads Israel", keyword: "prophetess",
    facts: ["Deborah is the only woman named among Israel's judges.", "She is also called a prophetess, holding both roles at once."],
  },
  {
    reference: "Judges 5:31", book: "Judges", chapter: 5, verseStart: 31,
    text: "So may all Your enemies perish, O LORD! But may those who love You shine like the sun at its brightest.",
    testament: "OT", speaker: "Deborah and Barak in song", audience: "The LORD and Israel",
    before: "The song of Deborah recounts the LORD's victory over Sisera.",
    after: "The land has rest for forty years.",
    theme: "Victory belongs to the LORD", keyword: "shine",
    facts: ["The Song of Deborah is among the oldest poetry in the Bible.", "Jesus echoes its imagery when He says the righteous will shine like the sun."],
  },
  {
    reference: "Judges 6:12", book: "Judges", chapter: 6, verseStart: 12,
    text: "The angel of the LORD appeared to Gideon and said, \"The LORD is with you, O mighty warrior.\"",
    testament: "OT", speaker: "The angel of the LORD", audience: "Gideon",
    before: "Gideon is threshing wheat in a winepress to hide it from the Midianites.",
    after: "Gideon asks why, if the LORD is with them, all this has happened to Israel.",
    theme: "God calls the unlikely", keyword: "warrior",
    facts: ["Gideon is greeted as a mighty warrior while hiding from his enemies.", "He later calls himself the least in the weakest clan of Manasseh."],
  },
  {
    reference: "Judges 6:24", book: "Judges", chapter: 6, verseStart: 24,
    text: "So Gideon built an altar to the LORD there and called it The LORD Is Peace. To this day it stands in Ophrah of the Abiezrites.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The LORD tells Gideon not to fear, for he will not die after seeing the angel.",
    after: "That night the LORD tells Gideon to tear down his father's altar to Baal.",
    theme: "The LORD is peace", keyword: "altar",
    facts: ["The Hebrew name Gideon gave the altar is Jehovah-Shalom.", "Naming an altar was a way of marking what God had revealed there."],
  },
  {
    reference: "Judges 7:2", book: "Judges", chapter: 7, verseStart: 2,
    text: "Then the LORD said to Gideon, \"You have too many people for Me to deliver Midian into their hands, lest Israel boast against Me, saying, 'My own hand has saved me.'\"",
    testament: "OT", speaker: "The LORD", audience: "Gideon",
    before: "Gideon and his men camp beside the spring of Harod, north of the Midianite camp.",
    after: "The LORD tells Gideon to send home everyone who trembles with fear.",
    theme: "Victory by God's hand, not ours", keyword: "boast",
    facts: ["Gideon's army shrank from 32,000 men to just 300.", "The reduction was designed so no one could credit the win to Israel's strength."],
  },
  {
    reference: "Judges 7:20", book: "Judges", chapter: 7, verseStart: 20,
    text: "The three companies blew their horns and shattered their jars. Holding the torches in their left hands and the horns in their right hands, they cried out, \"A sword for the LORD and for Gideon!\"",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Gideon's three hundred men surround the Midianite camp at the start of the middle watch.",
    after: "The Midianite army flees in confusion, turning their swords on one another.",
    theme: "God routs an army", keyword: "torches",
    facts: ["The 300 men carried torches hidden inside clay jars.", "They won without drawing their own swords at the moment of attack."],
  },
  {
    reference: "Judges 13:5", book: "Judges", chapter: 13, verseStart: 5,
    text: "For behold, you will conceive and give birth to a son. And no razor shall come over his head, because the boy will be a Nazirite to God from the womb, and he will begin the deliverance of Israel from the hand of the Philistines.",
    testament: "OT", speaker: "The angel of the LORD", audience: "Manoah's wife",
    before: "The angel of the LORD appears to a barren woman and tells her she will bear a son.",
    after: "The woman goes and tells her husband Manoah what the man of God said.",
    theme: "A deliverer set apart before birth", keyword: "Nazirite",
    facts: ["The child announced here is Samson.", "A Nazirite vow meant no wine, no cutting the hair, and no contact with the dead."],
  },
  {
    reference: "Judges 16:28", book: "Judges", chapter: 16, verseStart: 28,
    text: "Then Samson called out to the LORD, \"O Lord GOD, please remember me. Strengthen me, O God, just once more, so that with one act of vengeance I may pay back the Philistines for my two eyes.\"",
    testament: "OT", speaker: "Samson", audience: "The LORD",
    before: "The Philistines blind Samson and bring him out to entertain them at a feast for Dagon.",
    after: "Samson pushes against the pillars and the temple collapses on everyone inside.",
    theme: "A last prayer for strength", keyword: "Strengthen",
    facts: ["Samson killed more Philistines in his death than in his life.", "He is listed among the heroes of faith in Hebrews 11."],
  },
  {
    reference: "Judges 17:6", book: "Judges", chapter: 17, verseStart: 6,
    text: "In those days there was no king in Israel; everyone did what was right in his own eyes.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Micah sets up a shrine in his house and installs one of his sons as a priest.",
    after: "A young Levite from Bethlehem travels through the hill country looking for a place to stay.",
    theme: "Life without a king", keyword: "king",
    facts: ["This line is the refrain that closes the book of Judges.", "It sets up the need for the kingship that arrives in 1 Samuel."],
  },
  // — Ruth —
  {
    reference: "Ruth 1:16", book: "Ruth", chapter: 1, verseStart: 16,
    text: "But Ruth replied, \"Do not urge me to leave you or to turn from following you. For wherever you go, I will go, and wherever you live, I will live; your people will be my people, and your God will be my God.\"",
    testament: "OT", speaker: "Ruth", audience: "Naomi",
    before: "Naomi urges her widowed daughters-in-law to return to their mothers' homes in Moab.",
    after: "Ruth vows that only death will separate them.",
    theme: "Covenant loyalty", keyword: "following",
    facts: ["These words are often read at weddings, though they were first spoken between in-laws.", "Ruth was a Moabitess choosing the God of Israel."],
  },
  {
    reference: "Ruth 1:17", book: "Ruth", chapter: 1, verseStart: 17,
    text: "Where you die, I will die, and there I will be buried. May the LORD punish me, and ever so severely, if anything but death separates you and me.",
    testament: "OT", speaker: "Ruth", audience: "Naomi",
    before: "Ruth refuses to leave Naomi, pledging to make Naomi's people and God her own.",
    after: "Naomi sees that Ruth is determined and stops urging her to go back.",
    theme: "A vow that outlasts loss", keyword: "buried",
    facts: ["Ruth binds her promise with an oath in the name of the LORD.", "Her loyalty is the Hebrew idea of hesed, steadfast covenant kindness."],
  },
  {
    reference: "Ruth 1:20", book: "Ruth", chapter: 1, verseStart: 20,
    text: "\"Do not call me Naomi,\" she replied. \"Call me Mara, because the Almighty has dealt quite bitterly with me.\"",
    testament: "OT", speaker: "Naomi", audience: "The women of Bethlehem",
    before: "Naomi and Ruth arrive in Bethlehem and the whole town is stirred because of them.",
    after: "Naomi says she went away full but the LORD has brought her back empty.",
    theme: "Honest grief before God", keyword: "bitterly",
    facts: ["The name Naomi means 'pleasant'; Mara means 'bitter.'", "The book lets a grieving woman name her pain without rebuking her for it."],
  },
  {
    reference: "Ruth 2:2", book: "Ruth", chapter: 2, verseStart: 2,
    text: "And Ruth the Moabitess said to Naomi, \"Please let me go into the fields and glean heads of grain after someone in whose sight I may find favor.\" \"Go ahead, my daughter,\" Naomi replied.",
    testament: "OT", speaker: "Ruth", audience: "Naomi",
    before: "Naomi has a relative on her husband's side, a prominent man named Boaz.",
    after: "Ruth happens to come to the part of the field belonging to Boaz.",
    theme: "Work and providence", keyword: "glean",
    facts: ["The law required farmers to leave the edges of a field for the poor to glean.", "Ruth's gleaning is how she first meets Boaz."],
  },
  {
    reference: "Ruth 2:11", book: "Ruth", chapter: 2, verseStart: 11,
    text: "Boaz replied, \"I have been fully informed about all you have done for your mother-in-law since the death of your husband, how you left your father and mother and the land of your birth and came to a people you did not know before.\"",
    testament: "OT", speaker: "Boaz", audience: "Ruth",
    before: "Ruth bows down and asks why she has found such favor in Boaz's eyes as a foreigner.",
    after: "Boaz prays that the LORD would fully reward her work.",
    theme: "Kindness noticed", keyword: "informed",
    facts: ["Boaz had heard of Ruth's loyalty before he ever spoke to her.", "His description echoes God's call for Abraham to leave his homeland."],
  },
  {
    reference: "Ruth 2:12", book: "Ruth", chapter: 2, verseStart: 12,
    text: "May the LORD repay your work, and may you receive a rich reward from the LORD, the God of Israel, under whose wings you have taken refuge.",
    testament: "OT", speaker: "Boaz", audience: "Ruth",
    before: "Boaz tells Ruth he has heard how she left her homeland to care for Naomi.",
    after: "Ruth answers that she has found favor in his eyes though she is not one of his servants.",
    theme: "Refuge under God's wings", keyword: "refuge",
    facts: ["Boaz prays for a reward he himself will help provide.", "The image of sheltering wings runs through the Psalms as well."],
  },
  {
    reference: "Ruth 2:20", book: "Ruth", chapter: 2, verseStart: 20,
    text: "Then Naomi said to her daughter-in-law, \"May he be blessed by the LORD, who has not withdrawn His kindness from the living or the dead.\" Naomi continued, \"The man is a close relative. He is one of our kinsman-redeemers.\"",
    testament: "OT", speaker: "Naomi", audience: "Ruth",
    before: "Ruth tells Naomi that the man in whose field she worked is named Boaz.",
    after: "Ruth reports that Boaz told her to stay close to his servants until the harvest is finished.",
    theme: "God's kindness through a redeemer", keyword: "kindness",
    facts: ["A kinsman-redeemer was a relative who could buy back land and protect a family line.", "This is the first hint of how Naomi's emptiness will be reversed."],
  },
  {
    reference: "Ruth 3:11", book: "Ruth", chapter: 3, verseStart: 11,
    text: "And now, my daughter, do not be afraid. I will do for you whatever you request, since all my fellow townspeople know that you are a woman of noble character.",
    testament: "OT", speaker: "Boaz", audience: "Ruth",
    before: "Ruth asks Boaz to spread the corner of his garment over her, since he is a kinsman-redeemer.",
    after: "Boaz explains that there is a redeemer more closely related than he is.",
    theme: "A woman of noble character", keyword: "noble",
    facts: ["The Hebrew phrase here matches the 'wife of noble character' of Proverbs 31.", "In the Hebrew Bible, Ruth is placed right after Proverbs."],
  },
  {
    reference: "Ruth 4:14", book: "Ruth", chapter: 4, verseStart: 14,
    text: "Then the women said to Naomi, \"Blessed be the LORD, who has not left you without a kinsman-redeemer this day. May his name become famous in Israel.\"",
    testament: "OT", speaker: "The women of Bethlehem", audience: "Naomi",
    before: "Boaz marries Ruth and the LORD enables her to conceive, and she gives birth to a son.",
    after: "The women say the child will renew Naomi's life and sustain her in old age.",
    theme: "Emptiness turned to fullness", keyword: "famous",
    facts: ["The women who first heard Naomi's bitterness now bless her.", "The child is Obed, grandfather of King David."],
  },
  {
    reference: "Ruth 4:17", book: "Ruth", chapter: 4, verseStart: 17,
    text: "The neighbor women said, \"A son has been born to Naomi,\" and they named him Obed. He was the father of Jesse, the father of David.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The women bless Naomi and say her daughter-in-law who loves her is better to her than seven sons.",
    after: "The book closes with the genealogy from Perez to David.",
    theme: "A quiet story in a royal line", keyword: "Obed",
    facts: ["Ruth the Moabitess is King David's great-grandmother.", "Matthew names Ruth in the genealogy of Jesus."],
  },
  // — 2 Samuel —
  {
    reference: "2 Samuel 6:14", book: "2 Samuel", chapter: 6, verseStart: 14,
    text: "And David, wearing a linen ephod, danced with all his might before the LORD.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "David and all Israel bring the ark of God up to Jerusalem with shouting and trumpets.",
    after: "Michal, daughter of Saul, watches from a window and despises David in her heart.",
    theme: "Unrestrained worship", keyword: "danced",
    facts: ["David danced as a king, dressed in a simple priestly garment.", "The ark's journey to Jerusalem made the city Israel's worship center."],
  },
  {
    reference: "2 Samuel 7:16", book: "2 Samuel", chapter: 7, verseStart: 16,
    text: "Your house and kingdom will endure forever before Me, and your throne will be established forever.",
    testament: "OT", speaker: "The LORD, through Nathan", audience: "David",
    before: "The LORD promises to raise up David's offspring and establish his kingdom.",
    after: "Nathan reports all these words and this entire vision to David.",
    theme: "The everlasting covenant with David", keyword: "throne",
    facts: ["This promise is known as the Davidic covenant.", "The angel Gabriel echoes it when announcing Jesus to Mary."],
  },
  {
    reference: "2 Samuel 7:22", book: "2 Samuel", chapter: 7, verseStart: 22,
    text: "How great You are, O Lord GOD! For there is none like You, and there is no God but You, according to all that we have heard with our own ears.",
    testament: "OT", speaker: "David", audience: "The LORD",
    before: "David goes in and sits before the LORD, asking who he is that God has brought him this far.",
    after: "David asks who is like Israel, the one nation on earth God redeemed for Himself.",
    theme: "Praise for God's greatness", keyword: "great",
    facts: ["David's response to the covenant is prayer, not planning.", "He calls God incomparable, echoing the song of Moses."],
  },
  {
    reference: "2 Samuel 9:7", book: "2 Samuel", chapter: 9, verseStart: 7,
    text: "\"Do not be afraid,\" said David, \"for I will surely show you kindness for the sake of your father Jonathan. I will restore to you all the land of your grandfather Saul, and you will always eat at my table.\"",
    testament: "OT", speaker: "David", audience: "Mephibosheth",
    before: "David asks whether anyone is left of Saul's house to whom he can show kindness.",
    after: "Mephibosheth bows and asks why the king would notice a dead dog like him.",
    theme: "Kindness for a friend's sake", keyword: "kindness",
    facts: ["Mephibosheth was lame in both feet since childhood.", "David keeps a promise made years earlier to Jonathan."],
  },
  {
    reference: "2 Samuel 12:7", book: "2 Samuel", chapter: 12, verseStart: 7,
    text: "Then Nathan said to David, \"You are that man! This is what the LORD, the God of Israel, says: I anointed you king over Israel and delivered you from the hand of Saul.\"",
    testament: "OT", speaker: "Nathan the prophet", audience: "David",
    before: "Nathan tells David a parable about a rich man who took a poor man's only lamb.",
    after: "Nathan lists all the LORD gave David and asks why he despised the word of the LORD.",
    theme: "A prophet confronts a king", keyword: "anointed",
    facts: ["Nathan trapped David into judging his own sin by telling him a story.", "Psalm 51 is traditionally David's response to this confrontation."],
  },
  {
    reference: "2 Samuel 12:13", book: "2 Samuel", chapter: 12, verseStart: 13,
    text: "Then David said to Nathan, \"I have sinned against the LORD.\" \"The LORD has taken away your sin,\" Nathan replied. \"You will not die.\"",
    testament: "OT", speaker: "David and Nathan", audience: "Each other",
    before: "Nathan declares the LORD's judgment on David's house for taking Uriah's wife.",
    after: "Nathan says the child born to David will die.",
    theme: "Confession and forgiveness", keyword: "sinned",
    facts: ["David confesses in five Hebrew words, with no excuses.", "Forgiveness came, but the consequences within his family still unfolded."],
  },
  {
    reference: "2 Samuel 22:2", book: "2 Samuel", chapter: 22, verseStart: 2,
    text: "He said: \"The LORD is my rock, my fortress, and my deliverer.\"",
    testament: "OT", speaker: "David", audience: "The LORD",
    before: "David sings this song on the day the LORD delivers him from all his enemies and from Saul.",
    after: "David calls God his shield, the horn of his salvation, and his stronghold.",
    theme: "God as refuge", keyword: "fortress",
    facts: ["This song appears almost word for word as Psalm 18.", "David uses images from the wilderness caves where he once hid."],
  },
  {
    reference: "2 Samuel 22:31", book: "2 Samuel", chapter: 22, verseStart: 31,
    text: "As for God, His way is perfect; the word of the LORD is flawless. He is a shield to all who take refuge in Him.",
    testament: "OT", speaker: "David", audience: "The LORD",
    before: "David sings that God arms him with strength and makes his way clear.",
    after: "David asks who is God besides the LORD, and who is a rock except our God.",
    theme: "The flawless word of God", keyword: "flawless",
    facts: ["The word rendered 'flawless' pictures metal refined in a furnace.", "Proverbs 30 quotes this line almost exactly."],
  },
  {
    reference: "2 Samuel 23:3", book: "2 Samuel", chapter: 23, verseStart: 3,
    text: "The God of Israel spoke; the Rock of Israel said to me: \"He who rules the people with justice, who rules in the fear of God,\"",
    testament: "OT", speaker: "David", audience: "Readers of Israel",
    before: "These are introduced as the last words of David, the man anointed by the God of Jacob.",
    after: "David compares such a ruler to the morning light at sunrise on a cloudless day.",
    theme: "What a good ruler looks like", keyword: "justice",
    facts: ["These are recorded as David's final words.", "He measures kingship by justice and the fear of God, not conquest."],
  },
  {
    reference: "2 Samuel 24:24", book: "2 Samuel", chapter: 24, verseStart: 24,
    text: "But the king replied to Araunah, \"No, I insist on paying a price, for I will not offer to the LORD my God burnt offerings that cost me nothing.\" So David bought the threshing floor and the oxen for fifty shekels of silver.",
    testament: "OT", speaker: "David", audience: "Araunah the Jebusite",
    before: "Araunah offers to give David the threshing floor and the oxen for the offering free of charge.",
    after: "David builds an altar there and the plague on Israel is halted.",
    theme: "Worship that costs something", keyword: "threshing",
    facts: ["This threshing floor became the site of Solomon's temple.", "David refused a free offering because a costless gift felt like no gift at all."],
  },
  // — 1 Kings —
  {
    reference: "1 Kings 2:3", book: "1 Kings", chapter: 2, verseStart: 3,
    text: "And keep the charge of the LORD your God, to walk in His ways and to keep His statutes, commandments, ordinances, and testimonies, as it is written in the Law of Moses, so that you may prosper in all you do and wherever you turn.",
    testament: "OT", speaker: "David", audience: "His son Solomon",
    before: "David's time to die draws near and he charges Solomon to be strong and courageous.",
    after: "David says the LORD will keep His promise if Solomon's sons walk faithfully before Him.",
    theme: "A father's charge to walk in God's ways", keyword: "statutes",
    facts: ["These are David's parting instructions before Solomon takes the throne.", "The charge echoes the words God gave Joshua generations earlier."],
  },
  {
    reference: "1 Kings 3:9", book: "1 Kings", chapter: 3, verseStart: 9,
    text: "Therefore give Your servant an understanding heart to judge Your people and to discern between good and evil. For who is able to govern this great people of Yours?",
    testament: "OT", speaker: "Solomon", audience: "The LORD",
    before: "God appears to Solomon in a dream at Gibeon and invites him to ask for whatever he wants.",
    after: "The LORD is pleased that Solomon asked for discernment rather than riches or long life.",
    theme: "Asking God for wisdom", keyword: "discern",
    facts: ["Solomon could have asked for anything and asked for wisdom to govern.", "God gave him riches and honor as well, because he had not asked for them."],
  },
  {
    reference: "1 Kings 3:12", book: "1 Kings", chapter: 3, verseStart: 12,
    text: "behold, I will do what you have asked. I will give you a wise and discerning heart, so that there has never been anyone like you, nor will there ever be.",
    testament: "OT", speaker: "The LORD", audience: "Solomon",
    before: "God notes that Solomon asked for discernment instead of long life, wealth, or the death of his enemies.",
    after: "God also grants him the riches and honor he did not ask for.",
    theme: "God grants wisdom generously", keyword: "discerning",
    facts: ["Solomon's wisdom became proverbial across the ancient world.", "James later urges believers to ask God for wisdom in the same spirit."],
  },
  {
    reference: "1 Kings 8:27", book: "1 Kings", chapter: 8, verseStart: 27,
    text: "But will God indeed dwell upon the earth? Even heaven, the highest heaven, cannot contain You, much less this temple I have built.",
    testament: "OT", speaker: "Solomon", audience: "The LORD, before all Israel",
    before: "Solomon stands before the altar and spreads out his hands to dedicate the newly finished temple.",
    after: "Solomon asks God to hear the prayers offered toward this place.",
    theme: "God is greater than any temple", keyword: "contain",
    facts: ["Solomon dedicates the temple by admitting it could never hold God.", "Stephen quotes this idea in his speech in Acts 7."],
  },
  {
    reference: "1 Kings 8:56", book: "1 Kings", chapter: 8, verseStart: 56,
    text: "Blessed be the LORD, who has given rest to His people Israel according to all that He promised. Not one word has failed of all the good promises He made through His servant Moses.",
    testament: "OT", speaker: "Solomon", audience: "The assembly of Israel",
    before: "Solomon finishes his long prayer of dedication and rises to bless the whole assembly.",
    after: "Solomon prays that the LORD would incline their hearts to Him to walk in all His ways.",
    theme: "Not one promise has failed", keyword: "promises",
    facts: ["Solomon looks back on centuries of history and finds God's record unbroken.", "The 'rest' he names is the settled land promised back in Deuteronomy."],
  },
  {
    reference: "1 Kings 17:16", book: "1 Kings", chapter: 17, verseStart: 16,
    text: "The jar of flour was not exhausted and the jug of oil did not run dry, according to the word that the LORD had spoken through Elijah.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Elijah asks a widow of Zarephath to make him bread first from the last of her flour and oil.",
    after: "The widow's son becomes ill and stops breathing, and Elijah prays for his life.",
    theme: "God provides in famine", keyword: "flour",
    facts: ["The widow was a foreigner from Sidon, not an Israelite.", "Jesus points to this widow when preaching in Nazareth."],
  },
  {
    reference: "1 Kings 18:21", book: "1 Kings", chapter: 18, verseStart: 21,
    text: "Then Elijah approached all the people and said, \"How long will you waver between two opinions? If the LORD is God, follow Him. But if Baal is God, follow him.\" But the people did not answer a word.",
    testament: "OT", speaker: "Elijah", audience: "The people of Israel on Mount Carmel",
    before: "Ahab summons all Israel and the prophets of Baal to Mount Carmel at Elijah's request.",
    after: "Elijah proposes that each side prepare a bull and call on their God to answer by fire.",
    theme: "Choosing whom to follow", keyword: "waver",
    facts: ["The people's silence is as telling as any answer.", "The Hebrew pictures hobbling along on two different crutches."],
  },
  {
    reference: "1 Kings 18:39", book: "1 Kings", chapter: 18, verseStart: 39,
    text: "When all the people saw this, they fell facedown and cried out, \"The LORD, He is God! The LORD, He is God!\"",
    testament: "OT", speaker: "The people of Israel", audience: "One another, and the LORD",
    before: "Fire from the LORD falls and consumes the sacrifice, the wood, the stones, the soil, and the water in the trench.",
    after: "Elijah orders the prophets of Baal seized at the Kishon Valley.",
    theme: "God answers by fire", keyword: "facedown",
    facts: ["Elijah had soaked the altar with water three times before praying.", "The cry repeats twice in Hebrew, a way of making a verdict emphatic."],
  },
  {
    reference: "1 Kings 19:12", book: "1 Kings", chapter: 19, verseStart: 12,
    text: "After the earthquake there was a fire, but the LORD was not in the fire. And after the fire came a still, small voice.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Elijah stands on the mountain as a great wind tears into the rocks and an earthquake follows.",
    after: "Elijah wraps his face in his cloak and goes out to stand at the mouth of the cave.",
    theme: "God in the quiet", keyword: "earthquake",
    facts: ["The Hebrew phrase is sometimes rendered 'a gentle whisper' or 'a sound of sheer silence.'", "Elijah had just fled for his life and asked God to take it."],
  },
  {
    reference: "1 Kings 19:18", book: "1 Kings", chapter: 19, verseStart: 18,
    text: "Nevertheless, I have reserved seven thousand in Israel — all whose knees have not bowed to Baal and whose mouths have not kissed him.",
    testament: "OT", speaker: "The LORD", audience: "Elijah",
    before: "Elijah insists he alone is left and that they are trying to take his life.",
    after: "Elijah leaves and finds Elisha son of Shaphat plowing with twelve yoke of oxen.",
    theme: "God's faithful remnant", keyword: "reserved",
    facts: ["Elijah believed he was the last believer in Israel; God said otherwise.", "Paul cites this verse in Romans 11 as proof of a faithful remnant."],
  },
  // — 2 Kings —
  {
    reference: "2 Kings 2:9", book: "2 Kings", chapter: 2, verseStart: 9,
    text: "After they had crossed over, Elijah said to Elisha, \"Tell me, what can I do for you before I am taken away from you?\" \"Please, let me inherit a double portion of your spirit,\" Elisha replied.",
    testament: "OT", speaker: "Elijah and Elisha", audience: "Each other",
    before: "Elijah strikes the Jordan with his cloak and the two of them cross over on dry ground.",
    after: "Elijah says it is a difficult request, granted only if Elisha sees him taken away.",
    theme: "Passing on a calling", keyword: "portion",
    facts: ["A double portion was the inheritance share of a firstborn son.", "Elisha was asking to be Elijah's true successor, not to be twice as great."],
  },
  {
    reference: "2 Kings 2:11", book: "2 Kings", chapter: 2, verseStart: 11,
    text: "As they were walking along and talking together, suddenly a chariot of fire with horses of fire appeared and separated the two of them, and Elijah went up into heaven in a whirlwind.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Elisha asks for a double portion of Elijah's spirit as they walk on together.",
    after: "Elisha sees it and cries out, then picks up the cloak that fell from Elijah.",
    theme: "Taken up to heaven", keyword: "whirlwind",
    facts: ["Elijah is one of only two people in Scripture said not to have died.", "He appears again with Moses at the transfiguration of Jesus."],
  },
  {
    reference: "2 Kings 5:10", book: "2 Kings", chapter: 5, verseStart: 10,
    text: "Then Elisha sent him a messenger, who said, \"Go and wash yourself seven times in the Jordan, and your flesh will be restored, and you will be clean.\"",
    testament: "OT", speaker: "Elisha, through a messenger", audience: "Naaman the Aramean commander",
    before: "Naaman arrives with horses and chariots and stops at the door of Elisha's house.",
    after: "Naaman goes away angry, expecting the prophet to come out and wave his hand over him.",
    theme: "Healing on God's terms", keyword: "Jordan",
    facts: ["Naaman was the commander of an enemy army.", "Elisha never came outside to meet him, which offended Naaman deeply."],
  },
  {
    reference: "2 Kings 5:14", book: "2 Kings", chapter: 5, verseStart: 14,
    text: "So Naaman went down and dipped himself in the Jordan seven times, according to the word of the man of God, and his flesh was restored and became like that of a little child, and he was clean.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Naaman's servants persuade him to do the simple thing the prophet asked.",
    after: "Naaman returns and declares there is no God in all the earth except in Israel.",
    theme: "Obedience in small things", keyword: "dipped",
    facts: ["Naaman was healed only after doing something that felt beneath him.", "Jesus mentions Naaman as an outsider who received God's mercy."],
  },
  {
    reference: "2 Kings 6:16", book: "2 Kings", chapter: 6, verseStart: 16,
    text: "\"Do not be afraid,\" Elisha answered, \"for those who are with us are more than those who are with them.\"",
    testament: "OT", speaker: "Elisha", audience: "His servant",
    before: "Elisha's servant wakes to find the city surrounded by an Aramean army with horses and chariots.",
    after: "Elisha prays that the LORD would open the servant's eyes.",
    theme: "Unseen help outnumbers the threat", keyword: "afraid",
    facts: ["The servant could count the enemy but not the allies.", "Elisha's calm came from seeing what his servant could not yet see."],
  },
  {
    reference: "2 Kings 6:17", book: "2 Kings", chapter: 6, verseStart: 17,
    text: "Then Elisha prayed, \"O LORD, please open his eyes that he may see.\" And the LORD opened the eyes of the young man, and he saw that the hills were full of horses and chariots of fire all around Elisha.",
    testament: "OT", speaker: "Elisha", audience: "The LORD, on behalf of his servant",
    before: "Elisha tells his servant that those with them are more than those with the enemy.",
    after: "As the Arameans come down, Elisha prays that they would be struck with blindness.",
    theme: "Eyes opened to God's protection", keyword: "chariots",
    facts: ["Elisha's prayer was for sight, not for rescue.", "The fiery horses recall the chariot that carried Elijah away."],
  },
  {
    reference: "2 Kings 19:19", book: "2 Kings", chapter: 19, verseStart: 19,
    text: "And now, O LORD our God, please save us from his hand, so that all the kingdoms of the earth may know that You alone, O LORD, are God.",
    testament: "OT", speaker: "King Hezekiah", audience: "The LORD",
    before: "Hezekiah spreads the Assyrian king's threatening letter out before the LORD in the temple.",
    after: "Isaiah sends word that the LORD has heard Hezekiah's prayer concerning Sennacherib.",
    theme: "Praying about a threat", keyword: "kingdoms",
    facts: ["Hezekiah literally laid the enemy's letter open before God.", "His concern was God's reputation among the nations, not only his own survival."],
  },
  {
    reference: "2 Kings 20:5", book: "2 Kings", chapter: 20, verseStart: 5,
    text: "Go back and tell Hezekiah the leader of My people that this is what the LORD, the God of your father David, says: I have heard your prayer; I have seen your tears. Behold, I will heal you.",
    testament: "OT", speaker: "The LORD, through Isaiah", audience: "Hezekiah",
    before: "Isaiah tells Hezekiah to put his house in order because he is going to die.",
    after: "The LORD promises to add fifteen years to Hezekiah's life and defend the city.",
    theme: "God hears prayers and tears", keyword: "tears",
    facts: ["Isaiah had not even left the middle courtyard before God sent him back.", "Hezekiah's illness and recovery are also recorded in Isaiah 38."],
  },
  {
    reference: "2 Kings 22:8", book: "2 Kings", chapter: 22, verseStart: 8,
    text: "Then Hilkiah the high priest said to Shaphan the scribe, \"I have found the Book of the Law in the house of the LORD.\" And he gave it to Shaphan, who read it.",
    testament: "OT", speaker: "Hilkiah the high priest", audience: "Shaphan the scribe",
    before: "Josiah sends Shaphan to the temple to pay the workers repairing the house of the LORD.",
    after: "Shaphan reads the book aloud to the king, who tears his robes in grief.",
    theme: "Rediscovering God's word", keyword: "scribe",
    facts: ["The scroll had been lost inside the temple itself.", "Its rediscovery launched the most sweeping reform in Judah's history."],
  },
  {
    reference: "2 Kings 23:25", book: "2 Kings", chapter: 23, verseStart: 25,
    text: "Neither before nor after Josiah was there any king like him, who turned to the LORD with all his heart and with all his soul and with all his strength, according to all the Law of Moses.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Judah",
    before: "Josiah removes the mediums, idols, and household gods from Judah and Jerusalem.",
    after: "The LORD does not turn from the fierce anger kindled against Judah by Manasseh's deeds.",
    theme: "Wholehearted reform", keyword: "turned",
    facts: ["Josiah became king at just eight years old.", "The description borrows the wording of the Shema in Deuteronomy 6."],
  },
  // — Ezra —
  {
    reference: "Ezra 1:2", book: "Ezra", chapter: 1, verseStart: 2,
    text: "This is what Cyrus king of Persia says: The LORD, the God of heaven, who has given me all the kingdoms of the earth, has appointed me to build a house for Him at Jerusalem in Judah.",
    testament: "OT", speaker: "Cyrus king of Persia", audience: "His whole kingdom",
    before: "The LORD stirs the spirit of Cyrus to make a proclamation throughout his realm.",
    after: "Cyrus invites any of God's people to go up to Jerusalem and build the temple.",
    theme: "God moves a foreign king", keyword: "Persia",
    facts: ["Isaiah had named Cyrus as God's instrument long before he reigned.", "The decree ended roughly seventy years of exile in Babylon."],
  },
  {
    reference: "Ezra 3:11", book: "Ezra", chapter: 3, verseStart: 11,
    text: "And they sang responsively with praise and thanksgiving to the LORD: \"For He is good; His loving devotion toward Israel endures forever.\" Then all the people gave a great shout of praise to the LORD, because the foundation of the house of the LORD had been laid.",
    testament: "OT", speaker: "The returned exiles", audience: "The LORD",
    before: "The builders lay the foundation of the temple and the priests take their places with trumpets.",
    after: "Older priests who had seen the first temple weep aloud while others shout for joy.",
    theme: "Praise at a new beginning", keyword: "responsively",
    facts: ["The refrain they sang appears throughout the Psalms.", "This was the first temple foundation laid since Babylon destroyed the old one."],
  },
  {
    reference: "Ezra 3:12", book: "Ezra", chapter: 3, verseStart: 12,
    text: "But many of the older priests and Levites and family heads, who had seen the first temple, wept loudly when they saw the foundation of this temple, but many others shouted joyfully.",
    testament: "OT", speaker: "The narrator", audience: "Readers of the restoration",
    before: "The people shout praise to the LORD as the foundation of the house is laid.",
    after: "No one can distinguish the sound of joy from the sound of weeping.",
    theme: "Joy and grief together", keyword: "wept",
    facts: ["The second temple was far more modest than Solomon's.", "Haggai later addresses those who remembered the former glory."],
  },
  {
    reference: "Ezra 6:22", book: "Ezra", chapter: 6, verseStart: 22,
    text: "For seven days they kept the Feast of Unleavened Bread with joy, because the LORD had made them joyful and had turned the heart of the king of Assyria toward them to strengthen their hands in the work on the house of God, the God of Israel.",
    testament: "OT", speaker: "The narrator", audience: "Readers of the restoration",
    before: "The exiles celebrate the Passover and eat it with joy after the temple is completed.",
    after: "The account turns to Ezra's own journey up from Babylon.",
    theme: "Joy in finished work", keyword: "joyful",
    facts: ["The rebuilt temple was finished in the sixth year of Darius.", "God is credited with turning a pagan king's heart toward the project."],
  },
  {
    reference: "Ezra 7:6", book: "Ezra", chapter: 7, verseStart: 6,
    text: "This Ezra came up from Babylon. He was a scribe skilled in the Law of Moses, which the LORD, the God of Israel, had given. The king had granted him everything he requested, for the hand of the LORD his God was upon him.",
    testament: "OT", speaker: "The narrator", audience: "Readers of the restoration",
    before: "Ezra's priestly ancestry is traced back to Aaron the chief priest.",
    after: "Some of the Israelites, priests, and Levites go up to Jerusalem with him.",
    theme: "A scribe sent by God", keyword: "skilled",
    facts: ["Ezra is remembered as a teacher of the Law more than a builder.", "The phrase 'the hand of the LORD was upon him' recurs through his account."],
  },
  {
    reference: "Ezra 7:10", book: "Ezra", chapter: 7, verseStart: 10,
    text: "For Ezra had set his heart to study the Law of the LORD, to practice it, and to teach its statutes and ordinances in Israel.",
    testament: "OT", speaker: "The narrator", audience: "Readers of the restoration",
    before: "The king grants Ezra everything he requests because the hand of the LORD is on him.",
    after: "The text records the letter King Artaxerxes gave to Ezra the priest and scribe.",
    theme: "Study, practice, then teach", keyword: "practice",
    facts: ["The order matters: Ezra studied, then obeyed, then taught.", "This verse is often used as a model for teachers of Scripture."],
  },
  {
    reference: "Ezra 8:21", book: "Ezra", chapter: 8, verseStart: 21,
    text: "And there by the Ahava Canal I proclaimed a fast, so that we might humble ourselves before our God and ask Him for a safe journey for us and our children, with all our possessions.",
    testament: "OT", speaker: "Ezra", audience: "Readers of his account",
    before: "Ezra gathers the returning exiles at the canal and finds no Levites among them.",
    after: "Ezra explains he was ashamed to ask the king for soldiers to protect them.",
    theme: "Fasting before a hard journey", keyword: "humble",
    facts: ["The group was carrying a fortune in silver and gold for the temple.", "The road from Babylon to Jerusalem ran hundreds of miles through bandit country."],
  },
  {
    reference: "Ezra 8:22", book: "Ezra", chapter: 8, verseStart: 22,
    text: "For I was ashamed to ask the king for soldiers and horsemen to protect us from our enemies on the road, since we had told him, \"The hand of our God is gracious to all who seek Him, but His great anger is against all who forsake Him.\"",
    testament: "OT", speaker: "Ezra", audience: "Readers of his account",
    before: "Ezra proclaims a fast at the Ahava Canal to seek God for a safe journey.",
    after: "They fast and petition God about this, and He answers their prayer.",
    theme: "Living up to what we say we believe", keyword: "ashamed",
    facts: ["Ezra refused an armed escort because he had already testified to God's care.", "Nehemiah, by contrast, accepted a royal escort for his own journey."],
  },
  {
    reference: "Ezra 9:6", book: "Ezra", chapter: 9, verseStart: 6,
    text: "and said: O my God, I am ashamed and disgraced to lift up my face to You, my God, because our iniquities are higher than our heads, and our guilt has reached the heavens.",
    testament: "OT", speaker: "Ezra", audience: "The LORD",
    before: "Ezra tears his garment and cloak and sits appalled at the people's unfaithfulness.",
    after: "Ezra recalls that from the days of their fathers they have been deep in guilt.",
    theme: "Confession on behalf of a people", keyword: "iniquities",
    facts: ["Ezra confesses sins he did not personally commit, saying 'our guilt.'", "He prays at the time of the evening sacrifice, on his knees with hands spread out."],
  },
  {
    reference: "Ezra 10:4", book: "Ezra", chapter: 10, verseStart: 4,
    text: "Rise up, for this matter is your responsibility, and we will support you. Be strong and take action!",
    testament: "OT", speaker: "Shecaniah son of Jehiel", audience: "Ezra",
    before: "Shecaniah admits the people have been unfaithful but says there is still hope for Israel.",
    after: "Ezra rises and makes the leading priests and Levites take an oath to do as proposed.",
    theme: "Courage to lead a hard reform", keyword: "responsibility",
    facts: ["The encouragement came from the people to their leader, not the other way around.", "The phrase 'be strong' echoes the charge given to Joshua."],
  },
  // — Esther —
  {
    reference: "Esther 2:17", book: "Esther", chapter: 2, verseStart: 17,
    text: "And the king loved Esther more than all the other women, and she found grace and favor in his sight more than all the other virgins. So he placed the royal crown upon her head and made her queen in place of Vashti.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "Esther is taken to King Xerxes in the royal palace in the seventh year of his reign.",
    after: "The king holds a great banquet in Esther's honor for all his officials and servants.",
    theme: "An orphan becomes queen", keyword: "crown",
    facts: ["Esther was raised by her cousin Mordecai after her parents died.", "She had not yet revealed that she was Jewish."],
  },
  {
    reference: "Esther 4:3", book: "Esther", chapter: 4, verseStart: 3,
    text: "In every province to which the king's command and edict came, there was great mourning among the Jews. They fasted, wept, and lamented, and many lay in sackcloth and ashes.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "Mordecai learns of Haman's edict, tears his clothes, and goes out wailing in the city.",
    after: "Esther's maidens tell her about Mordecai, and she is deeply distressed.",
    theme: "A people under sentence", keyword: "sackcloth",
    facts: ["The edict called for the destruction of every Jew in 127 provinces.", "The book of Esther never mentions God by name, yet His hand shapes every turn."],
  },
  {
    reference: "Esther 4:14", book: "Esther", chapter: 4, verseStart: 14,
    text: "For if you remain silent at this time, relief and deliverance for the Jews will arise from another place, but you and your father's house will perish. And who knows if perhaps you have come to the kingdom for such a time as this?",
    testament: "OT", speaker: "Mordecai", audience: "Queen Esther",
    before: "Esther sends word that anyone approaching the king unsummoned risks death.",
    after: "Esther asks Mordecai to gather the Jews of Susa to fast for her for three days.",
    theme: "Called for this moment", keyword: "silent",
    facts: ["'For such a time as this' is the best-known line in the book.", "Mordecai is certain rescue will come; the question is whether Esther will be part of it."],
  },
  {
    reference: "Esther 4:16", book: "Esther", chapter: 4, verseStart: 16,
    text: "Go and assemble all the Jews who can be found in Susa, and fast for me. Do not eat or drink for three days, night or day, and I and my maidens will fast as you do. After that, I will go to the king, even though it is against the law. And if I perish, I perish!",
    testament: "OT", speaker: "Queen Esther", audience: "Mordecai",
    before: "Mordecai asks whether she has come to royal position for such a time as this.",
    after: "Mordecai goes and does everything Esther had instructed him.",
    theme: "Courage that counts the cost", keyword: "perish",
    facts: ["Esther risked execution simply by entering the king's presence uninvited.", "Her fast is the closest the book comes to describing prayer."],
  },
  {
    reference: "Esther 5:2", book: "Esther", chapter: 5, verseStart: 2,
    text: "As soon as the king saw Queen Esther standing in the court, she found favor in his sight. The king extended the gold scepter in his hand toward Esther, and she approached and touched the tip of the scepter.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "On the third day Esther puts on her royal robes and stands in the inner court of the palace.",
    after: "The king asks what her request is, offering up to half the kingdom.",
    theme: "The moment of risk", keyword: "scepter",
    facts: ["Extending the scepter was the sign that an uninvited visitor would live.", "Esther waited three days of fasting before taking this step."],
  },
  {
    reference: "Esther 6:1", book: "Esther", chapter: 6, verseStart: 1,
    text: "That night sleep escaped the king, so he ordered the Book of Records, the Chronicles, to be brought in and read to him.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "Haman has a gallows built, intending to hang Mordecai on it the next morning.",
    after: "The record is found of how Mordecai exposed a plot against the king's life.",
    theme: "A sleepless night that changes everything", keyword: "Chronicles",
    facts: ["The whole story turns on a king's insomnia.", "God is never named in Esther, but this coincidence is the book's quiet centerpiece."],
  },
  {
    reference: "Esther 7:3", book: "Esther", chapter: 7, verseStart: 3,
    text: "Queen Esther replied, \"If I have found favor in your sight, O king, and if it pleases the king, grant me my life as my petition, and the lives of my people as my request.\"",
    testament: "OT", speaker: "Queen Esther", audience: "King Xerxes",
    before: "At the second banquet the king again asks Esther what her petition is.",
    after: "Esther reveals that she and her people have been sold for destruction.",
    theme: "Speaking up for her people", keyword: "petition",
    facts: ["Esther identifies herself with the Jews for the first time before the king.", "Haman is present at the banquet as she speaks."],
  },
  {
    reference: "Esther 8:16", book: "Esther", chapter: 8, verseStart: 16,
    text: "For the Jews it was a time of light and gladness, of joy and honor.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "A new edict goes out allowing the Jews to assemble and defend themselves.",
    after: "In every province the news brings joy and gladness, with feasting and celebrating.",
    theme: "Sorrow turned to gladness", keyword: "gladness",
    facts: ["The reversal is total: the same couriers who carried doom now carry deliverance.", "Jewish tradition reads this verse aloud during the festival of Purim."],
  },
  {
    reference: "Esther 9:22", book: "Esther", chapter: 9, verseStart: 22,
    text: "as the days on which the Jews gained rest from their enemies, and as the month in which their sorrow turned to joy and their mourning into a holiday. He wrote that these were to be days of feasting and joy, of sending gifts of food to one another and to the poor.",
    testament: "OT", speaker: "The narrator, describing Mordecai's letters", audience: "The Jews in all the provinces",
    before: "Mordecai records these events and sends letters to the Jews in all the king's provinces.",
    after: "The Jews agree to continue the celebration they had begun.",
    theme: "The founding of Purim", keyword: "feasting",
    facts: ["This verse establishes the festival of Purim.", "Giving gifts to the poor was written into the celebration from the start."],
  },
  {
    reference: "Esther 10:3", book: "Esther", chapter: 10, verseStart: 3,
    text: "For Mordecai the Jew was second only to King Xerxes, preeminent among the Jews, and highly favored by his many kinsmen, seeking the good of his people and speaking peace to all his countrymen.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "The king's acts of power and the greatness of Mordecai are written in the chronicles of Media and Persia.",
    after: "The book of Esther ends here.",
    theme: "Power used for the good of others", keyword: "preeminent",
    facts: ["Mordecai began the story sitting at the king's gate and ends it second in the empire.", "The final word of the book is peace for his people."],
  },
  // — Song of Solomon —
  {
    reference: "Song of Solomon 1:2", book: "Song of Solomon", chapter: 1, verseStart: 2,
    text: "Let him kiss me with the kisses of his mouth! For your love is more delightful than wine.",
    testament: "OT", speaker: "The bride", audience: "Her beloved",
    before: "The book opens by naming itself the Song of Songs, which is Solomon's.",
    after: "The bride praises the fragrance of his perfumes and his name.",
    theme: "Love that delights", keyword: "delightful",
    facts: ["The Hebrew title 'Song of Songs' means the finest song of all.", "The book is a series of poems traded between two lovers."],
  },
  {
    reference: "Song of Solomon 2:1", book: "Song of Solomon", chapter: 2, verseStart: 1,
    text: "I am a rose of Sharon, a lily of the valleys.",
    testament: "OT", speaker: "The bride", audience: "Her beloved",
    before: "The lovers describe the green couch and cedar beams of their meeting place.",
    after: "The beloved answers that she is like a lily among thorns.",
    theme: "Beauty and belonging", keyword: "valleys",
    facts: ["Sharon was a fertile coastal plain famous for wildflowers.", "The imagery has inspired hymns and poetry for centuries."],
  },
  {
    reference: "Song of Solomon 2:4", book: "Song of Solomon", chapter: 2, verseStart: 4,
    text: "He has brought me to the banquet hall, and his banner over me is love.",
    testament: "OT", speaker: "The bride", audience: "The daughters of Jerusalem",
    before: "The bride sits in the shade of her beloved and finds his fruit sweet to her taste.",
    after: "She asks to be sustained with raisins and refreshed with apples.",
    theme: "Love declared openly", keyword: "banner",
    facts: ["A banner was a military standard, raised so everyone could see whose side you were on.", "The line has become one of the most quoted images in the book."],
  },
  {
    reference: "Song of Solomon 2:10", book: "Song of Solomon", chapter: 2, verseStart: 10,
    text: "My beloved calls to me, \"Arise, my darling. Come away with me, my beautiful one.\"",
    testament: "OT", speaker: "The bride, quoting her beloved", audience: "The daughters of Jerusalem",
    before: "She sees her beloved coming, leaping across the mountains and bounding over the hills.",
    after: "He tells her that the winter is past and the rains are over and gone.",
    theme: "An invitation to come away", keyword: "Arise",
    facts: ["The invitation arrives with the change of seasons.", "The same call is repeated a few verses later, framing the whole poem."],
  },
  {
    reference: "Song of Solomon 2:12", book: "Song of Solomon", chapter: 2, verseStart: 12,
    text: "The flowers have appeared in the countryside; the season of singing has come, and the cooing of turtledoves is heard in our land.",
    testament: "OT", speaker: "The beloved", audience: "The bride",
    before: "He announces that the winter is past and the rains are over and gone.",
    after: "He notes that the fig tree ripens its figs and the vines are in blossom.",
    theme: "Springtime and new beginnings", keyword: "turtledoves",
    facts: ["Spring in Israel arrives suddenly after the winter rains.", "The return of migrating doves was a well-known sign that the season had turned."],
  },
  {
    reference: "Song of Solomon 2:16", book: "Song of Solomon", chapter: 2, verseStart: 16,
    text: "My beloved is mine and I am his; he pastures his flock among the lilies.",
    testament: "OT", speaker: "The bride", audience: "The daughters of Jerusalem",
    before: "She asks that the little foxes ruining the vineyards be caught.",
    after: "She longs for him to return before the day breaks and the shadows flee.",
    theme: "Mutual belonging", keyword: "pastures",
    facts: ["The line is often engraved inside wedding rings.", "A nearly identical line reappears in chapter 6, with the order reversed."],
  },
  {
    reference: "Song of Solomon 4:7", book: "Song of Solomon", chapter: 4, verseStart: 7,
    text: "You are altogether beautiful, my darling; in you there is no flaw.",
    testament: "OT", speaker: "The beloved", audience: "The bride",
    before: "He praises her eyes, her hair, her teeth, and her lips in a series of images.",
    after: "He calls her to come with him from Lebanon.",
    theme: "Delighting in the beloved", keyword: "flaw",
    facts: ["The praise poem here follows an ancient Near Eastern form called a wasf.", "Paul echoes this language when describing Christ's love for the church."],
  },
  {
    reference: "Song of Solomon 6:3", book: "Song of Solomon", chapter: 6, verseStart: 3,
    text: "I belong to my beloved and he belongs to me; he pastures his flock among the lilies.",
    testament: "OT", speaker: "The bride", audience: "The daughters of Jerusalem",
    before: "She tells them her beloved has gone down to his garden to browse and gather lilies.",
    after: "He praises her as beautiful as Tirzah, as lovely as Jerusalem.",
    theme: "Belonging to each other", keyword: "belong",
    facts: ["This restates chapter 2's line with the possession reversed.", "The repetition acts like a refrain across the whole song."],
  },
  {
    reference: "Song of Solomon 8:6", book: "Song of Solomon", chapter: 8, verseStart: 6,
    text: "Set me as a seal over your heart, as a seal upon your arm. For love is as strong as death, its jealousy as unrelenting as Sheol. Its sparks are fiery flames, the fiercest of blazes.",
    testament: "OT", speaker: "The bride", audience: "Her beloved",
    before: "She comes up from the wilderness leaning on her beloved.",
    after: "She declares that mighty waters cannot quench love.",
    theme: "Love strong as death", keyword: "seal",
    facts: ["A seal was a personal mark of ownership pressed into wax or clay.", "This is widely considered the climax of the entire book."],
  },
  {
    reference: "Song of Solomon 8:7", book: "Song of Solomon", chapter: 8, verseStart: 7,
    text: "Mighty waters cannot quench love; rivers cannot sweep it away. If a man were to give all the wealth of his house for love, his offer would be utterly scorned.",
    testament: "OT", speaker: "The bride", audience: "Her beloved",
    before: "She asks to be set as a seal on his heart, for love is as strong as death.",
    after: "The speakers turn to a younger sister and what should be done for her.",
    theme: "Love cannot be bought", keyword: "quench",
    facts: ["The verse insists love is a gift, never a purchase.", "It is among the most frequently read passages at weddings."],
  },
  // — Ezekiel —
  {
    reference: "Ezekiel 1:28", book: "Ezekiel", chapter: 1, verseStart: 28,
    text: "The appearance of the brilliant light all around Him was like that of a rainbow in a cloud on a rainy day. This was the appearance of the likeness of the glory of the LORD. And when I saw it, I fell facedown and heard a voice speaking.",
    testament: "OT", speaker: "Ezekiel", audience: "Readers in exile",
    before: "Ezekiel describes the throne above the expanse and the figure with the appearance of a man.",
    after: "The voice tells him to stand on his feet so that God may speak with him.",
    theme: "The glory of the LORD", keyword: "rainbow",
    facts: ["Ezekiel piles up phrases like 'appearance of the likeness' because the vision resists description.", "He was a priest in exile by the Kebar River in Babylon."],
  },
  {
    reference: "Ezekiel 2:1", book: "Ezekiel", chapter: 2, verseStart: 1,
    text: "\"Son of man,\" He said to me, \"stand up on your feet and I will speak with you.\"",
    testament: "OT", speaker: "The LORD", audience: "Ezekiel",
    before: "Ezekiel falls facedown at the sight of the glory of the LORD.",
    after: "The Spirit enters him and sets him on his feet as the voice speaks.",
    theme: "Called to stand and listen", keyword: "feet",
    facts: ["'Son of man' is how God addresses Ezekiel more than ninety times.", "The phrase simply means 'mortal,' marking the gap between prophet and God."],
  },
  {
    reference: "Ezekiel 18:32", book: "Ezekiel", chapter: 18, verseStart: 32,
    text: "For I take no pleasure in anyone's death, declares the Lord GOD. So repent and live!",
    testament: "OT", speaker: "The Lord GOD", audience: "The house of Israel",
    before: "God calls the people to cast away their transgressions and get a new heart and a new spirit.",
    after: "The prophet takes up a lament concerning the princes of Israel.",
    theme: "God desires repentance, not ruin", keyword: "repent",
    facts: ["The chapter argues that each person answers for their own choices.", "It overturns a proverb the exiles used to blame their parents' generation."],
  },
  {
    reference: "Ezekiel 22:30", book: "Ezekiel", chapter: 22, verseStart: 30,
    text: "I searched for a man among them to repair the wall and stand in the gap before Me on behalf of the land, so that I should not destroy it. But I found no one.",
    testament: "OT", speaker: "The Lord GOD", audience: "Readers in exile",
    before: "God indicts the prophets, priests, officials, and people of the land for violence and injustice.",
    after: "God says He has poured out His indignation on them.",
    theme: "Standing in the gap", keyword: "gap",
    facts: ["The image is of a defender standing in a breach in the city wall.", "The phrase 'stand in the gap' has entered ordinary English from this verse."],
  },
  {
    reference: "Ezekiel 34:11", book: "Ezekiel", chapter: 34, verseStart: 11,
    text: "For this is what the Lord GOD says: Behold, I Myself will search for My flock and seek them out.",
    testament: "OT", speaker: "The Lord GOD", audience: "The scattered people of Israel",
    before: "God rebukes the shepherds of Israel who fed themselves instead of the flock.",
    after: "God promises to rescue His sheep from every place where they were scattered.",
    theme: "God the true shepherd", keyword: "flock",
    facts: ["The 'shepherds' rebuked here are Israel's leaders.", "Jesus draws on this chapter when He calls Himself the Good Shepherd."],
  },
  {
    reference: "Ezekiel 34:16", book: "Ezekiel", chapter: 34, verseStart: 16,
    text: "I will seek the lost, bring back the strays, bind up the broken, and strengthen the weak; but the sleek and strong I will destroy. I will shepherd them with justice.",
    testament: "OT", speaker: "The Lord GOD", audience: "The scattered people of Israel",
    before: "God promises to tend His flock and let them lie down in good pasture.",
    after: "God says He will judge between one sheep and another.",
    theme: "God cares for the weak", keyword: "strays",
    facts: ["The verse lists exactly what the failed shepherds had refused to do.", "Jesus' parable of the lost sheep echoes this promise."],
  },
  {
    reference: "Ezekiel 36:26", book: "Ezekiel", chapter: 36, verseStart: 26,
    text: "I will give you a new heart and put a new spirit within you; I will remove your heart of stone and give you a heart of flesh.",
    testament: "OT", speaker: "The Lord GOD", audience: "The house of Israel",
    before: "God promises to sprinkle clean water on them and cleanse them from all their impurities.",
    after: "God promises to put His Spirit within them so they will follow His statutes.",
    theme: "A new heart", keyword: "stone",
    facts: ["The promise is of transformation, not merely forgiveness.", "Jesus may have had this chapter in mind when He told Nicodemus he must be born again."],
  },
  {
    reference: "Ezekiel 36:27", book: "Ezekiel", chapter: 36, verseStart: 27,
    text: "And I will put My Spirit within you and cause you to walk in My statutes and to carefully observe My ordinances.",
    testament: "OT", speaker: "The Lord GOD", audience: "The house of Israel",
    before: "God promises to replace their heart of stone with a heart of flesh.",
    after: "God says they will live in the land He gave their fathers and be His people.",
    theme: "God's Spirit enables obedience", keyword: "Spirit",
    facts: ["Obedience is promised as God's gift rather than the people's achievement.", "Paul develops this hope when he writes about life in the Spirit."],
  },
  {
    reference: "Ezekiel 37:4", book: "Ezekiel", chapter: 37, verseStart: 4,
    text: "Then He said to me, \"Prophesy concerning these bones and tell them, 'Dry bones, hear the word of the LORD!'\"",
    testament: "OT", speaker: "The LORD", audience: "Ezekiel",
    before: "God asks Ezekiel whether these bones can live, and he answers that only God knows.",
    after: "God declares He will cause breath to enter the bones so they come to life.",
    theme: "Preaching to dry bones", keyword: "Prophesy",
    facts: ["Ezekiel is set down in a valley full of very dry bones.", "The vision is God's answer to exiles who said their hope was gone."],
  },
  {
    reference: "Ezekiel 37:5", book: "Ezekiel", chapter: 37, verseStart: 5,
    text: "This is what the Lord GOD says to these bones: I will cause breath to enter you, and you will come to life.",
    testament: "OT", speaker: "The Lord GOD", audience: "The dry bones, through Ezekiel",
    before: "Ezekiel is told to prophesy to the bones and call them to hear the word of the LORD.",
    after: "God promises to attach tendons, make flesh grow, and cover them with skin.",
    theme: "Life from death", keyword: "breath",
    facts: ["The Hebrew word for breath also means wind and spirit.", "God explains the vision as the restoration of the whole house of Israel."],
  },
  // — Daniel —
  {
    reference: "Daniel 1:8", book: "Daniel", chapter: 1, verseStart: 8,
    text: "But Daniel resolved in his heart not to defile himself with the king's food or wine. So he asked the chief official for permission not to defile himself.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "Daniel and his friends are chosen for three years of training in the language and literature of Babylon.",
    after: "God grants Daniel favor and compassion from the chief official.",
    theme: "Quiet conviction in a foreign court", keyword: "resolved",
    facts: ["Daniel was likely a teenager when he made this decision.", "He asked permission rather than staging a protest."],
  },
  {
    reference: "Daniel 2:20", book: "Daniel", chapter: 2, verseStart: 20,
    text: "and declared: Blessed be the name of God forever and ever, for wisdom and power belong to Him.",
    testament: "OT", speaker: "Daniel", audience: "God, in prayer",
    before: "The mystery of the king's dream is revealed to Daniel in a vision of the night.",
    after: "Daniel praises God for changing the times and seasons.",
    theme: "Praise before explanation", keyword: "wisdom",
    facts: ["Daniel praises God before telling anyone the answer.", "The prayer is in Aramaic, the trade language of the empire."],
  },
  {
    reference: "Daniel 2:21", book: "Daniel", chapter: 2, verseStart: 21,
    text: "He changes the times and seasons; He removes kings and establishes them. He gives wisdom to the wise and knowledge to the discerning.",
    testament: "OT", speaker: "Daniel", audience: "God, in prayer",
    before: "Daniel blesses the name of God, to whom wisdom and power belong.",
    after: "Daniel says God reveals the deep and hidden things and knows what lies in darkness.",
    theme: "God rules over empires", keyword: "seasons",
    facts: ["Daniel served under several kings and outlasted every one of them.", "The line is a favorite in seasons of political upheaval."],
  },
  {
    reference: "Daniel 3:17", book: "Daniel", chapter: 3, verseStart: 17,
    text: "If the God whom we serve exists, then He is able to deliver us from the blazing fiery furnace and from your hand, O king.",
    testament: "OT", speaker: "Shadrach, Meshach, and Abednego", audience: "King Nebuchadnezzar",
    before: "The king warns that anyone refusing to bow to the golden statue will be thrown into the furnace.",
    after: "The three men add that even if God does not rescue them, they will not serve his gods.",
    theme: "Confidence in God's power", keyword: "furnace",
    facts: ["The three men answered without needing time to deliberate.", "Their Hebrew names were Hananiah, Mishael, and Azariah."],
  },
  {
    reference: "Daniel 3:18", book: "Daniel", chapter: 3, verseStart: 18,
    text: "But even if He does not, let it be known to you, O king, that we will not serve your gods or worship the golden statue you have set up.",
    testament: "OT", speaker: "Shadrach, Meshach, and Abednego", audience: "King Nebuchadnezzar",
    before: "The three say God is able to deliver them from the blazing furnace.",
    after: "Nebuchadnezzar is filled with rage and orders the furnace heated seven times hotter.",
    theme: "Faithful whatever the outcome", keyword: "worship",
    facts: ["'But even if He does not' is the heart of the verse.", "Their loyalty did not depend on being rescued."],
  },
  {
    reference: "Daniel 6:10", book: "Daniel", chapter: 6, verseStart: 10,
    text: "Now when Daniel learned that the document had been signed, he went home to his upstairs room where the windows opened toward Jerusalem, and three times a day he got down on his knees, prayed, and gave thanks to his God, just as he had done before.",
    testament: "OT", speaker: "The narrator", audience: "Readers in exile",
    before: "King Darius signs a decree that no one may petition any god or man except the king for thirty days.",
    after: "The officials find Daniel petitioning and asking help from his God.",
    theme: "Prayer that does not flinch", keyword: "knees",
    facts: ["Daniel changed nothing about his routine when praying became illegal.", "He was probably in his eighties by this point in the story."],
  },
  {
    reference: "Daniel 6:22", book: "Daniel", chapter: 6, verseStart: 22,
    text: "My God sent His angel and shut the mouths of the lions. They have not hurt me, for I was found innocent in His sight, and I have done no wrong against you, O king.",
    testament: "OT", speaker: "Daniel", audience: "King Darius",
    before: "Darius hurries to the den at first light and calls out to Daniel in anguish.",
    after: "The king is overjoyed and orders Daniel lifted out of the den.",
    theme: "Rescued from the lions", keyword: "lions",
    facts: ["Hebrews 11 recalls those who 'shut the mouths of lions' by faith.", "Darius had spent the night fasting and unable to sleep."],
  },
  {
    reference: "Daniel 7:14", book: "Daniel", chapter: 7, verseStart: 14,
    text: "And He was given dominion, glory, and kingship, that the people of every nation and language should serve Him. His dominion is an everlasting dominion that will not pass away, and His kingdom is one that will never be destroyed.",
    testament: "OT", speaker: "Daniel", audience: "Readers in exile",
    before: "Daniel sees one like a son of man coming with the clouds of heaven and approaching the Ancient of Days.",
    after: "Daniel is troubled in spirit and the visions alarm him.",
    theme: "An everlasting kingdom", keyword: "dominion",
    facts: ["Jesus repeatedly called Himself 'the Son of Man,' drawing on this vision.", "The kingdoms Daniel had just seen all rise and fall; this one does not."],
  },
  {
    reference: "Daniel 9:19", book: "Daniel", chapter: 9, verseStart: 19,
    text: "O Lord, listen! O Lord, forgive! O Lord, hear and act! For Your sake, O my God, do not delay, because Your city and Your people bear Your name.",
    testament: "OT", speaker: "Daniel", audience: "The Lord",
    before: "Daniel confesses the sins of his people and appeals to God's great mercy, not their righteousness.",
    after: "While Daniel is still praying, the angel Gabriel comes to him in swift flight.",
    theme: "Urgent prayer for a people", keyword: "forgive",
    facts: ["Daniel prayed this after reading Jeremiah's prophecy of seventy years.", "He includes himself in the confession, saying 'we have sinned.'"],
  },
  {
    reference: "Daniel 12:3", book: "Daniel", chapter: 12, verseStart: 3,
    text: "Then the wise will shine like the brightness of the heavens, and those who lead many to righteousness will shine like the stars forever and ever.",
    testament: "OT", speaker: "The messenger in Daniel's vision", audience: "Daniel",
    before: "The vision speaks of a time of distress and of many who sleep in the dust awaking.",
    after: "Daniel is told to shut up the words and seal the book until the time of the end.",
    theme: "The hope of the wise", keyword: "stars",
    facts: ["This is one of the clearest Old Testament statements of resurrection hope.", "The verse is often read at the funerals of teachers."],
  },
  // — Hosea —
  {
    reference: "Hosea 2:19", book: "Hosea", chapter: 2, verseStart: 19,
    text: "So I will betroth you to Me forever; I will betroth you in righteousness and justice, in loving devotion and compassion.",
    testament: "OT", speaker: "The LORD", audience: "Israel, pictured as a wife",
    before: "God promises to allure Israel into the wilderness and speak tenderly to her.",
    after: "God promises to betroth her in faithfulness, and she will know the LORD.",
    theme: "God's covenant love", keyword: "betroth",
    facts: ["These words are recited in some Jewish morning prayers.", "God restores the relationship by promising marriage, not merely pardon."],
  },
  {
    reference: "Hosea 3:1", book: "Hosea", chapter: 3, verseStart: 1,
    text: "Then the LORD said to me, \"Go show love to your wife again, though she is loved by another and is an adulteress. Love her as the LORD loves the Israelites, though they turn to other gods.\"",
    testament: "OT", speaker: "The LORD", audience: "Hosea",
    before: "God promises that Israel will one day be as numerous as the sand of the sea.",
    after: "Hosea buys her back for fifteen shekels of silver and a measure of barley.",
    theme: "Love that goes and gets her back", keyword: "adulteress",
    facts: ["Hosea's marriage was itself the sermon God was preaching.", "He had to pay to reclaim his own wife."],
  },
  {
    reference: "Hosea 4:6", book: "Hosea", chapter: 4, verseStart: 6,
    text: "My people are destroyed for lack of knowledge. Because you have rejected knowledge, I will reject you as My priests. Since you have forgotten the law of your God, I will also forget your children.",
    testament: "OT", speaker: "The LORD", audience: "The priests of Israel",
    before: "God brings a charge against the people, saying there is no truth or loving devotion in the land.",
    after: "God says the more the priests multiplied, the more they sinned against Him.",
    theme: "The cost of forgotten teaching", keyword: "knowledge",
    facts: ["The rebuke falls hardest on the priests, whose job was to teach.", "The 'knowledge' in view is knowing God personally, not just knowing facts."],
  },
  {
    reference: "Hosea 6:1", book: "Hosea", chapter: 6, verseStart: 1,
    text: "Come, let us return to the LORD. For He has torn us to pieces, but He will heal us; He has wounded us, but He will bind up our wounds.",
    testament: "OT", speaker: "The people of Israel", audience: "One another",
    before: "God says He will return to His place until the people acknowledge their guilt and seek His face.",
    after: "The people say He will revive them after two days and raise them up on the third day.",
    theme: "A call to return", keyword: "wounded",
    facts: ["The invitation is communal: 'let us return.'", "Hosea preached in the northern kingdom in its final decades."],
  },
  {
    reference: "Hosea 6:3", book: "Hosea", chapter: 6, verseStart: 3,
    text: "So let us know — let us press on to know the LORD. As surely as the sun rises, He will appear; He will come to us like the rain, like the spring showers that water the earth.",
    testament: "OT", speaker: "The people of Israel", audience: "One another",
    before: "The people say God will revive them and raise them up to live in His presence.",
    after: "God asks what He should do with Ephraim, whose loyalty is like the morning mist.",
    theme: "Pressing on to know God", keyword: "showers",
    facts: ["Knowing God is described as something you pursue, not something you finish.", "The spring rains were what made the barley harvest possible."],
  },
  {
    reference: "Hosea 6:6", book: "Hosea", chapter: 6, verseStart: 6,
    text: "For I desire mercy, not sacrifice, and the knowledge of God rather than burnt offerings.",
    testament: "OT", speaker: "The LORD", audience: "Israel and Judah",
    before: "God compares the people's loyalty to the morning mist that quickly disappears.",
    after: "God says that like Adam they have broken the covenant and been unfaithful to Him.",
    theme: "Mercy over ritual", keyword: "mercy",
    facts: ["Jesus quotes this verse twice in Matthew's Gospel.", "It does not abolish sacrifice but refuses to let it replace compassion."],
  },
  {
    reference: "Hosea 10:12", book: "Hosea", chapter: 10, verseStart: 12,
    text: "Sow for yourselves righteousness and reap the fruit of loving devotion; break up your unplowed ground. For it is time to seek the LORD until He comes and sends righteousness upon you like rain.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "God warns that Israel has plowed wickedness and reaped injustice.",
    after: "God says they have eaten the fruit of lies because they trusted in their own strength.",
    theme: "Break up the hard ground", keyword: "unplowed",
    facts: ["Fallow ground is soil that has gone hard from neglect.", "The image treats repentance as farm work, not a single moment."],
  },
  {
    reference: "Hosea 11:1", book: "Hosea", chapter: 11, verseStart: 1,
    text: "When Israel was a child, I loved him, and out of Egypt I called My son.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "God has described the coming judgment on Israel's king and cities.",
    after: "God laments that the more He called them, the further they departed from Him.",
    theme: "God's fatherly love", keyword: "child",
    facts: ["Matthew applies this verse to Jesus' return from Egypt as a child.", "God shifts here from the image of husband to that of parent."],
  },
  {
    reference: "Hosea 11:4", book: "Hosea", chapter: 11, verseStart: 4,
    text: "I led them with cords of kindness, with ropes of love; I lifted the yoke from their necks and bent down to feed them.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "God recalls teaching Ephraim to walk and taking them up in His arms.",
    after: "God asks how He can give them up, saying His compassion is stirred within Him.",
    theme: "Gentle leading", keyword: "cords",
    facts: ["The picture is of a parent teaching a toddler to walk.", "God describes bending down to feed them, an unusually tender image for a king."],
  },
  {
    reference: "Hosea 14:4", book: "Hosea", chapter: 14, verseStart: 4,
    text: "I will heal their apostasy; I will freely love them, for My anger has turned away from them.",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "Hosea calls Israel to return to the LORD and take words of repentance with them.",
    after: "God promises to be like the dew to Israel, so that they blossom like the lily.",
    theme: "Freely loved and healed", keyword: "apostasy",
    facts: ["The book that opened with unfaithfulness closes with free forgiveness.", "God treats their wandering as a sickness He intends to cure."],
  },
  // — Joel —
  {
    reference: "Joel 1:3", book: "Joel", chapter: 1, verseStart: 3,
    text: "Tell it to your children; let your children tell it to their children, and their children to the next generation.",
    testament: "OT", speaker: "Joel", audience: "The elders and all who live in the land",
    before: "Joel asks whether anything like this locust devastation has happened in their days or their fathers' days.",
    after: "Joel describes wave after wave of locusts stripping the land bare.",
    theme: "Passing on what God has done", keyword: "generation",
    facts: ["Joel treats a natural disaster as a story worth handing down.", "The command to tell the next generation echoes through Deuteronomy and the Psalms."],
  },
  {
    reference: "Joel 2:12", book: "Joel", chapter: 2, verseStart: 12,
    text: "Yet even now, declares the LORD, return to Me with all your heart, with fasting, weeping, and mourning.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "Joel describes the day of the LORD as a day of darkness and gloom with an advancing army.",
    after: "God calls them to rend their hearts and not their garments.",
    theme: "It is not too late to return", keyword: "fasting",
    facts: ["'Yet even now' is the hinge of the whole book.", "The verse is read in many churches at the start of Lent."],
  },
  {
    reference: "Joel 2:13", book: "Joel", chapter: 2, verseStart: 13,
    text: "So rend your hearts and not your garments, and return to the LORD your God. For He is gracious and compassionate, slow to anger, abounding in loving devotion, and He relents from sending disaster.",
    testament: "OT", speaker: "The LORD, through Joel", audience: "The people of Judah",
    before: "God calls the people to return to Him with fasting, weeping, and mourning.",
    after: "Joel asks who knows whether God may turn and relent and leave behind a blessing.",
    theme: "Inward repentance", keyword: "rend",
    facts: ["Tearing one's clothes was the standard public sign of grief.", "The description of God's character quotes the words God spoke to Moses in Exodus 34."],
  },
  {
    reference: "Joel 2:25", book: "Joel", chapter: 2, verseStart: 25,
    text: "I will restore to you the years that the swarming locust has eaten — the young locust, the destroying locust, and the devouring locust — My great army that I sent against you.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "God promises rain, threshing floors full of grain, and vats overflowing with new wine.",
    after: "God says they will have plenty to eat and will praise His name.",
    theme: "God restores what was lost", keyword: "locust",
    facts: ["The promise is not just a new start but repayment for the lost years.", "Locust swarms could strip an entire region's harvest in a day."],
  },
  {
    reference: "Joel 2:26", book: "Joel", chapter: 2, verseStart: 26,
    text: "You will have plenty to eat until you are satisfied, and you will praise the name of the LORD your God, who has worked wonders for you. My people will never again be put to shame.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "God promises to repay the years the locusts have eaten.",
    after: "God says they will know that He is in the midst of Israel.",
    theme: "Plenty after famine", keyword: "satisfied",
    facts: ["The reversal is complete: from stripped fields to full tables.", "Praise, not just relief, is named as the goal of the restoration."],
  },
  {
    reference: "Joel 2:28", book: "Joel", chapter: 2, verseStart: 28,
    text: "And afterward, I will pour out My Spirit on all people. Your sons and daughters will prophesy, your old men will dream dreams, your young men will see visions.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "God promises that Israel will know He is among them and never again be put to shame.",
    after: "God says He will pour out His Spirit even on menservants and maidservants.",
    theme: "The Spirit poured out on all", keyword: "visions",
    facts: ["Peter quotes this passage in his sermon at Pentecost.", "The promise crosses lines of age, gender, and social status."],
  },
  {
    reference: "Joel 2:29", book: "Joel", chapter: 2, verseStart: 29,
    text: "Even on My menservants and maidservants, I will pour out My Spirit in those days.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "God promises that sons and daughters will prophesy and the old will dream dreams.",
    after: "Joel describes wonders in the heavens and on the earth before the day of the LORD.",
    theme: "No one left out", keyword: "maidservants",
    facts: ["Servants are named explicitly so no one could read themselves out of the promise.", "Peter includes this line when he quotes Joel at Pentecost."],
  },
  {
    reference: "Joel 2:32", book: "Joel", chapter: 2, verseStart: 32,
    text: "And everyone who calls on the name of the LORD will be saved; for on Mount Zion and in Jerusalem there will be deliverance, as the LORD promised, among the remnant called by the LORD.",
    testament: "OT", speaker: "The LORD, through Joel", audience: "The people of Judah",
    before: "Joel describes the sun turning to darkness and the moon to blood before the day of the LORD.",
    after: "The prophecy turns to the restoration of the fortunes of Judah and Jerusalem.",
    theme: "Everyone who calls will be saved", keyword: "deliverance",
    facts: ["Both Peter and Paul quote this verse in the New Testament.", "The offer is deliberately open: everyone who calls."],
  },
  {
    reference: "Joel 3:10", book: "Joel", chapter: 3, verseStart: 10,
    text: "Beat your plowshares into swords and your pruning hooks into spears. Let the weak say, \"I am strong!\"",
    testament: "OT", speaker: "The LORD, through Joel", audience: "The nations",
    before: "God summons the nations to prepare for war and rouse the mighty men.",
    after: "God calls the nations to assemble in the Valley of Jehoshaphat for judgment.",
    theme: "The nations summoned to judgment", keyword: "plowshares",
    facts: ["This deliberately reverses the famous line in Isaiah and Micah about swords into plowshares.", "It is an ironic summons, calling the nations to a battle they cannot win."],
  },
  {
    reference: "Joel 3:16", book: "Joel", chapter: 3, verseStart: 16,
    text: "The LORD will roar from Zion and raise His voice from Jerusalem; heaven and earth will tremble. But the LORD will be a refuge for His people, a stronghold for the people of Israel.",
    testament: "OT", speaker: "Joel", audience: "The people of Judah",
    before: "Joel describes multitudes in the valley of decision as the day of the LORD draws near.",
    after: "Joel says they will know the LORD their God dwells in Zion, His holy mountain.",
    theme: "Terror to the nations, refuge to His people", keyword: "stronghold",
    facts: ["The same voice that shakes the earth shelters God's people.", "Amos opens his book with almost identical words."],
  },
  // — Amos —
  {
    reference: "Amos 3:3", book: "Amos", chapter: 3, verseStart: 3,
    text: "Can two walk together without agreeing where to go?",
    testament: "OT", speaker: "Amos", audience: "The people of Israel",
    before: "God says Israel alone He has known of all the families of the earth, and so will punish their iniquities.",
    after: "Amos asks a chain of questions about lions roaring and traps springing.",
    theme: "Cause and effect", keyword: "agreeing",
    facts: ["Amos argues by asking obvious questions his hearers must answer themselves.", "The chain of questions leads to the point that a prophet speaks because God has spoken."],
  },
  {
    reference: "Amos 3:7", book: "Amos", chapter: 3, verseStart: 7,
    text: "Surely the Lord GOD does nothing without revealing His plan to His servants the prophets.",
    testament: "OT", speaker: "Amos", audience: "The people of Israel",
    before: "Amos asks whether disaster comes to a city unless the LORD has caused it.",
    after: "Amos says the lion has roared, so who will not fear.",
    theme: "God warns before He acts", keyword: "prophets",
    facts: ["The verse explains why prophets exist at all.", "Amos insists judgment is never a surprise ambush."],
  },
  {
    reference: "Amos 4:12", book: "Amos", chapter: 4, verseStart: 12,
    text: "Therefore this is what I will do to you, O Israel, and since I will do this to you, prepare to meet your God, O Israel!",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "God lists famine, drought, blight, and plague, each followed by 'yet you have not returned to Me.'",
    after: "Amos describes the One who forms the mountains and treads the heights of the earth.",
    theme: "Prepare to meet your God", keyword: "prepare",
    facts: ["The refrain 'yet you have not returned to Me' appears five times before this verse.", "It is one of the most quoted lines in all of Amos."],
  },
  {
    reference: "Amos 5:4", book: "Amos", chapter: 5, verseStart: 4,
    text: "For this is what the LORD says to the house of Israel: Seek Me and live!",
    testament: "OT", speaker: "The LORD", audience: "The house of Israel",
    before: "Amos takes up a lament over the fallen virgin Israel.",
    after: "God warns them not to seek Bethel or go to Gilgal.",
    theme: "Seek God and live", keyword: "Seek",
    facts: ["In the middle of a funeral song, God offers life.", "Amos contrasts seeking God with visiting the popular shrines."],
  },
  {
    reference: "Amos 5:14", book: "Amos", chapter: 5, verseStart: 14,
    text: "Seek good, not evil, that you may live. And the LORD, the God of Hosts, will be with you, as you have claimed.",
    testament: "OT", speaker: "Amos", audience: "The people of Israel",
    before: "Amos describes those who hate the one who reproves in the gate and detest the one who speaks with integrity.",
    after: "Amos calls them to hate evil, love good, and establish justice in the gate.",
    theme: "Seeking good is seeking God", keyword: "good",
    facts: ["The people claimed God was with them; Amos ties that claim to how they lived.", "The 'gate' was where legal cases were heard in an Israelite town."],
  },
  {
    reference: "Amos 5:15", book: "Amos", chapter: 5, verseStart: 15,
    text: "Hate evil, love good, and establish justice in the gate. Perhaps the LORD, the God of Hosts, will be gracious to the remnant of Joseph.",
    testament: "OT", speaker: "Amos", audience: "The people of Israel",
    before: "Amos urges them to seek good and not evil so that they may live.",
    after: "Amos foretells wailing in all the streets and cries of anguish in the public squares.",
    theme: "Justice in the courts", keyword: "remnant",
    facts: ["Amos targets the courtroom, where the poor were being cheated.", "'Perhaps' leaves room for mercy without presuming on it."],
  },
  {
    reference: "Amos 5:24", book: "Amos", chapter: 5, verseStart: 24,
    text: "But let justice roll on like a river, and righteousness like an ever-flowing stream.",
    testament: "OT", speaker: "The LORD, through Amos", audience: "The people of Israel",
    before: "God says He hates their feasts and will not accept their offerings or listen to their songs.",
    after: "God asks whether they brought Him sacrifices during forty years in the wilderness.",
    theme: "Justice like a river", keyword: "righteousness",
    facts: ["Martin Luther King Jr. quoted this verse in his 'I Have a Dream' speech.", "An ever-flowing stream is one that runs even in the dry season."],
  },
  {
    reference: "Amos 7:14", book: "Amos", chapter: 7, verseStart: 14,
    text: "So Amos answered Amaziah, \"I was not a prophet, nor was I the son of a prophet; rather, I was a herdsman and a tender of sycamore figs.\"",
    testament: "OT", speaker: "Amos", audience: "Amaziah, priest of Bethel",
    before: "Amaziah orders Amos to go back to Judah and stop prophesying at the king's sanctuary.",
    after: "Amos says the LORD took him from following the flock and told him to prophesy to Israel.",
    theme: "Called from ordinary work", keyword: "herdsman",
    facts: ["Amos had no professional standing as a prophet.", "Tending sycamore figs meant piercing the fruit by hand so it would ripen."],
  },
  {
    reference: "Amos 8:11", book: "Amos", chapter: 8, verseStart: 11,
    text: "Behold, the days are coming, declares the Lord GOD, when I will send a famine on the land — not a famine of bread or a thirst for water, but a famine of hearing the words of the LORD.",
    testament: "OT", speaker: "The Lord GOD", audience: "The people of Israel",
    before: "God condemns those who trample the needy and cheat with dishonest scales.",
    after: "God says people will stagger from sea to sea searching for the word of the LORD and not find it.",
    theme: "A famine of God's word", keyword: "famine",
    facts: ["The judgment is silence: God simply stops speaking.", "Amos frames hearing from God as a basic human need, like food and water."],
  },
  {
    reference: "Amos 9:14", book: "Amos", chapter: 9, verseStart: 14,
    text: "I will restore My people Israel from captivity; they will rebuild the ruined cities and inhabit them. They will plant vineyards and drink their wine; they will make gardens and eat their fruit.",
    testament: "OT", speaker: "The LORD", audience: "The people of Israel",
    before: "God promises days when the reaper will overtake the plowman and the mountains will drip with sweet wine.",
    after: "God promises to plant them in their own land, never again to be uprooted.",
    theme: "Rebuilt and replanted", keyword: "vineyards",
    facts: ["The harshest of the prophets ends with a promise of restoration.", "James quotes the surrounding passage at the council in Jerusalem."],
  },
  // — Obadiah —
  {
    reference: "Obadiah 1:1", book: "Obadiah", chapter: 1, verseStart: 1,
    text: "This is the vision of Obadiah. This is what the Lord GOD says about Edom: We have heard a message from the LORD; an envoy has been sent among the nations to say, \"Rise up, and let us go to battle against her!\"",
    testament: "OT", speaker: "Obadiah", audience: "Readers of Judah",
    before: "Nothing — this is the opening line of the shortest book in the Old Testament.",
    after: "God says He will make Edom small among the nations and deeply despised.",
    theme: "A word against Edom", keyword: "envoy",
    facts: ["Obadiah is the shortest book in the Old Testament, just 21 verses.", "Edom descended from Esau, making this a quarrel between brother nations."],
  },
  {
    reference: "Obadiah 1:2", book: "Obadiah", chapter: 1, verseStart: 2,
    text: "See, I will make you small among the nations; you will be deeply despised.",
    testament: "OT", speaker: "The Lord GOD", audience: "Edom",
    before: "An envoy calls the nations to rise up against Edom in battle.",
    after: "God says the pride of Edom's heart has deceived her.",
    theme: "The proud brought low", keyword: "despised",
    facts: ["Edom's fortress cities in the cliffs made her feel untouchable.", "The whole book is a single oracle against one nation."],
  },
  {
    reference: "Obadiah 1:3", book: "Obadiah", chapter: 1, verseStart: 3,
    text: "The pride of your heart has deceived you, O dwellers in the clefts of the rocks, whose habitation is the heights, who say in your heart, \"Who can bring me down to the ground?\"",
    testament: "OT", speaker: "The Lord GOD", audience: "Edom",
    before: "God declares He will make Edom small among the nations.",
    after: "God says that even if Edom soars like the eagle, He will bring her down.",
    theme: "Pride deceives", keyword: "clefts",
    facts: ["Edom's capital was carved into sheer rock, reachable only through a narrow gorge.", "The site is known today as Petra in southern Jordan."],
  },
  {
    reference: "Obadiah 1:4", book: "Obadiah", chapter: 1, verseStart: 4,
    text: "Though you soar like the eagle and make your nest among the stars, even from there I will bring you down, declares the LORD.",
    testament: "OT", speaker: "The LORD", audience: "Edom",
    before: "God says the pride of Edom's heart has deceived her in her rocky heights.",
    after: "God asks how completely Edom will be plundered and her hidden treasures pillaged.",
    theme: "No height is out of God's reach", keyword: "eagle",
    facts: ["Height was Edom's whole defense strategy.", "The image of nesting among the stars is deliberate exaggeration."],
  },
  {
    reference: "Obadiah 1:10", book: "Obadiah", chapter: 1, verseStart: 10,
    text: "Because of the violence against your brother Jacob, you will be covered with shame and cut off forever.",
    testament: "OT", speaker: "The Lord GOD", audience: "Edom",
    before: "God says He will destroy the wise men of Edom and the men of understanding in the mountains of Esau.",
    after: "God recalls the day Edom stood aloof while strangers carried off Jacob's wealth.",
    theme: "Violence against a brother", keyword: "violence",
    facts: ["Edom and Israel traced their descent to the twin brothers Esau and Jacob.", "The charge is not idolatry but betrayal of kin."],
  },
  {
    reference: "Obadiah 1:11", book: "Obadiah", chapter: 1, verseStart: 11,
    text: "On the day you stood aloof while strangers carried off his wealth and foreigners entered his gate and cast lots for Jerusalem, you were just like one of them.",
    testament: "OT", speaker: "The Lord GOD", audience: "Edom",
    before: "God charges Edom with violence against her brother Jacob.",
    after: "God warns Edom not to gloat in the day of her brother's misfortune.",
    theme: "The guilt of standing by", keyword: "aloof",
    facts: ["Edom is condemned for what she did not do as much as what she did.", "'You were just like one of them' equates bystanding with joining in."],
  },
  {
    reference: "Obadiah 1:12", book: "Obadiah", chapter: 1, verseStart: 12,
    text: "But you should not gloat in the day of your brother's misfortune, nor rejoice over the people of Judah in the day of their destruction, nor boast proudly in the day of their distress.",
    testament: "OT", speaker: "The Lord GOD", audience: "Edom",
    before: "God recalls the day Edom stood by while foreigners cast lots for Jerusalem.",
    after: "God warns Edom not to enter the gate of His people in the day of their disaster.",
    theme: "Do not gloat over the fallen", keyword: "gloat",
    facts: ["Eight short warnings in a row all begin the same way in Hebrew.", "The sin named here is taking pleasure in someone else's ruin."],
  },
  {
    reference: "Obadiah 1:15", book: "Obadiah", chapter: 1, verseStart: 15,
    text: "For the Day of the LORD is near for all nations. As you have done, it will be done to you; your recompense will return upon your own head.",
    testament: "OT", speaker: "The Lord GOD", audience: "Edom and all nations",
    before: "God warns Edom not to cut down her brother's fugitives or hand over the survivors.",
    after: "God says that as they drank on His holy mountain, so will all the nations drink continually.",
    theme: "What you do returns to you", keyword: "recompense",
    facts: ["The oracle widens here from Edom alone to every nation.", "The principle is the same one Jesus states as the measure you use being measured to you."],
  },
  {
    reference: "Obadiah 1:17", book: "Obadiah", chapter: 1, verseStart: 17,
    text: "But on Mount Zion there will be deliverance, and it will be holy, and the house of Jacob will reclaim their possession.",
    testament: "OT", speaker: "The Lord GOD", audience: "Readers of Judah",
    before: "God declares that the nations will drink the cup of judgment continually.",
    after: "God says the house of Jacob will be a fire and the house of Esau stubble.",
    theme: "Deliverance on Mount Zion", keyword: "deliverance",
    facts: ["The book turns from judgment to hope in a single verse.", "Joel uses almost the same phrase about deliverance on Mount Zion."],
  },
  {
    reference: "Obadiah 1:21", book: "Obadiah", chapter: 1, verseStart: 21,
    text: "The deliverers will ascend Mount Zion to rule over the mountains of Esau. And the kingdom will belong to the LORD.",
    testament: "OT", speaker: "Obadiah", audience: "Readers of Judah",
    before: "The exiles are promised the return of their land and cities.",
    after: "The book of Obadiah ends here.",
    theme: "The kingdom belongs to the LORD", keyword: "deliverers",
    facts: ["The shortest book in the Old Testament ends on its highest note.", "Its final claim is that the kingdom is God's, not Edom's or Israel's."],
  },
  // — Jonah —
  {
    reference: "Jonah 1:3", book: "Jonah", chapter: 1, verseStart: 3,
    text: "But Jonah got up to flee to Tarshish, away from the presence of the LORD. He went down to Joppa, found a ship bound for Tarshish, paid the fare, and went aboard to sail for Tarshish, away from the presence of the LORD.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The word of the LORD comes to Jonah, telling him to go to Nineveh and preach against it.",
    after: "The LORD hurls a great wind on the sea and a violent storm arises.",
    theme: "Running from God's call", keyword: "Tarshish",
    facts: ["Tarshish lay in the opposite direction from Nineveh.", "The word 'down' repeats through Jonah's flight: down to Joppa, down into the ship, down into the sea."],
  },
  {
    reference: "Jonah 1:17", book: "Jonah", chapter: 1, verseStart: 17,
    text: "Now the LORD had appointed a great fish to swallow Jonah, and Jonah was in the belly of the fish three days and three nights.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The sailors throw Jonah into the sea and the raging waters grow calm.",
    after: "From inside the fish Jonah prays to the LORD his God.",
    theme: "Rescue in an unlikely form", keyword: "swallow",
    facts: ["The text calls it a great fish, not specifically a whale.", "Jesus points to these three days and nights as a sign about His own burial."],
  },
  {
    reference: "Jonah 2:1", book: "Jonah", chapter: 2, verseStart: 1,
    text: "From inside the fish Jonah prayed to the LORD his God,",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The LORD appoints a great fish to swallow Jonah.",
    after: "Jonah says that in his distress he called to the LORD and was answered.",
    theme: "Prayer from the bottom", keyword: "prayed",
    facts: ["Jonah's prayer is almost entirely stitched together from lines in the Psalms.", "He prays inside the fish, before he is delivered from it."],
  },
  {
    reference: "Jonah 2:2", book: "Jonah", chapter: 2, verseStart: 2,
    text: "saying: In my distress I called to the LORD, and He answered me. From the belly of Sheol I called for help, and You heard my voice.",
    testament: "OT", speaker: "Jonah", audience: "The LORD",
    before: "Jonah begins to pray from inside the great fish.",
    after: "Jonah describes being cast into the deep with the currents swirling about him.",
    theme: "God hears from the depths", keyword: "distress",
    facts: ["Sheol was the Hebrew name for the realm of the dead.", "Jonah speaks of his rescue in the past tense while still inside the fish."],
  },
  {
    reference: "Jonah 2:9", book: "Jonah", chapter: 2, verseStart: 9,
    text: "But I, with the voice of thanksgiving, will sacrifice to You. I will fulfill what I have vowed. Salvation is from the LORD!",
    testament: "OT", speaker: "Jonah", audience: "The LORD",
    before: "Jonah says those who cling to worthless idols forsake His loving devotion.",
    after: "The LORD commands the fish, and it vomits Jonah onto dry land.",
    theme: "Salvation belongs to the LORD", keyword: "thanksgiving",
    facts: ["This line is the theological center of the whole book.", "Jonah confesses it about himself before he can accept it about Nineveh."],
  },
  {
    reference: "Jonah 3:2", book: "Jonah", chapter: 3, verseStart: 2,
    text: "Get up! Go to the great city of Nineveh and proclaim to it the message that I give you.",
    testament: "OT", speaker: "The LORD", audience: "Jonah",
    before: "The word of the LORD comes to Jonah a second time.",
    after: "Jonah gets up and goes to Nineveh in obedience to the word of the LORD.",
    theme: "A second chance to obey", keyword: "proclaim",
    facts: ["The call is repeated word for word from chapter 1.", "God's commission to Jonah did not change after he ran."],
  },
  {
    reference: "Jonah 3:5", book: "Jonah", chapter: 3, verseStart: 5,
    text: "And the Ninevites believed God. They proclaimed a fast and dressed in sackcloth, from the greatest of them to the least.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "Jonah walks into the city and announces that in forty days Nineveh will be overturned.",
    after: "The news reaches the king of Nineveh, who rises from his throne and sits in ashes.",
    theme: "A whole city turns", keyword: "sackcloth",
    facts: ["Jonah preached the shortest sermon in the Bible and got the largest response.", "Nineveh was the capital of Assyria, Israel's most feared enemy."],
  },
  {
    reference: "Jonah 3:10", book: "Jonah", chapter: 3, verseStart: 10,
    text: "When God saw their actions — that they had turned from their evil ways — He relented from the disaster He had threatened to bring upon them.",
    testament: "OT", speaker: "The narrator", audience: "Readers of Israel",
    before: "The king of Nineveh orders everyone to call urgently on God and turn from their violence.",
    after: "Jonah is greatly displeased and becomes angry.",
    theme: "God relents when people turn", keyword: "relented",
    facts: ["God responded to what the Ninevites did, not merely what they said.", "Jesus said the men of Nineveh would stand up at the judgment as witnesses."],
  },
  {
    reference: "Jonah 4:2", book: "Jonah", chapter: 4, verseStart: 2,
    text: "So he prayed to the LORD, \"O LORD, is this not what I said while I was still in my own country? This is why I was so quick to flee toward Tarshish. I knew that You are a gracious and compassionate God, slow to anger, abounding in loving devotion — One who relents from sending disaster.\"",
    testament: "OT", speaker: "Jonah", audience: "The LORD",
    before: "God relents from the disaster He had threatened, and Jonah becomes angry.",
    after: "Jonah asks God to take his life, saying it is better for him to die than to live.",
    theme: "Angry at God's mercy", keyword: "compassionate",
    facts: ["Jonah quotes God's own self-description from Exodus 34 as a complaint.", "He ran not because he feared failure but because he feared success."],
  },
  {
    reference: "Jonah 4:11", book: "Jonah", chapter: 4, verseStart: 11,
    text: "So should I not care about the great city of Nineveh, which has more than 120,000 people who cannot tell their right hand from their left, and many cattle as well?",
    testament: "OT", speaker: "The LORD", audience: "Jonah",
    before: "God points out that Jonah cared about a plant he did not tend or grow.",
    after: "The book of Jonah ends here, with the question unanswered.",
    theme: "God's care for outsiders", keyword: "cattle",
    facts: ["The book ends on a question, never recording Jonah's reply.", "God's last word in the book is concern for a foreign city and even its animals."],
  },
  // — Nahum —
  {
    reference: "Nahum 1:2", book: "Nahum", chapter: 1, verseStart: 2,
    text: "The LORD is a jealous and avenging God; the LORD is avenging and full of wrath. The LORD takes vengeance on His foes and reserves wrath for His enemies.",
    testament: "OT", speaker: "Nahum", audience: "Readers of Judah",
    before: "The book opens as an oracle concerning Nineveh, the vision of Nahum the Elkoshite.",
    after: "Nahum says the LORD is slow to anger and great in power.",
    theme: "God opposes cruelty", keyword: "avenging",
    facts: ["Nahum's name means 'comfort' — his message comforted Assyria's victims.", "Nineveh had repented in Jonah's day; a century later it had returned to violence."],
  },
  {
    reference: "Nahum 1:3", book: "Nahum", chapter: 1, verseStart: 3,
    text: "The LORD is slow to anger and great in power; the LORD will by no means leave the guilty unpunished. His path is in the whirlwind and storm, and clouds are the dust beneath His feet.",
    testament: "OT", speaker: "Nahum", audience: "Readers of Judah",
    before: "Nahum declares that the LORD is jealous and takes vengeance on His foes.",
    after: "Nahum describes the sea drying up and the mountains quaking before Him.",
    theme: "Patient but not indifferent", keyword: "whirlwind",
    facts: ["The phrase 'slow to anger' comes from God's self-description in Exodus 34.", "Patience here is not the same as overlooking cruelty."],
  },
  {
    reference: "Nahum 1:6", book: "Nahum", chapter: 1, verseStart: 6,
    text: "Who can withstand His indignation? Who can endure His burning anger? His wrath is poured out like fire; even rocks are shattered before Him.",
    testament: "OT", speaker: "Nahum", audience: "Readers of Judah",
    before: "Nahum describes mountains quaking and hills melting away before the LORD.",
    after: "Nahum declares that the LORD is good, a stronghold in the day of distress.",
    theme: "No one can stand against God", keyword: "indignation",
    facts: ["The questions are rhetorical: the expected answer is no one.", "The very next verse turns to God's goodness, holding both together."],
  },
  {
    reference: "Nahum 1:7", book: "Nahum", chapter: 1, verseStart: 7,
    text: "The LORD is good, a stronghold in the day of distress; He cares for those who trust in Him.",
    testament: "OT", speaker: "Nahum", audience: "Readers of Judah",
    before: "Nahum asks who can endure the burning anger of the LORD.",
    after: "Nahum says the LORD will make an end of Nineveh with an overwhelming flood.",
    theme: "A refuge in trouble", keyword: "stronghold",
    facts: ["This is the best-known verse in a book otherwise full of judgment.", "The Hebrew word for 'cares' pictures God recognizing someone personally."],
  },
  {
    reference: "Nahum 1:8", book: "Nahum", chapter: 1, verseStart: 8,
    text: "But with an overwhelming flood He will make an end of Nineveh and pursue His enemies into darkness.",
    testament: "OT", speaker: "Nahum", audience: "Readers of Judah",
    before: "Nahum says the LORD cares for those who take refuge in Him.",
    after: "Nahum asks what they can plot against the LORD, since He will bring it to an end.",
    theme: "The end of an empire", keyword: "flood",
    facts: ["Ancient accounts describe a river flooding Nineveh's defenses when it fell.", "Nineveh was destroyed in 612 BC and never rebuilt."],
  },
  {
    reference: "Nahum 1:15", book: "Nahum", chapter: 1, verseStart: 15,
    text: "Look to the mountains — the feet of one who brings good news, who proclaims peace! Celebrate your feasts, O Judah; fulfill your vows. For the wicked will never again march through you; they will be utterly cut off.",
    testament: "OT", speaker: "Nahum", audience: "The people of Judah",
    before: "God declares He will break the yoke of Assyria from Judah's neck.",
    after: "Nahum describes the attacker advancing against Nineveh itself.",
    theme: "Good news of peace", keyword: "feasts",
    facts: ["Isaiah uses nearly the same image of beautiful feet on the mountains.", "Paul echoes the phrase when writing about those who preach the gospel."],
  },
  {
    reference: "Nahum 2:1", book: "Nahum", chapter: 2, verseStart: 1,
    text: "An attacker advances against you, O Nineveh. Guard the fortress! Watch the road! Brace yourselves! Summon all your strength!",
    testament: "OT", speaker: "Nahum", audience: "Nineveh",
    before: "Nahum announces good news of peace for Judah as Assyria's yoke is broken.",
    after: "Nahum says the LORD will restore the splendor of Jacob.",
    theme: "A city under siege", keyword: "fortress",
    facts: ["Nahum's poetry is famous for its rapid, clipped commands.", "The mocking orders imitate a commander shouting on the wall."],
  },
  {
    reference: "Nahum 2:2", book: "Nahum", chapter: 2, verseStart: 2,
    text: "For the LORD will restore the splendor of Jacob, like the splendor of Israel, though destroyers have laid them waste and have ruined their vines.",
    testament: "OT", speaker: "Nahum", audience: "Readers of Judah",
    before: "Nahum calls Nineveh to brace itself as the attacker advances.",
    after: "Nahum describes the shields of the attackers turned red and the chariots flashing like fire.",
    theme: "Restoration for the plundered", keyword: "splendor",
    facts: ["Judgment on the oppressor is framed as restoration for the oppressed.", "Ruined vines were a standard image for a plundered countryside."],
  },
  {
    reference: "Nahum 3:1", book: "Nahum", chapter: 3, verseStart: 1,
    text: "Woe to the city of blood, full of lies, full of plunder, never without prey!",
    testament: "OT", speaker: "Nahum", audience: "Nineveh",
    before: "Nahum describes Nineveh's palace collapsing and its people fleeing.",
    after: "Nahum describes the crack of the whip and the galloping horses of battle.",
    theme: "The verdict on violence", keyword: "plunder",
    facts: ["Assyria was known across the ancient world for brutal treatment of captives.", "The charge is not idolatry alone but bloodshed and deceit."],
  },
  {
    reference: "Nahum 3:19", book: "Nahum", chapter: 3, verseStart: 19,
    text: "There is no healing for your injury; your wound is severe. All who hear the news of you applaud your downfall, for who has not experienced your constant cruelty?",
    testament: "OT", speaker: "Nahum", audience: "The king of Assyria",
    before: "Nahum says Assyria's shepherds slumber and its people are scattered on the mountains.",
    after: "The book of Nahum ends here.",
    theme: "When the oppressor falls", keyword: "cruelty",
    facts: ["The book closes with a question rather than a statement.", "Nations that had suffered under Assyria are pictured clapping at the news."],
  },
  // — Haggai —
  {
    reference: "Haggai 1:4", book: "Haggai", chapter: 1, verseStart: 4,
    text: "Is it a time for you yourselves to live in your paneled houses, while this house lies in ruins?",
    testament: "OT", speaker: "The LORD, through Haggai", audience: "The people of Judah",
    before: "The people are saying the time has not yet come to rebuild the house of the LORD.",
    after: "God tells them to consider carefully their ways.",
    theme: "Misplaced priorities", keyword: "paneled",
    facts: ["Paneled houses were a mark of comfort and finished work.", "The temple foundation had sat untouched for roughly sixteen years."],
  },
  {
    reference: "Haggai 1:5", book: "Haggai", chapter: 1, verseStart: 5,
    text: "Now this is what the LORD of Hosts says: Consider carefully your ways.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The people of Judah",
    before: "God asks whether it is time to live in paneled houses while His house lies in ruins.",
    after: "God describes planting much and harvesting little.",
    theme: "Take stock of your life", keyword: "Consider",
    facts: ["The phrase literally reads 'set your heart on your ways.'", "God repeats this same call twice more in the short book."],
  },
  {
    reference: "Haggai 1:6", book: "Haggai", chapter: 1, verseStart: 6,
    text: "You have planted much but harvested little. You eat but never have enough. You drink but never have your fill. You put on clothes but never get warm. You earn wages to put into a bag pierced through.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The people of Judah",
    before: "God tells the people to consider carefully their ways.",
    after: "God tells them to go up into the hills, bring down lumber, and build the house.",
    theme: "Effort without satisfaction", keyword: "harvested",
    facts: ["The image of a purse with holes in it has passed into common speech.", "Haggai links their frustration to their neglect of God's house."],
  },
  {
    reference: "Haggai 1:8", book: "Haggai", chapter: 1, verseStart: 8,
    text: "Go up into the hills, bring down lumber, and build the house, so that I may take pleasure in it and be glorified, says the LORD.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "God describes their labor yielding little satisfaction.",
    after: "God explains that they expected much but it turned out to be little.",
    theme: "Get to work", keyword: "lumber",
    facts: ["The instruction is entirely practical: go get wood and build.", "The rebuilding began within a month of this message."],
  },
  {
    reference: "Haggai 1:13", book: "Haggai", chapter: 1, verseStart: 13,
    text: "Then Haggai, the messenger of the LORD, delivered the message of the LORD to the people: I am with you, declares the LORD.",
    testament: "OT", speaker: "The LORD, through Haggai", audience: "The people of Judah",
    before: "The people obey the voice of the LORD and fear Him.",
    after: "The LORD stirs up the spirit of Zerubbabel and of all the remnant of the people.",
    theme: "God with the working people", keyword: "messenger",
    facts: ["The rebuke of chapter 1 is answered with four words of encouragement.", "'I am with you' comes after obedience, not before it."],
  },
  {
    reference: "Haggai 1:14", book: "Haggai", chapter: 1, verseStart: 14,
    text: "So the LORD stirred the spirit of Zerubbabel governor of Judah, the spirit of Joshua son of Jehozadak the high priest, and the spirits of all the remnant of the people. They came and began the work on the house of the LORD of Hosts, their God.",
    testament: "OT", speaker: "The narrator", audience: "Readers of the restoration",
    before: "Haggai delivers God's promise that He is with them.",
    after: "The work begins on the twenty-fourth day of the sixth month.",
    theme: "God stirs willing hearts", keyword: "stirred",
    facts: ["God is credited with stirring the leaders and the ordinary people alike.", "Zerubbabel was a descendant of David governing under Persian rule."],
  },
  {
    reference: "Haggai 2:3", book: "Haggai", chapter: 2, verseStart: 3,
    text: "Who is left among you who saw this house in its former glory? How does it look to you now? Does it not seem to you like nothing in comparison?",
    testament: "OT", speaker: "The LORD, through Haggai", audience: "The builders of the temple",
    before: "The word of the LORD comes through Haggai in the seventh month.",
    after: "God tells Zerubbabel, Joshua, and all the people to be strong and work.",
    theme: "When the new looks smaller", keyword: "comparison",
    facts: ["Some of the builders had seen Solomon's temple before it was destroyed.", "God names the discouragement directly rather than talking around it."],
  },
  {
    reference: "Haggai 2:4", book: "Haggai", chapter: 2, verseStart: 4,
    text: "But now be strong, O Zerubbabel, declares the LORD. Be strong, O Joshua son of Jehozadak, the high priest. Be strong, all you people of the land, declares the LORD. Work! For I am with you, declares the LORD of Hosts.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "Zerubbabel, Joshua, and the people",
    before: "God asks whether the new temple seems like nothing compared to the former one.",
    after: "God reminds them of the covenant made when they came out of Egypt.",
    theme: "Be strong and work", keyword: "strong",
    facts: ["'Be strong' is repeated three times, once for each group.", "The encouragement is paired with a command to keep working."],
  },
  {
    reference: "Haggai 2:9", book: "Haggai", chapter: 2, verseStart: 9,
    text: "The latter glory of this house will be greater than the former, says the LORD of Hosts. And in this place I will provide peace, declares the LORD of Hosts.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The builders of the temple",
    before: "God declares that the silver and the gold belong to Him.",
    after: "The word of the LORD comes to Haggai with a question for the priests about holiness.",
    theme: "Greater glory ahead", keyword: "latter",
    facts: ["The promise was made about a building the workers thought looked shabby.", "Jesus later taught in the temple that stood on this site."],
  },
  {
    reference: "Haggai 2:19", book: "Haggai", chapter: 2, verseStart: 19,
    text: "Is there still seed in the barn? The vine, the fig tree, the pomegranate, and the olive tree have not yet yielded fruit. But from this day on I will bless you.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "God calls them to consider carefully the day the foundation of the temple was laid.",
    after: "The word of the LORD comes to Haggai a second time that day, for Zerubbabel.",
    theme: "Blessing before the harvest", keyword: "pomegranate",
    facts: ["The blessing is promised before there is any visible sign of it.", "Haggai's whole ministry lasted less than four months."],
  },
  // — Zechariah —
  {
    reference: "Zechariah 1:3", book: "Zechariah", chapter: 1, verseStart: 3,
    text: "So tell the people that this is what the LORD of Hosts says: Return to Me, declares the LORD of Hosts, and I will return to you, says the LORD of Hosts.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The returned exiles",
    before: "The word of the LORD comes to Zechariah in the eighth month of Darius' second year.",
    after: "God warns them not to be like their fathers, who would not listen to the earlier prophets.",
    theme: "Return and be returned to", keyword: "Return",
    facts: ["Zechariah prophesied alongside Haggai during the temple rebuild.", "The promise is mutual: their step toward God is met by His step toward them."],
  },
  {
    reference: "Zechariah 2:5", book: "Zechariah", chapter: 2, verseStart: 5,
    text: "For I will be a wall of fire around it, declares the LORD, and I will be the glory within it.",
    testament: "OT", speaker: "The LORD", audience: "Jerusalem, through Zechariah",
    before: "An angel says Jerusalem will be a city without walls because of the multitude of people and livestock in it.",
    after: "God calls the exiles to flee from the land of the north.",
    theme: "God as the city's defense", keyword: "wall",
    facts: ["A city without walls was normally defenseless.", "God promises to be both the outer protection and the inner glory."],
  },
  {
    reference: "Zechariah 4:6", book: "Zechariah", chapter: 4, verseStart: 6,
    text: "So he said to me, This is the word of the LORD to Zerubbabel: Not by might nor by power, but by My Spirit, says the LORD of Hosts.",
    testament: "OT", speaker: "The angel, relaying the LORD", audience: "Zechariah, for Zerubbabel",
    before: "Zechariah sees a solid gold lampstand with a bowl on top and seven lamps.",
    after: "God asks what the mighty mountain is before Zerubbabel, saying it will become level ground.",
    theme: "God's Spirit, not human strength", keyword: "might",
    facts: ["The line was spoken to a governor rebuilding a temple with almost no resources.", "It is one of the most quoted verses in the Minor Prophets."],
  },
  {
    reference: "Zechariah 4:10", book: "Zechariah", chapter: 4, verseStart: 10,
    text: "For who has despised the day of small things? But these seven eyes of the LORD, which roam throughout the earth, will rejoice when they see the plumb line in the hand of Zerubbabel.",
    testament: "OT", speaker: "The angel, relaying the LORD", audience: "Zechariah",
    before: "God declares that the hands of Zerubbabel, which laid the foundation, will also complete the temple.",
    after: "Zechariah asks about the two olive trees on the right and left of the lampstand.",
    theme: "Small beginnings matter", keyword: "plumb",
    facts: ["A plumb line is a builder's weight used to check that a wall is straight.", "God is pictured rejoicing at the sight of ordinary construction work."],
  },
  {
    reference: "Zechariah 7:9", book: "Zechariah", chapter: 7, verseStart: 9,
    text: "This is what the LORD of Hosts says: Administer true justice. Show loving devotion and compassion to one another.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The people of Judah",
    before: "God asks whether their fasting was really for Him or for themselves.",
    after: "God forbids oppressing the widow, the fatherless, the foreigner, and the poor.",
    theme: "What God actually asks for", keyword: "compassion",
    facts: ["The people had asked whether they should keep fasting; God answered with justice.", "The same triad of justice, mercy, and faithfulness appears in Micah and in Matthew."],
  },
  {
    reference: "Zechariah 7:10", book: "Zechariah", chapter: 7, verseStart: 10,
    text: "Do not oppress the widow or the fatherless, the foreigner or the poor. And do not plot evil in your hearts against one another.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The people of Judah",
    before: "God calls them to administer true justice and show compassion to one another.",
    after: "God recalls how their fathers refused to pay attention and stopped their ears.",
    theme: "Protecting the vulnerable", keyword: "fatherless",
    facts: ["These four groups appear together throughout the Law and the Prophets.", "The command reaches past actions to what people plot in their hearts."],
  },
  {
    reference: "Zechariah 8:16", book: "Zechariah", chapter: 8, verseStart: 16,
    text: "These are the things you must do: Speak truth to one another, render true and sound judgments in your gates.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The people of Judah",
    before: "God says He has purposed to do good to Jerusalem and tells them not to be afraid.",
    after: "God tells them not to plot evil against a neighbor or love a false oath.",
    theme: "Truth in everyday dealings", keyword: "truth",
    facts: ["Paul quotes 'speak truth to one another' when writing to the Ephesians.", "The 'gates' were where legal disputes were settled."],
  },
  {
    reference: "Zechariah 9:9", book: "Zechariah", chapter: 9, verseStart: 9,
    text: "Rejoice greatly, O Daughter of Zion! Shout in triumph, O Daughter of Jerusalem! See, your King comes to you, righteous and victorious, humble and riding on a donkey, on a colt, the foal of a donkey.",
    testament: "OT", speaker: "Zechariah", audience: "Jerusalem",
    before: "God promises to camp around His house and guard it against marauders.",
    after: "God says He will remove the chariot from Ephraim and the warhorse from Jerusalem.",
    theme: "A humble king", keyword: "donkey",
    facts: ["Matthew and John both quote this verse on Palm Sunday.", "Kings rode donkeys in peacetime and horses to war."],
  },
  {
    reference: "Zechariah 13:9", book: "Zechariah", chapter: 13, verseStart: 9,
    text: "This third I will bring through the fire; I will refine them like silver and test them like gold. They will call on My name, and I will answer them. I will say, 'They are My people,' and they will say, 'The LORD is our God.'",
    testament: "OT", speaker: "The LORD", audience: "Readers of Judah",
    before: "God speaks of striking the shepherd so that the sheep are scattered.",
    after: "Zechariah describes a day of the LORD when the nations gather against Jerusalem.",
    theme: "Refined, not discarded", keyword: "refine",
    facts: ["Refining silver means heating it until the impurities can be lifted off.", "The point of the fire is the confession at the end of the verse."],
  },
  {
    reference: "Zechariah 14:9", book: "Zechariah", chapter: 14, verseStart: 9,
    text: "On that day the LORD will become King over all the earth — the LORD alone, and His name alone.",
    testament: "OT", speaker: "Zechariah", audience: "Readers of Judah",
    before: "Zechariah describes living water flowing out from Jerusalem in summer and winter alike.",
    after: "Zechariah describes the whole land being turned into a plain.",
    theme: "One King over all", keyword: "King",
    facts: ["This line is recited at the close of every Jewish synagogue service.", "Revelation echoes it in its vision of the kingdom of the world becoming God's."],
  },
  // — Malachi —
  {
    reference: "Malachi 1:2", book: "Malachi", chapter: 1, verseStart: 2,
    text: "I have loved you, says the LORD. But you ask, \"How have You loved us?\" Was not Esau Jacob's brother? declares the LORD. Yet Jacob I have loved,",
    testament: "OT", speaker: "The LORD", audience: "Israel",
    before: "The book opens as an oracle, the word of the LORD to Israel through Malachi.",
    after: "God says He has hated Esau and turned his hill country into a wasteland.",
    theme: "Love questioned and reaffirmed", keyword: "loved",
    facts: ["Malachi is built as a series of disputes: God speaks, the people object, God answers.", "Paul quotes this pair of brothers when writing about God's choosing in Romans 9."],
  },
  {
    reference: "Malachi 1:11", book: "Malachi", chapter: 1, verseStart: 11,
    text: "For My name will be great among the nations, from where the sun rises to where it sets. In every place, incense and pure offerings will be presented in My name, because My name will be great among the nations, says the LORD of Hosts.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The priests of Israel",
    before: "God rebukes the priests for offering blind, lame, and diseased animals on His altar.",
    after: "God says the priests profane His name by calling the Lord's table contemptible.",
    theme: "God's name among the nations", keyword: "incense",
    facts: ["The promise looks past Israel to worship offered everywhere on earth.", "It stands in sharp contrast to the careless offerings being made at the time."],
  },
  {
    reference: "Malachi 2:7", book: "Malachi", chapter: 2, verseStart: 7,
    text: "For the lips of a priest should preserve knowledge, and people should seek instruction from his mouth, because he is the messenger of the LORD of Hosts.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The priests of Israel",
    before: "God recalls the covenant with Levi, a covenant of life and peace.",
    after: "God says the priests have turned from the way and caused many to stumble.",
    theme: "Teachers entrusted with truth", keyword: "instruction",
    facts: ["Priests were expected to teach, not only to run sacrifices.", "The word for 'messenger' is the same root as the prophet's own name, Malachi."],
  },
  {
    reference: "Malachi 2:10", book: "Malachi", chapter: 2, verseStart: 10,
    text: "Do we not all have one Father? Did not one God create us? Why then do we break faith with one another so as to profane the covenant of our fathers?",
    testament: "OT", speaker: "Malachi", audience: "The people of Judah",
    before: "God rebukes the priests for showing partiality in matters of the law.",
    after: "Malachi says Judah has been unfaithful and a detestable thing has been done in Israel.",
    theme: "One Father, one family", keyword: "profane",
    facts: ["Malachi grounds social ethics in the fact of a shared Creator.", "The phrase 'break faith' runs through this section like a refrain."],
  },
  {
    reference: "Malachi 3:1", book: "Malachi", chapter: 3, verseStart: 1,
    text: "Behold, I will send My messenger, who will prepare the way before Me. Then the Lord whom you seek will suddenly come to His temple — the Messenger of the covenant, in whom you delight — see, He is coming, says the LORD of Hosts.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The people of Judah",
    before: "The people have wearied the LORD by asking where the God of justice is.",
    after: "Malachi asks who can endure the day of His coming, comparing Him to a refiner's fire.",
    theme: "A messenger prepares the way", keyword: "temple",
    facts: ["All three Synoptic Gospels apply this verse to John the Baptist.", "It is quoted in the very first chapter of Mark."],
  },
  {
    reference: "Malachi 3:6", book: "Malachi", chapter: 3, verseStart: 6,
    text: "Because I, the LORD, do not change, you descendants of Jacob have not been destroyed.",
    testament: "OT", speaker: "The LORD", audience: "The descendants of Jacob",
    before: "God announces judgment against sorcerers, adulterers, perjurers, and those who defraud workers.",
    after: "God calls them to return to Him, promising that He will return to them.",
    theme: "God's unchanging character", keyword: "change",
    facts: ["Israel's survival is credited to God's consistency, not their own.", "The verse is a favorite proof text for the unchangeableness of God."],
  },
  {
    reference: "Malachi 3:10", book: "Malachi", chapter: 3, verseStart: 10,
    text: "Bring the full tithe into the storehouse, so that there may be food in My house. Test Me in this, says the LORD of Hosts. See if I will not open the windows of heaven and pour out for you blessing without measure.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "The people of Judah",
    before: "God charges the whole nation with robbing Him in tithes and offerings.",
    after: "God promises to rebuke the devourer so that it will not destroy the fruit of their land.",
    theme: "Generosity put to the test", keyword: "storehouse",
    facts: ["This is the only place in Scripture where God invites people to test Him.", "The storehouse was a temple room where food offerings were kept for the Levites."],
  },
  {
    reference: "Malachi 3:16", book: "Malachi", chapter: 3, verseStart: 16,
    text: "At that time those who feared the LORD spoke with one another, and the LORD listened and heard. So a scroll of remembrance was written before Him regarding those who feared the LORD and honored His name.",
    testament: "OT", speaker: "Malachi", audience: "Readers of Judah",
    before: "The people have said it is futile to serve God and asked what they gained by keeping His requirements.",
    after: "God calls them His treasured possession on the day when He acts.",
    theme: "God notices the faithful", keyword: "remembrance",
    facts: ["The picture is of God keeping a written record of ordinary faithful conversations.", "It answers the complaint that serving God goes unnoticed."],
  },
  {
    reference: "Malachi 4:2", book: "Malachi", chapter: 4, verseStart: 2,
    text: "But for you who fear My name, the sun of righteousness will rise with healing in its wings, and you will go out and leap like calves from the stall.",
    testament: "OT", speaker: "The LORD of Hosts", audience: "Those who fear His name",
    before: "God says the day is coming, burning like a furnace, when the arrogant will be stubble.",
    after: "God says they will trample the wicked, who will be ashes under their feet.",
    theme: "Healing at sunrise", keyword: "healing",
    facts: ["Charles Wesley borrowed this image for the carol 'Hark! The Herald Angels Sing.'", "The picture of calves released from a stall is one of sheer relief and energy."],
  },
  {
    reference: "Malachi 4:6", book: "Malachi", chapter: 4, verseStart: 6,
    text: "And he will turn the hearts of the fathers to their children, and the hearts of the children to their fathers. Otherwise, I will come and strike the land with a curse.",
    testament: "OT", speaker: "The LORD", audience: "The people of Judah",
    before: "God promises to send the prophet Elijah before the great and awesome Day of the LORD.",
    after: "The Old Testament ends here.",
    theme: "Hearts turned home", keyword: "hearts",
    facts: ["This is the last verse of the Old Testament.", "Gabriel quotes it when announcing the birth of John the Baptist."],
  },
  // — Philemon —
  {
    reference: "Philemon 1:4", book: "Philemon", chapter: 1, verseStart: 4,
    text: "I always thank my God, remembering you in my prayers,",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul greets Philemon, Apphia, Archippus, and the church that meets in Philemon's home.",
    after: "Paul says he hears about Philemon's faith in the Lord Jesus and love for all the saints.",
    theme: "Thanking God for a friend", keyword: "remembering",
    facts: ["Philemon is the shortest of Paul's letters, just 25 verses.", "It was written to one household rather than a whole city's church."],
  },
  {
    reference: "Philemon 1:5", book: "Philemon", chapter: 1, verseStart: 5,
    text: "because I hear about your faith in the Lord Jesus and your love for all the saints.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul says he always thanks God when he remembers Philemon in his prayers.",
    after: "Paul prays that Philemon's partnership in the faith would be effective.",
    theme: "Faith that shows up as love", keyword: "saints",
    facts: ["Paul's reputation for Philemon reached him while he was in prison.", "Faith toward Jesus and love toward people are named in one breath."],
  },
  {
    reference: "Philemon 1:6", book: "Philemon", chapter: 1, verseStart: 6,
    text: "I pray that your partnership in the faith may become effective through the knowledge of every good thing that is ours in Christ.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul mentions Philemon's faith in the Lord Jesus and his love for all the saints.",
    after: "Paul says he takes great joy and encouragement in Philemon's love.",
    theme: "Faith shared and put to work", keyword: "partnership",
    facts: ["The Greek word behind 'partnership' is koinonia, often translated fellowship.", "Paul is quietly setting up the request he will make later in the letter."],
  },
  {
    reference: "Philemon 1:7", book: "Philemon", chapter: 1, verseStart: 7,
    text: "I take great joy and encouragement in your love, because you, brother, have refreshed the hearts of the saints.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul prays that Philemon's partnership in the faith would become effective.",
    after: "Paul says that although he could be bold and order him to do what is right, he appeals instead.",
    theme: "Refreshing other people", keyword: "refreshed",
    facts: ["Paul uses the same word 'refresh' again at the end of the letter as his one request.", "Encouragement is described as something one person can literally give another."],
  },
  {
    reference: "Philemon 1:9", book: "Philemon", chapter: 1, verseStart: 9,
    text: "I prefer to appeal on the basis of love. I, Paul, am now aged, and a prisoner of Christ Jesus as well.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul says that in Christ he could be bold enough to order Philemon to do what is right.",
    after: "Paul makes his appeal on behalf of his child Onesimus.",
    theme: "Persuading rather than commanding", keyword: "appeal",
    facts: ["Paul had the authority to command and deliberately set it aside.", "He wrote the letter as a prisoner, probably from Rome."],
  },
  {
    reference: "Philemon 1:10", book: "Philemon", chapter: 1, verseStart: 10,
    text: "I appeal to you for my child Onesimus, whose father I became while I was in chains.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul says he would rather appeal on the basis of love, as an old man and a prisoner.",
    after: "Paul notes that Onesimus was formerly useless to Philemon but is now useful to them both.",
    theme: "A runaway becomes a son", keyword: "Onesimus",
    facts: ["Onesimus was a runaway slave who had met Paul in prison.", "Paul waits until this verse to name the person he is writing about."],
  },
  {
    reference: "Philemon 1:11", book: "Philemon", chapter: 1, verseStart: 11,
    text: "Formerly he was useless to you, but now he has become useful both to you and to me.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul appeals for Onesimus, whose father he became while in chains.",
    after: "Paul says he is sending him back, and with him his very own heart.",
    theme: "Made useful again", keyword: "useless",
    facts: ["The name Onesimus itself means 'useful' — Paul is making a gentle pun.", "Paul stakes his own reputation on the change in Onesimus."],
  },
  {
    reference: "Philemon 1:16", book: "Philemon", chapter: 1, verseStart: 16,
    text: "no longer as a slave, but better than a slave, as a beloved brother. He is especially beloved to me, but even more so to you, both in person and in the Lord.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul suggests Onesimus was separated from Philemon for a while so he might be restored forever.",
    after: "Paul asks Philemon to welcome Onesimus as he would welcome Paul himself.",
    theme: "Brothers, not owner and property", keyword: "brother",
    facts: ["Paul asks Philemon to receive back the man who ran from him as family.", "This verse became central to later Christian arguments against slavery."],
  },
  {
    reference: "Philemon 1:18", book: "Philemon", chapter: 1, verseStart: 18,
    text: "But if he has wronged you or owes you anything, charge it to my account.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul asks Philemon to welcome Onesimus as he would welcome Paul himself.",
    after: "Paul writes with his own hand that he will repay it, without mentioning what Philemon owes him.",
    theme: "Taking on another's debt", keyword: "account",
    facts: ["Paul offers to personally cover whatever the runaway had cost his master.", "He writes this line in his own handwriting as a legal guarantee."],
  },
  {
    reference: "Philemon 1:21", book: "Philemon", chapter: 1, verseStart: 21,
    text: "Confident of your obedience, I write to you, knowing that you will do even more than I ask.",
    testament: "NT", speaker: "Paul", audience: "Philemon",
    before: "Paul asks Philemon to refresh his heart in Christ.",
    after: "Paul asks Philemon to prepare a guest room for him, hoping to be restored to them.",
    theme: "Trusting someone to do right", keyword: "Confident",
    facts: ["Paul never states outright what 'even more' would look like.", "Many readers hear it as a quiet request to set Onesimus free."],
  },
  // — 2 John —
  {
    reference: "2 John 1:1", book: "2 John", chapter: 1, verseStart: 1,
    text: "The elder, To the chosen lady and her children, whom I love in the truth — and not I alone, but also all who know the truth —",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "Nothing — this is the opening line of the letter.",
    after: "John says this love is because of the truth that abides in them and will be with them forever.",
    theme: "Love grounded in truth", keyword: "elder",
    facts: ["The writer calls himself only 'the elder,' never using his name.", "The 'chosen lady' may be an individual or a local church described affectionately."],
  },
  {
    reference: "2 John 1:2", book: "2 John", chapter: 1, verseStart: 2,
    text: "because of the truth that abides in us and will be with us forever:",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "John greets the chosen lady and her children, whom he loves in the truth.",
    after: "John pronounces grace, mercy, and peace from God the Father and from Jesus Christ.",
    theme: "Truth that stays", keyword: "abides",
    facts: ["The word 'truth' appears five times in the letter's first four verses.", "For John, truth is something that lives in people, not just something they hold."],
  },
  {
    reference: "2 John 1:3", book: "2 John", chapter: 1, verseStart: 3,
    text: "Grace, mercy, and peace from God the Father and from Jesus Christ, the Father's Son, will be with us in truth and love.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "John speaks of the truth that abides in them and will be with them forever.",
    after: "John says he was overjoyed to find some of her children walking in the truth.",
    theme: "Grace, mercy, and peace", keyword: "mercy",
    facts: ["Most New Testament greetings pair grace and peace; John adds mercy.", "He states the blessing as a fact rather than only a wish."],
  },
  {
    reference: "2 John 1:4", book: "2 John", chapter: 1, verseStart: 4,
    text: "I was overjoyed to find some of your children walking in the truth, just as the Father commanded us.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady",
    before: "John pronounces grace, mercy, and peace in truth and love.",
    after: "John asks her to keep the commandment they have had from the beginning, to love one another.",
    theme: "Joy over faithful children", keyword: "overjoyed",
    facts: ["'Walking' in the truth means living it out day by day.", "John's greatest joy in all three of his letters is the same: people walking in truth."],
  },
  {
    reference: "2 John 1:5", book: "2 John", chapter: 1, verseStart: 5,
    text: "And now, dear lady, I am not writing you a new commandment, but one we have had from the beginning: that we love one another.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady",
    before: "John rejoices to find her children walking in the truth.",
    after: "John explains that love means walking according to His commandments.",
    theme: "The old command to love", keyword: "commandment",
    facts: ["John insists the command is not an innovation but the original instruction.", "Jesus gave this command on the night before His crucifixion."],
  },
  {
    reference: "2 John 1:6", book: "2 John", chapter: 1, verseStart: 6,
    text: "And this is love, that we walk according to His commandments. This is the very commandment you have heard from the beginning, that you must walk in love.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "John reminds her of the command they have had from the beginning, to love one another.",
    after: "John warns that many deceivers have gone out into the world.",
    theme: "Love defined by obedience", keyword: "walk",
    facts: ["John defines love by action rather than by feeling.", "The verse loops love and obedience together so neither can stand alone."],
  },
  {
    reference: "2 John 1:7", book: "2 John", chapter: 1, verseStart: 7,
    text: "For many deceivers have gone out into the world, refusing to confess the coming of Jesus Christ in the flesh. Any such person is the deceiver and the antichrist.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "John says that love means walking according to His commandments.",
    after: "John warns them to watch themselves so they do not lose what they have worked for.",
    theme: "Guarding against deception", keyword: "deceivers",
    facts: ["The teaching John opposes denied that Jesus came in a real human body.", "John is one of the very few biblical writers to use the word 'antichrist.'"],
  },
  {
    reference: "2 John 1:8", book: "2 John", chapter: 1, verseStart: 8,
    text: "Watch yourselves, so that you do not lose what we have worked for, but that you may be fully rewarded.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "John warns that many deceivers who deny Christ's coming in the flesh have gone out.",
    after: "John says anyone who runs ahead without remaining in the teaching of Christ does not have God.",
    theme: "Do not lose what was gained", keyword: "rewarded",
    facts: ["The warning is about protecting progress already made.", "John writes as someone who invested years in these believers."],
  },
  {
    reference: "2 John 1:9", book: "2 John", chapter: 1, verseStart: 9,
    text: "Anyone who runs ahead without remaining in the teaching of Christ does not have God. Whoever remains in His teaching has both the Father and the Son.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "John urges them to watch themselves so they are not robbed of their reward.",
    after: "John tells them not to receive into their house anyone who does not bring this teaching.",
    theme: "Staying in the teaching", keyword: "remaining",
    facts: ["'Running ahead' pictures someone advancing past the faith rather than into it.", "The word 'remain' is one of John's favorites across his writings."],
  },
  {
    reference: "2 John 1:12", book: "2 John", chapter: 1, verseStart: 12,
    text: "I have many things to write to you, but I do not want to use paper and ink. Instead, I hope to come to you and speak face to face, so that our joy may be complete.",
    testament: "NT", speaker: "John the elder", audience: "The chosen lady and her children",
    before: "John warns against welcoming those who do not bring the teaching of Christ.",
    after: "The children of her chosen sister send their greetings.",
    theme: "Letters are no substitute for presence", keyword: "paper",
    facts: ["Ancient letters were written on papyrus with ink made from soot and gum.", "John closes 3 John with almost the same sentence."],
  },
  // — 3 John —
  {
    reference: "3 John 1:1", book: "3 John", chapter: 1, verseStart: 1,
    text: "The elder, To the beloved Gaius, whom I love in the truth:",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "Nothing — this is the opening line of the letter.",
    after: "John prays that Gaius may prosper in every way and enjoy good health.",
    theme: "A letter to a friend", keyword: "beloved",
    facts: ["3 John is the shortest book in the New Testament.", "It is one of the few New Testament letters written to a single named individual."],
  },
  {
    reference: "3 John 1:2", book: "3 John", chapter: 1, verseStart: 2,
    text: "Beloved, I pray that in every way you may prosper and enjoy good health, as your soul also prospers.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John greets Gaius, whom he loves in the truth.",
    after: "John says he was overjoyed when the brothers testified about Gaius' devotion to the truth.",
    theme: "Health of body and soul", keyword: "prosper",
    facts: ["The wish for good health was a common opening in ancient letters.", "John measures physical wellbeing against the state of the soul, not the other way around."],
  },
  {
    reference: "3 John 1:3", book: "3 John", chapter: 1, verseStart: 3,
    text: "For I was overjoyed when the brothers came and testified about your devotion to the truth, in which you continue to walk.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John prays that Gaius would prosper as his soul prospers.",
    after: "John says he has no greater joy than to hear his children are walking in the truth.",
    theme: "A good report from travelers", keyword: "testified",
    facts: ["Traveling believers carried news between scattered churches.", "Gaius' reputation reached John through people who had stayed in his home."],
  },
  {
    reference: "3 John 1:4", book: "3 John", chapter: 1, verseStart: 4,
    text: "I have no greater joy than to hear that my children are walking in the truth.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John rejoices at the report of Gaius' faithfulness to the truth.",
    after: "John commends Gaius for acting faithfully toward the brothers, even strangers.",
    theme: "The joy of a mentor", keyword: "greater",
    facts: ["'My children' likely means those John led to faith or taught.", "The verse is often quoted by parents, though John wrote it about spiritual children."],
  },
  {
    reference: "3 John 1:5", book: "3 John", chapter: 1, verseStart: 5,
    text: "Beloved, you are acting faithfully in what you are doing for the brothers, and especially since they are strangers to you.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John says he has no greater joy than hearing his children walk in the truth.",
    after: "John notes that they have testified to the church about Gaius' love.",
    theme: "Hospitality to strangers", keyword: "strangers",
    facts: ["Inns were scarce and unsafe, so traveling teachers depended on hospitality.", "Gaius helped people he had never met before they arrived."],
  },
  {
    reference: "3 John 1:8", book: "3 John", chapter: 1, verseStart: 8,
    text: "Therefore we ought to support such men, so that we may be fellow workers for the truth.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John notes that these travelers went out for the sake of the Name, accepting nothing from the Gentiles.",
    after: "John mentions Diotrephes, who loves to be first and will not accept their instruction.",
    theme: "Supporting the work makes you part of it", keyword: "support",
    facts: ["Hosting a missionary is described as sharing in the missionary's work.", "The traveling teachers refused payment from those they preached to."],
  },
  {
    reference: "3 John 1:9", book: "3 John", chapter: 1, verseStart: 9,
    text: "I have written to the church about this, but Diotrephes, who loves to be first, will not accept our instruction.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John says supporting such workers makes them fellow workers for the truth.",
    after: "John says that if he comes, he will call attention to what Diotrephes is doing.",
    theme: "When a leader loves being first", keyword: "Diotrephes",
    facts: ["Diotrephes is named plainly, an unusual step in a New Testament letter.", "His problem is described as ambition rather than false doctrine."],
  },
  {
    reference: "3 John 1:11", book: "3 John", chapter: 1, verseStart: 11,
    text: "Beloved, do not imitate what is evil, but what is good. The one who does good is of God; the one who does evil has not seen God.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John describes how Diotrephes refuses to welcome the brothers and puts out those who do.",
    after: "John says Demetrius has received a good testimony from everyone.",
    theme: "Choose your examples carefully", keyword: "imitate",
    facts: ["The warning follows immediately after a bad example is named.", "John assumes people become like whoever they copy."],
  },
  {
    reference: "3 John 1:12", book: "3 John", chapter: 1, verseStart: 12,
    text: "Demetrius has received a good testimony from everyone, and from the truth itself. We also testify for him, and you know that our testimony is true.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John urges Gaius to imitate what is good rather than what is evil.",
    after: "John says he has much to write but does not wish to do so with pen and ink.",
    theme: "A reputation worth having", keyword: "testimony",
    facts: ["Demetrius may have been the person who carried this letter to Gaius.", "A written commendation was how strangers were vouched for in the early church."],
  },
  {
    reference: "3 John 1:14", book: "3 John", chapter: 1, verseStart: 14,
    text: "Instead, I hope to see you soon and speak with you face to face. Peace to you. The friends here send you greetings. Greet each of our friends there by name.",
    testament: "NT", speaker: "John the elder", audience: "Gaius",
    before: "John says he has much to write but does not want to do it with pen and ink.",
    after: "The letter ends here.",
    theme: "Greet them by name", keyword: "greetings",
    facts: ["The New Testament's shortest book ends by asking for personal greetings.", "'By name' suggests John knew the individuals in Gaius' circle."],
  },
]
